import React, { useMemo, useContext, useState, useRef, forwardRef, Component, useEffect } from 'react';
import PropTypes from 'prop-types';
import { createUseStyles } from 'react-jss';
import { ChevronDown, ChevronUp } from 'react-feather';
import { useMediaQuery } from 'react-responsive';

const ThemeContext = /*#__PURE__*/React.createContext({});

const responsive = (prop, breakpoints) => {
  if (!prop) {
    return;
  }
  let value = prop;
  if (typeof value !== 'string' && !Array.isArray(value)) {
    value = prop.phone || prop.default;
    const pKeys = Object.keys(prop);
    for (let i = pKeys.length - 1; i > 0; i -= 1) {
      if (breakpoints[pKeys[i]]) {
        value = prop[pKeys[i]];
        break;
      }
    }
  }
  return value;
};

const useResponsive = (prop, breakpoints) => {
  return useMemo(() => responsive(prop, breakpoints), [prop, breakpoints]);
};

const spacing = (theme, space) => {
  if (!space) {
    return;
  }
  if (typeof space === 'string') {
    return theme.spacing[space] || space;
  }
  const processedVal = space.map(val => theme.spacing[val] || val);
  return [processedVal];
};

const radius = (theme, space) => {
  if (!space) {
    return;
  }
  if (typeof space === 'string') {
    return theme.spacing[space] || space;
  }
  if (typeof space === 'boolean') {
    return theme.radius['md'];
  }
  const processedVal = space.map(val => theme.radius[val] || val);
  return [processedVal];
};

const size = (theme, size) => {
  if (!size) {
    return size;
  }
  if (typeof size === 'string') {
    return theme[size] || size;
  }
  const processedVal = size.map(val => theme[val] || val);
  return [processedVal];
};

var styles$u = createUseStyles({
  Box: {
    display: ({
      display
    }) => display,
    flexDirection: ({
      verticalAlign
    }) => verticalAlign ? 'column' : 'row',
    position: 'relative',
    alignItems: ({
      align
    }) => align,
    justifyContent: ({
      justify,
      verticalAlign
    }) => justify || verticalAlign,
    width: ({
      width
    }) => width === 'auto' ? width : '100%',
    minHeight: ({
      height
    }) => height,
    maxWidth: ({
      width
    }) => width,
    backgroundColor: ({
      theme,
      bgColor
    }) => theme.colors[bgColor] || bgColor,
    backgroundImage: ({
      bgImage
    }) => bgImage ? `url(${bgImage})` : '',
    backgroundSize: ({
      bgSize
    }) => bgSize || 'cover',
    backgroundPosition: ({
      bgPos
    }) => bgPos || 'center',
    backgroundRepeat: 'no-repeat',
    borderRadius: ({
      rounded,
      theme
    }) => radius(theme, rounded),
    borderStyle: ({
      bdStyle
    }) => bdStyle,
    borderColor: ({
      bdColor,
      theme
    }) => theme.colors[bdColor] || bdColor,
    borderWidth: ({
      bdWidth,
      theme
    }) => size(theme.border, bdWidth) || 0,
    borderRight: ({
      brColor,
      brWidth,
      theme
    }) => `solid ${size(theme.border, brWidth)} ${theme.colors[brColor]}`,
    borderLeft: ({
      blColor,
      blWidth,
      theme
    }) => `solid ${size(theme.border, blWidth)} ${theme.colors[blColor]}`,
    borderTop: ({
      btColor,
      btWidth,
      theme
    }) => `solid ${size(theme.border, btWidth)} ${theme.colors[btColor]}`,
    borderBottom: ({
      bbColor,
      bbWidth,
      theme
    }) => `solid ${size(theme.border, bbWidth)} ${theme.colors[bbColor]}`,
    padding: ({
      theme,
      padding
    }) => spacing(theme, padding),
    margin: ({
      theme,
      margin
    }) => spacing(theme, margin),
    paddingTop: ({
      theme,
      pt
    }) => spacing(theme, pt),
    paddingRight: ({
      theme,
      pe
    }) => spacing(theme, pe),
    paddingBottom: ({
      theme,
      pb
    }) => spacing(theme, pb),
    paddingLeft: ({
      theme,
      ps
    }) => spacing(theme, ps),
    marginTop: ({
      theme,
      mt
    }) => spacing(theme, mt),
    marginRight: ({
      theme,
      me
    }) => spacing(theme, me),
    marginBottom: ({
      theme,
      mb
    }) => spacing(theme, mb),
    marginLeft: ({
      theme,
      ms
    }) => spacing(theme, ms),
    color: ({
      theme,
      txtColor
    }) => theme.colors[txtColor] || txtColor,
    textAlign: ({
      textAlign
    }) => textAlign
  }
});

const Box = ({
  className,
  children,
  wrapper,
  depth,
  style,
  href,
  target,
  onClick,
  onTouchStart,
  onMouseDown,
  id,
  margin: m,
  padding: p,
  pt: paddingT,
  pe: paddingE,
  pb: paddingB,
  ps: paddingS,
  mt: marginT,
  me: marginE,
  mb: marginB,
  ms: marginS,
  display: propsDisplay,
  bgPos: bgP,
  bgSize: bgS,
  bdStyle,
  bdWidth,
  bdColor,
  rounded,
  height,
  width,
  justify,
  verticalAlign,
  align,
  bgImage,
  bgColor,
  txtColor,
  textAlign,
  ...props
}) => {
  const theme = useContext(ThemeContext);
  const modifiedDisplay = useMemo(() => {
    if (propsDisplay) {
      return propsDisplay;
    }
    return justify || verticalAlign || align ? 'flex' : 'block';
  }, [propsDisplay, justify, verticalAlign, align]);
  const breakpoints = theme.mediaQueries;
  const margin = useResponsive(m, breakpoints);
  const mt = useResponsive(marginT, breakpoints);
  const me = useResponsive(marginE, breakpoints);
  const mb = useResponsive(marginB, breakpoints);
  const ms = useResponsive(marginS, breakpoints);
  const padding = useResponsive(p, breakpoints);
  const pt = useResponsive(paddingT, breakpoints);
  const pe = useResponsive(paddingE, breakpoints);
  const pb = useResponsive(paddingB, breakpoints);
  const ps = useResponsive(paddingS, breakpoints);
  const bgPos = useResponsive(bgP, breakpoints);
  const bgSize = useResponsive(bgS, breakpoints);
  const display = useResponsive(modifiedDisplay, breakpoints);
  const classes = styles$u({
    theme,
    height,
    width,
    bgImage,
    bgColor,
    txtColor,
    margin,
    display,
    padding,
    mt,
    me,
    mb,
    ms,
    pt,
    pe,
    pb,
    ps,
    bgPos,
    bgSize,
    rounded,
    bdStyle,
    bdWidth,
    bdColor,
    justify,
    verticalAlign,
    align,
    textAlign
  });
  let classList = classes.Box;
  if (className) {
    classList = classList + ' ' + className;
  }
  const boxShadow = theme.depth[depth];
  return /*#__PURE__*/React.createElement(props.tag, {
    href: href,
    className: classList,
    target: target,
    onClick: onClick,
    onTouchStart: onTouchStart,
    onMouseDown: onMouseDown,
    style: {
      boxShadow,
      ...style
    },
    id: id
  }, children);
};
Box.propTypes = {
  bgColor: PropTypes.string,
  bgImage: PropTypes.string,
  bgPos: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  bgSize: PropTypes.string,
  txtColor: PropTypes.string,
  align: PropTypes.string,
  textAlign: PropTypes.string,
  justify: PropTypes.string,
  tag: PropTypes.string,
  depth: PropTypes.string,
  href: PropTypes.string,
  target: PropTypes.string,
  display: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  padding: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  rounded: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.bool, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  margin: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  pt: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  pe: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  pb: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  ps: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  mt: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  me: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  mb: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  ms: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  brWidth: PropTypes.string,
  blWidth: PropTypes.string,
  btWidth: PropTypes.string,
  bbWidth: PropTypes.string,
  bdWidth: PropTypes.string,
  className: PropTypes.string,
  style: PropTypes.object,
  wrapper: PropTypes.bool,
  children: PropTypes.node
};
Box.defaultProps = {
  tag: 'div',
  bdStyle: 'solid'
};

let lastId = 0;
const uId = (prefix = 'id') => {
  lastId += 1;
  return `${prefix}${lastId}`;
};

class Transition extends React.Component {
  constructor(props) {
    super(props);
    this.uId = uId('transition');
    this.state = {
      enter: false,
      enterActive: props.in,
      exit: false,
      exitActive: !props.in,
      position: props.in ? 1 : 3,
      lastIn: this.props.in
    };
  }
  componentDidUpdate() {
    const {
      enter,
      enterActive,
      exit,
      exitActive,
      position,
      lastIn
    } = this.state;
    const {
      timeout,
      onExit,
      onEnter,
      onExited,
      onEntered
    } = this.props;
    let newEnter = false;
    let newEnterActive = false;
    let newExit = false;
    let newExitActive = false;
    let newPosition = 0;
    if (lastIn !== this.props.in) {
      newEnter = this.props.in;
      newExit = !this.props.in;
    } else {
      newEnterActive = this.props.in;
      newExitActive = !this.props.in;
    }
    if (newEnterActive) {
      newPosition = 1;
    } else if (newExit) {
      newPosition = 2;
    } else if (newExitActive) {
      newPosition = 3;
    }
    const shouldUpdate = newPosition !== position;
    if (shouldUpdate) {
      if (this.timer) {
        clearTimeout(this.timer);
      }
      this.timer = setTimeout(() => {
        this.setState({
          enter: newEnter,
          enterActive: newEnterActive,
          exit: newExit,
          exitActive: newExitActive,
          position: newPosition,
          lastIn: this.props.in
        }, () => {
          this.setState({}, () => {
            if (newExit && typeof onExit === 'function') {
              onExit();
            } else if (newEnter && typeof onEnter === 'function') {
              onEnter();
            } else if (newExitActive && typeof onExited === 'function') {
              this.timer = setTimeout(() => onExited(), timeout / 3);
            } else if (newEnterActive && typeof onEntered === 'function') {
              this.timer = setTimeout(() => onEntered(), timeout);
            }
          });
        });
      }, newExitActive ? timeout : 0);
    }
  }
  addTransitionClasses() {
    const {
      children,
      className,
      rootClass,
      enterStyles,
      enteredStyles,
      exitStyles,
      exitedStyles
    } = this.props;
    const {
      enter,
      enterActive,
      exit,
      exitActive,
      position
    } = this.state;
    const styles = [enterStyles, enteredStyles, exitStyles, exitedStyles];
    if (Array.isArray(children)) {
      return this.props.children.map((child, index) => {
        return /*#__PURE__*/React.cloneElement(child, {
          key: `${this.uId}-${index}`,
          style: styles[position]
        });
      });
    } else {
      return /*#__PURE__*/React.cloneElement(children, {
        style: styles[position]
      });
    }
  }
  render() {
    return this.addTransitionClasses();
  }
}
Transition.propTypes = {
  children: PropTypes.node,
  in: PropTypes.bool,
  timeout: PropTypes.number,
  enterStyles: PropTypes.object,
  enteredStyles: PropTypes.object,
  exitStyles: PropTypes.object,
  exitedStyles: PropTypes.object
};
Transition.defaultProps = {
  timeout: 300
};

const validateFields = async fields => {
  const fieldNames = Object.keys(fields);
  const errs = {
    errorCount: 0,
    invalidFields: [],
    fields: {}
  };
  let name;
  let field;
  for (let i = 0; i < fieldNames.length; i++) {
    name = fieldNames[i];
    field = fields[name];
    if (typeof field.validate === 'function') {
      const validate = await field.validate();
      errs.errorCount = validate.valid ? errs.errorCount : errs.errorCount + 1;
      errs.fields[name] = validate;
      if (validate.valid === false) {
        errs.invalidFields.push(validate);
      }
    }
  }
  return errs;
};
class ValidationForm extends React.Component {
  constructor(props) {
    super(props);
    this.inputs = {};
    this.cloneChild = this.cloneChild.bind(this);
    this.registerInputs = this.registerInputs.bind(this);
    this.attachToForm = this.attachToForm.bind(this);
    this.detachFromForm = this.detachFromForm.bind(this);
    this.theFields = this.theFields.bind(this);
    this.validate = this.validate.bind(this);
    this.onEnter = this.onEnter.bind(this);
    this.theData = this.theData.bind(this);
    this.state = {
      validated: false,
      valid: false,
      invalid: false
    };
  }
  cloneChild(child, key) {
    if (!child || !child.props) {
      return child;
    }
    if (child.props.name) {
      return /*#__PURE__*/React.cloneElement(child, {
        key,
        attachToForm: this.attachToForm,
        detachFromForm: this.detachFromForm
      });
    } else if (child.props.children) {
      return /*#__PURE__*/React.cloneElement(child, {
        key,
        children: this.registerInputs(child.props.children)
      });
    }
    return child;
  }
  registerInputs(children) {
    let key = 0;
    if (Array.isArray(children)) {
      return children.map(child => {
        key += 1;
        return this.cloneChild(child, key);
      }, this);
    }
    if (typeof children === 'object') {
      key += 1;
      return this.cloneChild(children, key);
    }
    return children;
  }
  attachToForm(component) {
    this.inputs[component.props.name] = component;
  }
  detachFromForm(component) {
    delete this.inputs[component.props.name];
  }
  theFields() {
    return this.inputs;
  }
  theData() {
    const data = {};
    Object.keys(this.inputs).forEach(key => {
      data[key] = this.inputs[key].val();
    });
    return data;
  }
  async validate(e) {
    if (e) {
      e.preventDefault();
    }
    const {
      disabled,
      onValidate,
      onSubmit,
      onError
    } = this.props;
    if (disabled) {
      return;
    }
    if (typeof onValidate === 'function') {
      onValidate();
    }
    const errors = await validateFields(this.inputs);
    this.setState({
      validated: true,
      valid: errors.errorCount === 0
    });
    if (errors.errorCount === 0) {
      if (typeof onSubmit === 'function') {
        onSubmit(this.form, this.theData());
      }
    } else if (typeof onError === 'function') {
      onError(errors);
    }
  }
  onEnter(event) {
    if (event.key === 'Enter') {
      if (typeof this.props.onEnter === 'function') {
        return this.props.onEnter();
      }
      event.preventDefault();
      this.validate();
    }
  }
  render() {
    const {
      action,
      children,
      id
    } = this.props;
    this.state;
    return /*#__PURE__*/React.createElement("form", {
      ref: element => {
        this.form = element;
      },
      onSubmit: this.validate,
      onKeyPress: this.onEnter,
      action: action,
      id: id
    }, this.registerInputs(children));
  }
}
ValidationForm.propTypes = {
  id: PropTypes.string,
  action: PropTypes.string,
  onValidate: PropTypes.func,
  onSubmit: PropTypes.func,
  onEnter: PropTypes.func,
  onError: PropTypes.func,
  disabled: PropTypes.bool,
  children: PropTypes.node
};

const syles = (axis, time, travel) => ({
  back: {
    enter: {
      transition: `all ease ${time}ms`,
      transform: `translate${axis}(-${travel}px)`,
      opacity: 0
    },
    entered: {
      transition: `all ease ${time}ms`,
      transform: `translate${axis}(0)`,
      opacity: 1
    },
    exit: {
      transition: `all ease ${time}ms`,
      transform: `translate${axis}(${travel}px)`,
      opacity: 0
    },
    exited: {
      display: 'none',
      transform: `translate${axis}(${travel}px)`,
      opacity: 0
    }
  },
  forward: {
    enter: {
      transition: `all ease ${time}ms`,
      transform: `translate${axis}(${travel}px)`,
      opacity: 0
    },
    entered: {
      transition: `all ease ${time}ms`,
      transform: `translate${axis}(0)`,
      opacity: 1
    },
    exit: {
      transition: `all ease ${time}ms`,
      transform: `translate${axis}(-${travel}px)`,
      opacity: 0
    },
    exited: {
      display: 'none',
      transform: `translate${axis}(-${travel}px)`,
      opacity: 0
    }
  }
});
class PanelSteps extends React.Component {
  constructor(props) {
    super(props);
    this.onEnter = this.onEnter.bind(this);
    this.onEntered = this.onEntered.bind(this);
    this.onExit = this.onExit.bind(this);
    this.onExited = this.onExited.bind(this);
    this.uId = uId('panel-steps');
    this.state = {
      show: true,
      activeStep: this.props.step
    };
  }
  componentDidMount() {
    const height = this.wrapper.offsetHeight;
    const width = this.wrapper.offsetWidth;
    this.setState({
      height,
      width
    });
  }
  componentDidUpdate() {
    const {
      step
    } = this.props;
    const {
      activeStep
    } = this.state;
    const height = this.wrapper.offsetHeight;
    const width = this.wrapper.offsetWidth;
    if (step !== activeStep) {
      this.setState({
        show: false,
        activeStep: step,
        direction: step > activeStep ? 'forward' : 'back',
        height,
        width
      });
    }
  }
  onEnter() {
    if (this.root) {
      const height = this.wrapper.offsetHeight;
      const width = this.wrapper.offsetWidth;
      this.setState({
        height,
        width,
        transitioning: true
      });
    }
  }
  onEntered() {
    this.setState({
      transitioning: false
    }, () => {
      if (typeof this.props.onEntered === 'function') {
        this.props.onEntered();
      }
    });
  }
  onExit() {
    this.setState({
      transitioning: true
    }, () => {
      if (typeof this.props.onExit === 'function') {
        this.props.onExit();
      }
    });
  }
  onExited() {
    this.setState({
      show: true
    });
  }
  render() {
    const {
      children,
      size,
      color,
      align,
      step,
      className,
      background,
      axis,
      timeout,
      travel
    } = this.props;
    const {
      activeStep,
      show,
      direction,
      height,
      width,
      transitioning
    } = this.state;
    const style = {
      transition: `all ease ${timeout}ms`,
      height: transitioning ? height + 'px' : '',
      width: transitioning ? width + 'px' : ''
    };
    const transitionStyle = syles(axis, timeout, travel)[direction || 'forward'];
    const generateSlides = children => {
      if (children) {
        return children.map((child, index) => {
          return /*#__PURE__*/React.createElement(Transition, {
            key: `panel-steps-${index}`,
            in: activeStep === index && show,
            timeout: timeout,
            enterStyles: transitionStyle.enter,
            enteredStyles: transitionStyle.entered,
            exitStyles: transitionStyle.exit,
            exitedStyles: transitionStyle.exited,
            onEnter: this.onEnter,
            onEntered: this.onEntered,
            onExit: this.onExit,
            onExited: this.onExited
          }, /*#__PURE__*/React.createElement("div", null, child));
        });
      }
      return;
    };
    return /*#__PURE__*/React.createElement("div", {
      style: style,
      ref: element => {
        this.root = element;
      },
      className: className
    }, /*#__PURE__*/React.createElement("div", {
      ref: element => {
        this.wrapper = element;
      }
    }, generateSlides(children)));
  }
}
PanelSteps.propTypes = {
  color: PropTypes.string,
  size: PropTypes.string,
  align: PropTypes.string,
  step: PropTypes.number,
  timeout: PropTypes.number,
  axis: PropTypes.oneOf(['X', 'Y']),
  travel: PropTypes.number,
  className: PropTypes.string,
  background: PropTypes.string,
  children: PropTypes.node,
  onExit: PropTypes.func,
  onExited: PropTypes.func,
  onEnter: PropTypes.func,
  onEntered: PropTypes.func
};
PanelSteps.defaultProps = {
  color: 'primary',
  size: 'md',
  align: '',
  timeout: 300,
  axis: 'X',
  travel: 30,
  step: 0
};

const sizingMap$1 = {
  padding: {
    xs: ['xxs', 'xs'],
    sm: ['xxs', 'sm'],
    md: ['xs', 'md'],
    lg: ['xs', 'lg'],
    xl: ['sm', 'lg'],
    xxl: ['md', 'xl']
  },
  height: {
    xs: 'calc(1.25rem + 2px)',
    sm: 'calc(2.5rem + 2px)',
    md: 'calc(3rem + 2px)',
    lg: 'calc(3.5rem + 2px)',
    xl: 'calc(4rem + 2px)',
    xxl: 'calc(5rem + 2px)'
  }
};
const buttonStyles$2 = {
  display: 'inline-flex',
  justifyContent: 'center',
  position: 'relative',
  alignItems: 'stretch',
  transition: 'background .3s ease, border .3s ease, padding .3s ease',
  cursor: ' pointer',
  overflow: 'hidden',
  textDecoration: 'none',
  border: 'none'
};
var styles$t = createUseStyles({
  '@keyframes Shake': {
    '10%, 90%': {
      transform: 'translate3d(-1px, 0, 0)'
    },
    '20%, 80%': {
      transform: 'translate3d(2px, 0, 0)'
    },
    '30%, 50%, 70%': {
      transform: 'translate3d(-4px, 0, 0)'
    },
    '40%, 60%': {
      transform: 'translate3d(4px, 0, 0)'
    }
  },
  '@keyframes Spin': {
    from: {
      transform: 'rotate(0deg)'
    },
    to: {
      transform: 'rotate(360deg)'
    }
  },
  Button: {
    ...buttonStyles$2,
    background: ({
      theme,
      color,
      gradient
    }) => gradient ? `linear-gradient(to bottom, ${theme.colors[gradient[0]]} 0%, ${theme.colors[gradient[1]]} 100%)` : theme.colors[color] || color,
    color: ({
      theme,
      txtColor
    }) => theme.colors[txtColor] || txtColor,
    width: ({
      full
    }) => full ? '100%' : 'auto',
    minHeight: ({
      size
    }) => sizingMap$1.height[size],
    transform: 'translate3d(0, 0, 0)',
    animation: ({
      shake
    }) => shake ? '$Shake 0.75s cubic-bezier(0.5, 0, 0.5, 1) both' : false,
    '&:hover': {
      background: ({
        theme,
        hoverColor
      }) => theme.colors[hoverColor] || hoverColor,
      color: ({
        theme,
        hoverTxtColor
      }) => theme.colors[hoverTxtColor] || hoverTxtColor
    }
  },
  ButtonOutline: {
    ...buttonStyles$2,
    background: 'none',
    border: '2px solid rgba(0, 0, 0, 0)',
    borderColor: ({
      theme,
      color
    }) => theme.colors[color] || color,
    color: ({
      theme,
      txtColor,
      color
    }) => theme.colors[txtColor] || theme.colors[color] || txtColor || color,
    width: ({
      full
    }) => full ? '100%' : 'auto',
    minHeight: ({
      size
    }) => sizingMap$1.height[size],
    '&:hover': {
      color: ({
        theme,
        hoverColor
      }) => theme.colors[hoverColor] || hoverColor,
      borderColor: ({
        theme,
        hoverColor
      }) => theme.colors[hoverColor] || hoverColor
    }
  },
  ButtonTitle: {
    display: 'inline-flex',
    position: 'relative',
    alignItems: 'center',
    justifyContent: 'center',
    flexWrap: 'wrap',
    margin: '0 auto',
    transform: 'translateY(0)',
    transition: 'transform .3s ease, padding .3s ease',
    zIndex: '5',
    pointerEvents: 'none',
    textDecoration: 'none',
    fontWeight: ({
      weight
    }) => weight,
    whiteSpace: ({
      whiteSpace
    }) => whiteSpace,
    padding: ({
      theme,
      size
    }) => [[theme.spacing[sizingMap$1.padding[size][0]], theme.spacing[sizingMap$1.padding[size][1]]]]
  },
  ButtonType: ({
    theme,
    font
  }) => ({
    ...theme.typography[font],
    lineHeight: 1
  }),
  ButtonIcon: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    '&:first-child': {
      paddingLeft: ({
        theme,
        size
      }) => theme.spacing[sizingMap$1.padding[size][1]]
    },
    '&:last-child': {
      paddingRight: ({
        theme,
        size
      }) => theme.spacing[sizingMap$1.padding[size][1]]
    }
  },
  ButtonIconCenter: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    paddingBottom: ({
      theme,
      size
    }) => theme.spacing.sm,
    '& img': {
      maxWidth: '120px',
      width: '100%'
    }
  },
  ButtonIconItem: {
    display: 'flex'
  },
  ButtonShake: {
    animation: '$Shake 0.5s cubic-bezier(0.5, 0, 0.5, 1) infinite'
  },
  ButtonIconLoading: {
    animation: '$Spin 2s linear infinite'
  },
  ButtonFailed: {
    background: ({
      theme
    }) => theme.danger,
    borderColor: ({
      theme
    }) => theme.danger,
    color: 'white'
  }
});

const Btn = props => {
  const {
    children,
    color,
    hoverColor,
    gradient,
    outline,
    rounded,
    txtColor,
    hoverTxtColor,
    depth,
    align,
    full,
    style,
    iconLeft,
    iconRight,
    iconCenter,
    iconLoading,
    iconFailed,
    iconSuccess,
    iconHover,
    width,
    whiteSpace,
    padding,
    margin,
    mt,
    me,
    mb,
    ms,
    className,
    onClick,
    weight,
    loading: propLoading,
    failed: propFailed,
    success: propSuccess,
    shake: propShake
  } = props;
  useState(false);
  useState(false);
  useState(false);
  useState(false);
  useState(false);
  const [progress, setProgress] = useState();
  const [hover, setHover] = useState(false);
  const theme = useContext(ThemeContext);
  const breakpoints = theme.mediaQueries;
  const font = useResponsive(props.font, breakpoints);
  const size = useResponsive(props.size, breakpoints);
  const iconsLeft = [];
  const hasLeftIcon = iconLeft || iconLoading && propLoading || iconFailed && propFailed || iconSuccess && propSuccess;
  let step = 0;
  let adjColor = color;
  if (propLoading) {
    step = 1;
  } else if (propFailed) {
    step = 2;
    adjColor = 'danger';
  } else if (propSuccess) {
    step = 3;
    adjColor = 'success';
  } else if (iconHover && hover) {
    step = 4;
  }
  const classes = styles$t({
    theme,
    font,
    full,
    size,
    txtColor,
    hoverTxtColor,
    color: adjColor,
    hoverColor,
    gradient,
    align,
    weight,
    whiteSpace,
    shake: propShake
  });
  const boxShadow = theme.depth[depth];
  if (hasLeftIcon) {
    iconsLeft.push( /*#__PURE__*/React.createElement("div", {
      key: "icon",
      className: classes.ButtonIconItem
    }, iconLeft));
    iconsLeft.push( /*#__PURE__*/React.createElement("div", {
      key: "loading_icon",
      className: `${classes.ButtonIconItem} ${classes.ButtonIconLoading}`
    }, iconLoading));
    iconsLeft.push( /*#__PURE__*/React.createElement("div", {
      key: "failed_icon",
      className: classes.ButtonIconItem
    }, iconFailed));
    iconsLeft.push( /*#__PURE__*/React.createElement("div", {
      key: "success_icon",
      className: classes.ButtonIconItem
    }, iconSuccess));
    iconsLeft.push( /*#__PURE__*/React.createElement("div", {
      key: "hover_icon",
      className: classes.ButtonIconItem
    }, iconHover));
  }
  const jssClassNames = props.outline ? classes.ButtonOutline : classes.Button;
  const classNames = props.className ? `${jssClassNames} ${props.className}` : jssClassNames;
  return /*#__PURE__*/React.createElement(Box, {
    tag: props.tag,
    className: classNames,
    onClick: onClick,
    onMouseDown: props.onMouseDown,
    onTouchStart: props.onTouchStart,
    onMouseEnter: () => setHover(true),
    onMouseLeave: () => setHover(false),
    style: {
      width,
      boxShadow,
      ...style
    },
    href: props.href,
    target: props.target,
    padding: padding,
    margin: margin,
    depth: depth,
    mt: mt,
    me: me,
    mb: mb,
    ms: ms,
    rounded: rounded
  }, progress && /*#__PURE__*/React.createElement("span", {
    className: "btn__progress",
    width: progress && `${progress}%`
  }), hasLeftIcon && /*#__PURE__*/React.createElement("span", {
    className: classes.ButtonIcon
  }, /*#__PURE__*/React.createElement(PanelSteps, {
    step: step,
    timeout: 150,
    axis: "Y",
    travel: 16
  }, iconsLeft)), children && /*#__PURE__*/React.createElement("span", {
    className: `${classes.ButtonTitle} ${classes.ButtonType}`
  }, iconCenter && /*#__PURE__*/React.createElement("span", {
    className: classes.ButtonIconCenter
  }, iconCenter), children), iconRight && /*#__PURE__*/React.createElement("span", {
    className: classes.ButtonIcon
  }, iconRight));
};
Btn.propTypes = {
  font: PropTypes.string,
  color: PropTypes.string,
  hoverColor: PropTypes.string,
  gradient: PropTypes.arrayOf(PropTypes.string),
  size: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  txtColor: PropTypes.string,
  depth: PropTypes.string,
  outline: PropTypes.bool,
  align: PropTypes.string,
  weight: PropTypes.string,
  iconLeft: PropTypes.node,
  iconRight: PropTypes.node,
  iconLoading: PropTypes.node,
  iconFailed: PropTypes.node,
  iconSuccess: PropTypes.node,
  iconHover: PropTypes.node,
  rounded: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.bool, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  full: PropTypes.bool,
  width: PropTypes.string,
  whiteSpace: PropTypes.string,
  children: PropTypes.node,
  onClick: PropTypes.func,
  loading: PropTypes.bool,
  failed: PropTypes.bool,
  succeeded: PropTypes.bool,
  shake: PropTypes.bool,
  tag: PropTypes.oneOf(['a', 'div', 'span', 'button', 'input']),
  margin: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  mt: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  me: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  mb: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  ms: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))])
};
Btn.defaultProps = {
  font: 'p',
  color: 'primary',
  size: 'md',
  align: '',
  icon: '',
  iconFont: 'sf',
  iconPrefix: '',
  tag: 'button',
  txtColor: '#fff',
  rounded: true
};

function _extends$d() {
  _extends$d = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$d.apply(this, arguments);
}

const sizingMap = {
  padding: {
    xs: 'xs',
    sm: 'xs',
    md: 'xs',
    lg: 'sm',
    xl: 'md'
  },
  size: {
    xs: '1rem',
    sm: '1.5rem',
    md: '2rem',
    lg: '2.5rem',
    xl: '3rem',
    xxl: '4rem'
  }
};
const buttonStyles$1 = {
  display: 'inline-flex',
  justifyContent: 'center',
  position: 'relative',
  alignItems: 'center',
  transition: 'background .3s ease, border .3s ease, padding .3s ease',
  border: '2px solid rgba(0, 0, 0, 0)',
  cursor: ' pointer',
  overflow: 'hidden',
  borderRadius: '100%',
  flex: 'none'
};
var styles$s = createUseStyles({
  Button: {
    ...buttonStyles$1,
    borderRadius: '100%',
    background: ({
      theme,
      color
    }) => theme.colors[color],
    color: ({
      theme,
      txtColor
    }) => theme.colors[txtColor] || 'white',
    width: ({
      size
    }) => sizingMap.size[size],
    height: ({
      size
    }) => sizingMap.size[size]
  },
  ButtonOutline: {
    ...buttonStyles$1,
    background: 'none',
    borderColor: ({
      theme,
      color
    }) => theme.colors[color],
    color: ({
      theme,
      color
    }) => theme.colors[color],
    width: ({
      size
    }) => sizingMap.size[size],
    height: ({
      size
    }) => sizingMap.size[size]
  }
});

const BtnDot = function BtnDot({
  children,
  outline,
  icon,
  tag,
  className,
  onClick,
  color,
  txtColor,
  size,
  ...props
}) {
  const theme = useContext(ThemeContext);
  const classes = styles$s({
    theme,
    color,
    txtColor,
    size,
    outline
  });
  let classList = outline ? classes.ButtonOutline : classes.Button;
  classList = classList + (className ? ' ' + className : '');
  return /*#__PURE__*/React.createElement(Box, _extends$d({
    tag: tag,
    type: "button",
    className: classList,
    onClick: onClick
  }, props), icon && /*#__PURE__*/React.createElement(React.Fragment, null, icon), children);
};
BtnDot.propTypes = {
  color: PropTypes.string,
  outline: PropTypes.bool,
  size: PropTypes.string,
  align: PropTypes.string,
  icon: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  className: PropTypes.string,
  tag: PropTypes.string,
  margin: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  children: PropTypes.node,
  onClick: PropTypes.func
};
BtnDot.defaultProps = {
  color: 'primary',
  size: 'md',
  align: '',
  icon: '',
  iconFont: 'sf',
  iconPrefix: '',
  tag: 'button'
};

const buttonStyles = {
  display: 'inline-flex',
  position: 'relative',
  cursor: ' pointer',
  overflow: 'hidden',
  border: 'none',
  background: 'none',
  textDecoration: 'none'
};
var styles$r = createUseStyles({
  Button: {
    ...buttonStyles,
    color: ({
      theme,
      txtColor
    }) => theme.colors[txtColor] || txtColor
  },
  ButtonType: ({
    theme,
    font,
    noWrap
  }) => ({
    ...theme.typography[font],
    lineHeight: 1,
    whiteSpace: noWrap ? 'nowrap' : null
  })
});

const BtnText = ({
  children,
  font,
  txtColor,
  align,
  onClick,
  margin,
  padding,
  pt,
  pe,
  pb,
  ps,
  mt,
  me,
  mb,
  ms,
  noWrap,
  ...props
}) => {
  const theme = useContext(ThemeContext);
  const classes = styles$r({
    theme,
    font,
    txtColor,
    align,
    noWrap
  });
  return /*#__PURE__*/React.createElement(Box, _extends$d({
    className: classes.Button,
    onClick: onClick,
    onMouseDown: props.onMouseDown,
    onTouchStart: props.onTouchStart,
    href: props.href,
    target: props.target,
    padding: padding,
    margin: margin,
    pt: pt,
    pe: pe,
    pb: pb,
    ps: ps,
    mt: mt,
    me: me,
    mb: mb,
    ms: ms
  }, props), children && /*#__PURE__*/React.createElement("span", {
    className: classes.ButtonType
  }, children));
};
BtnText.propTypes = {
  font: PropTypes.string,
  color: PropTypes.string,
  txtColor: PropTypes.string,
  size: PropTypes.string,
  outline: PropTypes.bool,
  align: PropTypes.string,
  iconLeft: PropTypes.node,
  iconRight: PropTypes.node,
  iconLoading: PropTypes.node,
  iconFailed: PropTypes.node,
  iconSuccess: PropTypes.node,
  iconHover: PropTypes.node,
  rounded: PropTypes.bool,
  full: PropTypes.bool,
  width: PropTypes.string,
  children: PropTypes.node,
  onClick: PropTypes.func,
  noWrap: PropTypes.bool,
  tag: PropTypes.oneOf(['a', 'div', 'span', 'button', 'input'])
};
BtnText.defaultProps = {
  font: 'p',
  size: 'md',
  tag: 'button',
  txtColor: 'inherit',
  padding: 0,
  margin: 0,
  noWrap: true,
  width: 'auto'
};

const padding$3 = {
  xs: 'sm',
  sm: 'sm',
  md: 'sm',
  lg: 'sm',
  xl: 'md',
  xxl: 'lg'
};
const type$1 = {
  xs: 'control',
  sm: 'control',
  md: 'p',
  lg: 'h4',
  xl: 'h3',
  xxl: 'h2'
};
var styles$q = createUseStyles({
  Container: {
    display: 'block',
    position: 'relative',
    transition: 'all 0.3s ease',
    height: 0
  },
  Tooltip: {
    display: 'flex',
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    transition: 'all 0.3s ease',
    marginTop: ({
      tip
    }) => tip ? '-0.5rem' : 0,
    borderRadius: ({
      theme,
      size,
      rounded
    }) => rounded ? theme.radius[size] : 0,
    padding: ({
      theme,
      size
    }) => theme.spacing[padding$3[size]],
    backgroundColor: ({
      theme,
      color
    }) => theme.colors[color],
    color: ({
      theme,
      txtColor
    }) => theme.colors[txtColor],
    '&::before': {
      content: ({
        tip
      }) => tip ? '" "' : 'none',
      position: 'absolute',
      top: ({
        align
      }) => align.includes('t') ? 'calc(100% - 0.5rem)' : '-0.5rem',
      left: ({
        align
      }) => {
        if (align.includes('c')) {
          return 'calc(50% - 0.5rem)';
        }
        return align.includes('r') ? '3rem' : 'auto';
      },
      right: ({
        align
      }) => align.includes('l') ? '3rem' : 'auto',
      width: '1rem',
      height: '1rem',
      transform: 'rotate(45deg)',
      backgroundColor: ({
        theme,
        color
      }) => theme.colors[color]
    }
  },
  Type: ({
    theme,
    size,
    font
  }) => ({
    ...theme.typography[font || type$1[size]]
  })
});

const allowedSizes = ['xs', 'sm', 'md', 'lg', 'xl', 'xxl'];
const ToolTip = ({
  children,
  ...props
}) => {
  const [height, setHeight] = useState(0);
  const tooltip = useRef();
  const onEnter = () => {
    setHeight(tooltip.current.offsetHeight);
  };
  const onExit = () => {
    setHeight(0);
  };
  const theme = useContext(ThemeContext);
  const classes = styles$q({
    theme,
    ...props
  });
  const transitionStyles = {
    enter: {
      opacity: 0,
      marginTop: props.tip ? 0 : -16
    },
    entered: {
      opacity: 1,
      marginTop: props.tip ? 8 : 0
    },
    exit: {
      opacity: 0,
      marginTop: -16
    },
    exited: {
      opacity: 0,
      display: 'none'
    }
  };
  return /*#__PURE__*/React.createElement("div", {
    className: classes.Container,
    style: {
      height: `${height}px`
    }
  }, /*#__PURE__*/React.createElement(Transition, {
    in: props.in,
    onEnter: onEnter,
    onExit: onExit,
    enterStyles: transitionStyles.enter,
    enteredStyles: transitionStyles.entered,
    exitStyles: transitionStyles.exit,
    exitedStyles: transitionStyles.exited
  }, /*#__PURE__*/React.createElement("div", {
    className: `${classes.Tooltip} ${classes.Type}`,
    ref: tooltip
  }, children)));
};
ToolTip.propTypes = {
  size: PropTypes.oneOf(allowedSizes),
  color: PropTypes.string,
  font: PropTypes.string,
  txtColor: PropTypes.string,
  rounded: PropTypes.bool,
  align: PropTypes.oneOf(['tl', 'tc', 'tr', 'br', 'bc', 'bl']),
  tip: PropTypes.bool,
  in: PropTypes.bool,
  timeout: PropTypes.number,
  children: PropTypes.node
};
ToolTip.defaultProps = {
  align: 'br',
  color: 'neutral900',
  txtColor: 'white',
  size: 'md',
  tip: true,
  timeout: 300,
  rounded: true
};

const height$4 = {
  xs: '1.25rem',
  sm: '2.5rem',
  md: '3rem',
  lg: '3.5rem',
  xl: '4rem',
  xxl: '5rem'
};
var styles$p = createUseStyles({
  Checkbox: {
    display: 'inline-flex',
    position: 'relative'
  },
  Element: {
    position: 'absolute',
    top: 0,
    left: 0,
    opacity: 0
  },
  Type: ({
    theme,
    font
  }) => ({
    ...theme.typography[font]
  }),
  Icon: {
    color: ({
      theme,
      color
    }) => theme.colors[color],
    position: 'relative',
    display: 'inline-flex',
    border: '2px solid',
    borderColor: ({
      theme,
      color,
      focus,
      invalid
    }) => invalid ? theme.colors.danger : focus ? theme.colors.primary : '',
    cursor: 'pointer',
    height: ({
      size
    }) => height$4[size],
    width: ({
      size
    }) => height$4[size],
    borderRadius: ({
      theme,
      rounded,
      size
    }) => rounded ? theme.radius[size] : 0,
    '&::after': {
      content: '" "',
      position: 'absolute',
      right: '20%',
      top: '35%',
      height: '50%',
      width: '30%',
      borderBottom: '2px solid',
      borderRight: '2px solid',
      transformOrigin: 'top right',
      opacity: 0,
      transition: 'all 0.3s ease'
    }
  },
  IconChecked: {
    extend: 'Icon',
    height: ({
      size
    }) => height$4[size],
    width: ({
      size
    }) => height$4[size],
    color: ({
      theme,
      color
    }) => theme.colors[color],
    '&::after': {
      transform: 'rotate(45deg)',
      opacity: 1
    }
  }
});

/*
 *  @mixin
 *  @description Validates form.
 *  @returns {object} { valid: {boolean}, message: {string}, el: {object} self element}
*/
const validate = async function validate(element, value, rules) {
  if (typeof rules !== 'object') {
    return {
      valid: true
    };
  }
  let response;
  const methods = {
    required: (rule, msg) => ({
      valid: Boolean(value),
      message: msg || 'This field is required',
      element
    }),
    number: (rule, msg) => {
      const re = /^[0-9.]+$/;
      return {
        valid: re.test(value),
        message: msg || 'Please provide a valid number',
        element
      };
    },
    email: (rule, msg) => {
      const re = /^[a-zA-Z0-9.!#$%&’*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/;
      return {
        valid: re.test(value),
        message: msg || 'Please input a valid email address',
        element
      };
    },
    checked: (rule, msg) => ({
      valid: value,
      message: msg || 'This field is required'
    }),
    phone: (rule, msg) => {
      const usphone = /^\D?(\d{3})\D?\D?(\d{3})\D?(\d{4})$/;
      return {
        valid: value.match(usphone),
        message: msg || 'Please provide a valid phone number',
        element
      };
    },
    ukphonenumber: (rule, msg) => {
      const re = /^0[\d]{9,10}$/;
      return {
        valid: re.test(value),
        message: msg || 'Please provide a valid phone number',
        element
      };
    },
    ssn: (rule, msg) => {
      const pattern = /^\d{3}-?\d{2}-?\d{4}$/;
      return {
        valid: value.match(pattern),
        message: msg || 'Please provide a valid social security number',
        element
      };
    },
    fileExtension: (rule, msg) => {
      const alphaExp = /.*\.(gif)|(jpeg)|(jpg)|(png)$/;
      return {
        valid: value.toLowerCase().match(alphaExp),
        message: msg || 'Not a valid file format, please upload a gif, jpg, or png',
        element
      };
    },
    date(rule, msg) {
      let message = msg;
      const format = (rule.format || 'MM-DD-YYYY').toUpperCase();

      /*
       *   @description Takes a date format string and returns an object with the indexes of each date letter
       *   @param {string} fmt a date format string
       *   @returns {object} {d: {array}, m: {array}, y: {array}, delimiter: {string}}
      */
      function parseDateFormat(fmt) {
        const d = [];
        const m = [];
        const y = [];
        let delimiter;
        for (let i = 0; i < fmt.length; i += 1) {
          switch (fmt[i]) {
            case 'D':
              d.push(i);
              break;
            case 'M':
              m.push(i);
              break;
            case 'Y':
              y.push(i);
              break;
            default:
              delimiter = fmt[i];
          }
        }
        return {
          d,
          m,
          y,
          delimiter
        };
      }

      /*
       *  @description Checks the day to see if it is a valid day, including leap years.
       *  @param {number} d the day
       *  @param {number} m the month
       *  @param {number} y the year
       *  @returns {boolean}
      */
      function isValidDay(d, m, y) {
        let days;
        switch (m) {
          case 2:
            days = y % 4 === 0 && y % 100 || y % 400 === 0 ? 29 : 28;
            break;
          case 11:
            days = 30;
            break;
          default:
            days = 31;
            break;
        }
        return d <= days;
      }
      function isValidRange(date, min, max) {
        const maxDate = max ? date <= new Date(max) : true;
        const minDate = min ? date >= new Date(min) : true;
        return maxDate && minDate;
      }

      // Fail if field value length is not equal to format length
      if (format.length !== value.length) {
        return {
          valid: false,
          message: message || `Please input a valid date (${format})`,
          element
        };
      }

      // Parse date format
      const parsedFormat = parseDateFormat(format);

      // Extract day, month, and year from value based on parsed date format positions
      const day = value.substring(parsedFormat.d[0], parsedFormat.d[parsedFormat.d.length - 1] + 1);
      const month = value.substring(parsedFormat.m[0], parsedFormat.m[parsedFormat.m.length - 1] + 1);
      const year = value.substring(parsedFormat.y[0], parsedFormat.y[parsedFormat.y.length - 1] + 1);

      // Validate day, month, and year
      const validDay = !Number.isNaN(day) && day.length >= parsedFormat.d.length && day >= 1 && day <= 31 && isValidDay(day, month, year);
      const validMonth = !Number.isNaN(month) && month.length >= parsedFormat.m.length && month >= 1 && month <= 12;
      const validYear = !Number.isNaN(year) && year.length > 1 && year.length <= 4 && year.length >= parsedFormat.y.length;
      const validRange = isValidRange(new Date(year, month - 1, day), rule.min, rule.max);
      if (!validRange) {
        message = 'Invalid date range';
      }
      if (!validDay) {
        message = 'Please input a valid day';
      }
      if (!validMonth) {
        message = 'Please input a valid month';
      }
      if (!validYear) {
        message = 'Please input a valid year';
      }
      return {
        valid: validDay && validMonth && validYear && validRange,
        message: message || 'Please input a valid date',
        date: {
          year,
          month,
          day
        },
        element
      };
    },
    name: (rule, msg) => {
      const re = /^[a-z ,.'-]+$/i;
      return {
        valid: re.test(value),
        message: msg || 'Please provide a valid name',
        element
      };
    },
    password: (rule, msg) => {
      const re = /^(?=.*\d).{8,20}$/;
      return {
        valid: re.test(value),
        message: msg || 'Please provide a password of at least 8 characters',
        element
      };
    },
    url: (rule, msg) => {
      const re = /(ftp|http|https):\/\/(\w+:{0,1}\w*@)?(\S+)(:[0-9]+)?(\/|\/([\w#!:.?+=&%@!\-/]))?/;
      return {
        valid: re.test(value),
        message: msg || 'Please provide a valid url (http://yoursite.com)',
        element
      };
    },
    addresscharacters: (rule, msg) => {
      // Valid characters OR empty. If field cannot be empty, add "required" rule.
      const re = /(^$|^[a-zA-Z0-9#]+[a-zA-Z0-9#.\-\s]*$)/i;
      return {
        valid: re.test(value),
        message: msg || 'Valid address characters only, please.',
        element
      };
    },
    creditcardnumber: (rule, msg) => {
      const visa = /^4/.test(value);
      const mc = /^(5[1-5]\d{14})|(2(?:22[1-9]|2[3-9][0-9]|[3-6][0-9]{2}|7[0-1][0-9]|720)\d{12})$/.test(value);
      return {
        valid: visa || mc,
        message: msg || 'We only accept Visa and Mastercard.',
        element
      };
    },
    ukpostcode: (rule, msg) => {
      const re = /^[a-z0-9]{2,4} [a-z0-9]{3}$/i;
      return {
        valid: re.test(value),
        message: msg || 'Must be a valid UK postcode.',
        element
      };
    },
    uspostcode: (rule, msg) => {
      const re = /^\d{5}(?:[-\s]\d{4})?$/;
      return {
        valid: re.test(value),
        message: msg || 'Please provide a valid 5 digit zip code.',
        element
      };
    },
    minmax: (rule, msg) => {
      const min = Number(rule.min);
      const max = Number(rule.max);
      return {
        valid: rule.max ? min <= max : true,
        message: msg || 'The max value must be equal to or more than the min',
        element
      };
    },
    lte: (rule, msg) => {
      return {
        valid: value <= Number(rule),
        message: msg || `The value must be equal to or less than ${rule}`,
        element
      };
    },
    gte: (rule, msg) => {
      return {
        valid: value >= Number(rule),
        message: msg || `The value must be equal to or greater than ${rule}`,
        element
      };
    },
    maxlength: (rule, msg) => ({
      valid: value.length <= rule,
      message: msg || `This field must be less than ${rule} characters`,
      element
    }),
    minlength: (rule, msg) => ({
      valid: value.length >= rule,
      message: msg || `This field must be at least ${rule} characters`,
      element
    }),
    equalTo: (rule, msg) => {
      let el;
      let match;
      if (typeof rule === 'string') {
        el = document.querySelector(`[name="${rule}"]`);
      } else if (typeof rule === 'object') {
        el = rule;
      } else {
        match = false;
      }
      match = el ? el.value : false;
      return {
        valid: value === match,
        message: msg || 'This field must match',
        element
      };
    },
    notEqualTo: (rule, msg) => {
      let el;
      let match;
      if (typeof rule === 'string') {
        el = document.querySelector(`[name="${rule}"]`);
      } else if (typeof rule === 'object') {
        el = rule;
      } else {
        match = false;
      }
      match = el ? el.value : false;
      return {
        valid: value !== match,
        message: msg || 'This field must not match',
        element
      };
    }
  };

  // If field has a value or the field is required, excecute validation rules
  if (rules && value !== '' || rules.required) {
    const ruleKeys = Object.keys(rules);
    for (let i = 0; i < ruleKeys.length; i += 1) {
      const rule = ruleKeys[i];
      if (typeof rules[rule] === 'function') {
        response = await rules[rule](value, element);
      } else if (typeof methods[rule] === 'function') {
        response = methods[rule](rules[rule], rules[rule].message);
      }
      if (!response.valid) {
        break;
      }
    }
  }
  return response;
};

const Element$2 = /*#__PURE__*/forwardRef((props, ref) => {
  const {
    id,
    name,
    value,
    checked,
    tabIndex,
    onChange,
    onFocus,
    onBlur
  } = props;
  const theme = useContext(ThemeContext);
  const classes = styles$p({
    theme,
    ...props
  });
  return /*#__PURE__*/React.createElement("label", {
    className: classes.Checkbox
  }, /*#__PURE__*/React.createElement("input", {
    ref: ref,
    className: classes.Element,
    id: id,
    type: "checkbox",
    name: name,
    value: value,
    tabIndex: tabIndex,
    onChange: onChange,
    onFocus: onFocus,
    onBlur: onBlur,
    checked: checked
  }), /*#__PURE__*/React.createElement("div", {
    className: checked ? classes.IconChecked : classes.Icon
  }));
});
Element$2.displayName = 'CheckboxControl';
class Checkbox extends React.Component {
  static displayName = 'Checkbox';
  constructor(props) {
    super(props);
    this.theField = this.theField.bind(this);
    this.val = this.val.bind(this);
    this.validate = this.validate.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.handleBlur = this.handleBlur.bind(this);
    this.handleFocus = this.handleFocus.bind(this);
    this.state = {
      validated: true,
      checked: props.checked
    };
  }
  componentDidMount() {
    const {
      attachToForm
    } = this.props;
    if (typeof attachToForm === 'function') {
      attachToForm(this);
    }
  }
  componentWillUnmount() {
    const {
      detachFromForm
    } = this.props;
    if (typeof detachFromForm === 'function') {
      detachFromForm(this);
    }
  }
  componentDidUpdate(prevProps) {
    // Typical usage (don't forget to compare props):
    if (this.props.checked !== prevProps.checked) {
      this.setState({
        checked: this.props.checked
      });
    }
  }
  theField() {
    return this.field;
  }
  val() {
    const {
      checked
    } = this.props;
    return checked;
  }
  validate() {
    const {
      checked
    } = this.props;
    const {
      rules,
      onValidate
    } = this.props;
    const valid = validate(this.field, checked, rules);
    this.setState({
      validated: true
    });
    if (typeof onValidate === 'function') {
      onValidate();
    }
    return valid;
  }
  handleChange(e) {
    const {
      onChange
    } = this.props;
    const {
      checked
    } = e.target;
    this.setState({
      checked
    });
    if (typeof onChange === 'function') {
      onChange(e, checked);
    }
  }
  handleBlur(e) {
    const {
      onBlur
    } = this.props;
    if (typeof onBlur === 'function') {
      onBlur(e);
    }
  }
  handleFocus(e) {
    const {
      onFocus
    } = this.props;
    if (typeof onFocus === 'function') {
      onFocus(e);
    }
  }
  render() {
    return /*#__PURE__*/React.createElement(Element$2, _extends$d({
      ref: field => {
        this.field = field;
      },
      onChange: this.handleChange,
      onFocus: this.handleFocus,
      onBlur: this.handleBlur,
      checked: this.state.checked
    }, this.props));
  }
}
Checkbox.propTypes = {
  attachToForm: PropTypes.func,
  detachFromForm: PropTypes.func,
  onChange: PropTypes.func,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  onValidate: PropTypes.func,
  rules: PropTypes.object,
  id: PropTypes.string,
  value: PropTypes.string,
  name: PropTypes.string,
  size: PropTypes.string,
  tabIndex: PropTypes.number
};
Checkbox.defaultProps = {
  size: 'md'
};

class HiddenInput extends React.Component {
  static displayName = 'HiddenInput';
  constructor(props) {
    super(props);
    this.theField = this.theField.bind(this);
    this.val = this.val.bind(this);
    this.validate = this.validate.bind(this);
    this.state = {
      validated: true
    };
  }
  componentDidMount() {
    const {
      attachToForm
    } = this.props;
    if (typeof attachToForm === 'function') {
      attachToForm(this);
    }
  }
  componentWillUnmount() {
    const {
      detachFromForm
    } = this.props;
    if (typeof detachFromForm === 'function') {
      detachFromForm(this);
    }
  }
  theField() {
    return this.field;
  }
  val() {
    const {
      value
    } = this.field;
    return value;
  }
  async validate() {
    const {
      value
    } = this.field;
    const {
      rules,
      onValidate
    } = this.props;
    const valid = await validate(this.field, value, rules);
    this.setState({
      validated: true
    });
    if (typeof onValidate === 'function') {
      onValidate();
    }
    return valid;
  }
  render() {
    const {
      className,
      type,
      id,
      name,
      size,
      value
    } = this.props;
    return /*#__PURE__*/React.createElement("input", {
      ref: field => {
        this.field = field;
      },
      className: "hidden-input",
      id: id,
      type: "hidden",
      name: name,
      value: value
    });
  }
}
HiddenInput.propTypes = {
  attachToForm: PropTypes.func,
  detachFromForm: PropTypes.func,
  onValidate: PropTypes.func,
  rules: PropTypes.object,
  id: PropTypes.string,
  value: PropTypes.string,
  className: PropTypes.string,
  name: PropTypes.string,
  size: PropTypes.string
};
HiddenInput.defaultProps = {
  value: ''
};

const padding$2 = {
  xs: 'sm',
  sm: 'sm',
  md: 'sm',
  lg: 'md',
  xl: 'lg',
  xxl: 'lg'
};
const height$3 = {
  xs: '1.25rem',
  sm: '2.5rem',
  md: '3rem',
  lg: '3.5rem',
  xl: '4rem',
  xxl: '5rem'
};
var styles$o = createUseStyles({
  Input: {
    display: 'inline-flex',
    width: ({
      width
    }) => width,
    height: ({
      size
    }) => height$3[size],
    padding: ({
      theme,
      size
    }) => [[0, theme.spacing[padding$2[size]]]],
    color: ({
      theme,
      txtColor
    }) => theme.colors[txtColor],
    background: 'none',
    border: 'none',
    '&:focus': {
      outline: 'none'
    }
  },
  Type: ({
    theme,
    font
  }) => ({
    ...theme.typography[font]
  })
});

const functionKeys$1 = [8, 9, 13, 27, 37, 38, 39, 40, 91];
const numKeys$1 = [48, 49, 50, 51, 52, 53, 54, 55, 56, 57, 96, 97, 98, 99, 100, 101, 102, 103, 104, 105, 110, 190];
const limitInput$1 = (e, maxlength) => {
  const el = e.target;
  const {
    value
  } = el;
  const selected = window ? window.getSelection() : undefined;
  const {
    metaKey
  } = e;
  function getPreviousInput(field) {
    let index;
    const elements = field.parentNode.children;
    for (let i = 0; i < elements.length; i += 1) {
      if (field === elements[i]) {
        index = i - 1;
        break;
      }
    }
    try {
      while (elements[index].getAttribute('type') === 'hidden' || elements[index].tagName !== 'INPUT' && elements[index].tagName !== 'TEXTAREA' && elements[index].tagName !== 'SELECT') {
        index -= 1;
      }
      return elements[index];
    } catch (err) {
      return field;
    }
  }

  // If control/command key is pressed, continue as normal
  if (metaKey) {
    return;
  }

  // If backspace is pressed on an empty field focus previous field
  if (value === '' && e.which === 8) {
    getPreviousInput(el).focus();
  }

  // If a function key is pressed, continue as normal
  if (functionKeys$1.indexOf(e.which) >= 0) {
    return;
  }

  // If field type is tel, only allow number keys
  if (el.getAttribute('type') === 'tel' && numKeys$1.includes(e.which) === false) {
    e.preventDefault();
    return;
  }

  // If maxlength is reached prevent further input
  if (maxlength && value.length === maxlength && selected.toString() === '') {
    e.preventDefault();
  }
};
const InputElement = /*#__PURE__*/forwardRef(({
  name,
  placeholder,
  id,
  type,
  value,
  tabIndex,
  onChange,
  onInput,
  onFocus,
  onBlur,
  onKeyDown,
  inputSize,
  onPaste,
  font: fnt,
  size: sz,
  ...props
}, ref) => {
  const theme = useContext(ThemeContext);
  const size = useResponsive(sz, theme.breakpoints);
  const font = useResponsive(fnt, theme.breakpoints);
  const classes = styles$o({
    theme,
    font,
    size,
    ...props
  });
  const onClick = e => {
    e.stopPropagation();
  };
  return /*#__PURE__*/React.createElement("input", {
    ref: ref,
    id: id,
    className: `${classes.Input} ${classes.Type}`,
    type: type,
    name: name,
    value: value,
    onKeyDown: onKeyDown,
    placeholder: placeholder,
    tabIndex: tabIndex,
    onChange: onChange,
    onInput: onInput,
    onFocus: onFocus,
    onBlur: onBlur,
    onClick: onClick,
    onPaste: onPaste,
    size: inputSize
  });
});
InputElement.displayName = 'InputControl';
class Input extends React.Component {
  static displayName = 'Input';
  constructor(props) {
    super(props);
    this.theField = this.theField.bind(this);
    this.val = this.val.bind(this);
    this.validate = this.validate.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.handleInput = this.handleInput.bind(this);
    this.handleBlur = this.handleBlur.bind(this);
    this.handleFocus = this.handleFocus.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.state = {
      validated: false,
      value: props.value,
      prepop: props.value
    };
  }
  componentDidMount() {
    const {
      attachToForm
    } = this.props;
    if (typeof attachToForm === 'function') {
      attachToForm(this);
    }
  }
  componentWillUnmount() {
    const {
      detachFromForm
    } = this.props;
    if (typeof detachFromForm === 'function') {
      detachFromForm(this);
    }
  }
  componentDidUpdate(prevProps) {
    // Typical usage (don't forget to compare props):
    if (this.props.value !== prevProps.value) {
      this.setState({
        value: this.props.value
      });
    }
  }
  theField() {
    return this.field;
  }
  val() {
    const {
      value
    } = this.state;
    return value;
  }
  async validate() {
    const {
      value
    } = this.field;
    const {
      rules,
      onValidate
    } = this.props;
    const valid = await validate(this.field, value, rules);
    this.setState({
      valid: valid.valid,
      invalid: !valid.valid,
      invalidMessage: valid.message,
      validated: true
    }, () => {
      if (typeof onValidate === 'function') {
        setTimeout(() => {
          onValidate(valid);
        }, 10);
      }
    });
    return valid;
  }
  handleKeyDown(e) {
    const {
      onKeyDown,
      maxlength
    } = this.props;
    limitInput$1(e, maxlength);
    if (typeof onKeyDown === 'function') {
      onKeyDown(e);
    }
  }
  handleChange(e) {
    const {
      value
    } = e.target;
    this.setState({
      value
    });
  }
  handleInput(e) {
    const el = e.target;
    const {
      value
    } = el;
    const formEl = el.form;
    const {
      validated
    } = this.state;
    const {
      onInput,
      autotab,
      maxlength,
      controlled
    } = this.props;
    function getNextInput(field, form) {
      let index;
      const elements = form ? form.elements : field.parentNode.children;
      for (let i = 0; i < elements.length; i += 1) {
        if (field === elements[i]) {
          index = i + 1;
          break;
        }
      }
      try {
        while (elements[index].getAttribute('type') === 'hidden' || elements[index].tagName !== 'INPUT' && elements[index].tagName !== 'TEXTAREA' && elements[index].tagName !== 'SELECT') {
          index += 1;
        }
        return elements[index];
      } catch (err) {
        return field;
      }
    }

    // If field has autotab attr, maxlength is reached, and a non function key is pressed, focus next field
    if (autotab && maxlength && value.length >= maxlength && functionKeys$1.indexOf(e.which) === -1) {
      getNextInput(el, formEl).focus();
    }

    // If the field has been validated, validate on input
    if (validated) {
      this.validate();
    }
    if (!controlled) {
      this.setState({
        value
      });
    }
    if (typeof onInput === 'function') {
      onInput(e);
    }
  }
  handleBlur(e) {
    const {
      onBlur
    } = this.props;
    if (typeof onBlur === 'function') {
      onBlur(e);
    }
  }
  handleFocus(e) {
    const {
      onFocus
    } = this.props;
    if (typeof onFocus === 'function') {
      onFocus(e);
    }
  }
  render() {
    const {
      type,
      id,
      name,
      size,
      font,
      placeholder,
      style,
      tabIndex,
      controlled,
      inputSize,
      width,
      txtColor,
      onPaste
    } = this.props;
    const {
      value
    } = controlled ? this.props : this.state;
    return /*#__PURE__*/React.createElement(InputElement, {
      ref: field => {
        this.field = field;
      },
      id: id,
      type: type,
      font: font,
      size: size,
      txtColor: txtColor,
      name: name,
      value: value,
      style: style,
      onKeyDown: this.handleKeyDown,
      placeholder: placeholder,
      tabIndex: tabIndex,
      onChange: this.handleChange,
      onInput: this.handleInput,
      onFocus: this.handleFocus,
      onBlur: this.handleBlur,
      onPaste: onPaste,
      width: width,
      inputSize: inputSize
    });
  }
}
Input.propTypes = {
  attachToForm: PropTypes.func,
  detachFromForm: PropTypes.func,
  onInput: PropTypes.func,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  onValidate: PropTypes.func,
  rules: PropTypes.object,
  id: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  className: PropTypes.string,
  type: PropTypes.oneOf(['text', 'tel', 'number', 'password', 'date', 'email', 'url', 'time', 'hidden']),
  placeholder: PropTypes.string,
  name: PropTypes.string,
  size: PropTypes.string,
  txtColor: PropTypes.string,
  inputSize: PropTypes.string,
  width: PropTypes.string,
  font: PropTypes.string,
  style: PropTypes.object,
  tabIndex: PropTypes.number,
  maxlength: PropTypes.number
};
Input.defaultProps = {
  type: 'text',
  value: '',
  size: 'md',
  width: '100%',
  font: 'control',
  txtColor: 'black'
};

const height$2 = {
  xs: '1.25rem',
  sm: '2.5rem',
  md: '3rem',
  lg: '3.5rem',
  xl: '4rem',
  xxl: '5rem'
};
var styles$n = createUseStyles({
  Radio: {
    display: 'flex',
    position: 'relative',
    height: '100%',
    textAlign: 'center'
  },
  Element: {
    position: 'absolute',
    top: 0,
    left: 0,
    opacity: 0,
    '&:checked + .Icon::after': {
      transform: 'rotate(45deg)',
      opacity: 1
    }
  },
  Type: ({
    theme,
    font
  }) => ({
    ...theme.typography[font]
  }),
  Icon: {
    position: 'relative',
    display: 'inline-flex',
    border: '2px solid',
    cursor: 'pointer',
    height: size => height$2[size],
    width: size => height$2[size],
    borderRadius: ({
      theme,
      rounded,
      size
    }) => rounded ? theme.radius[size] : 0,
    '&::after': {
      content: '" "',
      position: 'absolute',
      right: '20%',
      top: '35%',
      height: '50%',
      width: '30%',
      borderBottom: '2px solid',
      borderRight: '2px solid',
      transformOrigin: 'top right',
      opacity: 0,
      transition: 'all 0.3s ease'
    }
  }
});

const Element$1 = /*#__PURE__*/forwardRef((props, ref) => {
  const {
    name,
    id,
    size,
    rounded,
    value,
    iconChecked,
    iconUnChecked,
    iconCenter,
    checkedIconCenter,
    label,
    checked,
    tabIndex,
    onChange,
    onFocus,
    onBlur,
    checkedTxtColor,
    uncheckedTxtColor,
    checkedHoverTxtColor,
    uncheckedHoverTxtColor,
    checkedColor,
    checkedHover,
    uncheckedColor,
    uncheckedHover,
    checkedOutline,
    uncheckedOutline,
    checkedClassName,
    checkedDepth,
    uncheckedDepth,
    uncheckedClassName,
    padding,
    margin,
    font,
    disabled
  } = props;
  const theme = useContext(ThemeContext);
  const classes = styles$n({
    theme,
    ...props
  });
  return /*#__PURE__*/React.createElement("label", {
    className: classes.Radio
  }, /*#__PURE__*/React.createElement("input", {
    ref: ref,
    className: classes.Element,
    id: id,
    type: "radio",
    name: name,
    value: value,
    tabIndex: tabIndex,
    onChange: onChange,
    onFocus: onFocus,
    onBlur: onBlur,
    checked: checked,
    disabled: disabled
  }), /*#__PURE__*/React.createElement(Btn, {
    tag: "div",
    className: checked ? checkedClassName : uncheckedClassName,
    color: checked ? checkedColor : uncheckedColor,
    txtColor: checked ? checkedTxtColor : uncheckedTxtColor,
    hoverTxtColor: checked ? checkedHoverTxtColor : uncheckedHoverTxtColor,
    hoverColor: checked ? checkedHover : uncheckedHover,
    outline: checked ? checkedOutline : uncheckedOutline,
    depth: checked ? checkedDepth : uncheckedDepth,
    align: "full",
    font: font,
    size: size,
    iconLeft: checked ? iconChecked : iconUnChecked,
    iconCenter: checked ? checkedIconCenter || iconCenter : iconCenter,
    disabled: disabled,
    rounded: rounded,
    padding: padding,
    margin: margin,
    full: true
  }, label));
});
Element$1.displayName = 'RadioControl';
class Radio extends React.Component {
  static displayName = 'Radio';
  constructor(props) {
    super(props);
    this.theField = this.theField.bind(this);
    this.val = this.val.bind(this);
    this.validate = this.validate.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.handleBlur = this.handleBlur.bind(this);
    this.handleFocus = this.handleFocus.bind(this);
    this.state = {
      validated: true,
      checked: props.checked
    };
  }
  componentDidMount() {
    const {
      attachToForm
    } = this.props;
    if (typeof attachToForm === 'function') {
      attachToForm(this);
    }
  }
  componentWillUnmount() {
    const {
      detachFromForm
    } = this.props;
    if (typeof detachFromForm === 'function') {
      detachFromForm(this);
    }
  }
  componentDidUpdate(prevProps) {
    // Typical usage (don't forget to compare props):
    if (this.props.checked !== prevProps.checked) {
      this.setState({
        value: this.props.checked
      });
    }
  }
  theField() {
    return this.field;
  }
  val() {
    const {
      checked
    } = this.props;
    return checked;
  }
  async validate() {
    const {
      checked
    } = this.props;
    const {
      rules,
      onValidate
    } = this.props;
    const valid = await validate(this.field, checked, rules);
    this.setState({
      validated: true
    });
    if (typeof onValidate === 'function') {
      onValidate();
    }
    return valid;
  }
  handleChange(e) {
    const {
      onChange,
      checked
    } = this.props;
    if (typeof onChange === 'function') {
      onChange(e, checked);
    }
  }
  handleBlur(e) {
    const {
      onBlur
    } = this.props;
    if (typeof onBlur === 'function') {
      onBlur(e);
    }
  }
  handleFocus(e) {
    const {
      onFocus
    } = this.props;
    if (typeof onFocus === 'function') {
      onFocus(e);
    }
  }
  render() {
    return /*#__PURE__*/React.createElement(Element$1, _extends$d({
      ref: field => this.field = field,
      checked: this.checked,
      onChange: this.handleChange,
      onFocus: this.handleFocus,
      onBlur: this.handleBlur
    }, this.props));
  }
}
Radio.propTypes = {
  attachToForm: PropTypes.func,
  detachFromForm: PropTypes.func,
  onChange: PropTypes.func,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  onValidate: PropTypes.func,
  rules: PropTypes.object,
  value: PropTypes.string,
  rounded: PropTypes.string,
  className: PropTypes.string,
  name: PropTypes.string,
  size: PropTypes.string,
  font: PropTypes.string,
  tabIndex: PropTypes.number,
  checkedColor: PropTypes.string,
  checkedHover: PropTypes.string,
  uncheckedColor: PropTypes.string,
  uncheckedHover: PropTypes.string,
  checkedOutline: PropTypes.bool,
  uncheckedOutline: PropTypes.bool,
  checkedClassName: PropTypes.string,
  uncheckedClassName: PropTypes.string,
  iconUnChecked: PropTypes.node,
  iconChecked: PropTypes.node,
  iconCenter: PropTypes.node,
  checkedIconCenter: PropTypes.node
};
Radio.defaultProps = {
  value: '',
  size: 'md',
  checkedColor: 'primary',
  uncheckedColor: 'neutral'
};

var styles$m = createUseStyles({
  Type: {
    display: ({
      icon
    }) => icon && 'flex',
    color: ({
      theme,
      color
    }) => theme.colors[color] || color,
    fontWeight: ({
      weight
    }) => weight,
    textAlign: ({
      align
    }) => align,
    textDecoration: ({
      decoration
    }) => decoration || 'none',
    whiteSpace: ({
      whiteSpace
    }) => whiteSpace,
    fontFamily: ({
      fontFamily
    }) => fontFamily,
    fontSize: ({
      fontSize
    }) => fontSize,
    lineHeight: ({
      lineHeight
    }) => lineHeight,
    textTransform: ({
      uppercase
    }) => uppercase ? 'uppercase' : undefined,
    '& a': {
      textDecoration: 'inherit',
      color: ({
        theme,
        color
      }) => theme.colors[color] || color
    }
  },
  Title: {
    verticalAlign: 'middle',
    '& *': {
      verticalAlign: 'middle'
    }
  },
  Icon: {
    paddingRight: ({
      theme,
      iconGap
    }) => theme.spacing[iconGap] || iconGap,
    flexShrink: 0
  }
});

var styles$l = createUseStyles({
  Cell: {
    gridArea: ({
      area
    }) => area,
    gridColumn: ({
      column
    }) => column,
    gridRow: ({
      row
    }) => row,
    alignSelf: ({
      align
    }) => align,
    justifySelf: ({
      justify
    }) => justify,
    order: ({
      order
    }) => order
  }
});

const Cell = ({
  style,
  bgColor,
  bgImage,
  bgPos,
  bgSize,
  width,
  txtColor,
  verticalAlign,
  margin,
  padding,
  pt,
  pe,
  pb,
  ps,
  mt,
  me,
  mb,
  ms,
  spacing,
  spacingT,
  spacingL,
  spacingB,
  spacingR,
  justify,
  area: propArea,
  column: propColumn,
  row: propRow,
  order: propOrder,
  align,
  children
}) => {
  const theme = useContext(ThemeContext);
  const breakpoints = theme.mediaQueries;
  const area = useResponsive(propArea, breakpoints);
  const column = useResponsive(propColumn, breakpoints);
  const row = useResponsive(propRow, breakpoints);
  const order = useResponsive(propOrder, breakpoints);
  const cellStyles = {
    align,
    justify,
    area,
    column,
    row,
    order
  };
  const classes = styles$l({
    theme,
    ...cellStyles
  });
  return /*#__PURE__*/React.createElement(Box, {
    className: classes.Cell,
    style: style,
    spacing: spacing,
    bgColor: bgColor,
    txtColor: txtColor,
    bgImage: bgImage,
    bgPos: bgPos,
    bgSize: bgSize,
    margin: margin,
    padding: padding,
    verticalAlign: verticalAlign,
    pt: pt,
    pe: pe,
    pb: pb,
    ps: ps,
    mt: mt,
    me: me,
    mb: mb,
    ms: ms,
    t: spacingT,
    r: spacingR,
    b: spacingB,
    l: spacingL,
    width: width,
    wrapper: true
  }, children);
};
Cell.propTypes = {
  align: PropTypes.oneOf(['start', 'end', 'center', 'stretch']),
  area: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  column: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  row: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  justify: PropTypes.oneOf(['start', 'end', 'center', 'stretch']),
  padding: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  margin: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  pt: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  pe: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  pb: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  ps: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  mt: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  me: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  mb: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  ms: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  bgColor: PropTypes.string,
  bgImage: PropTypes.string,
  txtColor: PropTypes.string,
  width: PropTypes.string,
  verticalAlign: PropTypes.string,
  className: PropTypes.string,
  style: PropTypes.object,
  wrapper: PropTypes.bool,
  children: PropTypes.node,
  order: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.object])
};

var styles$k = createUseStyles({
  Grid: {
    width: ({
      width
    }) => width === 'auto' ? width : '100%',
    minHeight: ({
      minHeight
    }) => minHeight,
    gridGap: ({
      theme,
      gridGap
    }) => theme.spacing[gridGap],
    columnGap: ({
      theme,
      columnGap
    }) => theme.spacing[columnGap],
    rowGap: ({
      theme,
      rowGap
    }) => theme.spacing[rowGap],
    backgroundColor: ({
      theme,
      bgColor
    }) => theme.colors[bgColor] || bgColor,
    backgroundImage: ({
      bgImage
    }) => `url(${bgImage})`,
    backgroundSize: ({
      bgSize
    }) => bgSize || 'cover',
    backgroundPosition: ({
      bgPos
    }) => bgPos || 'center',
    backgroundRepeat: 'no-repeat',
    padding: ({
      theme,
      padding
    }) => spacing(theme, padding),
    margin: ({
      theme,
      margin
    }) => spacing(theme, margin),
    color: ({
      theme,
      txtColor
    }) => theme.colors[txtColor] || txtColor,
    alignItems: ({
      align
    }) => align,
    justifyItems: ({
      justify
    }) => justify,
    flexWrap: 'wrap',
    gridTemplate: ({
      gridTemplate
    }) => gridTemplate,
    gridTemplateColumns: ({
      gridTemplateColumns
    }) => gridTemplateColumns,
    gridAutoColumns: ({
      gridAutoColumns
    }) => gridAutoColumns,
    gridTemplateRows: ({
      gridTemplateRows
    }) => gridTemplateRows,
    gridAutoRows: ({
      gridAutoRows
    }) => gridAutoRows,
    gridTemplateAreas: ({
      gridTemplateAreas
    }) => gridTemplateAreas
  }
});

const Grid = ({
  className,
  gridGap: gg,
  columnGap: cg,
  rowGap: rg,
  columns,
  template,
  autoColumns,
  height,
  rows,
  autoRows,
  area,
  size,
  style,
  children,
  ...props
}) => {
  const theme = useContext(ThemeContext);
  const breakpoints = theme.mediaQueries;
  const gridTemplateColumns = useResponsive(columns, breakpoints);
  const gridAutoColumns = useResponsive(autoColumns, breakpoints);
  const gridTemplateRows = useResponsive(rows, breakpoints);
  const gridAutoRows = useResponsive(autoRows, breakpoints);
  const gridTemplateAreas = useResponsive(area, breakpoints);
  const gridTemplate = useResponsive(template, breakpoints);
  const columnGap = useResponsive(cg, breakpoints);
  const rowGap = useResponsive(rg, breakpoints);
  const gridGap = useResponsive(gg, breakpoints);
  const classes = styles$k({
    theme,
    gridTemplate,
    gridTemplateColumns,
    gridAutoColumns,
    gridTemplateRows,
    gridAutoRows,
    gridTemplateAreas,
    rowGap,
    columnGap,
    gridGap,
    minHeight: height,
    ...props
  });
  return /*#__PURE__*/React.createElement(Box, _extends$d({
    className: `${className} ${classes.Grid}`,
    style: style
  }, props), children);
};
Grid.propTypes = {
  bgColor: PropTypes.string,
  bgImage: PropTypes.string,
  bgPos: PropTypes.string,
  bgSize: PropTypes.string,
  txtColor: PropTypes.string,
  className: PropTypes.string,
  columnGap: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  rowGap: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  gridGap: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  size: PropTypes.string,
  align: PropTypes.string,
  height: PropTypes.string,
  width: PropTypes.string,
  justify: PropTypes.string,
  display: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  padding: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  margin: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  columns: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  autoColumns: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  autoRows: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  rows: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  area: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  template: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  children: PropTypes.node
};
Grid.defaultProps = {
  display: 'grid'
};

var styles$j = createUseStyles({
  Spacer: {
    padding: ({
      theme,
      spacing
    }) => theme.spacing[spacing],
    paddingTop: ({
      theme,
      t
    }) => theme.spacing[t],
    paddingRight: ({
      theme,
      r
    }) => theme.spacing[r],
    paddingBottom: ({
      theme,
      b
    }) => theme.spacing[b],
    paddingLeft: ({
      theme,
      l
    }) => theme.spacing[l]
  }
});

const Spacer = ({
  className,
  style,
  wrapper,
  children,
  spacing,
  t,
  r,
  b,
  l
}) => {
  const theme = useContext(ThemeContext);
  const classes = styles$j({
    theme,
    spacing,
    t,
    r,
    b,
    l
  });
  const renderSpacer = () => {
    if (wrapper) {
      if (Array.isArray(children)) {
        return children.map((child, index) => {
          return /*#__PURE__*/React.cloneElement(child, {
            key: `${uId}-${index}`,
            className: `${child.props.className} ${classes.Spacer}`
          });
        });
      } else {
        return /*#__PURE__*/React.cloneElement(children, {
          className: `${children.props.className} ${classes.Spacer}`
        });
      }
    }
    return /*#__PURE__*/React.createElement("div", {
      className: classes.Spacer,
      style: style
    }, children);
  };
  return renderSpacer();
};
Spacer.propTypes = {
  spacing: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  t: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  l: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  b: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  r: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  wrapper: PropTypes.bool,
  className: PropTypes.string,
  style: PropTypes.object,
  children: PropTypes.node
};

const Container = ({
  size,
  children,
  ...props
}) => {
  const theme = useContext(ThemeContext);
  const sizes = {
    sm: `${theme.breakpoints.tablet.minWidth}px`,
    md: `${theme.breakpoints.laptop.minWidth}px`,
    lg: `${theme.breakpoints.desktop.minWidth}px`,
    xl: `${theme.breakpoints.hd.minWidth}px`
  };
  const width = sizes[size] || size;
  return /*#__PURE__*/React.createElement(Box, _extends$d({
    width: width
  }, props), children);
};
Container.propTypes = {
  bgColor: PropTypes.string,
  bgImage: PropTypes.string,
  bgPos: PropTypes.string,
  bgSize: PropTypes.string,
  txtColor: PropTypes.string,
  align: PropTypes.string,
  justify: PropTypes.string,
  tag: PropTypes.string,
  depth: PropTypes.string,
  padding: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  margin: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  pt: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  pe: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  pb: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  ps: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  mt: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  me: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  mb: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  ms: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  brWidth: PropTypes.string,
  blWidth: PropTypes.string,
  btWidth: PropTypes.string,
  bbWidth: PropTypes.string,
  className: PropTypes.string,
  style: PropTypes.object,
  wrapper: PropTypes.bool,
  children: PropTypes.node
};
Container.defaultProps = {
  size: 'lg'
};

const Type = ({
  children,
  className,
  icon,
  href,
  target,
  style,
  onClick,
  onMouseDown,
  onTouchStart,
  display,
  font: fn,
  fontSize: fs,
  margin,
  padding,
  decoration,
  color,
  mt,
  me,
  mb,
  ms,
  pt,
  pe,
  pb,
  ps,
  tag,
  id,
  align,
  width,
  iconGap,
  iconAlign,
  weight: fw,
  nowrap: nw
}) => {
  const theme = useContext(ThemeContext);
  const breakpoints = theme.mediaQueries;
  const font = useResponsive(fn, breakpoints);
  const typeography = useMemo(() => {
    return theme.typography[font] ? {
      ...theme.typography[font],
      whiteSpace: nw ? 'nowrap' : null,
      weight: fw
    } : {};
  }, [font, fw, nw]);
  const {
    fontFamily,
    fontSize,
    lineHeight,
    textTransform,
    whiteSpace,
    weight
  } = typeography;
  const classes = styles$m({
    theme,
    color,
    icon,
    fontFamily,
    fontSize: fs || fontSize,
    lineHeight,
    textTransform,
    weight,
    whiteSpace,
    decoration,
    align,
    iconGap
  });
  const classNames = className ? `${className} ${classes.Type}` : classes.Type;
  return /*#__PURE__*/React.createElement(Box, {
    className: classNames,
    href: href,
    onClick: onClick,
    onTouchStart: onTouchStart,
    onMouseDown: onMouseDown,
    style: style,
    target: target,
    margin: margin,
    padding: padding,
    mt: mt,
    me: me,
    mb: mb,
    ms: ms,
    pt: pt,
    pe: pe,
    pb: pb,
    ps: ps,
    tag: tag,
    display: tag === 'span' || tag === 'a' ? 'inline' : display,
    id: id,
    width: width,
    align: icon ? iconAlign : false
  }, icon && /*#__PURE__*/React.createElement("span", {
    className: classes.Icon
  }, icon), children);
};
Type.propTypes = {
  id: PropTypes.string,
  color: PropTypes.string,
  font: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  fontSize: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  tag: PropTypes.oneOf(['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'p', 'li', 'strong', 'span', 'a', 'i']),
  underline: PropTypes.string,
  align: PropTypes.string,
  badge: PropTypes.string,
  badgeSize: PropTypes.string,
  badgeColor: PropTypes.string,
  href: PropTypes.string,
  icon: PropTypes.node,
  iconSize: PropTypes.string,
  decoration: PropTypes.string,
  display: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  spacing: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  weight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  nowrap: PropTypes.bool,
  padding: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  margin: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  pt: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  pe: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  pb: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  ps: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  mt: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  me: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  mb: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  ms: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  inline: PropTypes.bool,
  style: PropTypes.object
};
Type.defaultProps = {
  tag: 'span',
  font: 'p',
  decoration: 'none',
  width: 'auto',
  iconGap: 'sm',
  iconAlign: 'center'
};

const H1 = props => /*#__PURE__*/React.createElement(Type, props, props.children);
H1.propTypes = {
  color: PropTypes.string,
  font: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  underline: PropTypes.string,
  align: PropTypes.string,
  badge: PropTypes.string,
  badgeSize: PropTypes.string,
  badgeColor: PropTypes.string,
  href: PropTypes.string,
  icon: PropTypes.node,
  iconSize: PropTypes.string,
  spacing: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  weight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  nowrap: PropTypes.bool,
  padding: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  margin: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  pt: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  pe: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  pb: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  ps: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  mt: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  me: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  mb: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  ms: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  inline: PropTypes.bool,
  style: PropTypes.object
};
H1.defaultProps = {
  tag: 'h1',
  font: 'h1'
};

const H2 = props => /*#__PURE__*/React.createElement(Type, props, props.children);
H2.propTypes = {
  color: PropTypes.string,
  font: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  underline: PropTypes.string,
  align: PropTypes.string,
  badge: PropTypes.string,
  badgeSize: PropTypes.string,
  badgeColor: PropTypes.string,
  href: PropTypes.string,
  icon: PropTypes.node,
  iconSize: PropTypes.string,
  spacing: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  weight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  nowrap: PropTypes.bool,
  padding: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  margin: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  pt: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  pe: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  pb: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  ps: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  mt: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  me: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  mb: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  ms: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  inline: PropTypes.bool,
  style: PropTypes.object
};
H2.defaultProps = {
  tag: 'h2',
  font: 'h2'
};

const H3 = props => /*#__PURE__*/React.createElement(Type, props, props.children);
H3.propTypes = {
  color: PropTypes.string,
  font: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  underline: PropTypes.string,
  align: PropTypes.string,
  badge: PropTypes.string,
  badgeSize: PropTypes.string,
  badgeColor: PropTypes.string,
  href: PropTypes.string,
  icon: PropTypes.node,
  iconSize: PropTypes.string,
  spacing: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  weight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  nowrap: PropTypes.bool,
  padding: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  margin: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  pt: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  pe: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  pb: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  ps: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  mt: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  me: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  mb: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  ms: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  inline: PropTypes.bool,
  style: PropTypes.object
};
H3.defaultProps = {
  tag: 'h3',
  font: 'h3'
};

const H4 = props => /*#__PURE__*/React.createElement(Type, props, props.children);
H4.propTypes = {
  color: PropTypes.string,
  font: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  underline: PropTypes.string,
  align: PropTypes.string,
  badge: PropTypes.string,
  badgeSize: PropTypes.string,
  badgeColor: PropTypes.string,
  href: PropTypes.string,
  icon: PropTypes.node,
  iconSize: PropTypes.string,
  spacing: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  weight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  nowrap: PropTypes.bool,
  padding: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  margin: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  pt: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  pe: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  pb: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  ps: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  mt: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  me: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  mb: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  ms: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  inline: PropTypes.bool,
  style: PropTypes.object
};
H4.defaultProps = {
  tag: 'h4',
  font: 'h4'
};

const H5 = props => /*#__PURE__*/React.createElement(Type, props, props.children);
H5.propTypes = {
  color: PropTypes.string,
  font: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  underline: PropTypes.string,
  align: PropTypes.string,
  badge: PropTypes.string,
  badgeSize: PropTypes.string,
  badgeColor: PropTypes.string,
  href: PropTypes.string,
  icon: PropTypes.node,
  iconSize: PropTypes.string,
  spacing: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  weight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  nowrap: PropTypes.bool,
  padding: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  margin: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  pt: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  pe: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  pb: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  ps: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  mt: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  me: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  mb: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  ms: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  inline: PropTypes.bool,
  style: PropTypes.object
};
H5.defaultProps = {
  tag: 'h5',
  font: 'h5'
};

const H6 = props => /*#__PURE__*/React.createElement(Type, props, props.children);
H6.propTypes = {
  color: PropTypes.string,
  font: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  underline: PropTypes.string,
  align: PropTypes.string,
  badge: PropTypes.string,
  badgeSize: PropTypes.string,
  badgeColor: PropTypes.string,
  href: PropTypes.string,
  icon: PropTypes.node,
  iconSize: PropTypes.string,
  spacing: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  weight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  nowrap: PropTypes.bool,
  padding: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  margin: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  pt: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  pe: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  pb: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  ps: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  mt: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  me: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  mb: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  ms: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  inline: PropTypes.bool,
  style: PropTypes.object
};
H6.defaultProps = {
  tag: 'h6',
  font: 'h6'
};

const A = props => /*#__PURE__*/React.createElement(Type, props, props.children);
A.propTypes = {
  color: PropTypes.string,
  font: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  underline: PropTypes.string,
  align: PropTypes.string,
  badge: PropTypes.string,
  badgeSize: PropTypes.string,
  badgeColor: PropTypes.string,
  href: PropTypes.string,
  target: PropTypes.string,
  icon: PropTypes.node,
  iconSize: PropTypes.string,
  spacing: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  weight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  nowrap: PropTypes.bool,
  padding: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  margin: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  pt: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  pe: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  pb: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  ps: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  mt: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  me: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  mb: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  ms: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  inline: PropTypes.bool,
  style: PropTypes.object
};
A.defaultProps = {
  tag: 'a',
  color: 'primary',
  weight: 'bold',
  font: 'p',
  target: '_blank'
};

const P = props => /*#__PURE__*/React.createElement(Type, props, props.children);
P.propTypes = {
  color: PropTypes.string,
  font: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  underline: PropTypes.string,
  align: PropTypes.string,
  badge: PropTypes.string,
  badgeSize: PropTypes.string,
  badgeColor: PropTypes.string,
  href: PropTypes.string,
  icon: PropTypes.node,
  iconSize: PropTypes.string,
  spacing: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  weight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  nowrap: PropTypes.bool,
  padding: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  margin: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  pt: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  pe: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  pb: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  ps: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  mt: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  me: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  mb: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  ms: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  inline: PropTypes.bool,
  style: PropTypes.object
};
P.defaultProps = {
  tag: 'p',
  font: 'p',
  mb: 'lg'
};

const Li = props => /*#__PURE__*/React.createElement(Type, _extends$d({
  display: "list-item"
}, props), props.children);
Li.propTypes = {
  color: PropTypes.string,
  font: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  underline: PropTypes.string,
  align: PropTypes.string,
  badge: PropTypes.string,
  badgeSize: PropTypes.string,
  badgeColor: PropTypes.string,
  href: PropTypes.string,
  icon: PropTypes.node,
  iconSize: PropTypes.string,
  spacing: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  weight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  nowrap: PropTypes.bool,
  padding: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  margin: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  pt: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  pe: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  pb: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  ps: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  mt: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  me: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  mb: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  ms: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  inline: PropTypes.bool,
  style: PropTypes.object
};
Li.defaultProps = {
  tag: 'li',
  font: 'p'
};

const Strong = props => /*#__PURE__*/React.createElement(Type, props, props.children);
Strong.propTypes = {
  color: PropTypes.string,
  font: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  underline: PropTypes.string,
  align: PropTypes.string,
  badge: PropTypes.string,
  badgeSize: PropTypes.string,
  badgeColor: PropTypes.string,
  href: PropTypes.string,
  icon: PropTypes.node,
  iconSize: PropTypes.string,
  spacing: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  weight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  nowrap: PropTypes.bool,
  padding: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  margin: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  pt: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  pe: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  pb: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  ps: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  mt: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  me: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  mb: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  ms: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  inline: PropTypes.bool,
  style: PropTypes.object
};
Strong.defaultProps = {
  tag: 'strong',
  font: 'inherit',
  weight: 'bold',
  display: 'inline'
};

var styles$i = createUseStyles({
  List: {
    counterReset: 'item',
    display: 'block',
    listStylePosition: 'outside',
    padding: ({
      theme,
      padding
    }) => spacing(theme, padding) || 0,
    margin: ({
      theme,
      margin
    }) => spacing(theme, margin) || 0,
    listStyleType: ({
      listType
    }) => listType === 'nested' ? 'none' : listType,
    '& li': {
      display: ({
        listType
      }) => listType === 'nested' ? 'block' : 'list',
      padding: 0,
      marginBottom: ({
        theme,
        spacing
      }) => theme.spacing[spacing] || spacing,
      '&:before': {
        content: ({
          listType
        }) => listType === 'nested' ? 'counters(item, ".") " "' : ''
      }
    },
    '& li $List': {
      marginTop: ({
        theme,
        spacing
      }) => theme.spacing[spacing] || spacing,
      marginLeft: ({
        theme,
        indent
      }) => theme.spacing[indent] || indent || ''
    },
    '& > li': {
      counterIncrement: 'item'
    }
  },
  Type: ({
    theme,
    color,
    font,
    weight
  }) => ({
    color: theme.colors[color],
    ...theme.typography[font],
    fontWeight: weight || 'normal'
  })
});

const List = props => {
  const theme = useContext(ThemeContext);
  const classes = styles$i({
    theme,
    ...props
  });
  return /*#__PURE__*/React.createElement(props.tag, {
    className: `${classes.List} ${classes.Type}`,
    style: props.style
  }, props.children);
};
List.propTypes = {
  tag: PropTypes.oneOf(['ul', 'ol']),
  font: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  color: PropTypes.string,
  weight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  spacing: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  indent: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  padding: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  margin: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  style: PropTypes.object
};
List.defaultProps = {
  tag: 'ul',
  listType: 'disc',
  indent: 'sm'
};

const rangeThumb = {
  height: '1.5rem',
  width: '1.5rem',
  borderRadius: '1.5rem',
  marginTop: '-0.5rem',
  cursor: 'pointer',
  border: 'none'
};
const rangeThumbSliding = {
  height: '2rem',
  width: '2rem',
  borderRadius: '2.5rem',
  marginTop: '-0.75rem',
  cursor: 'pointer',
  border: 'none'
};
const rangeTrack = {
  width: '100%',
  height: '0.5rem',
  background: 'none',
  cursor: 'pointer'
};
var styles$h = createUseStyles({
  Range: {
    display: 'inline-flex',
    position: 'relative',
    height: '7rem',
    width: '100%'
  },
  Disabled: {
    '& Title': {
      color: ({
        theme
      }) => theme.colors.neutral400
    },
    '& .TrackSelected': {
      background: ({
        theme
      }) => theme.colors.neutral400
    },
    '& .Element': {
      '&[disabled]::-webkit-slider-thumb': {
        backgroundColor: ({
          theme
        }) => theme.colors.neutral400
      },
      '::-moz-range-thumb': {
        backgroundColor: ({
          theme
        }) => theme.colors.neutral400
      },
      '::-ms-thumb': {
        backgroundColor: ({
          theme
        }) => theme.colors.neutral400
      }
    }
  },
  Max: {
    position: 'absolute',
    right: 0,
    bottom: 0
  },
  Min: {
    position: 'absolute',
    left: 0,
    bottom: 0
  },
  Track: {
    position: 'absolute',
    top: 'calc(50% - 0.25rem)',
    left: 0,
    height: '0.5rem',
    width: '100%',
    background: ({
      theme
    }) => theme.colors.neutral200,
    borderRadius: '0.5rem',
    pointerEvents: 'none',
    '-webkit-tap-highlight-color': 'transparent'
  },
  TrackSelected: {
    position: 'absolute',
    top: 'calc(50% - 0.25rem)',
    left: 0,
    height: '0.5rem',
    background: ({
      theme,
      color
    }) => theme.colors[color],
    borderTopLeftRadius: '0.5rem',
    borderBottomLeftRadius: '0.5rem',
    pointerEvents: 'none',
    '-webkit-tap-highlight-color': 'transparent'
  },
  Output: {
    position: 'absolute',
    transform: 'translateX(-50%)',
    fontWeight: 'bold',
    whiteSpace: 'nowrap',
    padding: ({
      theme
    }) => theme.spacing.xs,
    borderRadius: ({
      theme,
      rounded
    }) => theme.radius[rounded] || rounded || theme.radius.md,
    background: ({
      theme,
      color
    }) => theme.colors[color],
    color: '#ffffff'
  },
  Element: {
    padding: 0,
    margin: 0,
    height: '2rem',
    width: '100%',
    background: 'transparent',
    alignSelf: 'center',
    position: 'relative',
    zIndex: 5,
    '-webkit-appearance': 'none',
    '&:focus': {
      outline: 'none',
      border: 'none'
    },
    '&::-moz-focus-outer': {
      border: 0
    },
    '&::-moz-focus-inner': {
      border: 0
    },
    '&::-ms-track': {
      width: '100%',
      cursor: 'pointer',
      background: 'transparent',
      borderColor: 'transparent',
      color: 'transparent'
    },
    '&::-webkit-slider-thumb': {
      '-webkit-appearance': 'none',
      background: ({
        theme,
        color
      }) => theme.colors[color],
      ...rangeThumb
    },
    '&::-moz-range-thumb': {
      background: ({
        theme,
        color
      }) => theme.colors[color],
      ...rangeThumb
    },
    '&::-ms-thumb': {
      marginTop: 0,
      background: ({
        theme,
        color
      }) => theme.colors[color],
      ...rangeThumb
    },
    '&::-webkit-slider-runnable-track': {
      ...rangeTrack
    },
    '&::-moz-range-track': {
      ...rangeTrack
    },
    '&::-ms-track': {
      ...rangeTrack
    },
    '&::-ms-fill-lower': {
      background: ({
        theme,
        color
      }) => theme.colors[color]
    },
    '&::-ms-tooltip': {
      display: 'none'
    }
  },
  SlidingElement: {
    padding: 0,
    margin: 0,
    height: '2rem',
    width: '100%',
    background: 'transparent',
    alignSelf: 'center',
    position: 'relative',
    zIndex: 5,
    '-webkit-appearance': 'none',
    '&:focus': {
      outline: 'none',
      border: 'none'
    },
    '&::-moz-focus-outer': {
      border: 0
    },
    '&::-moz-focus-inner': {
      border: 0
    },
    '&::-ms-track': {
      width: '100%',
      cursor: 'pointer',
      background: 'transparent',
      borderColor: 'transparent',
      color: 'transparent'
    },
    '&::-webkit-slider-thumb': {
      '-webkit-appearance': 'none',
      background: ({
        theme,
        color
      }) => theme.colors[color],
      ...rangeThumbSliding
    },
    '&::-moz-range-thumb': {
      background: ({
        theme,
        color
      }) => theme.colors[color],
      ...rangeThumbSliding
    },
    '&::-ms-thumb': {
      marginTop: 0,
      background: ({
        theme,
        color
      }) => theme.colors[color],
      ...rangeThumbSliding
    },
    '&::-webkit-slider-runnable-track': {
      ...rangeTrack
    },
    '&::-moz-range-track': {
      ...rangeTrack
    },
    '&::-ms-track': {
      ...rangeTrack
    },
    '&::-ms-fill-lower': {
      background: ({
        theme,
        color
      }) => theme.colors[color]
    },
    '&::-ms-tooltip': {
      display: 'none'
    }
  },
  Type: ({
    theme,
    font
  }) => ({
    ...theme.typography[font]
  })
});

const offsetOuput = (left, right, percentage) => {
  if (left < 0) {
    return {
      left: `calc(${percentage}% + ${-left}px)`
    };
  } else if (right < 0) {
    return {
      left: `calc(${percentage}% + ${right}px)`
    };
  }
  return {
    left: `${percentage}%`
  };
};
const Element = /*#__PURE__*/forwardRef((props, refs) => {
  const {
    field,
    output
  } = refs;
  const {
    name,
    id,
    setValue,
    tabIndex,
    min,
    max,
    step,
    sliding,
    formattedMin,
    formattedMax,
    formattedValue,
    outputStyles,
    trackStyles,
    startSlide,
    endSlide,
    handleInput,
    handleChange,
    handleFocus,
    handleBlur,
    txtColor
  } = props;
  const theme = useContext(ThemeContext);
  const classes = styles$h({
    theme,
    ...props
  });
  return /*#__PURE__*/React.createElement("div", {
    className: classes.Range
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.Min
  }, /*#__PURE__*/React.createElement(Type, {
    font: "control",
    color: txtColor
  }, formattedMin)), /*#__PURE__*/React.createElement("div", {
    className: classes.Max
  }, /*#__PURE__*/React.createElement(Type, {
    font: "control",
    color: txtColor
  }, formattedMax)), /*#__PURE__*/React.createElement("output", {
    ref: output,
    className: classes.Output,
    style: outputStyles
  }, /*#__PURE__*/React.createElement(Type, {
    font: "control"
  }, formattedValue)), /*#__PURE__*/React.createElement("div", {
    className: classes.Track
  }), /*#__PURE__*/React.createElement("div", {
    className: classes.TrackSelected,
    style: trackStyles
  }), /*#__PURE__*/React.createElement("input", {
    ref: field,
    className: sliding ? classes.SlidingElement : classes.Element,
    id: id,
    type: "range",
    name: name,
    value: setValue,
    min: min,
    max: max,
    tabIndex: tabIndex,
    onMouseDown: startSlide,
    onTouchStart: startSlide,
    onMouseUp: endSlide,
    onTouchEnd: endSlide,
    onInput: handleInput,
    onChange: handleChange,
    onFocus: handleFocus,
    onBlur: handleBlur,
    step: step
  }));
});
Element.displayName = 'RangeControl';
class Range extends React.Component {
  static displayName = 'Range';
  constructor(props) {
    super(props);
    this.theField = this.theField.bind(this);
    this.val = this.val.bind(this);
    this.validate = this.validate.bind(this);
    this.startSlide = this.startSlide.bind(this);
    this.endSlide = this.endSlide.bind(this);
    this.outputStyles = this.outputStyles.bind(this);
    this.trackStyles = this.trackStyles.bind(this);
    this.handleInput = this.handleInput.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.handleBlur = this.handleBlur.bind(this);
    this.handleFocus = this.handleFocus.bind(this);
    this.state = {
      validated: true,
      mounted: false,
      value: Number(props.value)
    };
  }
  componentDidMount() {
    const {
      attachToForm
    } = this.props;
    this.state;
    if (typeof attachToForm === 'function') {
      attachToForm(this);
    }
    window.document.addEventListener('mousemove', this.moveSlide);
    window.document.addEventListener('touchmove', this.moveSlide);
    this.setState({
      mounted: true
    });
  }
  componentWillUnmount() {
    const {
      detachFromForm
    } = this.props;
    if (typeof detachFromForm === 'function') {
      detachFromForm(this);
    }
    window.document.removeEventListener('mousemove', this.moveSlide);
    window.document.removeEventListener('touchmove', this.moveSlide);
  }
  componentDidUpdate(prevProps) {
    // Typical usage (don't forget to compare props):
    if (this.props.value !== prevProps.value) {
      this.setState({
        value: this.props.value
      });
    }
  }
  outputStyles() {
    if (!this.field) {
      return {};
    }
    const value = this.props.controlled ? this.props.value : this.state.value;
    const {
      min,
      max
    } = this.props;
    const width = this.field.offsetWidth;
    const range = max - min;
    const offset = range * (12 / width);
    const percentage = (value - min + offset) / (range + offset * 2) * 100;
    const outputWidth = this.output.offsetWidth;
    const outputLeft = width * (percentage / 100) - outputWidth / 2;
    const outputRight = width - (width * (percentage / 100) + outputWidth / 2);
    return offsetOuput(outputLeft, outputRight, percentage);
  }
  trackStyles() {
    if (!this.field) {
      return {};
    }
    const value = this.props.controlled ? this.props.value : this.state.value;
    const {
      min,
      max
    } = this.props;
    const width = this.field.offsetWidth;
    const range = max - min;
    const offset = range * (12 / width);
    const percentage = (value - min + offset) / (range + offset * 2) * 100;
    return {
      width: `${percentage}%`
    };
  }
  startSlide(e) {
    const {
      disabled
    } = this.state;
    this.props;
    if (e.touches && !disabled) {
      this.field.offsetWidth;
      e.touches[0].clientX - this.field.getBoundingClientRect().left;
      const step = step || 1;
      this.setState({
        sliding: true
      });
    } else {
      this.setState({
        sliding: true
      });
    }
  }
  endSlide() {
    this.setState({
      sliding: false
    });
  }
  theField() {
    return this.field;
  }
  val() {
    const {
      value
    } = this.state;
    return value;
  }
  async validate() {
    const {
      value
    } = this.state;
    const {
      rules,
      onValidate
    } = this.props;
    const valid = await validate(this.field, value, rules);
    this.setState({
      validated: true
    });
    if (typeof onValidate === 'function') {
      onValidate();
    }
    return valid;
  }
  handleInput(e) {
    const {
      onInput,
      controlled
    } = this.props;
    const {
      value
    } = e.target;
    if (!controlled) {
      this.setState({
        value: Number(value)
      });
    }
    if (typeof onInput === 'function') {
      onInput(e, value);
    }
  }
  handleChange(e) {
    const {
      onInput,
      controlled
    } = this.props;
    const {
      value
    } = e.target;
    if (!controlled) {
      this.setState({
        value: Number(value)
      });
    }
    if (typeof onInput === 'function') {
      onInput(e, value);
    }
  }
  handleBlur(e) {
    const {
      onBlur
    } = this.props;
    if (typeof onBlur === 'function') {
      onBlur(e);
    }
  }
  handleFocus(e) {
    const {
      onFocus
    } = this.props;
    if (typeof onFocus === 'function') {
      onFocus(e);
    }
  }
  formatValue(value) {
    const {
      formatValue
    } = this.props;
    if (value && typeof formatValue === 'function') {
      return formatValue(value);
    }
    return value;
  }
  render() {
    return /*#__PURE__*/React.createElement(Element, _extends$d({
      ref: {
        output: ref => this.output = ref,
        field: ref => this.field = ref
      },
      setValue: this.state.value,
      formattedMin: this.formatValue(this.props.min || 0),
      formattedMax: this.formatValue(this.props.max || 100),
      formattedValue: this.formatValue(this.state.value),
      outputStyles: this.outputStyles(),
      sliding: this.state.sliding,
      trackStyles: this.trackStyles(),
      startSlide: this.startSlide,
      endSlide: this.endSlide,
      handleInput: this.handleInput,
      handleChange: this.handleChange,
      handleFocus: this.handleFocus,
      handleBlur: this.handleBlur
    }, this.props));
  }
}
Range.propTypes = {
  attachToForm: PropTypes.func,
  detachFromForm: PropTypes.func,
  onChange: PropTypes.func,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  onValidate: PropTypes.func,
  rules: PropTypes.object,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  className: PropTypes.string,
  name: PropTypes.string,
  size: PropTypes.string,
  value: PropTypes.number,
  txtColor: PropTypes.string,
  tabIndex: PropTypes.number
};
Range.defaultProps = {
  type: 'text',
  size: 'md',
  color: 'primary'
};

const padding$1 = {
  xs: 'sm',
  sm: 'sm',
  md: 'sm',
  lg: 'md',
  xl: 'lg',
  xxl: 'lg'
};
const height$1 = {
  xs: '1.25rem',
  sm: '2.5rem',
  md: '3rem',
  lg: '3.5rem',
  xl: '4rem',
  xxl: '5rem'
};
var styles$g = createUseStyles({
  Select: {
    display: 'inline-flex',
    position: 'relative',
    alignItems: 'stretch',
    color: ({
      theme,
      txtColor
    }) => theme.colors[txtColor],
    width: '100%'
  },
  Element: {
    display: 'inline-flex',
    position: 'relative',
    width: '100%',
    zIndex: '5',
    appearance: 'none',
    height: ({
      size
    }) => height$1[size],
    lineHeight: ({
      size
    }) => height$1[size],
    padding: ({
      theme,
      size
    }) => [[0, theme.spacing[padding$1[size]]]],
    border: 'none',
    borderRadius: 0,
    background: 'none',
    '&:focus': {
      outline: 'none'
    }
  },
  Type: ({
    theme,
    font
  }) => ({
    ...theme.typography[font],
    lineHeight: false
  }),
  Icon: {
    display: 'inline-flex',
    position: 'absolute',
    top: 0,
    right: 0,
    alignItems: 'center',
    height: '100%',
    padding: ({
      theme
    }) => theme.spacing.md
  }
});

const SelectElement = /*#__PURE__*/forwardRef((props, ref) => {
  const {
    name,
    options,
    placeholder,
    id,
    size,
    value,
    tabIndex,
    handleChange,
    handleFocus,
    handleBlur
  } = props;
  const theme = useContext(ThemeContext);
  const classes = styles$g({
    theme,
    ...props
  });
  const onClick = e => {
    e.stopPropagation();
  };
  return /*#__PURE__*/React.createElement("div", {
    className: classes.Select
  }, /*#__PURE__*/React.createElement("select", {
    ref: ref,
    className: `${classes.Element} ${classes.Type}`,
    id: id,
    name: name,
    value: value,
    tabIndex: tabIndex,
    onChange: handleChange,
    onFocus: handleFocus,
    onBlur: handleBlur,
    onClick: onClick
  }, placeholder && /*#__PURE__*/React.createElement("option", {
    value: ""
  }, placeholder), Array.isArray(options) ? options.map(option => /*#__PURE__*/React.createElement("option", {
    key: `option_${option.value}`,
    value: option.value
  }, option.label)) : Object.keys(options).map(key => /*#__PURE__*/React.createElement("option", {
    key: `option_${key}`,
    value: key
  }, options[key]))), /*#__PURE__*/React.createElement("span", {
    className: classes.Icon
  }, /*#__PURE__*/React.createElement(ChevronDown, null)));
});
SelectElement.displayName = 'SelectControl';
class Select extends React.Component {
  static displayName = 'Select';
  constructor(props) {
    super(props);
    this.theField = this.theField.bind(this);
    this.val = this.val.bind(this);
    this.validate = this.validate.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.handleBlur = this.handleBlur.bind(this);
    this.handleFocus = this.handleFocus.bind(this);
    this.state = {
      validated: false,
      value: props.value || ''
    };
  }
  componentDidMount() {
    const {
      attachToForm
    } = this.props;
    if (typeof attachToForm === 'function') {
      attachToForm(this);
    }
  }
  componentWillUnmount() {
    const {
      detachFromForm
    } = this.props;
    if (typeof detachFromForm === 'function') {
      detachFromForm(this);
    }
  }
  componentDidUpdate(prevProps) {
    // Typical usage (don't forget to compare props):
    if (this.props.value !== prevProps.value) {
      this.setState({
        value: this.props.value
      });
    }
  }
  theField() {
    return this.field;
  }
  val() {
    return this.state.value;
  }
  async validate() {
    const {
      value
    } = this.state;
    const {
      rules
    } = this.props;
    this.setState({
      validated: true
    });
    return await validate(this.field, value, rules);
  }
  handleChange(e) {
    const {
      onChange
    } = this.props;
    const {
      validated
    } = this.state;
    this.setState({
      value: e.target.value
    }, () => {
      if (validated) {
        this.validate();
      }
      if (typeof onChange === 'function') {
        onChange(e);
      }
    });
  }
  handleBlur(e) {
    const {
      onBlur
    } = this.props;
    onBlur(e);
  }
  handleFocus(e) {
    const {
      onFocus
    } = this.props;
    onFocus(e);
  }
  render() {
    this.context;
    const {
      name,
      options,
      placeholder,
      id,
      size,
      font,
      tabIndex,
      txtColor
    } = this.props;
    const {
      value
    } = this.state;
    return /*#__PURE__*/React.createElement(SelectElement, {
      ref: field => {
        this.field = field;
      },
      value: value,
      name: name,
      placeholder: placeholder,
      size: size,
      font: font,
      tabIndex: tabIndex,
      options: options,
      txtColor: txtColor,
      handleBlur: this.handleBlur,
      handleFocus: this.handleFocus,
      handleChange: this.handleChange
    });
  }
}

// https://www.fullstackreact.com/30-days-of-react/day-8/
Select.propTypes = {
  attachToForm: PropTypes.func,
  detachFromForm: PropTypes.func,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  options: PropTypes.oneOfType([PropTypes.objectOf(PropTypes.string), PropTypes.arrayOf(PropTypes.object)]).isRequired,
  rules: PropTypes.object,
  name: PropTypes.string,
  id: PropTypes.string,
  size: PropTypes.string,
  font: PropTypes.string,
  txtColor: PropTypes.string,
  className: PropTypes.string,
  placeholder: PropTypes.string,
  tabIndex: PropTypes.number
};
Select.defaultProps = {
  onFocus: () => {},
  onBlur: () => {},
  onChange: () => {},
  txtColor: 'black',
  name: '',
  size: 'md',
  font: 'control',
  options: {
    select: '--Select--'
  }
};

const padding = {
  xs: 'sm',
  sm: 'sm',
  md: 'sm',
  lg: 'md',
  xl: 'lg',
  xxl: 'lg'
};
const height = {
  xs: '4.5rem',
  sm: '6rem',
  md: '7.5rem',
  lg: '9rem',
  xl: '10.5rem',
  xxl: '12rem'
};
var styles$f = createUseStyles({
  TextArea: {
    display: 'inline-flex',
    width: '100%',
    height: ({
      size
    }) => height[size],
    padding: ({
      theme,
      size
    }) => [[theme.spacing[padding[size]]]],
    background: 'none',
    border: 'none',
    '&:focus': {
      outline: 'none'
    }
  },
  Type: ({
    theme,
    font
  }) => ({
    ...theme.typography[font]
  })
});

const functionKeys = [8, 9, 13, 27, 37, 38, 39, 40, 91];
const numKeys = [48, 49, 50, 51, 52, 53, 54, 55, 56, 57, 96, 97, 98, 99, 100, 101, 102, 103, 104, 105, 110, 190];
const limitInput = e => {
  const el = e.target;
  const {
    value
  } = el;
  const maxlength = el.getAttribute('maxlength');
  const selected = window ? window.getSelection() : undefined;
  const {
    metaKey
  } = e;
  function getPreviousInput(field) {
    let index;
    const elements = field.parentNode.children;
    for (let i = 0; i < elements.length; i += 1) {
      if (field === elements[i]) {
        index = i - 1;
        break;
      }
    }
    try {
      while (elements[index].getAttribute('type') === 'hidden' || elements[index].tagName !== 'INPUT' && elements[index].tagName !== 'TEXTAREA' && elements[index].tagName !== 'SELECT') {
        index -= 1;
      }
      return elements[index];
    } catch (err) {
      return field;
    }
  }

  // If control/command key is pressed, continue as normal
  if (metaKey) {
    return;
  }

  // If backspace is pressed on an empty field focus previous field
  if (value === '' && e.which === 8) {
    getPreviousInput(el).focus();
  }

  // If a function key is pressed, continue as normal
  if (functionKeys.indexOf(e.which) >= 0) {
    return;
  }

  // If field type is tel, only allow number keys
  if (el.getAttribute('type') === 'tel' && numKeys.includes(e.which) === false) {
    e.preventDefault();
    return;
  }

  // If maxlength is reached prevent further input
  if (maxlength && value.length === maxlength && selected.toString() === '') {
    e.preventDefault();
  }
};
const TextAreaElement = /*#__PURE__*/forwardRef((props, ref) => {
  const {
    name,
    placeholder,
    id,
    type,
    size,
    value,
    tabIndex,
    onChange,
    onInput,
    onFocus,
    onBlur
  } = props;
  const theme = useContext(ThemeContext);
  const classes = styles$f({
    theme,
    ...props
  });
  const onClick = e => {
    e.stopPropagation();
  };
  return /*#__PURE__*/React.createElement("textarea", {
    ref: ref,
    id: id,
    className: `${classes.TextArea} ${classes.Type}`,
    name: name,
    value: value,
    onKeyDown: limitInput,
    placeholder: placeholder,
    tabIndex: tabIndex,
    onChange: onChange,
    onInput: onInput,
    onFocus: onFocus,
    onBlur: onBlur,
    onClick: onClick
  });
});
TextAreaElement.displayName = 'TextAreaControl';
class TextArea extends React.Component {
  static displayName = 'TextArea';
  constructor(props) {
    super(props);
    this.theField = this.theField.bind(this);
    this.val = this.val.bind(this);
    this.validate = this.validate.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.handleInput = this.handleInput.bind(this);
    this.handleBlur = this.handleBlur.bind(this);
    this.handleFocus = this.handleFocus.bind(this);
    this.state = {
      validated: false,
      value: props.value
    };
  }
  componentDidMount() {
    const {
      attachToForm
    } = this.props;
    if (typeof attachToForm === 'function') {
      attachToForm(this);
    }
  }
  componentWillUnmount() {
    const {
      detachFromForm
    } = this.props;
    if (typeof detachFromForm === 'function') {
      detachFromForm(this);
    }
  }
  componentDidUpdate(prevProps) {
    // Typical usage (don't forget to compare props):
    if (this.props.value !== prevProps.value) {
      this.setState({
        value: this.props.value
      });
    }
  }
  theField() {
    return this.field;
  }
  val() {
    const {
      value
    } = this.state;
    return value;
  }
  async validate() {
    const {
      value
    } = this.field;
    const {
      rules,
      onValidate
    } = this.props;
    const valid = await validate(this.field, value, rules);
    this.setState({
      valid: valid.valid,
      invalid: !valid.valid,
      invalidMessage: valid.message,
      validated: true
    }, () => {
      if (typeof onValidate === 'function') {
        setTimeout(() => {
          onValidate(valid);
        }, 10);
      }
    });
    return valid;
  }
  handleKeyDown(e) {
    const {
      onKeyDown
    } = this.props;
    limitInput(e);
    if (typeof onKeyDown === 'function') {
      onKeyDown(e);
    }
  }
  handleChange(e) {
    const {
      value
    } = e.target;
    this.setState({
      value
    });
  }
  handleInput(e) {
    const el = e.target;
    const {
      value
    } = el;
    const formEl = el.form;
    const {
      validated
    } = this.state;
    const {
      onInput,
      autotab,
      maxlength,
      controlled
    } = this.props;
    function getNextInput(field, form) {
      let index;
      const elements = form ? form.elements : field.parentNode.children;
      for (let i = 0; i < elements.length; i += 1) {
        if (field === elements[i]) {
          index = i + 1;
          break;
        }
      }
      try {
        while (elements[index].getAttribute('type') === 'hidden' || elements[index].tagName !== 'INPUT' && elements[index].tagName !== 'TEXTAREA' && elements[index].tagName !== 'SELECT') {
          index += 1;
        }
        return elements[index];
      } catch (err) {
        return field;
      }
    }

    // If field has autotab attr, maxlength is reached, and a non function key is pressed, focus next field
    if (autotab && maxlength && value.length >= maxlength && functionKeys.indexOf(e.which) === -1) {
      getNextInput(el, formEl).focus();
    }

    // If the field has been validated, validate on input
    if (validated) {
      this.validate();
    }
    if (!controlled) {
      this.setState({
        value
      });
    }
    if (typeof onInput === 'function') {
      onInput(e);
    }
  }
  handleBlur(e) {
    const {
      onBlur
    } = this.props;
    if (typeof onBlur === 'function') {
      onBlur(e);
    }
  }
  handleFocus(e) {
    const {
      onFocus
    } = this.props;
    if (typeof onFocus === 'function') {
      onFocus(e);
    }
  }
  render() {
    const {
      className,
      type,
      id,
      name,
      size,
      font,
      placeholder,
      style,
      tabIndex,
      controlled
    } = this.props;
    const {
      value
    } = controlled ? this.props : this.state;
    return /*#__PURE__*/React.createElement(TextAreaElement, {
      ref: field => {
        this.field = field;
      },
      id: id,
      font: font,
      size: size,
      name: name,
      value: value,
      style: style,
      onKeyDown: limitInput,
      placeholder: placeholder,
      tabIndex: tabIndex,
      onChange: this.handleChange,
      onInput: this.handleInput,
      onFocus: this.handleFocus,
      onBlur: this.handleBlur
    });
  }
}
TextArea.propTypes = {
  attachToForm: PropTypes.func,
  detachFromForm: PropTypes.func,
  onInput: PropTypes.func,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  onValidate: PropTypes.func,
  rules: PropTypes.object,
  id: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  className: PropTypes.string,
  type: PropTypes.oneOf(['text', 'tel', 'number', 'password', 'date', 'email', 'url', 'time', 'hidden']),
  placeholder: PropTypes.string,
  name: PropTypes.string,
  size: PropTypes.string,
  font: PropTypes.string,
  style: PropTypes.object,
  tabIndex: PropTypes.number
};
TextArea.defaultProps = {
  value: '',
  size: 'md',
  font: 'control'
};

var styles$e = createUseStyles({
  Field: {
    display: 'block',
    position: 'relative',
    margin: ({
      theme,
      margin
    }) => spacing(theme, margin),
    color: ({
      theme,
      txtColor
    }) => theme.colors[txtColor]
  },
  Body: {
    display: 'flex',
    position: 'relative',
    padding: 0
  },
  Heading: {
    textAlign: ({
      theme,
      align,
      justify
    }) => align || justify,
    marginBottom: '0.5rem'
  },
  Label: ({
    theme,
    font
  }) => ({
    ...theme.typography[font]
  })
});

const generateIcon = (icon, label) => {
  if (typeof icon === 'string') {
    return /*#__PURE__*/React.createElement("img", {
      src: icon,
      alt: label
    });
  }
  return icon;
};
const Field$b = /*#__PURE__*/forwardRef((props, ref) => {
  const {
    id,
    name,
    label,
    value,
    size,
    rounded,
    align,
    justify,
    display,
    disabled,
    iconChecked,
    iconUnChecked,
    options,
    checkedClassName,
    uncheckedClassName,
    checkedColor,
    checkedHover,
    checkedTxtColor,
    checkedHoverTxtColor,
    uncheckedColor,
    uncheckedHover,
    uncheckedTxtColor,
    uncheckedHoverTxtColor,
    checkedOutline,
    uncheckedOutline,
    checkedDepth,
    uncheckedDepth,
    radioSize,
    font,
    padding,
    margin,
    tabIndex,
    columns,
    rows,
    area,
    columnGap,
    rowGap,
    filled,
    focused,
    validated,
    invalid,
    valid,
    invalidMessage,
    onValidate,
    onChange,
    onFocus,
    onBlur
  } = props;
  const theme = useContext(ThemeContext);
  const classes = styles$e({
    theme,
    ...props
  });
  return /*#__PURE__*/React.createElement("div", {
    className: classes.Field
  }, label && /*#__PURE__*/React.createElement("div", {
    className: classes.Heading
  }, /*#__PURE__*/React.createElement("label", {
    className: classes.Label,
    htmlFor: id
  }, label)), /*#__PURE__*/React.createElement("div", {
    className: classes.Body
  }, /*#__PURE__*/React.createElement(Grid, {
    columnGap: columnGap,
    rowGap: rowGap,
    columns: columns,
    rows: rows,
    area: area,
    align: align,
    justify: justify,
    display: display
  }, Array.isArray(options) && options.map((option, index) => /*#__PURE__*/React.createElement(Cell, {
    area: option.area,
    width: "auto",
    key: `${name}_${option.value}`
  }, /*#__PURE__*/React.createElement(Radio, {
    value: option.value,
    name: name,
    label: option.label,
    checkedClassName: option.checkedClassName || checkedClassName,
    uncheckedClassName: option.uncheckedClassName || uncheckedClassName,
    checkedColor: option.checkedColor || checkedColor,
    checkedHover: option.checkedHover || checkedHover,
    checkedTxtColor: option.checkedTxtColor || checkedTxtColor,
    checkedHoverTxtColor: option.checkedHoverTxtColor || checkedHoverTxtColor,
    onChange: onChange,
    onBlur: onBlur,
    onFocus: onFocus,
    checked: value === option.value,
    size: radioSize,
    font: font,
    tabIndex: tabIndex,
    uncheckedColor: option.uncheckedColor || uncheckedColor,
    uncheckedHover: option.uncheckedHover || uncheckedHover,
    uncheckedTxtColor: option.uncheckedTxtColor || uncheckedTxtColor,
    uncheckedHoverTxtColor: option.uncheckedHoverTxtColor || uncheckedHoverTxtColor,
    uncheckedOutline: typeof option.uncheckedOutline === 'boolean' ? option.uncheckedOutline : option.uncheckedOutline || uncheckedOutline,
    checkedOutline: typeof option.checkedOutline === 'boolean' ? option.checkedOutline : option.checkedOutline || checkedOutline,
    checkedDepth: option.checkedDepth || checkedDepth,
    uncheckedDepth: option.uncheckedDepth || uncheckedDepth,
    iconChecked: option.iconChecked || iconChecked,
    iconUnChecked: option.iconUnChecked || iconUnChecked,
    iconCenter: generateIcon(option.iconCenter, option.label),
    checkedIconCenter: generateIcon(option.checkedIconCenter, option.label),
    disabled: option.disabled || disabled,
    rounded: option.rounded || rounded,
    padding: option.padding || padding,
    margin: option.margin || margin
  }))))), /*#__PURE__*/React.createElement(ToolTip, {
    color: "danger",
    in: invalid === true,
    size: size
  }, invalidMessage));
});
Field$b.displayName = 'FieldRadioControl';
class FieldRadio extends React.Component {
  static displayName = 'FieldRadio';
  constructor(props) {
    super(props);
    this.theField = this.theField.bind(this);
    this.val = this.val.bind(this);
    this.validate = this.validate.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.handleBlur = this.handleBlur.bind(this);
    this.handleFocus = this.handleFocus.bind(this);
    this.state = {
      id: uId(props.id),
      validated: false,
      value: props.value
    };
  }
  componentDidMount() {
    const {
      attachToForm
    } = this.props;
    if (typeof attachToForm === 'function') {
      attachToForm(this);
    }
  }
  componentWillUnmount() {
    const {
      detachFromForm
    } = this.props;
    if (typeof detachFromForm === 'function') {
      detachFromForm(this);
    }
  }
  componentDidUpdate(prevProps) {
    // Typical usage (don't forget to compare props):
    if (this.props.value !== prevProps.value) {
      this.setState({
        value: this.props.value
      });
    }
  }
  theField() {
    return this.field.theField();
  }
  val() {
    const {
      value
    } = this.state;
    return value;
  }
  async validate() {
    const {
      value
    } = this.state;
    const {
      rules,
      onValidate
    } = this.props;
    const valid = await validate(this, value, rules);
    this.setState({
      validated: true,
      valid: valid.valid,
      invalid: !valid.valid,
      invalidMessage: rules.message || valid.message
    }, () => {
      if (typeof onValidate === 'function') {
        onValidate();
      }
    });
    return valid;
  }
  handleChange(e) {
    const {
      onChange
    } = this.props;
    const {
      validated,
      value
    } = this.state;
    const {
      checked
    } = e.target;
    this.setState({
      filled: checked,
      value: checked ? e.target.value : value
    }, () => {
      if (validated) {
        this.validate();
      }
      if (typeof onChange === 'function') {
        onChange(e);
      }
    });
  }
  handleBlur(e) {
    const {
      onBlur
    } = this.props;
    if (typeof onBlur === 'function') {
      onBlur(e);
    }
    this.setState({
      focused: false
    });
  }
  handleFocus(e) {
    const {
      onFocus
    } = this.props;
    this.setState({
      focused: true
    });
    if (typeof onFocus === 'function') {
      onFocus(e);
    }
  }
  render() {
    console.log(this.props.checkedClassName);
    const {
      filled,
      focused,
      validated,
      invalid,
      valid,
      value,
      invalidMessage
    } = this.state;
    return /*#__PURE__*/React.createElement(Field$b, {
      filled: filled,
      focused: focused,
      validated: validated,
      invalid: invalid,
      valid: valid,
      value: value,
      invalidMessage: invalidMessage,
      disabled: this.props.disabled,
      onChange: this.handleChange,
      onFocus: this.handleFocus,
      onBlur: this.handleBlur,
      attachToForm: this.props.attachToForm,
      detachFromForm: this.props.detachFromForm,
      name: this.props.name,
      label: this.props.label,
      id: this.props.id,
      options: this.props.options,
      color: this.props.color,
      font: this.props.font,
      checkedClassName: this.props.checkedClassName,
      uncheckedClassName: this.props.uncheckedClassName,
      checkedColor: this.props.checkedColor,
      checkedTxtColor: this.props.checkedTxtColor,
      checkedHoverTxtColor: this.props.checkedHoverTxtColor,
      checkedHover: this.props.checkedHover,
      uncheckedColor: this.props.uncheckedColor,
      uncheckedHover: this.props.uncheckedHover,
      uncheckedTxtColor: this.props.uncheckedTxtColor,
      uncheckedHoverTxtColor: this.props.uncheckedHoverTxtColor,
      uncheckedOutline: this.props.uncheckedOutline,
      checkedOutline: this.props.checkedOutline,
      checkedDepth: this.props.checkedDepth,
      uncheckedDepth: this.props.uncheckedDepth,
      size: this.props.size,
      padding: this.props.padding,
      rounded: this.props.rounded,
      radioSize: this.props.radioSize,
      align: this.props.align,
      justify: this.props.justify,
      display: this.props.display,
      tabIndex: this.props.tabIndex,
      rules: this.props.rules,
      columnGap: this.props.columnGap,
      rowGap: this.props.rowGap,
      columns: this.props.columns,
      area: this.props.area,
      margin: this.props.margin,
      iconChecked: this.props.iconChecked,
      iconUnChecked: this.props.iconUnChecked
    });
  }
}
FieldRadio.propTypes = {
  attachToForm: PropTypes.func,
  detachFromForm: PropTypes.func,
  onChange: PropTypes.func,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  onValidate: PropTypes.func,
  name: PropTypes.string,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  id: PropTypes.string,
  options: PropTypes.arrayOf(PropTypes.object),
  value: PropTypes.string,
  color: PropTypes.string,
  font: PropTypes.string,
  checkedClassName: PropTypes.string,
  uncheckedClassName: PropTypes.string,
  checkedColor: PropTypes.string,
  checkedHover: PropTypes.string,
  uncheckedColor: PropTypes.string,
  uncheckedHover: PropTypes.string,
  uncheckedOutline: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  checkedOutline: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  checkedDepth: PropTypes.string,
  uncheckedDepth: PropTypes.string,
  size: PropTypes.string,
  rounded: PropTypes.oneOfType([PropTypes.string, PropTypes.bool, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  radioSize: PropTypes.string,
  iconChecked: PropTypes.node,
  iconUnChecked: PropTypes.node,
  align: PropTypes.string,
  justify: PropTypes.string,
  display: PropTypes.string,
  tabIndex: PropTypes.number,
  rules: PropTypes.object,
  columnGap: PropTypes.string,
  rowGap: PropTypes.string,
  columns: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  rows: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  area: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  padding: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  margin: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))])
};
FieldRadio.defaultProps = {
  size: 'md',
  align: '',
  columnGap: PropTypes.string,
  rowGap: PropTypes.string,
  columns: '1fr',
  columnGap: 'xs',
  rowGap: 'xs',
  margin: [0, 0, 'md', 0],
  id: 'radio-field',
  rules: {}
};

var styles$d = createUseStyles({
  Field: {
    display: 'block',
    position: 'relative',
    margin: ({
      theme,
      margin
    }) => spacing(theme, margin)
  },
  Body: {
    display: 'flex',
    position: 'relative',
    padding: 0,
    borderStyle: 'solid',
    borderWidth: ({
      chunky
    }) => chunky ? '4px' : '1px',
    background: ({
      theme,
      bgColor
    }) => theme.colors[bgColor],
    borderColor: ({
      theme,
      borderColor,
      invalid
    }) => invalid ? theme.colors.danger : theme.colors[borderColor] || 'rgba(255, 255, 255, 0)',
    borderRadius: ({
      theme,
      rounded
    }) => radius(theme, rounded)
  },
  Label: ({
    theme,
    size,
    font,
    txtColor
  }) => ({
    ...theme.typography[font],
    display: 'inline-block',
    marginBottom: theme.spacing['xs'],
    color: theme.colors[txtColor]
  }),
  Delimiter: {
    display: 'inline-flex',
    alignItems: 'center',
    color: ({
      theme,
      inputTxtColor
    }) => theme.colors[inputTxtColor]
  },
  Icon: {
    display: 'flex',
    flexShrink: '0',
    alignItems: 'center',
    justifyContent: 'center',
    width: '2.5rem',
    color: '#fff',
    borderTopLeftRadius: 'radius(field)',
    borderBottomLeftRadius: 'radius(field)',
    background: ({
      theme,
      color,
      invalid
    }) => invalid ? theme.colors.danger : theme.colors[color],
    borderRadius: ({
      theme,
      rounded,
      size
    }) => rounded ? [[theme.radius[size], 0, 0, theme.radius[size]]] : 0,
    margin: '-1px 0 -1px -1px'
  }
});

const Field$a = /*#__PURE__*/forwardRef(({
  id,
  label,
  size: sz,
  icon,
  invalid,
  focused,
  invalidMessage,
  ...props
}, ref) => {
  const theme = useContext(ThemeContext);
  const size = useResponsive(sz, theme.breakpoints);
  const classes = styles$d({
    theme,
    ...props
  });
  return /*#__PURE__*/React.createElement("div", {
    className: classes.Field
  }, label && /*#__PURE__*/React.createElement("div", {
    className: classes.Header
  }, /*#__PURE__*/React.createElement("label", {
    className: classes.Label,
    htmlFor: id
  }, label)), /*#__PURE__*/React.createElement("div", {
    className: classes.Body
  }, icon && /*#__PURE__*/React.createElement("div", {
    className: classes.Icon
  }, icon), /*#__PURE__*/React.createElement(Input, _extends$d({
    ref: ref,
    className: classes.Control,
    size: size
  }, props))), /*#__PURE__*/React.createElement(ToolTip, {
    color: "danger",
    in: invalid === true && focused,
    size: "md"
  }, invalidMessage));
});
Field$a.displayName = 'FieldTextControl';
class FieldText extends React.Component {
  static displayName = 'FieldText';
  constructor(props) {
    super(props);
    this.theField = this.theField.bind(this);
    this.val = this.val.bind(this);
    this.validate = this.validate.bind(this);
    this.handleInput = this.handleInput.bind(this);
    this.handleBlur = this.handleBlur.bind(this);
    this.handleFocus = this.handleFocus.bind(this);
    this.state = {
      id: uId(props.id),
      validated: false
    };
  }
  componentDidMount() {
    const {
      attachToForm
    } = this.props;
    if (typeof attachToForm === 'function') {
      attachToForm(this);
    }
  }
  componentWillUnmount() {
    const {
      detachFromForm
    } = this.props;
    if (typeof detachFromForm === 'function') {
      detachFromForm(this);
    }
  }
  theField() {
    return this.field.theField();
  }
  val() {
    return this.field.val();
  }
  async validate() {
    const valid = await this.field.validate();
    this.setState({
      valid: valid.valid,
      invalid: !valid.valid,
      invalidMessage: !valid.valid ? valid.message : '',
      validated: true
    });
    return valid;
  }
  handleInput(e) {
    const {
      onInput
    } = this.props;
    const {
      validated
    } = this.state;
    if (typeof onInput === 'function') {
      onInput(e);
    }
    this.setState({
      filled: this.field.val() === true
    });
    if (validated) {
      this.validate();
    }
  }
  handleBlur(e) {
    const {
      onBlur
    } = this.props;
    if (typeof onBlur === 'function') {
      onBlur(e);
    }
    this.setState({
      focused: false
    });
  }
  handleFocus(e) {
    const {
      onFocus
    } = this.props;
    this.setState({
      focused: true
    });
    if (typeof onFocus === 'function') {
      onFocus(e);
    }
  }
  render() {
    const {
      id,
      filled,
      focused,
      validated,
      invalid,
      valid,
      invalidMessage
    } = this.state;
    return /*#__PURE__*/React.createElement(Field$a, _extends$d({
      id: id || this.props.name,
      ref: field => {
        this.field = field;
      },
      onInput: this.handleInput,
      onFocus: this.handleFocus,
      onBlur: this.handleBlur,
      filled: filled,
      focused: focused,
      validated: validated,
      invalid: invalid,
      valid: valid,
      invalidMessage: invalidMessage
    }, this.props));
  }
}
FieldText.propTypes = {
  attachToForm: PropTypes.func,
  detachFromForm: PropTypes.func,
  onInput: PropTypes.func,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  onValidate: PropTypes.func,
  name: PropTypes.string,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  placeholder: PropTypes.string,
  id: PropTypes.string,
  value: PropTypes.string,
  color: PropTypes.string,
  borderColor: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  icon: PropTypes.node,
  theme: PropTypes.string,
  size: PropTypes.string,
  rounded: PropTypes.oneOfType([PropTypes.string, PropTypes.bool, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  chunky: PropTypes.bool,
  align: PropTypes.string,
  tabIndex: PropTypes.number,
  rules: PropTypes.object,
  type: PropTypes.string,
  bgColor: PropTypes.string,
  padding: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  margin: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))])
};
FieldText.defaultProps = {
  color: 'primary',
  bgColor: 'white',
  borderColor: 'neutral200',
  size: 'md',
  rounded: 'md',
  align: '',
  id: 'text-field',
  type: 'text'
};

const Field$9 = /*#__PURE__*/forwardRef(({
  desc,
  name,
  label,
  onChange,
  onInput,
  rules,
  options,
  mobileOptions,
  ...props
}, ref) => {
  const theme = useContext(ThemeContext);
  const breakpoints = theme.mediaQueries;
  const show = useResponsive({
    default: false,
    tablet: true
  }, breakpoints);
  const opts = show ? options : mobileOptions || options;
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(FieldText, _extends$d({
    ref: ref,
    name: name,
    type: "tel",
    onInput: onInput,
    rules: rules
  }, props)), desc && /*#__PURE__*/React.createElement(P, {
    align: "center"
  }, desc), /*#__PURE__*/React.createElement(FieldRadio, _extends$d({
    onChange: onChange,
    options: opts,
    label: label,
    rules: {
      number: true,
      ...rules
    }
  }, props)));
});
Field$9.displayName = 'FieldAmountControl';
class FieldAmount extends React.Component {
  static displayName = 'FieldAmount';
  constructor(props) {
    super(props);
    this.handleChange = this.handleChange.bind(this);
    this.handleInput = this.handleInput.bind(this);
    this.state = {
      value: props.value
    };
  }
  handleInput(e) {
    const {
      onInput
    } = this.props;
    this.setState({
      value: e.target.value
    }, () => {
      if (typeof onInput === 'function') {
        onInput(e);
      }
    });
  }
  handleChange(e) {
    const {
      onChange
    } = this.props;
    this.state;
    this.setState({
      value: e.target.value,
      filled: e.target.value ? true : false
    }, () => {
      if (typeof onChange === 'function') {
        onChange(e);
      }
    });
  }
  render() {
    const {
      id,
      filled,
      value
    } = this.state;
    const {
      value: defaultValue,
      onInput: defaultOnInput,
      onChange: defaultOnChange,
      ...props
    } = this.props;
    return /*#__PURE__*/React.createElement(Field$9, _extends$d({
      value: value,
      onInput: this.handleInput,
      onChange: this.handleChange
    }, props));
  }
}
FieldAmount.propTypes = {
  attachToForm: PropTypes.func,
  detachFromForm: PropTypes.func,
  onChange: PropTypes.func,
  onInput: PropTypes.func,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  onValidate: PropTypes.func,
  name: PropTypes.string,
  placeholder: PropTypes.string,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  id: PropTypes.string,
  desc: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  color: PropTypes.string,
  bgColor: PropTypes.string,
  icon: PropTypes.node,
  size: PropTypes.string,
  rounded: PropTypes.oneOfType([PropTypes.string, PropTypes.bool, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  chunky: PropTypes.bool,
  align: PropTypes.string,
  tabIndex: PropTypes.number,
  rules: PropTypes.object,
  options: PropTypes.arrayOf(PropTypes.object),
  checkedColor: PropTypes.string,
  checkedHover: PropTypes.string,
  uncheckedColor: PropTypes.string,
  uncheckedHover: PropTypes.string,
  uncheckedOutline: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  checkedOutline: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  radioSize: PropTypes.string,
  iconChecked: PropTypes.node,
  iconUnChecked: PropTypes.node,
  columnGap: PropTypes.string,
  rowGap: PropTypes.string,
  columns: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  rows: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  area: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  padding: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  margin: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))])
};
FieldAmount.defaultProps = {
  size: 'md',
  color: 'primary',
  bgColor: 'white',
  borderColor: 'neutral200',
  rounded: 'md',
  align: '',
  id: 'amount-field'
};

const Field$8 = /*#__PURE__*/forwardRef((props, ref) => {
  const {
    label,
    size,
    icon,
    invalid,
    focused,
    invalidMessage,
    id
  } = props;
  const theme = useContext(ThemeContext);
  const classes = styles$d({
    theme,
    ...props
  });
  return /*#__PURE__*/React.createElement("div", {
    className: classes.Field
  }, label && /*#__PURE__*/React.createElement("div", {
    className: classes.Header
  }, /*#__PURE__*/React.createElement("label", {
    className: classes.Label,
    htmlFor: id
  }, label)), /*#__PURE__*/React.createElement("div", {
    className: classes.Body
  }, icon && /*#__PURE__*/React.createElement("div", {
    className: classes.Icon
  }, icon), /*#__PURE__*/React.createElement(TextArea, _extends$d({
    ref: ref,
    id: id,
    className: classes.Control
  }, props))), /*#__PURE__*/React.createElement(ToolTip, {
    color: "danger",
    in: invalid === true && focused,
    size: size
  }, invalidMessage));
});
Field$8.displayName = 'FieldAreaControl';
class FieldArea extends React.Component {
  static displayName = 'FieldArea';
  constructor(props) {
    super(props);
    this.theField = this.theField.bind(this);
    this.val = this.val.bind(this);
    this.validate = this.validate.bind(this);
    this.handleInput = this.handleInput.bind(this);
    this.handleBlur = this.handleBlur.bind(this);
    this.handleFocus = this.handleFocus.bind(this);
    this.state = {
      id: uId(props.id),
      validated: false
    };
  }
  componentDidMount() {
    const {
      attachToForm
    } = this.props;
    if (typeof attachToForm === 'function') {
      attachToForm(this);
    }
  }
  componentWillUnmount() {
    const {
      detachFromForm
    } = this.props;
    if (typeof detachFromForm === 'function') {
      detachFromForm(this);
    }
  }
  theField() {
    return this.field.theField();
  }
  val() {
    return this.field.val();
  }
  async validate() {
    const valid = await this.field.validate();
    this.setState({
      valid: valid.valid,
      invalid: !valid.valid,
      invalidMessage: !valid.valid ? valid.message : '',
      validated: true
    });
    return valid;
  }
  handleInput(e) {
    const {
      onInput
    } = this.props;
    const {
      validated
    } = this.state;
    if (typeof onInput === 'function') {
      onInput(e);
    }
    this.setState({
      filled: this.field.val() === true
    });
    if (validated) {
      this.validate();
    }
  }
  handleBlur(e) {
    const {
      onBlur
    } = this.props;
    if (typeof onBlur === 'function') {
      onBlur(e);
    }
    this.setState({
      focused: false
    });
  }
  handleFocus(e) {
    const {
      onFocus
    } = this.props;
    this.setState({
      focused: true
    });
    if (typeof onFocus === 'function') {
      onFocus(e);
    }
  }
  render() {
    const {
      id,
      filled,
      focused,
      validated,
      invalid,
      valid,
      invalidMessage,
      value
    } = this.state;
    return /*#__PURE__*/React.createElement(Field$8, _extends$d({
      ref: field => {
        this.field = field;
      },
      id: id,
      value: value,
      onInput: this.handleInput,
      onFocus: this.handleFocus,
      onBlur: this.handleBlur,
      filled: filled,
      focused: focused,
      validated: validated,
      invalid: invalid,
      valid: valid,
      invalidMessage: invalidMessage
    }, this.props));
  }
}
FieldArea.propTypes = {
  attachToForm: PropTypes.func,
  detachFromForm: PropTypes.func,
  onInput: PropTypes.func,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  onValidate: PropTypes.func,
  name: PropTypes.string,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  id: PropTypes.string,
  value: PropTypes.string,
  color: PropTypes.string,
  borderColor: PropTypes.string,
  icon: PropTypes.node,
  theme: PropTypes.string,
  size: PropTypes.string,
  rounded: PropTypes.oneOfType([PropTypes.string, PropTypes.bool, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  chunky: PropTypes.bool,
  align: PropTypes.string,
  tabIndex: PropTypes.number,
  rules: PropTypes.object,
  type: PropTypes.string,
  padding: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  margin: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))])
};
FieldArea.defaultProps = {
  color: 'primary',
  bgColor: 'white',
  borderColor: 'neutral200',
  size: 'md',
  rounded: 'md',
  align: '',
  id: 'text-field',
  type: 'text'
};

const type = {
  sm: 'control',
  md: 'label',
  lg: 'p',
  xl: 'h5',
  xxl: 'h4'
};
const controlMargin = {
  xs: [0, 'xs', 0, 0],
  sm: [0, 'xs', 0, 0],
  md: [0, 'md', 0, 0],
  lg: [0, 'md', 0, 0],
  xl: [0, 'lg', 0, 0]
};
var styles$c = createUseStyles({
  Field: {
    display: 'block',
    position: 'relative',
    marginBottom: '1rem',
    margin: ({
      theme,
      margin
    }) => spacing(theme, margin),
    color: ({
      theme,
      txtColor
    }) => theme.colors[txtColor]
  },
  Body: {
    display: ({
      inline
    }) => inline ? 'block' : 'flex',
    position: 'relative',
    alignItems: ({
      align
    }) => align,
    padding: 0
  },
  Control: {
    float: ({
      inline
    }) => inline ? 'left' : 'none',
    lineHeight: 0,
    margin: ({
      theme,
      size
    }) => spacing(theme, controlMargin[size])
  },
  Label: ({
    theme,
    size
  }) => ({
    cursor: 'pointer',
    ...theme.typography[type[size]]
  })
});

const Field$7 = /*#__PURE__*/forwardRef((props, ref) => {
  const {
    id,
    filled,
    focused,
    validated,
    invalid,
    valid,
    invalidMessage,
    name,
    label,
    value,
    size,
    color,
    checked,
    rules,
    icon,
    tabIndex,
    onValidate,
    onClick,
    onChange,
    onFocus,
    onBlur
  } = props;
  const theme = useContext(ThemeContext);
  const classes = styles$c({
    theme,
    ...props
  });
  return /*#__PURE__*/React.createElement("div", {
    className: classes.Field,
    onClick: onClick
  }, /*#__PURE__*/React.createElement("label", {
    className: classes.Body
  }, icon && /*#__PURE__*/React.createElement("div", {
    className: classes.Icon
  }, icon), /*#__PURE__*/React.createElement("div", {
    className: classes.Control
  }, /*#__PURE__*/React.createElement(Checkbox, {
    ref: ref,
    id: id,
    name: name,
    value: value,
    onValidate: onValidate,
    invalid: invalid,
    onChange: onChange,
    onFocus: onFocus,
    onBlur: onBlur,
    rules: rules,
    checked: checked,
    size: size,
    color: color,
    tabIndex: tabIndex
  })), label && /*#__PURE__*/React.createElement("div", {
    className: classes.Label,
    htmlFor: id
  }, label)), /*#__PURE__*/React.createElement(ToolTip, {
    color: "danger",
    in: invalid === true,
    size: "md"
  }, invalidMessage));
});
Field$7.displayName = 'FieldCheckboxControl';
class FieldCheckbox extends React.Component {
  static displayName = 'FieldCheckbox';
  constructor(props) {
    super(props);
    this.theField = this.theField.bind(this);
    this.val = this.val.bind(this);
    this.validate = this.validate.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.handleBlur = this.handleBlur.bind(this);
    this.handleFocus = this.handleFocus.bind(this);
    this.state = {
      validated: false,
      checked: props.checked || false
    };
  }
  componentDidMount() {
    const {
      attachToForm
    } = this.props;
    if (typeof attachToForm === 'function') {
      attachToForm(this);
    }
  }
  componentWillUnmount() {
    const {
      detachFromForm
    } = this.props;
    if (typeof detachFromForm === 'function') {
      detachFromForm(this);
    }
  }
  theField() {
    return this.field.theField();
  }
  val() {
    return this.field.val();
  }
  async validate() {
    const valid = await this.field.validate();
    this.setState({
      valid: valid.valid,
      invalid: !valid.valid,
      validated: true,
      invalidMessage: this.props.rules ? this.props.rules.message || valid.message : valid.message
    });
    return valid;
  }
  handleChange(e) {
    const {
      onInput
    } = this.props;
    const {
      validated
    } = this.state;
    const checked = e.target.checked;
    this.setState({
      filled: checked,
      checked
    }, () => {
      if (validated) {
        this.validate();
      }
      if (typeof onInput === 'function') {
        onChange(e);
      }
    });
  }
  handleBlur(e) {
    const {
      onBlur
    } = this.props;
    if (typeof onBlur === 'function') {
      onBlur(e);
    }
    this.setState({
      focused: false
    });
  }
  handleFocus(e) {
    const {
      onFocus
    } = this.props;
    this.setState({
      focused: true
    });
    if (typeof onFocus === 'function') {
      onFocus(e);
    }
  }
  render() {
    const {
      id,
      filled,
      focused,
      validated,
      invalid,
      valid,
      invalidMessage,
      checked
    } = this.state;
    const {
      rules,
      color,
      size,
      label,
      align,
      spacing,
      inline,
      padding,
      margin,
      value,
      name
    } = this.props;
    return /*#__PURE__*/React.createElement(Field$7, {
      ref: field => this.field = field,
      name: name,
      filled: filled,
      focused: focused,
      validated: validated,
      invalid: invalid,
      valid: valid,
      invalidMessage: invalidMessage,
      checked: checked,
      onChange: this.handleChange,
      onFocus: this.handleFocus,
      onBlur: this.handleBlur,
      rules: rules,
      color: color,
      size: size,
      label: label,
      align: align,
      inline: inline,
      spacing: spacing,
      padding: padding,
      margin: margin,
      value: value
    });
  }
}
FieldCheckbox.propTypes = {
  attachToForm: PropTypes.func,
  detachFromForm: PropTypes.func,
  onInput: PropTypes.func,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  onValidate: PropTypes.func,
  name: PropTypes.string,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  id: PropTypes.string,
  value: PropTypes.string,
  color: PropTypes.string,
  txtColor: PropTypes.string,
  theme: PropTypes.string,
  size: PropTypes.string,
  align: PropTypes.string,
  tabIndex: PropTypes.number,
  rules: PropTypes.object,
  spacing: PropTypes.string,
  inline: PropTypes.bool,
  padding: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  margin: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))])
};
FieldCheckbox.defaultProps = {
  color: 'light',
  size: 'md',
  align: 'center',
  inline: true,
  txtColor: 'black',
  id: 'text-field',
  spacing: 'md'
};

var styles$b = createUseStyles({
  Field: {
    display: 'block',
    position: 'relative',
    margin: ({
      theme,
      margin
    }) => spacing(theme, margin)
  },
  Body: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
    padding: 0,
    borderStyle: 'solid',
    borderWidth: ({
      chunky
    }) => chunky ? '4px' : '1px',
    background: ({
      theme,
      bgColor
    }) => theme.colors[bgColor],
    borderColor: ({
      theme,
      borderColor,
      invalid
    }) => invalid ? theme.colors.danger : theme.colors[borderColor] || 'rgba(255, 255, 255, 0)',
    borderRadius: ({
      theme,
      rounded,
      size
    }) => rounded ? theme.radius[size] : 0,
    '& p': {
      color: ({
        theme,
        inputTxtColor
      }) => theme.colors[inputTxtColor] || '#333',
      margin: 'auto 0',
      flex: 0
    }
  },
  Label: ({
    theme,
    font,
    txtColor
  }) => ({
    ...theme.typography[font],
    display: 'inline-block',
    marginBottom: theme.spacing['xs'],
    color: theme.colors[txtColor]
  }),
  Delimiter: {
    display: 'inline-flex',
    alignItems: 'center',
    color: ({
      theme,
      inputTxtColor
    }) => theme.colors[inputTxtColor]
  },
  Icon: {
    display: 'flex',
    flexShrink: '0',
    alignItems: 'center',
    justifyContent: 'center',
    width: '2.5rem',
    color: '#fff',
    borderTopLeftRadius: 'radius(field)',
    borderBottomLeftRadius: 'radius(field)',
    background: ({
      theme,
      color,
      invalid
    }) => invalid ? theme.colors.danger : theme.colors[color],
    borderRadius: ({
      theme,
      rounded,
      size
    }) => rounded ? [[theme.radius[size], 0, 0, theme.radius[size]]] : 0,
    margin: '-1px 0 -1px -1px'
  }
});

const Field$6 = /*#__PURE__*/forwardRef((props, ref) => {
  const {
    label,
    icon,
    invalid,
    focused,
    size,
    invalidMessage,
    children,
    id,
    onClick
  } = props;
  const theme = useContext(ThemeContext);
  const classes = styles$b({
    theme,
    ...props
  });
  return /*#__PURE__*/React.createElement("div", {
    className: classes.Field,
    onClick: onClick
  }, label && /*#__PURE__*/React.createElement("div", {
    className: classes.Header
  }, /*#__PURE__*/React.createElement("label", {
    className: classes.Label,
    htmlFor: id
  }, label)), /*#__PURE__*/React.createElement("div", {
    className: classes.Body
  }, icon && /*#__PURE__*/React.createElement("div", {
    className: classes.Icon
  }, icon), children), /*#__PURE__*/React.createElement(ToolTip, {
    color: "danger",
    in: invalid === true && focused,
    size: size
  }, invalidMessage));
});
Field$6.displayName = 'FieldCompositeControl';
class FieldComposite extends React.Component {
  static displayName = 'FieldComposite';
  constructor(props) {
    super(props);
    this.field = {};
    this.validation = this.validation.bind(this);
    this.handleClick = this.handleClick.bind(this);
    this.handleInput = this.handleInput.bind(this);
    this.handleBlur = this.handleBlur.bind(this);
    this.handleFocus = this.handleFocus.bind(this);
    this.state = {
      id: uId(props.id),
      validated: false
    };
  }
  componentDidMount() {
    const {
      attachToForm
    } = this.props;
    if (typeof attachToForm === 'function') {
      attachToForm(this);
    }
  }
  componentWillUnmount() {
    const {
      detachFromForm
    } = this.props;
    if (typeof detachFromForm === 'function') {
      detachFromForm(this);
    }
  }
  val() {
    return Object.keys(this.field).reduce((value, key) => value + this.field[key].val(), '');
  }
  validation(fieldValid) {
    const {
      onValidate
    } = this.props;
    const valid = Object.keys(this.field).every(key => {
      return this.field[key].state.valid === true;
    });
    let invalidMessage = '';
    Object.keys(this.field).forEach(key => {
      const field = this.field[key];
      if (field.state.invalid) {
        invalidMessage = field.state.invalidMessage;
      }
    });
    this.setState({
      valid: valid,
      invalid: !valid,
      validated: true,
      invalidMessage
    }, () => {
      if (typeof onValidate === 'function') {
        onValidate(fieldValid);
      }
    });
    return valid;
  }
  handleInput(e) {
    const {
      onInput
    } = this.props;
    if (typeof onInput === 'function') {
      onInput(e);
    }
    this.setState({
      filled: Object.keys(this.field).some(key => this.field[key].val() === true)
    });
  }
  handleBlur(e) {
    const {
      onBlur
    } = this.props;
    if (typeof onBlur === 'function') {
      onBlur(e);
    }
    clearTimeout(this.timer);
    this.timer = setTimeout(() => {
      this.setState({
        focused: false
      });
    }, 15);
  }
  handleFocus(e) {
    const {
      onFocus
    } = this.props;
    clearTimeout(this.timer);
    this.setState({
      focused: true
    });
    if (typeof onFocus === 'function') {
      onFocus(e);
    }
  }
  handleClick(e) {
    const element = e.currentTarget.querySelector('select') || e.currentTarget.querySelector('input') || e.currentTarget.querySelector('textarea');
    element.focus();
  }
  render() {
    const {
      size,
      filled,
      focused,
      validated,
      invalid,
      valid,
      invalidMessage,
      value
    } = this.state;
    const childrenWithProps = React.Children.map(this.props.children, (child, index) => {
      if (!child || !child.props) {
        return child;
      }
      if (child.props.name) {
        return /*#__PURE__*/React.cloneElement(child, {
          key: child.name,
          size: child.size || size,
          ref: field => {
            if (field) {
              this.field[field.props.name] = field;
            }
          },
          attachToForm: this.props.attachToForm,
          detachFromForm: this.props.detachFromForm,
          onInput: this.handleInput,
          onFocus: this.handleFocus,
          onBlur: this.handleBlur,
          onValidate: this.validation
        });
      }
      return child;
    });
    return /*#__PURE__*/React.createElement(Field$6, _extends$d({
      value: value,
      filled: filled,
      focused: focused,
      validated: validated,
      invalid: invalid,
      valid: valid,
      invalidMessage: invalidMessage,
      onClick: this.handleClick
    }, this.props), childrenWithProps);
  }
}
FieldComposite.propTypes = {
  attachToForm: PropTypes.func,
  detachFromForm: PropTypes.func,
  onInput: PropTypes.func,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  onValidate: PropTypes.func,
  name: PropTypes.string,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  id: PropTypes.string,
  value: PropTypes.string,
  color: PropTypes.string,
  borderColor: PropTypes.string,
  bgColor: PropTypes.string,
  icon: PropTypes.node,
  size: PropTypes.string,
  rounded: PropTypes.oneOfType([PropTypes.string, PropTypes.bool, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  align: PropTypes.string,
  tabIndex: PropTypes.number,
  rules: PropTypes.object,
  padding: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  margin: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))])
};
FieldComposite.defaultProps = {
  color: 'primary',
  bgColor: 'white',
  borderColor: 'neutral200',
  size: 'md',
  rounded: 'md',
  align: '',
  id: 'text-field',
  type: 'text'
};

const splitDate$1 = (value, format) => {
  let day = '';
  let month = '';
  let year = '';
  if (value) {
    // Extract day, month, and year from value based on parsed date format positions
    day = value.substring(format.d[0], format.d[format.d.length - 1] + 1);
    month = value.substring(format.m[0], format.m[format.m.length - 1] + 1);
    year = value.substring(format.y[0], format.y[format.y.length - 1] + 1);
  }
  return {
    day,
    month,
    year
  };
};
const toIterableFormatString$1 = format => {
  const formatStrings = 'DMY';
  return format.split('').filter((letter, index) => formatStrings.includes(letter) ? format.indexOf(letter) === index : true).join('');
};
const parseDateFormat$1 = fmt => {
  const d = [];
  const m = [];
  const y = [];
  let delimiter;
  for (let i = 0; i < fmt.length; i += 1) {
    switch (fmt[i]) {
      case 'D':
        d.push(i);
        break;
      case 'M':
        m.push(i);
        break;
      case 'Y':
        y.push(i);
        break;
      default:
        delimiter = fmt[i];
    }
  }
  return {
    d,
    m,
    y,
    delimiter
  };
};
const formatDateValue$1 = (format, day, month, year) => {
  const order = toIterableFormatString$1(format);
  return order.split('').map(letter => {
    let value = '';
    switch (letter) {
      case 'D':
        value = day;
        break;
      case 'M':
        value = month;
        break;
      case 'Y':
        value = year;
        break;
      default:
        value = letter;
    }
    return value;
  }).join('');
};
const Field$5 = /*#__PURE__*/forwardRef((props, ref) => {
  const {
    id,
    focused,
    invalid,
    invalidMessage,
    name,
    label,
    value,
    format,
    font,
    rules,
    minDate,
    maxDate,
    icon,
    size,
    tabIndex,
    onValidate,
    handleInputDay,
    handleInputMonth,
    handleInputYear,
    onFocus,
    onBlur,
    day,
    month,
    year
  } = props;
  const theme = useContext(ThemeContext);
  const classes = styles$d({
    theme,
    ...props
  });
  const outputDateFields = () => {
    const order = toIterableFormatString$1(format);
    return order.split('').map(letter => {
      let value = '';
      switch (letter) {
        case 'D':
          value = /*#__PURE__*/React.createElement(Input, {
            key: `${name}_day`,
            className: classes.Control,
            placeholder: "DD",
            type: "tel",
            value: day,
            font: font,
            autotab: true,
            tabIndex: tabIndex,
            onValidate: onValidate,
            onInput: handleInputDay,
            onFocus: onFocus,
            onBlur: onBlur,
            maxlength: 2,
            width: "auto",
            rules: {
              maxlength: 2
            },
            size: size,
            inputSize: "3"
          });
          break;
        case 'M':
          value = /*#__PURE__*/React.createElement(Input, {
            key: `${name}_month`,
            className: classes.Control,
            placeholder: "MM",
            type: "tel",
            value: month,
            font: font,
            autotab: true,
            tabIndex: tabIndex,
            onValidate: onValidate,
            onInput: handleInputMonth,
            onFocus: onFocus,
            onBlur: onBlur,
            maxlength: 2,
            width: "auto",
            rules: {
              maxlength: 2
            },
            size: size,
            inputSize: "3"
          });
          break;
        case 'Y':
          value = /*#__PURE__*/React.createElement(Input, {
            key: `${name}_year`,
            className: classes.Control,
            placeholder: "YYYY",
            type: "tel",
            value: year,
            font: font,
            tabIndex: tabIndex,
            onValidate: onValidate,
            onInput: handleInputYear,
            onFocus: onFocus,
            onBlur: onBlur,
            maxlength: 4,
            width: "auto",
            rules: {
              maxlength: 4
            },
            size: size,
            inputSize: "5"
          });
          break;
        default:
          value = /*#__PURE__*/React.createElement("span", {
            key: uId(`${name}_del`),
            className: classes.Delimiter
          }, letter);
      }
      return value;
    });
  };
  return /*#__PURE__*/React.createElement("div", {
    className: classes.Field
  }, label && /*#__PURE__*/React.createElement("div", {
    className: classes.Header
  }, /*#__PURE__*/React.createElement("label", {
    className: classes.Label,
    htmlFor: id
  }, label)), /*#__PURE__*/React.createElement("label", {
    className: classes.Body,
    style: {
      justifyContent: 'center'
    }
  }, icon && /*#__PURE__*/React.createElement("div", {
    className: classes.Icon
  }, icon), /*#__PURE__*/React.createElement(HiddenInput, {
    ref: ref,
    type: "hidden",
    name: name,
    value: value,
    rules: {
      date: {
        format,
        min: minDate,
        max: maxDate
      },
      ...rules
    }
  }), outputDateFields()), /*#__PURE__*/React.createElement(ToolTip, {
    color: "danger",
    in: invalid === true && focused,
    size: size
  }, invalidMessage));
});
Field$5.displayName = 'FieldDateControl';
class FieldDate extends React.Component {
  static displayName = 'FieldDate';
  constructor(props) {
    super(props);
    const format = parseDateFormat$1(props.format || 'MM/DD/YYYY');
    const value = splitDate$1(props.value, format);
    this.delimiter = format.delimiter;
    this.theField = this.theField.bind(this);
    this.val = this.val.bind(this);
    this.validate = this.validate.bind(this);
    this.updateValue = this.updateValue.bind(this);
    this.handleInputDay = this.handleInputDay.bind(this);
    this.handleInputMonth = this.handleInputMonth.bind(this);
    this.handleInputYear = this.handleInputYear.bind(this);
    this.handleBlur = this.handleBlur.bind(this);
    this.handleFocus = this.handleFocus.bind(this);
    this.state = {
      id: uId(props.id),
      validated: false,
      value: formatDateValue$1(props.format, value.day, value.month, value.year),
      day: value.day,
      month: value.month,
      year: value.year,
      lastVal: props.value
    };
  }
  componentDidMount() {
    const {
      attachToForm
    } = this.props;
    if (typeof attachToForm === 'function') {
      attachToForm(this);
    }
  }
  componentWillUnmount() {
    const {
      detachFromForm
    } = this.props;
    if (typeof detachFromForm === 'function') {
      detachFromForm(this);
    }
  }
  async componentDidUpdate() {
    const {
      format,
      value
    } = this.props;
    const {
      lastVal
    } = this.state;
    if (value !== lastVal) {
      const format = parseDateFormat$1(format || 'MM/DD/YYYY');
      const splitVal = splitDate$1(value, format);
      await this.setState({
        lastVal: value
      });
      this.updateValue(splitVal.day, splitVal.month, splitVal.year);
    }
  }
  theField() {
    return this.field;
  }
  val() {
    return this.state.value;
  }
  async validate() {
    const valid = await this.field.validate();
    this.setState({
      valid: valid.valid,
      invalid: !valid.valid,
      invalidMessage: !valid.valid ? valid.message : '',
      validated: true
    });
    return valid;
  }
  async updateValue(day, month, year) {
    const {
      format,
      onInput
    } = this.props;
    const value = formatDateValue$1(format, day, month, year);
    await this.setState({
      value,
      day,
      month,
      year
    });
    if (typeof onInput === 'function') {
      onInput({
        target: this.field.theField()
      });
    }
  }
  async handleInputDay(e) {
    const {
      onDayInput,
      onChange
    } = this.props;
    const {
      validated,
      month,
      year
    } = this.state;
    const {
      value: day
    } = e.target;
    if (typeof onDayInput === 'function') {
      onDayInput(e);
    }
    if (typeof onChange === 'function' && year && month && day) {
      onChange(new Date(year, Number(month - 1), day));
    }
    this.setState({
      filled: this.field.val() === true
    });
    await this.updateValue(day, month, year);
    if (validated) {
      this.validate();
    }
  }
  async handleInputMonth(e) {
    const {
      onMonthInput,
      onChange
    } = this.props;
    const {
      validated,
      day,
      year
    } = this.state;
    const {
      value: month
    } = e.target;
    if (typeof onMonthInput === 'function') {
      onMonthInput(e);
    }
    if (typeof onChange === 'function' && year && month && day) {
      onChange(new Date(year, Number(month - 1), day));
    }
    this.setState({
      filled: this.field.val() === true
    });
    await this.updateValue(day, month, year);
    if (validated) {
      this.validate();
    }
  }
  async handleInputYear(e) {
    const {
      onYearInput,
      onChange
    } = this.props;
    const {
      validated,
      day,
      month
    } = this.state;
    const {
      value: year
    } = e.target;
    if (typeof onYearInput === 'function') {
      onYearInput(e);
    }
    if (typeof onChange === 'function' && year && month && day) {
      onChange(new Date(year, Number(month - 1), day));
    }
    this.setState({
      filled: this.field.val() === true
    });
    await this.updateValue(day, month, year);
    if (validated) {
      this.validate();
    }
  }
  handleBlur(e) {
    const {
      onBlur
    } = this.props;
    if (typeof onBlur === 'function') {
      onBlur(e);
    }
    this.timer = setTimeout(() => {
      this.setState({
        focused: false
      });
    }, 15);
  }
  handleFocus(e) {
    const {
      onFocus
    } = this.props;
    clearTimeout(this.timer);
    this.setState({
      focused: true
    });
    if (typeof onFocus === 'function') {
      onFocus(e);
    }
  }
  render() {
    const {
      id,
      filled,
      focused,
      validated,
      onValidate,
      invalidMessage,
      invalid,
      valid,
      value,
      day,
      month,
      year
    } = this.state;
    return /*#__PURE__*/React.createElement(Field$5, {
      ref: field => {
        this.field = field;
      },
      id: id,
      filled: filled,
      focused: focused,
      validated: validated,
      invalid: invalid,
      valid: valid,
      value: value,
      day: day,
      month: month,
      year: year,
      onValidate: onValidate,
      handleInputDay: this.handleInputDay,
      handleInputMonth: this.handleInputMonth,
      handleInputYear: this.handleInputYear,
      invalidMessage: invalidMessage,
      onInput: this.props.onInput,
      onFocus: this.handleFocus,
      onBlur: this.handleBlur,
      attachToForm: this.props.attachToForm,
      detachFromForm: this.props.detachFromForm,
      name: this.props.name,
      label: this.props.label,
      format: this.props.format,
      color: this.props.color,
      icon: this.props.icon,
      size: this.props.size,
      font: this.props.font,
      align: this.props.align,
      tabIndex: this.props.tabIndex,
      rules: this.props.rules,
      minDate: this.props.minDate,
      maxDate: this.props.maxDate,
      bgColor: this.props.bgColor,
      borderColor: this.props.borderColor,
      rounded: this.props.rounded,
      padding: this.props.padding,
      margin: this.props.margin,
      inputTxtColor: this.props.inputTxtColor
    });
  }
}
FieldDate.propTypes = {
  attachToForm: PropTypes.func,
  detachFromForm: PropTypes.func,
  onInput: PropTypes.func,
  onChange: PropTypes.func,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  onValidate: PropTypes.func,
  name: PropTypes.string,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  id: PropTypes.string,
  format: PropTypes.string,
  value: PropTypes.string,
  color: PropTypes.string,
  font: PropTypes.string,
  inputTxtColor: PropTypes.string,
  icon: PropTypes.string,
  theme: PropTypes.string,
  size: PropTypes.string,
  align: PropTypes.string,
  tabIndex: PropTypes.number,
  rules: PropTypes.object,
  minDate: PropTypes.object,
  maxDate: PropTypes.object,
  bgColor: PropTypes.string,
  borderColor: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  rounded: PropTypes.oneOfType([PropTypes.string, PropTypes.bool, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  padding: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  margin: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))])
};
FieldDate.defaultProps = {
  color: 'primary',
  bgColor: 'white',
  borderColor: 'neutral200',
  inputTxtColor: 'black',
  size: 'md',
  rounded: 'md',
  align: '',
  id: 'text-field',
  type: 'text',
  format: 'MM/DD/YYYY'
};

function mergeClassNames() {
  return Array.prototype.slice.call(arguments).reduce(function (classList, arg) {
    return classList.concat(arg);
  }, []).filter(function (arg) {
    return typeof arg === 'string';
  }).join(' ');
}

var commonjsGlobal = typeof globalThis !== 'undefined' ? globalThis : typeof window !== 'undefined' ? window : typeof global !== 'undefined' ? global : typeof self !== 'undefined' ? self : {};

/**
 * lodash (Custom Build) <https://lodash.com/>
 * Build: `lodash modularize exports="npm" -o ./`
 * Copyright jQuery Foundation and other contributors <https://jquery.org/>
 * Released under MIT license <https://lodash.com/license>
 * Based on Underscore.js 1.8.3 <http://underscorejs.org/LICENSE>
 * Copyright Jeremy Ashkenas, DocumentCloud and Investigative Reporters & Editors
 */

/** Used as the `TypeError` message for "Functions" methods. */
var FUNC_ERROR_TEXT = 'Expected a function';

/** Used to stand-in for `undefined` hash values. */
var HASH_UNDEFINED = '__lodash_hash_undefined__';

/** `Object#toString` result references. */
var funcTag = '[object Function]',
  genTag = '[object GeneratorFunction]';

/**
 * Used to match `RegExp`
 * [syntax characters](http://ecma-international.org/ecma-262/7.0/#sec-patterns).
 */
var reRegExpChar = /[\\^$.*+?()[\]{}|]/g;

/** Used to detect host constructors (Safari). */
var reIsHostCtor = /^\[object .+?Constructor\]$/;

/** Detect free variable `global` from Node.js. */
var freeGlobal = typeof commonjsGlobal == 'object' && commonjsGlobal && commonjsGlobal.Object === Object && commonjsGlobal;

/** Detect free variable `self`. */
var freeSelf = typeof self == 'object' && self && self.Object === Object && self;

/** Used as a reference to the global object. */
var root = freeGlobal || freeSelf || Function('return this')();

/**
 * Gets the value at `key` of `object`.
 *
 * @private
 * @param {Object} [object] The object to query.
 * @param {string} key The key of the property to get.
 * @returns {*} Returns the property value.
 */
function getValue$2(object, key) {
  return object == null ? undefined : object[key];
}

/**
 * Checks if `value` is a host object in IE < 9.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a host object, else `false`.
 */
function isHostObject(value) {
  // Many host objects are `Object` objects that can coerce to strings
  // despite having improperly defined `toString` methods.
  var result = false;
  if (value != null && typeof value.toString != 'function') {
    try {
      result = !!(value + '');
    } catch (e) {}
  }
  return result;
}

/** Used for built-in method references. */
var arrayProto = Array.prototype,
  funcProto = Function.prototype,
  objectProto = Object.prototype;

/** Used to detect overreaching core-js shims. */
var coreJsData = root['__core-js_shared__'];

/** Used to detect methods masquerading as native. */
var maskSrcKey = function () {
  var uid = /[^.]+$/.exec(coreJsData && coreJsData.keys && coreJsData.keys.IE_PROTO || '');
  return uid ? 'Symbol(src)_1.' + uid : '';
}();

/** Used to resolve the decompiled source of functions. */
var funcToString = funcProto.toString;

/** Used to check objects for own properties. */
var hasOwnProperty$1 = objectProto.hasOwnProperty;

/**
 * Used to resolve the
 * [`toStringTag`](http://ecma-international.org/ecma-262/7.0/#sec-object.prototype.tostring)
 * of values.
 */
var objectToString = objectProto.toString;

/** Used to detect if a method is native. */
var reIsNative = RegExp('^' + funcToString.call(hasOwnProperty$1).replace(reRegExpChar, '\\$&').replace(/hasOwnProperty|(function).*?(?=\\\()| for .+?(?=\\\])/g, '$1.*?') + '$');

/** Built-in value references. */
var splice = arrayProto.splice;

/* Built-in method references that are verified to be native. */
var Map$1 = getNative(root, 'Map'),
  nativeCreate = getNative(Object, 'create');

/**
 * Creates a hash object.
 *
 * @private
 * @constructor
 * @param {Array} [entries] The key-value pairs to cache.
 */
function Hash(entries) {
  var index = -1,
    length = entries ? entries.length : 0;
  this.clear();
  while (++index < length) {
    var entry = entries[index];
    this.set(entry[0], entry[1]);
  }
}

/**
 * Removes all key-value entries from the hash.
 *
 * @private
 * @name clear
 * @memberOf Hash
 */
function hashClear() {
  this.__data__ = nativeCreate ? nativeCreate(null) : {};
}

/**
 * Removes `key` and its value from the hash.
 *
 * @private
 * @name delete
 * @memberOf Hash
 * @param {Object} hash The hash to modify.
 * @param {string} key The key of the value to remove.
 * @returns {boolean} Returns `true` if the entry was removed, else `false`.
 */
function hashDelete(key) {
  return this.has(key) && delete this.__data__[key];
}

/**
 * Gets the hash value for `key`.
 *
 * @private
 * @name get
 * @memberOf Hash
 * @param {string} key The key of the value to get.
 * @returns {*} Returns the entry value.
 */
function hashGet(key) {
  var data = this.__data__;
  if (nativeCreate) {
    var result = data[key];
    return result === HASH_UNDEFINED ? undefined : result;
  }
  return hasOwnProperty$1.call(data, key) ? data[key] : undefined;
}

/**
 * Checks if a hash value for `key` exists.
 *
 * @private
 * @name has
 * @memberOf Hash
 * @param {string} key The key of the entry to check.
 * @returns {boolean} Returns `true` if an entry for `key` exists, else `false`.
 */
function hashHas(key) {
  var data = this.__data__;
  return nativeCreate ? data[key] !== undefined : hasOwnProperty$1.call(data, key);
}

/**
 * Sets the hash `key` to `value`.
 *
 * @private
 * @name set
 * @memberOf Hash
 * @param {string} key The key of the value to set.
 * @param {*} value The value to set.
 * @returns {Object} Returns the hash instance.
 */
function hashSet(key, value) {
  var data = this.__data__;
  data[key] = nativeCreate && value === undefined ? HASH_UNDEFINED : value;
  return this;
}

// Add methods to `Hash`.
Hash.prototype.clear = hashClear;
Hash.prototype['delete'] = hashDelete;
Hash.prototype.get = hashGet;
Hash.prototype.has = hashHas;
Hash.prototype.set = hashSet;

/**
 * Creates an list cache object.
 *
 * @private
 * @constructor
 * @param {Array} [entries] The key-value pairs to cache.
 */
function ListCache(entries) {
  var index = -1,
    length = entries ? entries.length : 0;
  this.clear();
  while (++index < length) {
    var entry = entries[index];
    this.set(entry[0], entry[1]);
  }
}

/**
 * Removes all key-value entries from the list cache.
 *
 * @private
 * @name clear
 * @memberOf ListCache
 */
function listCacheClear() {
  this.__data__ = [];
}

/**
 * Removes `key` and its value from the list cache.
 *
 * @private
 * @name delete
 * @memberOf ListCache
 * @param {string} key The key of the value to remove.
 * @returns {boolean} Returns `true` if the entry was removed, else `false`.
 */
function listCacheDelete(key) {
  var data = this.__data__,
    index = assocIndexOf(data, key);
  if (index < 0) {
    return false;
  }
  var lastIndex = data.length - 1;
  if (index == lastIndex) {
    data.pop();
  } else {
    splice.call(data, index, 1);
  }
  return true;
}

/**
 * Gets the list cache value for `key`.
 *
 * @private
 * @name get
 * @memberOf ListCache
 * @param {string} key The key of the value to get.
 * @returns {*} Returns the entry value.
 */
function listCacheGet(key) {
  var data = this.__data__,
    index = assocIndexOf(data, key);
  return index < 0 ? undefined : data[index][1];
}

/**
 * Checks if a list cache value for `key` exists.
 *
 * @private
 * @name has
 * @memberOf ListCache
 * @param {string} key The key of the entry to check.
 * @returns {boolean} Returns `true` if an entry for `key` exists, else `false`.
 */
function listCacheHas(key) {
  return assocIndexOf(this.__data__, key) > -1;
}

/**
 * Sets the list cache `key` to `value`.
 *
 * @private
 * @name set
 * @memberOf ListCache
 * @param {string} key The key of the value to set.
 * @param {*} value The value to set.
 * @returns {Object} Returns the list cache instance.
 */
function listCacheSet(key, value) {
  var data = this.__data__,
    index = assocIndexOf(data, key);
  if (index < 0) {
    data.push([key, value]);
  } else {
    data[index][1] = value;
  }
  return this;
}

// Add methods to `ListCache`.
ListCache.prototype.clear = listCacheClear;
ListCache.prototype['delete'] = listCacheDelete;
ListCache.prototype.get = listCacheGet;
ListCache.prototype.has = listCacheHas;
ListCache.prototype.set = listCacheSet;

/**
 * Creates a map cache object to store key-value pairs.
 *
 * @private
 * @constructor
 * @param {Array} [entries] The key-value pairs to cache.
 */
function MapCache(entries) {
  var index = -1,
    length = entries ? entries.length : 0;
  this.clear();
  while (++index < length) {
    var entry = entries[index];
    this.set(entry[0], entry[1]);
  }
}

/**
 * Removes all key-value entries from the map.
 *
 * @private
 * @name clear
 * @memberOf MapCache
 */
function mapCacheClear() {
  this.__data__ = {
    'hash': new Hash(),
    'map': new (Map$1 || ListCache)(),
    'string': new Hash()
  };
}

/**
 * Removes `key` and its value from the map.
 *
 * @private
 * @name delete
 * @memberOf MapCache
 * @param {string} key The key of the value to remove.
 * @returns {boolean} Returns `true` if the entry was removed, else `false`.
 */
function mapCacheDelete(key) {
  return getMapData(this, key)['delete'](key);
}

/**
 * Gets the map value for `key`.
 *
 * @private
 * @name get
 * @memberOf MapCache
 * @param {string} key The key of the value to get.
 * @returns {*} Returns the entry value.
 */
function mapCacheGet(key) {
  return getMapData(this, key).get(key);
}

/**
 * Checks if a map value for `key` exists.
 *
 * @private
 * @name has
 * @memberOf MapCache
 * @param {string} key The key of the entry to check.
 * @returns {boolean} Returns `true` if an entry for `key` exists, else `false`.
 */
function mapCacheHas(key) {
  return getMapData(this, key).has(key);
}

/**
 * Sets the map `key` to `value`.
 *
 * @private
 * @name set
 * @memberOf MapCache
 * @param {string} key The key of the value to set.
 * @param {*} value The value to set.
 * @returns {Object} Returns the map cache instance.
 */
function mapCacheSet(key, value) {
  getMapData(this, key).set(key, value);
  return this;
}

// Add methods to `MapCache`.
MapCache.prototype.clear = mapCacheClear;
MapCache.prototype['delete'] = mapCacheDelete;
MapCache.prototype.get = mapCacheGet;
MapCache.prototype.has = mapCacheHas;
MapCache.prototype.set = mapCacheSet;

/**
 * Gets the index at which the `key` is found in `array` of key-value pairs.
 *
 * @private
 * @param {Array} array The array to inspect.
 * @param {*} key The key to search for.
 * @returns {number} Returns the index of the matched value, else `-1`.
 */
function assocIndexOf(array, key) {
  var length = array.length;
  while (length--) {
    if (eq(array[length][0], key)) {
      return length;
    }
  }
  return -1;
}

/**
 * The base implementation of `_.isNative` without bad shim checks.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a native function,
 *  else `false`.
 */
function baseIsNative(value) {
  if (!isObject(value) || isMasked(value)) {
    return false;
  }
  var pattern = isFunction(value) || isHostObject(value) ? reIsNative : reIsHostCtor;
  return pattern.test(toSource(value));
}

/**
 * Gets the data for `map`.
 *
 * @private
 * @param {Object} map The map to query.
 * @param {string} key The reference key.
 * @returns {*} Returns the map data.
 */
function getMapData(map, key) {
  var data = map.__data__;
  return isKeyable(key) ? data[typeof key == 'string' ? 'string' : 'hash'] : data.map;
}

/**
 * Gets the native function at `key` of `object`.
 *
 * @private
 * @param {Object} object The object to query.
 * @param {string} key The key of the method to get.
 * @returns {*} Returns the function if it's native, else `undefined`.
 */
function getNative(object, key) {
  var value = getValue$2(object, key);
  return baseIsNative(value) ? value : undefined;
}

/**
 * Checks if `value` is suitable for use as unique object key.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is suitable, else `false`.
 */
function isKeyable(value) {
  var type = typeof value;
  return type == 'string' || type == 'number' || type == 'symbol' || type == 'boolean' ? value !== '__proto__' : value === null;
}

/**
 * Checks if `func` has its source masked.
 *
 * @private
 * @param {Function} func The function to check.
 * @returns {boolean} Returns `true` if `func` is masked, else `false`.
 */
function isMasked(func) {
  return !!maskSrcKey && maskSrcKey in func;
}

/**
 * Converts `func` to its source code.
 *
 * @private
 * @param {Function} func The function to process.
 * @returns {string} Returns the source code.
 */
function toSource(func) {
  if (func != null) {
    try {
      return funcToString.call(func);
    } catch (e) {}
    try {
      return func + '';
    } catch (e) {}
  }
  return '';
}

/**
 * Creates a function that memoizes the result of `func`. If `resolver` is
 * provided, it determines the cache key for storing the result based on the
 * arguments provided to the memoized function. By default, the first argument
 * provided to the memoized function is used as the map cache key. The `func`
 * is invoked with the `this` binding of the memoized function.
 *
 * **Note:** The cache is exposed as the `cache` property on the memoized
 * function. Its creation may be customized by replacing the `_.memoize.Cache`
 * constructor with one whose instances implement the
 * [`Map`](http://ecma-international.org/ecma-262/7.0/#sec-properties-of-the-map-prototype-object)
 * method interface of `delete`, `get`, `has`, and `set`.
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Function
 * @param {Function} func The function to have its output memoized.
 * @param {Function} [resolver] The function to resolve the cache key.
 * @returns {Function} Returns the new memoized function.
 * @example
 *
 * var object = { 'a': 1, 'b': 2 };
 * var other = { 'c': 3, 'd': 4 };
 *
 * var values = _.memoize(_.values);
 * values(object);
 * // => [1, 2]
 *
 * values(other);
 * // => [3, 4]
 *
 * object.a = 2;
 * values(object);
 * // => [1, 2]
 *
 * // Modify the result cache.
 * values.cache.set(object, ['a', 'b']);
 * values(object);
 * // => ['a', 'b']
 *
 * // Replace `_.memoize.Cache`.
 * _.memoize.Cache = WeakMap;
 */
function memoize(func, resolver) {
  if (typeof func != 'function' || resolver && typeof resolver != 'function') {
    throw new TypeError(FUNC_ERROR_TEXT);
  }
  var memoized = function () {
    var args = arguments,
      key = resolver ? resolver.apply(this, args) : args[0],
      cache = memoized.cache;
    if (cache.has(key)) {
      return cache.get(key);
    }
    var result = func.apply(this, args);
    memoized.cache = cache.set(key, result);
    return result;
  };
  memoized.cache = new (memoize.Cache || MapCache)();
  return memoized;
}

// Assign cache to `_.memoize`.
memoize.Cache = MapCache;

/**
 * Performs a
 * [`SameValueZero`](http://ecma-international.org/ecma-262/7.0/#sec-samevaluezero)
 * comparison between two values to determine if they are equivalent.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to compare.
 * @param {*} other The other value to compare.
 * @returns {boolean} Returns `true` if the values are equivalent, else `false`.
 * @example
 *
 * var object = { 'a': 1 };
 * var other = { 'a': 1 };
 *
 * _.eq(object, object);
 * // => true
 *
 * _.eq(object, other);
 * // => false
 *
 * _.eq('a', 'a');
 * // => true
 *
 * _.eq('a', Object('a'));
 * // => false
 *
 * _.eq(NaN, NaN);
 * // => true
 */
function eq(value, other) {
  return value === other || value !== value && other !== other;
}

/**
 * Checks if `value` is classified as a `Function` object.
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a function, else `false`.
 * @example
 *
 * _.isFunction(_);
 * // => true
 *
 * _.isFunction(/abc/);
 * // => false
 */
function isFunction(value) {
  // The use of `Object#toString` avoids issues with the `typeof` operator
  // in Safari 8-9 which returns 'object' for typed array and other constructors.
  var tag = isObject(value) ? objectToString.call(value) : '';
  return tag == funcTag || tag == genTag;
}

/**
 * Checks if `value` is the
 * [language type](http://www.ecma-international.org/ecma-262/7.0/#sec-ecmascript-language-types)
 * of `Object`. (e.g. arrays, functions, objects, regexes, `new Number(0)`, and `new String('')`)
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is an object, else `false`.
 * @example
 *
 * _.isObject({});
 * // => true
 *
 * _.isObject([1, 2, 3]);
 * // => true
 *
 * _.isObject(_.noop);
 * // => true
 *
 * _.isObject(null);
 * // => false
 */
function isObject(value) {
  var type = typeof value;
  return !!value && (type == 'object' || type == 'function');
}
var lodash_memoize = memoize;

function resolver(options) {
  return JSON.stringify(options);
}
function uniqDefined(arr) {
  return arr.filter(function (el, index) {
    return el && arr.indexOf(el) === index;
  });
}
function normalizeLocales(arr) {
  return arr.map(function (el) {
    if (!el || el.indexOf('-') === -1 || el.toLowerCase() !== el) {
      return el;
    }
    var splitEl = el.split('-');
    return splitEl[0] + "-" + splitEl[1].toUpperCase();
  });
}
function getUserLocalesInternal(_temp) {
  var _ref = _temp === void 0 ? {} : _temp,
    _ref$useFallbackLocal = _ref.useFallbackLocale,
    useFallbackLocale = _ref$useFallbackLocal === void 0 ? true : _ref$useFallbackLocal,
    _ref$fallbackLocale = _ref.fallbackLocale,
    fallbackLocale = _ref$fallbackLocale === void 0 ? 'en-US' : _ref$fallbackLocale;
  var languageList = [];
  if (typeof window !== 'undefined') {
    var _window = window,
      navigator = _window.navigator;
    languageList = languageList.concat(navigator.languages, navigator.language, navigator.userLanguage, navigator.browserLanguage, navigator.systemLanguage);
  }
  if (useFallbackLocale) {
    languageList.push(fallbackLocale);
  }
  return normalizeLocales(uniqDefined(languageList));
}
var getUserLocales = lodash_memoize(getUserLocalesInternal, resolver);
function getUserLocaleInternal(options) {
  return getUserLocales(options)[0] || null;
}
var getUserLocale = lodash_memoize(getUserLocaleInternal, resolver);

/**
 * Utils
 */
function makeGetEdgeOfNeighbor(getPeriod, getEdgeOfPeriod, defaultOffset) {
  return function makeGetEdgeOfNeighborInternal(date) {
    var offset = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : defaultOffset;
    var previousPeriod = getPeriod(date) + offset;
    return getEdgeOfPeriod(previousPeriod);
  };
}
function makeGetEnd(getBeginOfNextPeriod) {
  return function makeGetEndInternal(date) {
    return new Date(getBeginOfNextPeriod(date).getTime() - 1);
  };
}
function makeGetRange(functions) {
  return function makeGetRangeInternal(date) {
    return functions.map(function (fn) {
      return fn(date);
    });
  };
}
/**
 * Simple getters - getting a property of a given point in time
 */

/**
 * Gets year from date.
 *
 * @param {Date|number|string} date Date to get year from.
 */

function getYear(date) {
  if (date instanceof Date) {
    return date.getFullYear();
  }
  if (typeof date === 'number') {
    return date;
  }
  var year = parseInt(date, 10);
  if (typeof date === 'string' && !isNaN(year)) {
    return year;
  }
  throw new Error("Failed to get year from date: ".concat(date, "."));
}
/**
 * Gets month from date.
 *
 * @param {Date} date Date to get month from.
 */

function getMonth(date) {
  if (date instanceof Date) {
    return date.getMonth();
  }
  throw new Error("Failed to get month from date: ".concat(date, "."));
}
/**
 * Gets human-readable day of the month from date.
 *
 * @param {Date} date Date to get day of the month from.
 */

function getDate(date) {
  if (date instanceof Date) {
    return date.getDate();
  }
  throw new Error("Failed to get year from date: ".concat(date, "."));
}
/**
 * Century
 */

function getCenturyStart(date) {
  var year = getYear(date);
  var centuryStartYear = year + (-year + 1) % 100;
  var centuryStartDate = new Date();
  centuryStartDate.setFullYear(centuryStartYear, 0, 1);
  centuryStartDate.setHours(0, 0, 0, 0);
  return centuryStartDate;
}
var getPreviousCenturyStart = makeGetEdgeOfNeighbor(getYear, getCenturyStart, -100);
var getNextCenturyStart = makeGetEdgeOfNeighbor(getYear, getCenturyStart, 100);
var getCenturyEnd = makeGetEnd(getNextCenturyStart);
var getPreviousCenturyEnd = makeGetEdgeOfNeighbor(getYear, getCenturyEnd, -100);
var getCenturyRange = makeGetRange([getCenturyStart, getCenturyEnd]);
/**
 * Decade
 */

function getDecadeStart(date) {
  var year = getYear(date);
  var decadeStartYear = year + (-year + 1) % 10;
  var decadeStartDate = new Date();
  decadeStartDate.setFullYear(decadeStartYear, 0, 1);
  decadeStartDate.setHours(0, 0, 0, 0);
  return decadeStartDate;
}
var getPreviousDecadeStart = makeGetEdgeOfNeighbor(getYear, getDecadeStart, -10);
var getNextDecadeStart = makeGetEdgeOfNeighbor(getYear, getDecadeStart, 10);
var getDecadeEnd = makeGetEnd(getNextDecadeStart);
var getPreviousDecadeEnd = makeGetEdgeOfNeighbor(getYear, getDecadeEnd, -10);
var getDecadeRange = makeGetRange([getDecadeStart, getDecadeEnd]);
/**
 * Year
 */

function getYearStart(date) {
  var year = getYear(date);
  var yearStartDate = new Date();
  yearStartDate.setFullYear(year, 0, 1);
  yearStartDate.setHours(0, 0, 0, 0);
  return yearStartDate;
}
var getPreviousYearStart = makeGetEdgeOfNeighbor(getYear, getYearStart, -1);
var getNextYearStart = makeGetEdgeOfNeighbor(getYear, getYearStart, 1);
var getYearEnd = makeGetEnd(getNextYearStart);
var getPreviousYearEnd = makeGetEdgeOfNeighbor(getYear, getYearEnd, -1);
var getYearRange = makeGetRange([getYearStart, getYearEnd]);
/**
 * Month
 */

function makeGetEdgeOfNeighborMonth(getEdgeOfPeriod, defaultOffset) {
  return function makeGetEdgeOfNeighborMonthInternal(date) {
    var offset = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : defaultOffset;
    var year = getYear(date);
    var month = getMonth(date) + offset;
    var previousPeriod = new Date();
    previousPeriod.setFullYear(year, month, 1);
    previousPeriod.setHours(0, 0, 0, 0);
    return getEdgeOfPeriod(previousPeriod);
  };
}
function getMonthStart(date) {
  var year = getYear(date);
  var month = getMonth(date);
  var monthStartDate = new Date();
  monthStartDate.setFullYear(year, month, 1);
  monthStartDate.setHours(0, 0, 0, 0);
  return monthStartDate;
}
var getPreviousMonthStart = makeGetEdgeOfNeighborMonth(getMonthStart, -1);
var getNextMonthStart = makeGetEdgeOfNeighborMonth(getMonthStart, 1);
var getMonthEnd = makeGetEnd(getNextMonthStart);
var getPreviousMonthEnd = makeGetEdgeOfNeighborMonth(getMonthEnd, -1);
var getMonthRange = makeGetRange([getMonthStart, getMonthEnd]);
/**
 * Day
 */

function makeGetEdgeOfNeighborDay(getEdgeOfPeriod, defaultOffset) {
  return function makeGetEdgeOfNeighborDayInternal(date) {
    var offset = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : defaultOffset;
    var year = getYear(date);
    var month = getMonth(date);
    var day = getDate(date) + offset;
    var previousPeriod = new Date();
    previousPeriod.setFullYear(year, month, day);
    previousPeriod.setHours(0, 0, 0, 0);
    return getEdgeOfPeriod(previousPeriod);
  };
}
function getDayStart(date) {
  var year = getYear(date);
  var month = getMonth(date);
  var day = getDate(date);
  var dayStartDate = new Date();
  dayStartDate.setFullYear(year, month, day);
  dayStartDate.setHours(0, 0, 0, 0);
  return dayStartDate;
}
var getNextDayStart = makeGetEdgeOfNeighborDay(getDayStart, 1);
var getDayEnd = makeGetEnd(getNextDayStart);
var getDayRange = makeGetRange([getDayStart, getDayEnd]);
/**
 * Other
 */

/**
 * Returns a number of days in a month of a given date.
 *
 * @param {Date} date Date.
 */

function getDaysInMonth(date) {
  return getDate(getMonthEnd(date));
}

var _CALENDAR_TYPE_LOCALE;
function _toConsumableArray$2(arr) {
  return _arrayWithoutHoles$2(arr) || _iterableToArray$2(arr) || _unsupportedIterableToArray$2(arr) || _nonIterableSpread$2();
}
function _nonIterableSpread$2() {
  throw new TypeError("Invalid attempt to spread non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
function _unsupportedIterableToArray$2(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray$2(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray$2(o, minLen);
}
function _iterableToArray$2(iter) {
  if (typeof Symbol !== "undefined" && iter[Symbol.iterator] != null || iter["@@iterator"] != null) return Array.from(iter);
}
function _arrayWithoutHoles$2(arr) {
  if (Array.isArray(arr)) return _arrayLikeToArray$2(arr);
}
function _arrayLikeToArray$2(arr, len) {
  if (len == null || len > arr.length) len = arr.length;
  for (var i = 0, arr2 = new Array(len); i < len; i++) {
    arr2[i] = arr[i];
  }
  return arr2;
}
function _defineProperty$d(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }
  return obj;
}
var CALENDAR_TYPES = {
  ARABIC: 'Arabic',
  HEBREW: 'Hebrew',
  ISO_8601: 'ISO 8601',
  US: 'US'
};
var CALENDAR_TYPE_LOCALES = (_CALENDAR_TYPE_LOCALE = {}, _defineProperty$d(_CALENDAR_TYPE_LOCALE, CALENDAR_TYPES.US, ['en-CA', 'en-US', 'es-AR', 'es-BO', 'es-CL', 'es-CO', 'es-CR', 'es-DO', 'es-EC', 'es-GT', 'es-HN', 'es-MX', 'es-NI', 'es-PA', 'es-PE', 'es-PR', 'es-SV', 'es-VE', 'pt-BR']), _defineProperty$d(_CALENDAR_TYPE_LOCALE, CALENDAR_TYPES.ARABIC, [
// ar-LB, ar-MA intentionally missing
'ar', 'ar-AE', 'ar-BH', 'ar-DZ', 'ar-EG', 'ar-IQ', 'ar-JO', 'ar-KW', 'ar-LY', 'ar-OM', 'ar-QA', 'ar-SA', 'ar-SD', 'ar-SY', 'ar-YE', 'dv', 'dv-MV', 'ps', 'ps-AR']), _defineProperty$d(_CALENDAR_TYPE_LOCALE, CALENDAR_TYPES.HEBREW, ['he', 'he-IL']), _CALENDAR_TYPE_LOCALE);
var WEEKDAYS = _toConsumableArray$2(Array(7)).map(function (el, index) {
  return index;
});

var formatterCache = new Map();
function getFormatter(options) {
  return function (locale, date) {
    var localeWithDefault = locale || getUserLocale();
    if (!formatterCache.has(localeWithDefault)) {
      formatterCache.set(localeWithDefault, new Map());
    }
    var formatterCacheLocale = formatterCache.get(localeWithDefault);
    if (!formatterCacheLocale.has(options)) {
      formatterCacheLocale.set(options, new Intl.DateTimeFormat(localeWithDefault, options).format);
    }
    return formatterCacheLocale.get(options)(date);
  };
}
/**
 * Changes the hour in a Date to ensure right date formatting even if DST is messed up.
 * Workaround for bug in WebKit and Firefox with historical dates.
 * For more details, see:
 * https://bugs.chromium.org/p/chromium/issues/detail?id=750465
 * https://bugzilla.mozilla.org/show_bug.cgi?id=1385643
 *
 * @param {Date} date Date.
 */

function toSafeHour(date) {
  var safeDate = new Date(date);
  return new Date(safeDate.setHours(12));
}
function getSafeFormatter(options) {
  return function (locale, date) {
    return getFormatter(options)(locale, toSafeHour(date));
  };
}
var formatDayOptions = {
  day: 'numeric'
};
var formatLongDateOptions = {
  day: 'numeric',
  month: 'long',
  year: 'numeric'
};
var formatMonthOptions = {
  month: 'long'
};
var formatMonthYearOptions = {
  month: 'long',
  year: 'numeric'
};
var formatShortWeekdayOptions = {
  weekday: 'short'
};
var formatWeekdayOptions = {
  weekday: 'long'
};
var formatYearOptions = {
  year: 'numeric'
};
var formatDay = getSafeFormatter(formatDayOptions);
var formatLongDate = getSafeFormatter(formatLongDateOptions);
var formatMonth = getSafeFormatter(formatMonthOptions);
var formatMonthYear = getSafeFormatter(formatMonthYearOptions);
var formatShortWeekday = getSafeFormatter(formatShortWeekdayOptions);
var formatWeekday = getSafeFormatter(formatWeekdayOptions);
var formatYear = getSafeFormatter(formatYearOptions);

var SUNDAY = WEEKDAYS[0];
var FRIDAY = WEEKDAYS[5];
var SATURDAY = WEEKDAYS[6];
/* Simple getters - getting a property of a given point in time */

function getDayOfWeek(date) {
  var calendarType = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : CALENDAR_TYPES.ISO_8601;
  var weekday = date.getDay();
  switch (calendarType) {
    case CALENDAR_TYPES.ISO_8601:
      // Shifts days of the week so that Monday is 0, Sunday is 6
      return (weekday + 6) % 7;
    case CALENDAR_TYPES.ARABIC:
      return (weekday + 1) % 7;
    case CALENDAR_TYPES.HEBREW:
    case CALENDAR_TYPES.US:
      return weekday;
    default:
      throw new Error('Unsupported calendar type.');
  }
}
/**
 * Century
 */

function getBeginOfCenturyYear(date) {
  var beginOfCentury = getCenturyStart(date);
  return getYear(beginOfCentury);
}
/**
 * Decade
 */

function getBeginOfDecadeYear(date) {
  var beginOfDecade = getDecadeStart(date);
  return getYear(beginOfDecade);
}
/**
 * Week
 */

/**
 * Returns the beginning of a given week.
 *
 * @param {Date} date Date.
 * @param {string} calendarType Calendar type. Can be ISO 8601 or US.
 */

function getBeginOfWeek(date) {
  var calendarType = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : CALENDAR_TYPES.ISO_8601;
  var year = getYear(date);
  var monthIndex = getMonth(date);
  var day = date.getDate() - getDayOfWeek(date, calendarType);
  return new Date(year, monthIndex, day);
}
/**
 * Gets week number according to ISO 8601 or US standard.
 * In ISO 8601, Arabic and Hebrew week 1 is the one with January 4.
 * In US calendar week 1 is the one with January 1.
 *
 * @param {Date} date Date.
 * @param {string} calendarType Calendar type. Can be ISO 8601 or US.
 */

function getWeekNumber(date) {
  var calendarType = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : CALENDAR_TYPES.ISO_8601;
  var calendarTypeForWeekNumber = calendarType === CALENDAR_TYPES.US ? CALENDAR_TYPES.US : CALENDAR_TYPES.ISO_8601;
  var beginOfWeek = getBeginOfWeek(date, calendarType);
  var year = getYear(date) + 1;
  var dayInWeekOne;
  var beginOfFirstWeek; // Look for the first week one that does not come after a given date

  do {
    dayInWeekOne = new Date(year, 0, calendarTypeForWeekNumber === CALENDAR_TYPES.ISO_8601 ? 4 : 1);
    beginOfFirstWeek = getBeginOfWeek(dayInWeekOne, calendarType);
    year -= 1;
  } while (date < beginOfFirstWeek);
  return Math.round((beginOfWeek - beginOfFirstWeek) / (8.64e7 * 7)) + 1;
}
/**
 * Others
 */

/**
 * Returns the beginning of a given range.
 *
 * @param {string} rangeType Range type (e.g. 'day')
 * @param {Date} date Date.
 */

function getBegin(rangeType, date) {
  switch (rangeType) {
    case 'century':
      return getCenturyStart(date);
    case 'decade':
      return getDecadeStart(date);
    case 'year':
      return getYearStart(date);
    case 'month':
      return getMonthStart(date);
    case 'day':
      return getDayStart(date);
    default:
      throw new Error("Invalid rangeType: ".concat(rangeType));
  }
}
function getBeginPrevious(rangeType, date) {
  switch (rangeType) {
    case 'century':
      return getPreviousCenturyStart(date);
    case 'decade':
      return getPreviousDecadeStart(date);
    case 'year':
      return getPreviousYearStart(date);
    case 'month':
      return getPreviousMonthStart(date);
    default:
      throw new Error("Invalid rangeType: ".concat(rangeType));
  }
}
function getBeginNext(rangeType, date) {
  switch (rangeType) {
    case 'century':
      return getNextCenturyStart(date);
    case 'decade':
      return getNextDecadeStart(date);
    case 'year':
      return getNextYearStart(date);
    case 'month':
      return getNextMonthStart(date);
    default:
      throw new Error("Invalid rangeType: ".concat(rangeType));
  }
}
var getBeginPrevious2 = function getBeginPrevious2(rangeType, date) {
  switch (rangeType) {
    case 'decade':
      return getPreviousDecadeStart(date, -100);
    case 'year':
      return getPreviousYearStart(date, -10);
    case 'month':
      return getPreviousMonthStart(date, -12);
    default:
      throw new Error("Invalid rangeType: ".concat(rangeType));
  }
};
var getBeginNext2 = function getBeginNext2(rangeType, date) {
  switch (rangeType) {
    case 'decade':
      return getNextDecadeStart(date, 100);
    case 'year':
      return getNextYearStart(date, 10);
    case 'month':
      return getNextMonthStart(date, 12);
    default:
      throw new Error("Invalid rangeType: ".concat(rangeType));
  }
};
/**
 * Returns the end of a given range.
 *
 * @param {string} rangeType Range type (e.g. 'day')
 * @param {Date} date Date.
 */

function getEnd(rangeType, date) {
  switch (rangeType) {
    case 'century':
      return getCenturyEnd(date);
    case 'decade':
      return getDecadeEnd(date);
    case 'year':
      return getYearEnd(date);
    case 'month':
      return getMonthEnd(date);
    case 'day':
      return getDayEnd(date);
    default:
      throw new Error("Invalid rangeType: ".concat(rangeType));
  }
}
function getEndPrevious(rangeType, date) {
  switch (rangeType) {
    case 'century':
      return getPreviousCenturyEnd(date);
    case 'decade':
      return getPreviousDecadeEnd(date);
    case 'year':
      return getPreviousYearEnd(date);
    case 'month':
      return getPreviousMonthEnd(date);
    default:
      throw new Error("Invalid rangeType: ".concat(rangeType));
  }
}
var getEndPrevious2 = function getEndPrevious2(rangeType, date) {
  switch (rangeType) {
    case 'decade':
      return getPreviousDecadeEnd(date, -100);
    case 'year':
      return getPreviousYearEnd(date, -10);
    case 'month':
      return getPreviousMonthEnd(date, -12);
    default:
      throw new Error("Invalid rangeType: ".concat(rangeType));
  }
};
/**
 * Returns an array with the beginning and the end of a given range.
 *
 * @param {string} rangeType Range type (e.g. 'day')
 * @param {Date} date Date.
 */

function getRange(rangeType, date) {
  switch (rangeType) {
    case 'century':
      return getCenturyRange(date);
    case 'decade':
      return getDecadeRange(date);
    case 'year':
      return getYearRange(date);
    case 'month':
      return getMonthRange(date);
    case 'day':
      return getDayRange(date);
    default:
      throw new Error("Invalid rangeType: ".concat(rangeType));
  }
}
/**
 * Creates a range out of two values, ensuring they are in order and covering entire period ranges.
 *
 * @param {string} rangeType Range type (e.g. 'day')
 * @param {Date} date1 First date.
 * @param {Date} date2 Second date.
 */

function getValueRange(rangeType, date1, date2) {
  var rawNextValue = [date1, date2].sort(function (a, b) {
    return a - b;
  });
  return [getBegin(rangeType, rawNextValue[0]), getEnd(rangeType, rawNextValue[1])];
}
function toYearLabel(locale) {
  var formatYear$1 = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : formatYear;
  var dates = arguments.length > 2 ? arguments[2] : undefined;
  return dates.map(function (date) {
    return formatYear$1(locale, date);
  }).join(' – ');
}
/**
 * Returns a string labelling a century of a given date.
 * For example, for 2017 it will return 2001-2100.
 *
 * @param {Date|String|Number} date Date or a year as a string or as a number.
 */

function getCenturyLabel(locale, formatYear, date) {
  return toYearLabel(locale, formatYear, getCenturyRange(date));
}
/**
 * Returns a string labelling a century of a given date.
 * For example, for 2017 it will return 2011-2020.
 *
 * @param {Date|String|Number} date Date or a year as a string or as a number.
 */

function getDecadeLabel(locale, formatYear, date) {
  return toYearLabel(locale, formatYear, getDecadeRange(date));
}
/**
 * Returns a boolean determining whether a given date is on Saturday or Sunday.
 *
 * @param {Date} date Date.
 */

function isWeekend(date) {
  var calendarType = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : CALENDAR_TYPES.ISO_8601;
  var weekday = date.getDay();
  switch (calendarType) {
    case CALENDAR_TYPES.ARABIC:
    case CALENDAR_TYPES.HEBREW:
      return weekday === FRIDAY || weekday === SATURDAY;
    case CALENDAR_TYPES.ISO_8601:
    case CALENDAR_TYPES.US:
      return weekday === SATURDAY || weekday === SUNDAY;
    default:
      throw new Error('Unsupported calendar type.');
  }
}

function _typeof$2(obj) {
  "@babel/helpers - typeof";

  return _typeof$2 = "function" == typeof Symbol && "symbol" == typeof Symbol.iterator ? function (obj) {
    return typeof obj;
  } : function (obj) {
    return obj && "function" == typeof Symbol && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj;
  }, _typeof$2(obj);
}
var calendarTypes = Object.values(CALENDAR_TYPES);
var allViews$1 = ['century', 'decade', 'year', 'month'];
var isCalendarType = PropTypes.oneOf(calendarTypes);
var isClassName = PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]);
var isMinDate = function isMinDate(props, propName, componentName) {
  var minDate = props[propName];
  if (!minDate) {
    return null;
  }
  if (!(minDate instanceof Date)) {
    return new Error("Invalid prop `".concat(propName, "` of type `").concat(_typeof$2(minDate), "` supplied to `").concat(componentName, "`, expected instance of `Date`."));
  }
  var maxDate = props.maxDate;
  if (maxDate && minDate > maxDate) {
    return new Error("Invalid prop `".concat(propName, "` of type `").concat(_typeof$2(minDate), "` supplied to `").concat(componentName, "`, minDate cannot be larger than maxDate."));
  }
  return null;
};
var isMaxDate = function isMaxDate(props, propName, componentName) {
  var maxDate = props[propName];
  if (!maxDate) {
    return null;
  }
  if (!(maxDate instanceof Date)) {
    return new Error("Invalid prop `".concat(propName, "` of type `").concat(_typeof$2(maxDate), "` supplied to `").concat(componentName, "`, expected instance of `Date`."));
  }
  var minDate = props.minDate;
  if (minDate && maxDate < minDate) {
    return new Error("Invalid prop `".concat(propName, "` of type `").concat(_typeof$2(maxDate), "` supplied to `").concat(componentName, "`, maxDate cannot be smaller than minDate."));
  }
  return null;
};
var isRef = PropTypes.oneOfType([PropTypes.func, PropTypes.shape({
  current: PropTypes.any
})]);
var isValue = PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.arrayOf(PropTypes.instanceOf(Date))]);
var isViews = PropTypes.arrayOf(PropTypes.oneOf(allViews$1));
var isView = function isView(props, propName, componentName) {
  var view = props[propName];
  var views = props.views;
  var allowedViews = views || allViews$1;
  if (view !== undefined && allowedViews.indexOf(view) === -1) {
    return new Error("Invalid prop `".concat(propName, "` of value `").concat(view, "` supplied to `").concat(componentName, "`, expected one of [").concat(allowedViews.map(function (a) {
      return "\"".concat(a, "\"");
    }).join(', '), "]."));
  } // Everything is fine

  return null;
};
isView.isRequired = function (props, propName, componentName) {
  var view = props[propName];
  if (!view) {
    return new Error("The prop `".concat(propName, "` is marked as required in `").concat(componentName, "`, but its value is `").concat(view, "`."));
  }
  return isView(props, propName, componentName);
};
var tileGroupProps = {
  activeStartDate: PropTypes.instanceOf(Date).isRequired,
  hover: PropTypes.instanceOf(Date),
  locale: PropTypes.string,
  maxDate: isMaxDate,
  minDate: isMinDate,
  onClick: PropTypes.func,
  onMouseOver: PropTypes.func,
  tileClassName: PropTypes.oneOfType([PropTypes.func, isClassName]),
  tileContent: PropTypes.oneOfType([PropTypes.func, PropTypes.node]),
  value: isValue,
  valueType: PropTypes.string
};
var tileProps = {
  activeStartDate: PropTypes.instanceOf(Date).isRequired,
  classes: PropTypes.arrayOf(PropTypes.string).isRequired,
  date: PropTypes.instanceOf(Date).isRequired,
  locale: PropTypes.string,
  maxDate: isMaxDate,
  minDate: isMinDate,
  onClick: PropTypes.func,
  onMouseOver: PropTypes.func,
  style: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  tileClassName: PropTypes.oneOfType([PropTypes.func, isClassName]),
  tileContent: PropTypes.oneOfType([PropTypes.func, PropTypes.node]),
  tileDisabled: PropTypes.func
};

var className$6 = 'react-calendar__navigation';
function Navigation(_ref) {
  var activeStartDate = _ref.activeStartDate,
    drillUp = _ref.drillUp,
    _ref$formatMonthYear = _ref.formatMonthYear,
    formatMonthYear$1 = _ref$formatMonthYear === void 0 ? formatMonthYear : _ref$formatMonthYear,
    _ref$formatYear = _ref.formatYear,
    formatYear$1 = _ref$formatYear === void 0 ? formatYear : _ref$formatYear,
    locale = _ref.locale,
    maxDate = _ref.maxDate,
    minDate = _ref.minDate,
    _ref$navigationAriaLa = _ref.navigationAriaLabel,
    navigationAriaLabel = _ref$navigationAriaLa === void 0 ? '' : _ref$navigationAriaLa,
    navigationAriaLive = _ref.navigationAriaLive,
    navigationLabel = _ref.navigationLabel,
    _ref$next2AriaLabel = _ref.next2AriaLabel,
    next2AriaLabel = _ref$next2AriaLabel === void 0 ? '' : _ref$next2AriaLabel,
    _ref$next2Label = _ref.next2Label,
    next2Label = _ref$next2Label === void 0 ? '»' : _ref$next2Label,
    _ref$nextAriaLabel = _ref.nextAriaLabel,
    nextAriaLabel = _ref$nextAriaLabel === void 0 ? '' : _ref$nextAriaLabel,
    _ref$nextLabel = _ref.nextLabel,
    nextLabel = _ref$nextLabel === void 0 ? '›' : _ref$nextLabel,
    _ref$prev2AriaLabel = _ref.prev2AriaLabel,
    prev2AriaLabel = _ref$prev2AriaLabel === void 0 ? '' : _ref$prev2AriaLabel,
    _ref$prev2Label = _ref.prev2Label,
    prev2Label = _ref$prev2Label === void 0 ? '«' : _ref$prev2Label,
    _ref$prevAriaLabel = _ref.prevAriaLabel,
    prevAriaLabel = _ref$prevAriaLabel === void 0 ? '' : _ref$prevAriaLabel,
    _ref$prevLabel = _ref.prevLabel,
    prevLabel = _ref$prevLabel === void 0 ? '‹' : _ref$prevLabel,
    setActiveStartDate = _ref.setActiveStartDate,
    showDoubleView = _ref.showDoubleView,
    view = _ref.view,
    views = _ref.views;
  var drillUpAvailable = views.indexOf(view) > 0;
  var shouldShowPrevNext2Buttons = view !== 'century';
  var previousActiveStartDate = getBeginPrevious(view, activeStartDate);
  var previousActiveStartDate2 = shouldShowPrevNext2Buttons && getBeginPrevious2(view, activeStartDate);
  var nextActiveStartDate = getBeginNext(view, activeStartDate);
  var nextActiveStartDate2 = shouldShowPrevNext2Buttons && getBeginNext2(view, activeStartDate);
  var prevButtonDisabled = function () {
    if (previousActiveStartDate.getFullYear() < 0) {
      return true;
    }
    var previousActiveEndDate = getEndPrevious(view, activeStartDate);
    return minDate && minDate >= previousActiveEndDate;
  }();
  var prev2ButtonDisabled = shouldShowPrevNext2Buttons && function () {
    if (previousActiveStartDate2.getFullYear() < 0) {
      return true;
    }
    var previousActiveEndDate = getEndPrevious2(view, activeStartDate);
    return minDate && minDate >= previousActiveEndDate;
  }();
  var nextButtonDisabled = maxDate && maxDate < nextActiveStartDate;
  var next2ButtonDisabled = shouldShowPrevNext2Buttons && maxDate && maxDate < nextActiveStartDate2;
  function onClickPrevious() {
    setActiveStartDate(previousActiveStartDate, 'prev');
  }
  function onClickPrevious2() {
    setActiveStartDate(previousActiveStartDate2, 'prev2');
  }
  function onClickNext() {
    setActiveStartDate(nextActiveStartDate, 'next');
  }
  function onClickNext2() {
    setActiveStartDate(nextActiveStartDate2, 'next2');
  }
  function renderLabel(date) {
    var label = function () {
      switch (view) {
        case 'century':
          return getCenturyLabel(locale, formatYear$1, date);
        case 'decade':
          return getDecadeLabel(locale, formatYear$1, date);
        case 'year':
          return formatYear$1(locale, date);
        case 'month':
          return formatMonthYear$1(locale, date);
        default:
          throw new Error("Invalid view: ".concat(view, "."));
      }
    }();
    return navigationLabel ? navigationLabel({
      date: date,
      label: label,
      locale: locale || getUserLocale(),
      view: view
    }) : label;
  }
  function renderButton() {
    var labelClassName = "".concat(className$6, "__label");
    return /*#__PURE__*/React.createElement("button", {
      "aria-label": navigationAriaLabel,
      "aria-live": navigationAriaLive,
      className: labelClassName,
      disabled: !drillUpAvailable,
      onClick: drillUp,
      style: {
        flexGrow: 1
      },
      type: "button"
    }, /*#__PURE__*/React.createElement("span", {
      className: "".concat(labelClassName, "__labelText ").concat(labelClassName, "__labelText--from")
    }, renderLabel(activeStartDate)), showDoubleView && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("span", {
      className: "".concat(labelClassName, "__divider")
    }, " \u2013 "), /*#__PURE__*/React.createElement("span", {
      className: "".concat(labelClassName, "__labelText ").concat(labelClassName, "__labelText--to")
    }, renderLabel(nextActiveStartDate))));
  }
  return /*#__PURE__*/React.createElement("div", {
    className: className$6
  }, prev2Label !== null && shouldShowPrevNext2Buttons && /*#__PURE__*/React.createElement("button", {
    "aria-label": prev2AriaLabel,
    className: "".concat(className$6, "__arrow ").concat(className$6, "__prev2-button"),
    disabled: prev2ButtonDisabled,
    onClick: onClickPrevious2,
    type: "button"
  }, prev2Label), prevLabel !== null && /*#__PURE__*/React.createElement("button", {
    "aria-label": prevAriaLabel,
    className: "".concat(className$6, "__arrow ").concat(className$6, "__prev-button"),
    disabled: prevButtonDisabled,
    onClick: onClickPrevious,
    type: "button"
  }, prevLabel), renderButton(), nextLabel !== null && /*#__PURE__*/React.createElement("button", {
    "aria-label": nextAriaLabel,
    className: "".concat(className$6, "__arrow ").concat(className$6, "__next-button"),
    disabled: nextButtonDisabled,
    onClick: onClickNext,
    type: "button"
  }, nextLabel), next2Label !== null && shouldShowPrevNext2Buttons && /*#__PURE__*/React.createElement("button", {
    "aria-label": next2AriaLabel,
    className: "".concat(className$6, "__arrow ").concat(className$6, "__next2-button"),
    disabled: next2ButtonDisabled,
    onClick: onClickNext2,
    type: "button"
  }, next2Label));
}
Navigation.propTypes = {
  activeStartDate: PropTypes.instanceOf(Date).isRequired,
  drillUp: PropTypes.func.isRequired,
  formatMonthYear: PropTypes.func,
  formatYear: PropTypes.func,
  locale: PropTypes.string,
  maxDate: PropTypes.instanceOf(Date),
  minDate: PropTypes.instanceOf(Date),
  navigationAriaLabel: PropTypes.string,
  navigationAriaLive: PropTypes.string,
  navigationLabel: PropTypes.func,
  next2AriaLabel: PropTypes.string,
  next2Label: PropTypes.node,
  nextAriaLabel: PropTypes.string,
  nextLabel: PropTypes.node,
  prev2AriaLabel: PropTypes.string,
  prev2Label: PropTypes.node,
  prevAriaLabel: PropTypes.string,
  prevLabel: PropTypes.node,
  setActiveStartDate: PropTypes.func.isRequired,
  showDoubleView: PropTypes.bool,
  view: isView.isRequired,
  views: isViews.isRequired
};

var _excluded$9 = ["children", "className", "direction", "count", "offset", "style", "wrap"];
function _extends$c() {
  _extends$c = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$c.apply(this, arguments);
}
function ownKeys$c(object, enumerableOnly) {
  var keys = Object.keys(object);
  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    enumerableOnly && (symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    })), keys.push.apply(keys, symbols);
  }
  return keys;
}
function _objectSpread$c(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = null != arguments[i] ? arguments[i] : {};
    i % 2 ? ownKeys$c(Object(source), !0).forEach(function (key) {
      _defineProperty$c(target, key, source[key]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys$c(Object(source)).forEach(function (key) {
      Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
  }
  return target;
}
function _defineProperty$c(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }
  return obj;
}
function _objectWithoutProperties$9(source, excluded) {
  if (source == null) return {};
  var target = _objectWithoutPropertiesLoose$9(source, excluded);
  var key, i;
  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }
  return target;
}
function _objectWithoutPropertiesLoose$9(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;
  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }
  return target;
}
function toPercent(num) {
  return "".concat(num, "%");
}
function Flex(_ref) {
  var children = _ref.children,
    className = _ref.className,
    direction = _ref.direction,
    count = _ref.count,
    offset = _ref.offset,
    style = _ref.style,
    wrap = _ref.wrap,
    otherProps = _objectWithoutProperties$9(_ref, _excluded$9);
  return /*#__PURE__*/React.createElement("div", _extends$c({
    className: className,
    style: _objectSpread$c({
      display: 'flex',
      flexDirection: direction,
      flexWrap: wrap ? 'wrap' : 'no-wrap'
    }, style)
  }, otherProps), React.Children.map(children, function (child, index) {
    return /*#__PURE__*/React.cloneElement(child, _objectSpread$c(_objectSpread$c({}, child.props), {}, {
      style: {
        flexBasis: toPercent(100 / count),
        flexShrink: 0,
        flexGrow: 0,
        overflow: 'hidden',
        marginLeft: offset && index === 0 ? toPercent(100 * offset / count) : null
      }
    }));
  }));
}
Flex.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  count: PropTypes.number.isRequired,
  direction: PropTypes.string,
  offset: PropTypes.number,
  style: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  wrap: PropTypes.bool
};

function _toConsumableArray$1(arr) {
  return _arrayWithoutHoles$1(arr) || _iterableToArray$1(arr) || _unsupportedIterableToArray$1(arr) || _nonIterableSpread$1();
}
function _nonIterableSpread$1() {
  throw new TypeError("Invalid attempt to spread non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
function _unsupportedIterableToArray$1(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray$1(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray$1(o, minLen);
}
function _iterableToArray$1(iter) {
  if (typeof Symbol !== "undefined" && iter[Symbol.iterator] != null || iter["@@iterator"] != null) return Array.from(iter);
}
function _arrayWithoutHoles$1(arr) {
  if (Array.isArray(arr)) return _arrayLikeToArray$1(arr);
}
function _arrayLikeToArray$1(arr, len) {
  if (len == null || len > arr.length) len = arr.length;
  for (var i = 0, arr2 = new Array(len); i < len; i++) {
    arr2[i] = arr[i];
  }
  return arr2;
}
/**
 * Returns a value no smaller than min and no larger than max.
 *
 * @param {*} value Value to return.
 * @param {*} min Minimum return value.
 * @param {*} max Maximum return value.
 */

function between(value, min, max) {
  if (min && min > value) {
    return min;
  }
  if (max && max < value) {
    return max;
  }
  return value;
}
function isValueWithinRange(value, range) {
  return range[0] <= value && range[1] >= value;
}
function isRangeWithinRange(greaterRange, smallerRange) {
  return greaterRange[0] <= smallerRange[0] && greaterRange[1] >= smallerRange[1];
}
function doRangesOverlap(range1, range2) {
  return isValueWithinRange(range1[0], range2) || isValueWithinRange(range1[1], range2);
}
function getRangeClassNames(valueRange, dateRange, baseClassName) {
  var isRange = doRangesOverlap(dateRange, valueRange);
  var classes = [];
  if (isRange) {
    classes.push(baseClassName);
    var isRangeStart = isValueWithinRange(valueRange[0], dateRange);
    var isRangeEnd = isValueWithinRange(valueRange[1], dateRange);
    if (isRangeStart) {
      classes.push("".concat(baseClassName, "Start"));
    }
    if (isRangeEnd) {
      classes.push("".concat(baseClassName, "End"));
    }
    if (isRangeStart && isRangeEnd) {
      classes.push("".concat(baseClassName, "BothEnds"));
    }
  }
  return classes;
}
function getTileClasses() {
  var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
    value = _ref.value,
    valueType = _ref.valueType,
    date = _ref.date,
    dateType = _ref.dateType,
    hover = _ref.hover;
  var className = 'react-calendar__tile';
  var classes = [className];
  if (!date) {
    return classes;
  }
  if (!Array.isArray(date) && !dateType) {
    throw new Error('getTileClasses(): Unable to get tile activity classes because one or more required arguments were not passed.');
  }
  var now = new Date();
  var dateRange = Array.isArray(date) ? date : getRange(dateType, date);
  if (isValueWithinRange(now, dateRange)) {
    classes.push("".concat(className, "--now"));
  }
  if (!value) {
    return classes;
  }
  if (!Array.isArray(value) && !valueType) {
    throw new Error('getTileClasses(): Unable to get tile activity classes because one or more required arguments were not passed.');
  }
  var valueRange = Array.isArray(value) ? value : getRange(valueType, value);
  if (isRangeWithinRange(valueRange, dateRange)) {
    classes.push("".concat(className, "--active"));
  } else if (doRangesOverlap(valueRange, dateRange)) {
    classes.push("".concat(className, "--hasActive"));
  }
  var valueRangeClassNames = getRangeClassNames(valueRange, dateRange, "".concat(className, "--range"));
  classes.push.apply(classes, _toConsumableArray$1(valueRangeClassNames));
  var valueArray = [].concat(value);
  if (hover && valueArray.length === 1) {
    var hoverRange = hover > valueRange[0] ? [valueRange[0], hover] : [hover, valueRange[0]];
    var hoverRangeClassNames = getRangeClassNames(hoverRange, dateRange, "".concat(className, "--hover"));
    classes.push.apply(classes, _toConsumableArray$1(hoverRangeClassNames));
  }
  return classes;
}

var _excluded$8 = ["className", "count", "dateTransform", "dateType", "end", "hover", "offset", "start", "step", "tile", "value", "valueType"];
function ownKeys$b(object, enumerableOnly) {
  var keys = Object.keys(object);
  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    enumerableOnly && (symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    })), keys.push.apply(keys, symbols);
  }
  return keys;
}
function _objectSpread$b(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = null != arguments[i] ? arguments[i] : {};
    i % 2 ? ownKeys$b(Object(source), !0).forEach(function (key) {
      _defineProperty$b(target, key, source[key]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys$b(Object(source)).forEach(function (key) {
      Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
  }
  return target;
}
function _defineProperty$b(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }
  return obj;
}
function _extends$b() {
  _extends$b = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$b.apply(this, arguments);
}
function _objectWithoutProperties$8(source, excluded) {
  if (source == null) return {};
  var target = _objectWithoutPropertiesLoose$8(source, excluded);
  var key, i;
  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }
  return target;
}
function _objectWithoutPropertiesLoose$8(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;
  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }
  return target;
}
function TileGroup(_ref) {
  var className = _ref.className,
    _ref$count = _ref.count,
    count = _ref$count === void 0 ? 3 : _ref$count,
    dateTransform = _ref.dateTransform,
    dateType = _ref.dateType,
    end = _ref.end,
    hover = _ref.hover,
    offset = _ref.offset,
    start = _ref.start,
    _ref$step = _ref.step,
    step = _ref$step === void 0 ? 1 : _ref$step,
    Tile = _ref.tile,
    value = _ref.value,
    valueType = _ref.valueType,
    tileProps = _objectWithoutProperties$8(_ref, _excluded$8);
  var tiles = [];
  for (var point = start; point <= end; point += step) {
    var date = dateTransform(point);
    tiles.push( /*#__PURE__*/React.createElement(Tile, _extends$b({
      key: date.getTime(),
      classes: getTileClasses({
        value: value,
        valueType: valueType,
        date: date,
        dateType: dateType,
        hover: hover
      }),
      date: date,
      point: point
    }, tileProps)));
  }
  return /*#__PURE__*/React.createElement(Flex, {
    className: className,
    count: count,
    offset: offset,
    wrap: true
  }, tiles);
}
TileGroup.propTypes = _objectSpread$b(_objectSpread$b({}, tileGroupProps), {}, {
  activeStartDate: PropTypes.instanceOf(Date),
  count: PropTypes.number,
  dateTransform: PropTypes.func.isRequired,
  dateType: PropTypes.string,
  offset: PropTypes.number,
  step: PropTypes.number,
  tile: PropTypes.func.isRequired
});

function _typeof$1(obj) {
  "@babel/helpers - typeof";

  return _typeof$1 = "function" == typeof Symbol && "symbol" == typeof Symbol.iterator ? function (obj) {
    return typeof obj;
  } : function (obj) {
    return obj && "function" == typeof Symbol && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj;
  }, _typeof$1(obj);
}
function ownKeys$a(object, enumerableOnly) {
  var keys = Object.keys(object);
  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    enumerableOnly && (symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    })), keys.push.apply(keys, symbols);
  }
  return keys;
}
function _objectSpread$a(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = null != arguments[i] ? arguments[i] : {};
    i % 2 ? ownKeys$a(Object(source), !0).forEach(function (key) {
      _defineProperty$a(target, key, source[key]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys$a(Object(source)).forEach(function (key) {
      Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
  }
  return target;
}
function _classCallCheck$1(instance, Constructor) {
  if (!(instance instanceof Constructor)) {
    throw new TypeError("Cannot call a class as a function");
  }
}
function _defineProperties$1(target, props) {
  for (var i = 0; i < props.length; i++) {
    var descriptor = props[i];
    descriptor.enumerable = descriptor.enumerable || false;
    descriptor.configurable = true;
    if ("value" in descriptor) descriptor.writable = true;
    Object.defineProperty(target, descriptor.key, descriptor);
  }
}
function _createClass$1(Constructor, protoProps, staticProps) {
  if (protoProps) _defineProperties$1(Constructor.prototype, protoProps);
  if (staticProps) _defineProperties$1(Constructor, staticProps);
  Object.defineProperty(Constructor, "prototype", {
    writable: false
  });
  return Constructor;
}
function _inherits$1(subClass, superClass) {
  if (typeof superClass !== "function" && superClass !== null) {
    throw new TypeError("Super expression must either be null or a function");
  }
  subClass.prototype = Object.create(superClass && superClass.prototype, {
    constructor: {
      value: subClass,
      writable: true,
      configurable: true
    }
  });
  Object.defineProperty(subClass, "prototype", {
    writable: false
  });
  if (superClass) _setPrototypeOf$1(subClass, superClass);
}
function _setPrototypeOf$1(o, p) {
  _setPrototypeOf$1 = Object.setPrototypeOf ? Object.setPrototypeOf.bind() : function _setPrototypeOf(o, p) {
    o.__proto__ = p;
    return o;
  };
  return _setPrototypeOf$1(o, p);
}
function _createSuper$1(Derived) {
  var hasNativeReflectConstruct = _isNativeReflectConstruct$1();
  return function _createSuperInternal() {
    var Super = _getPrototypeOf$1(Derived),
      result;
    if (hasNativeReflectConstruct) {
      var NewTarget = _getPrototypeOf$1(this).constructor;
      result = Reflect.construct(Super, arguments, NewTarget);
    } else {
      result = Super.apply(this, arguments);
    }
    return _possibleConstructorReturn$1(this, result);
  };
}
function _possibleConstructorReturn$1(self, call) {
  if (call && (_typeof$1(call) === "object" || typeof call === "function")) {
    return call;
  } else if (call !== void 0) {
    throw new TypeError("Derived constructors may only return object or undefined");
  }
  return _assertThisInitialized$1(self);
}
function _assertThisInitialized$1(self) {
  if (self === void 0) {
    throw new ReferenceError("this hasn't been initialised - super() hasn't been called");
  }
  return self;
}
function _isNativeReflectConstruct$1() {
  if (typeof Reflect === "undefined" || !Reflect.construct) return false;
  if (Reflect.construct.sham) return false;
  if (typeof Proxy === "function") return true;
  try {
    Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function () {}));
    return true;
  } catch (e) {
    return false;
  }
}
function _getPrototypeOf$1(o) {
  _getPrototypeOf$1 = Object.setPrototypeOf ? Object.getPrototypeOf.bind() : function _getPrototypeOf(o) {
    return o.__proto__ || Object.getPrototypeOf(o);
  };
  return _getPrototypeOf$1(o);
}
function _defineProperty$a(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }
  return obj;
}
function datesAreDifferent(date1, date2) {
  return date1 && !date2 || !date1 && date2 || date1 && date2 && date1.getTime() !== date2.getTime();
}
function getValue$1(nextProps, prop) {
  var activeStartDate = nextProps.activeStartDate,
    date = nextProps.date,
    view = nextProps.view;
  return typeof prop === 'function' ? prop({
    activeStartDate: activeStartDate,
    date: date,
    view: view
  }) : prop;
}
var Tile = /*#__PURE__*/function (_Component) {
  _inherits$1(Tile, _Component);
  var _super = _createSuper$1(Tile);
  function Tile() {
    var _this;
    _classCallCheck$1(this, Tile);
    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }
    _this = _super.call.apply(_super, [this].concat(args));
    _defineProperty$a(_assertThisInitialized$1(_this), "state", {});
    return _this;
  }
  _createClass$1(Tile, [{
    key: "render",
    value: function render() {
      var _this$props = this.props,
        activeStartDate = _this$props.activeStartDate,
        children = _this$props.children,
        classes = _this$props.classes,
        date = _this$props.date,
        formatAbbr = _this$props.formatAbbr,
        locale = _this$props.locale,
        maxDate = _this$props.maxDate,
        maxDateTransform = _this$props.maxDateTransform,
        minDate = _this$props.minDate,
        minDateTransform = _this$props.minDateTransform,
        onClick = _this$props.onClick,
        onMouseOver = _this$props.onMouseOver,
        style = _this$props.style,
        tileDisabled = _this$props.tileDisabled,
        view = _this$props.view;
      var _this$state = this.state,
        tileClassName = _this$state.tileClassName,
        tileContent = _this$state.tileContent;
      return /*#__PURE__*/React.createElement("button", {
        className: mergeClassNames(classes, tileClassName),
        disabled: minDate && minDateTransform(minDate) > date || maxDate && maxDateTransform(maxDate) < date || tileDisabled && tileDisabled({
          activeStartDate: activeStartDate,
          date: date,
          view: view
        }),
        onClick: onClick && function (event) {
          return onClick(date, event);
        },
        onFocus: onMouseOver && function () {
          return onMouseOver(date);
        },
        onMouseOver: onMouseOver && function () {
          return onMouseOver(date);
        },
        style: style,
        type: "button"
      }, formatAbbr ? /*#__PURE__*/React.createElement("abbr", {
        "aria-label": formatAbbr(locale, date)
      }, children) : children, tileContent);
    }
  }], [{
    key: "getDerivedStateFromProps",
    value: function getDerivedStateFromProps(nextProps, prevState) {
      var activeStartDate = nextProps.activeStartDate,
        tileClassName = nextProps.tileClassName,
        tileContent = nextProps.tileContent;
      var nextState = {};
      if (tileClassName !== prevState.tileClassNameProps || datesAreDifferent(activeStartDate, prevState.activeStartDateProps)) {
        nextState.tileClassName = getValue$1(nextProps, tileClassName);
        nextState.tileClassNameProps = tileClassName;
      }
      if (tileContent !== prevState.tileContentProps || datesAreDifferent(activeStartDate, prevState.activeStartDateProps)) {
        nextState.tileContent = getValue$1(nextProps, tileContent);
        nextState.tileContentProps = tileContent;
      }
      nextState.activeStartDateProps = activeStartDate;
      return nextState;
    }
  }]);
  return Tile;
}(Component);
Tile.propTypes = _objectSpread$a(_objectSpread$a({}, tileProps), {}, {
  children: PropTypes.node.isRequired,
  formatAbbr: PropTypes.func,
  maxDateTransform: PropTypes.func.isRequired,
  minDateTransform: PropTypes.func.isRequired
});

var _excluded$7 = ["classes", "formatYear"];
function ownKeys$9(object, enumerableOnly) {
  var keys = Object.keys(object);
  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    enumerableOnly && (symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    })), keys.push.apply(keys, symbols);
  }
  return keys;
}
function _objectSpread$9(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = null != arguments[i] ? arguments[i] : {};
    i % 2 ? ownKeys$9(Object(source), !0).forEach(function (key) {
      _defineProperty$9(target, key, source[key]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys$9(Object(source)).forEach(function (key) {
      Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
  }
  return target;
}
function _defineProperty$9(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }
  return obj;
}
function _extends$a() {
  _extends$a = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$a.apply(this, arguments);
}
function _objectWithoutProperties$7(source, excluded) {
  if (source == null) return {};
  var target = _objectWithoutPropertiesLoose$7(source, excluded);
  var key, i;
  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }
  return target;
}
function _objectWithoutPropertiesLoose$7(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;
  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }
  return target;
}
var className$5 = 'react-calendar__century-view__decades__decade';
function Decade(_ref) {
  var classes = _ref.classes,
    _ref$formatYear = _ref.formatYear,
    formatYear$1 = _ref$formatYear === void 0 ? formatYear : _ref$formatYear,
    otherProps = _objectWithoutProperties$7(_ref, _excluded$7);
  var date = otherProps.date,
    locale = otherProps.locale;
  return /*#__PURE__*/React.createElement(Tile, _extends$a({}, otherProps, {
    classes: [].concat(classes, className$5),
    maxDateTransform: getDecadeEnd,
    minDateTransform: getDecadeStart,
    view: "century"
  }), getDecadeLabel(locale, formatYear$1, date));
}
Decade.propTypes = _objectSpread$9(_objectSpread$9({}, tileProps), {}, {
  formatYear: PropTypes.func
});

function ownKeys$8(object, enumerableOnly) {
  var keys = Object.keys(object);
  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    enumerableOnly && (symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    })), keys.push.apply(keys, symbols);
  }
  return keys;
}
function _objectSpread$8(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = null != arguments[i] ? arguments[i] : {};
    i % 2 ? ownKeys$8(Object(source), !0).forEach(function (key) {
      _defineProperty$8(target, key, source[key]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys$8(Object(source)).forEach(function (key) {
      Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
  }
  return target;
}
function _defineProperty$8(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }
  return obj;
}
function _extends$9() {
  _extends$9 = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$9.apply(this, arguments);
}
function Decades(props) {
  var activeStartDate = props.activeStartDate;
  var start = getBeginOfCenturyYear(activeStartDate);
  var end = start + 99;
  return /*#__PURE__*/React.createElement(TileGroup, _extends$9({}, props, {
    className: "react-calendar__century-view__decades",
    dateTransform: getDecadeStart,
    dateType: "decade",
    end: end,
    start: start,
    step: 10,
    tile: Decade
  }));
}
Decades.propTypes = _objectSpread$8({}, tileGroupProps);

function CenturyView(props) {
  function renderDecades() {
    return /*#__PURE__*/React.createElement(Decades, props);
  }
  return /*#__PURE__*/React.createElement("div", {
    className: "react-calendar__century-view"
  }, renderDecades());
}

var _excluded$6 = ["classes", "formatYear"];
function ownKeys$7(object, enumerableOnly) {
  var keys = Object.keys(object);
  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    enumerableOnly && (symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    })), keys.push.apply(keys, symbols);
  }
  return keys;
}
function _objectSpread$7(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = null != arguments[i] ? arguments[i] : {};
    i % 2 ? ownKeys$7(Object(source), !0).forEach(function (key) {
      _defineProperty$7(target, key, source[key]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys$7(Object(source)).forEach(function (key) {
      Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
  }
  return target;
}
function _defineProperty$7(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }
  return obj;
}
function _extends$8() {
  _extends$8 = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$8.apply(this, arguments);
}
function _objectWithoutProperties$6(source, excluded) {
  if (source == null) return {};
  var target = _objectWithoutPropertiesLoose$6(source, excluded);
  var key, i;
  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }
  return target;
}
function _objectWithoutPropertiesLoose$6(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;
  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }
  return target;
}
var className$4 = 'react-calendar__decade-view__years__year';
function Year(_ref) {
  var classes = _ref.classes,
    _ref$formatYear = _ref.formatYear,
    formatYear$1 = _ref$formatYear === void 0 ? formatYear : _ref$formatYear,
    otherProps = _objectWithoutProperties$6(_ref, _excluded$6);
  var date = otherProps.date,
    locale = otherProps.locale;
  return /*#__PURE__*/React.createElement(Tile, _extends$8({}, otherProps, {
    classes: [].concat(classes, className$4),
    maxDateTransform: getYearEnd,
    minDateTransform: getYearStart,
    view: "decade"
  }), formatYear$1(locale, date));
}
Year.propTypes = _objectSpread$7(_objectSpread$7({}, tileProps), {}, {
  formatYear: PropTypes.func
});

function ownKeys$6(object, enumerableOnly) {
  var keys = Object.keys(object);
  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    enumerableOnly && (symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    })), keys.push.apply(keys, symbols);
  }
  return keys;
}
function _objectSpread$6(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = null != arguments[i] ? arguments[i] : {};
    i % 2 ? ownKeys$6(Object(source), !0).forEach(function (key) {
      _defineProperty$6(target, key, source[key]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys$6(Object(source)).forEach(function (key) {
      Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
  }
  return target;
}
function _defineProperty$6(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }
  return obj;
}
function _extends$7() {
  _extends$7 = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$7.apply(this, arguments);
}
function Years(props) {
  var activeStartDate = props.activeStartDate;
  var start = getBeginOfDecadeYear(activeStartDate);
  var end = start + 9;
  return /*#__PURE__*/React.createElement(TileGroup, _extends$7({}, props, {
    className: "react-calendar__decade-view__years",
    dateTransform: function dateTransform(year) {
      var date = new Date();
      date.setFullYear(year, 0, 1);
      date.setHours(0, 0, 0, 0);
      return date;
    },
    dateType: "year",
    end: end,
    start: start,
    tile: Year
  }));
}
Years.propTypes = _objectSpread$6({}, tileGroupProps);

function DecadeView(props) {
  function renderYears() {
    return /*#__PURE__*/React.createElement(Years, props);
  }
  return /*#__PURE__*/React.createElement("div", {
    className: "react-calendar__decade-view"
  }, renderYears());
}

var _excluded$5 = ["classes", "formatMonth", "formatMonthYear"];
function ownKeys$5(object, enumerableOnly) {
  var keys = Object.keys(object);
  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    enumerableOnly && (symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    })), keys.push.apply(keys, symbols);
  }
  return keys;
}
function _objectSpread$5(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = null != arguments[i] ? arguments[i] : {};
    i % 2 ? ownKeys$5(Object(source), !0).forEach(function (key) {
      _defineProperty$5(target, key, source[key]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys$5(Object(source)).forEach(function (key) {
      Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
  }
  return target;
}
function _defineProperty$5(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }
  return obj;
}
function _extends$6() {
  _extends$6 = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$6.apply(this, arguments);
}
function _objectWithoutProperties$5(source, excluded) {
  if (source == null) return {};
  var target = _objectWithoutPropertiesLoose$5(source, excluded);
  var key, i;
  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }
  return target;
}
function _objectWithoutPropertiesLoose$5(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;
  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }
  return target;
}
var className$3 = 'react-calendar__year-view__months__month';
function Month(_ref) {
  var classes = _ref.classes,
    _ref$formatMonth = _ref.formatMonth,
    formatMonth$1 = _ref$formatMonth === void 0 ? formatMonth : _ref$formatMonth,
    _ref$formatMonthYear = _ref.formatMonthYear,
    formatMonthYear$1 = _ref$formatMonthYear === void 0 ? formatMonthYear : _ref$formatMonthYear,
    otherProps = _objectWithoutProperties$5(_ref, _excluded$5);
  var date = otherProps.date,
    locale = otherProps.locale;
  return /*#__PURE__*/React.createElement(Tile, _extends$6({}, otherProps, {
    classes: [].concat(classes, className$3),
    formatAbbr: formatMonthYear$1,
    maxDateTransform: getMonthEnd,
    minDateTransform: getMonthStart,
    view: "year"
  }), formatMonth$1(locale, date));
}
Month.propTypes = _objectSpread$5(_objectSpread$5({}, tileProps), {}, {
  formatMonth: PropTypes.func,
  formatMonthYear: PropTypes.func
});

function ownKeys$4(object, enumerableOnly) {
  var keys = Object.keys(object);
  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    enumerableOnly && (symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    })), keys.push.apply(keys, symbols);
  }
  return keys;
}
function _objectSpread$4(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = null != arguments[i] ? arguments[i] : {};
    i % 2 ? ownKeys$4(Object(source), !0).forEach(function (key) {
      _defineProperty$4(target, key, source[key]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys$4(Object(source)).forEach(function (key) {
      Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
  }
  return target;
}
function _defineProperty$4(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }
  return obj;
}
function _extends$5() {
  _extends$5 = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$5.apply(this, arguments);
}
function Months(props) {
  var activeStartDate = props.activeStartDate;
  var start = 0;
  var end = 11;
  var year = getYear(activeStartDate);
  return /*#__PURE__*/React.createElement(TileGroup, _extends$5({}, props, {
    className: "react-calendar__year-view__months",
    dateTransform: function dateTransform(monthIndex) {
      var date = new Date();
      date.setFullYear(year, monthIndex, 1);
      date.setHours(0, 0, 0, 0);
      return date;
    },
    dateType: "month",
    end: end,
    start: start,
    tile: Month
  }));
}
Months.propTypes = _objectSpread$4(_objectSpread$4({}, tileGroupProps), {}, {
  locale: PropTypes.string
});

function YearView(props) {
  function renderMonths() {
    return /*#__PURE__*/React.createElement(Months, props);
  }
  return /*#__PURE__*/React.createElement("div", {
    className: "react-calendar__year-view"
  }, renderMonths());
}

var _excluded$4 = ["formatDay", "formatLongDate", "calendarType", "classes", "currentMonthIndex"];
function ownKeys$3(object, enumerableOnly) {
  var keys = Object.keys(object);
  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    enumerableOnly && (symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    })), keys.push.apply(keys, symbols);
  }
  return keys;
}
function _objectSpread$3(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = null != arguments[i] ? arguments[i] : {};
    i % 2 ? ownKeys$3(Object(source), !0).forEach(function (key) {
      _defineProperty$3(target, key, source[key]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys$3(Object(source)).forEach(function (key) {
      Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
  }
  return target;
}
function _defineProperty$3(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }
  return obj;
}
function _extends$4() {
  _extends$4 = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$4.apply(this, arguments);
}
function _objectWithoutProperties$4(source, excluded) {
  if (source == null) return {};
  var target = _objectWithoutPropertiesLoose$4(source, excluded);
  var key, i;
  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }
  return target;
}
function _objectWithoutPropertiesLoose$4(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;
  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }
  return target;
}
var className$2 = 'react-calendar__month-view__days__day';
function Day(_ref) {
  var _ref$formatDay = _ref.formatDay,
    formatDay$1 = _ref$formatDay === void 0 ? formatDay : _ref$formatDay,
    _ref$formatLongDate = _ref.formatLongDate,
    formatLongDate$1 = _ref$formatLongDate === void 0 ? formatLongDate : _ref$formatLongDate,
    calendarType = _ref.calendarType,
    classes = _ref.classes,
    currentMonthIndex = _ref.currentMonthIndex,
    otherProps = _objectWithoutProperties$4(_ref, _excluded$4);
  var date = otherProps.date,
    locale = otherProps.locale;
  return /*#__PURE__*/React.createElement(Tile, _extends$4({}, otherProps, {
    classes: [].concat(classes, className$2, isWeekend(date, calendarType) ? "".concat(className$2, "--weekend") : null, date.getMonth() !== currentMonthIndex ? "".concat(className$2, "--neighboringMonth") : null),
    formatAbbr: formatLongDate$1,
    maxDateTransform: getDayEnd,
    minDateTransform: getDayStart,
    view: "month"
  }), formatDay$1(locale, date));
}
Day.propTypes = _objectSpread$3(_objectSpread$3({}, tileProps), {}, {
  currentMonthIndex: PropTypes.number.isRequired,
  formatDay: PropTypes.func,
  formatLongDate: PropTypes.func
});

var _excluded$3 = ["showFixedNumberOfWeeks", "showNeighboringMonth"];
function ownKeys$2(object, enumerableOnly) {
  var keys = Object.keys(object);
  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    enumerableOnly && (symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    })), keys.push.apply(keys, symbols);
  }
  return keys;
}
function _objectSpread$2(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = null != arguments[i] ? arguments[i] : {};
    i % 2 ? ownKeys$2(Object(source), !0).forEach(function (key) {
      _defineProperty$2(target, key, source[key]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys$2(Object(source)).forEach(function (key) {
      Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
  }
  return target;
}
function _defineProperty$2(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }
  return obj;
}
function _extends$3() {
  _extends$3 = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$3.apply(this, arguments);
}
function _objectWithoutProperties$3(source, excluded) {
  if (source == null) return {};
  var target = _objectWithoutPropertiesLoose$3(source, excluded);
  var key, i;
  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }
  return target;
}
function _objectWithoutPropertiesLoose$3(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;
  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }
  return target;
}
function Days(props) {
  var activeStartDate = props.activeStartDate,
    calendarType = props.calendarType;
  var showFixedNumberOfWeeks = props.showFixedNumberOfWeeks,
    showNeighboringMonth = props.showNeighboringMonth,
    otherProps = _objectWithoutProperties$3(props, _excluded$3);
  var year = getYear(activeStartDate);
  var monthIndex = getMonth(activeStartDate);
  var hasFixedNumberOfWeeks = showFixedNumberOfWeeks || showNeighboringMonth;
  var dayOfWeek = getDayOfWeek(activeStartDate, calendarType);
  var offset = hasFixedNumberOfWeeks ? 0 : dayOfWeek;
  /**
   * Defines on which day of the month the grid shall start. If we simply show current
   * month, we obviously start on day one, but if showNeighboringMonth is set to
   * true, we need to find the beginning of the week the first day of the month is in.
   */

  var start = (hasFixedNumberOfWeeks ? -dayOfWeek : 0) + 1;
  /**
   * Defines on which day of the month the grid shall end. If we simply show current
   * month, we need to stop on the last day of the month, but if showNeighboringMonth
   * is set to true, we need to find the end of the week the last day of the month is in.
   */

  var end = function () {
    if (showFixedNumberOfWeeks) {
      // Always show 6 weeks
      return start + 6 * 7 - 1;
    }
    var daysInMonth = getDaysInMonth(activeStartDate);
    if (showNeighboringMonth) {
      var activeEndDate = new Date();
      activeEndDate.setFullYear(year, monthIndex, daysInMonth);
      activeEndDate.setHours(0, 0, 0, 0);
      var daysUntilEndOfTheWeek = 7 - getDayOfWeek(activeEndDate, calendarType) - 1;
      return daysInMonth + daysUntilEndOfTheWeek;
    }
    return daysInMonth;
  }();
  return /*#__PURE__*/React.createElement(TileGroup, _extends$3({}, otherProps, {
    className: "react-calendar__month-view__days",
    count: 7,
    currentMonthIndex: monthIndex,
    dateTransform: function dateTransform(day) {
      var date = new Date();
      date.setFullYear(year, monthIndex, day);
      date.setHours(0, 0, 0, 0);
      return date;
    },
    dateType: "day",
    end: end,
    offset: offset,
    start: start,
    tile: Day
  }));
}
Days.propTypes = _objectSpread$2({
  calendarType: isCalendarType.isRequired,
  showFixedNumberOfWeeks: PropTypes.bool,
  showNeighboringMonth: PropTypes.bool
}, tileGroupProps);

var className$1 = 'react-calendar__month-view__weekdays';
var weekdayClassName = "".concat(className$1, "__weekday");
function Weekdays(props) {
  var calendarType = props.calendarType,
    _props$formatShortWee = props.formatShortWeekday,
    formatShortWeekday$1 = _props$formatShortWee === void 0 ? formatShortWeekday : _props$formatShortWee,
    _props$formatWeekday = props.formatWeekday,
    formatWeekday$1 = _props$formatWeekday === void 0 ? formatWeekday : _props$formatWeekday,
    locale = props.locale,
    onMouseLeave = props.onMouseLeave;
  var anyDate = new Date();
  var beginOfMonth = getMonthStart(anyDate);
  var year = getYear(beginOfMonth);
  var monthIndex = getMonth(beginOfMonth);
  var weekdays = [];
  for (var weekday = 1; weekday <= 7; weekday += 1) {
    var weekdayDate = new Date(year, monthIndex, weekday - getDayOfWeek(beginOfMonth, calendarType));
    var abbr = formatWeekday$1(locale, weekdayDate);
    weekdays.push( /*#__PURE__*/React.createElement("div", {
      key: weekday,
      className: mergeClassNames(weekdayClassName, isWeekend(weekdayDate, calendarType) && "".concat(weekdayClassName, "--weekend"))
    }, /*#__PURE__*/React.createElement("abbr", {
      "aria-label": abbr,
      title: abbr
    }, formatShortWeekday$1(locale, weekdayDate).replace('.', ''))));
  }
  return /*#__PURE__*/React.createElement(Flex, {
    className: className$1,
    count: 7,
    onFocus: onMouseLeave,
    onMouseOver: onMouseLeave
  }, weekdays);
}
Weekdays.propTypes = {
  calendarType: isCalendarType.isRequired,
  formatShortWeekday: PropTypes.func,
  formatWeekday: PropTypes.func,
  locale: PropTypes.string,
  onMouseLeave: PropTypes.func
};

var _excluded$2 = ["date", "onClickWeekNumber", "weekNumber"];
function _extends$2() {
  _extends$2 = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$2.apply(this, arguments);
}
function ownKeys$1(object, enumerableOnly) {
  var keys = Object.keys(object);
  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    enumerableOnly && (symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    })), keys.push.apply(keys, symbols);
  }
  return keys;
}
function _objectSpread$1(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = null != arguments[i] ? arguments[i] : {};
    i % 2 ? ownKeys$1(Object(source), !0).forEach(function (key) {
      _defineProperty$1(target, key, source[key]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys$1(Object(source)).forEach(function (key) {
      Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
  }
  return target;
}
function _defineProperty$1(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }
  return obj;
}
function _objectWithoutProperties$2(source, excluded) {
  if (source == null) return {};
  var target = _objectWithoutPropertiesLoose$2(source, excluded);
  var key, i;
  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }
  return target;
}
function _objectWithoutPropertiesLoose$2(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;
  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }
  return target;
}
var className = 'react-calendar__tile';
function WeekNumber(_ref) {
  var date = _ref.date,
    onClickWeekNumber = _ref.onClickWeekNumber,
    weekNumber = _ref.weekNumber,
    otherProps = _objectWithoutProperties$2(_ref, _excluded$2);
  var props = _objectSpread$1({
    className: className
  }, otherProps);
  var children = /*#__PURE__*/React.createElement("span", null, weekNumber);
  return onClickWeekNumber ? /*#__PURE__*/React.createElement("button", _extends$2({}, props, {
    onClick: function onClick(event) {
      return onClickWeekNumber(weekNumber, date, event);
    },
    type: "button"
  }), children) : /*#__PURE__*/React.createElement("div", props, children);
}
WeekNumber.propTypes = {
  date: PropTypes.instanceOf(Date).isRequired,
  onClickWeekNumber: PropTypes.func,
  weekNumber: PropTypes.node.isRequired
};

function WeekNumbers(props) {
  var activeStartDate = props.activeStartDate,
    calendarType = props.calendarType,
    onClickWeekNumber = props.onClickWeekNumber,
    onMouseLeave = props.onMouseLeave,
    showFixedNumberOfWeeks = props.showFixedNumberOfWeeks;
  var numberOfWeeks = function () {
    if (showFixedNumberOfWeeks) {
      return 6;
    }
    var numberOfDays = getDaysInMonth(activeStartDate);
    var startWeekday = getDayOfWeek(activeStartDate, calendarType);
    var days = numberOfDays - (7 - startWeekday);
    return 1 + Math.ceil(days / 7);
  }();
  var dates = function () {
    var year = getYear(activeStartDate);
    var monthIndex = getMonth(activeStartDate);
    var day = getDate(activeStartDate);
    var result = [];
    for (var index = 0; index < numberOfWeeks; index += 1) {
      result.push(getBeginOfWeek(new Date(year, monthIndex, day + index * 7), calendarType));
    }
    return result;
  }();
  var weekNumbers = dates.map(function (date) {
    return getWeekNumber(date, calendarType);
  });
  return /*#__PURE__*/React.createElement(Flex, {
    className: "react-calendar__month-view__weekNumbers",
    count: numberOfWeeks,
    direction: "column",
    onFocus: onMouseLeave,
    onMouseOver: onMouseLeave,
    style: {
      flexBasis: 'calc(100% * (1 / 8)',
      flexShrink: 0
    }
  }, weekNumbers.map(function (weekNumber, weekIndex) {
    return /*#__PURE__*/React.createElement(WeekNumber, {
      key: weekNumber,
      date: dates[weekIndex],
      onClickWeekNumber: onClickWeekNumber,
      weekNumber: weekNumber
    });
  }));
}
WeekNumbers.propTypes = {
  activeStartDate: PropTypes.instanceOf(Date).isRequired,
  calendarType: isCalendarType.isRequired,
  onClickWeekNumber: PropTypes.func,
  onMouseLeave: PropTypes.func,
  showFixedNumberOfWeeks: PropTypes.bool
};

var _excluded$1 = ["calendarType", "formatShortWeekday", "formatWeekday", "onClickWeekNumber", "showWeekNumbers"];
function _extends$1() {
  _extends$1 = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$1.apply(this, arguments);
}
function _objectWithoutProperties$1(source, excluded) {
  if (source == null) return {};
  var target = _objectWithoutPropertiesLoose$1(source, excluded);
  var key, i;
  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }
  return target;
}
function _objectWithoutPropertiesLoose$1(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;
  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }
  return target;
}
function getCalendarTypeFromLocale(locale) {
  return Object.keys(CALENDAR_TYPE_LOCALES).find(function (calendarType) {
    return CALENDAR_TYPE_LOCALES[calendarType].includes(locale);
  }) || CALENDAR_TYPES.ISO_8601;
}
function MonthView(props) {
  var activeStartDate = props.activeStartDate,
    locale = props.locale,
    onMouseLeave = props.onMouseLeave,
    showFixedNumberOfWeeks = props.showFixedNumberOfWeeks;
  var _props$calendarType = props.calendarType,
    calendarType = _props$calendarType === void 0 ? getCalendarTypeFromLocale(locale) : _props$calendarType,
    formatShortWeekday = props.formatShortWeekday,
    formatWeekday = props.formatWeekday,
    onClickWeekNumber = props.onClickWeekNumber,
    showWeekNumbers = props.showWeekNumbers,
    childProps = _objectWithoutProperties$1(props, _excluded$1);
  function renderWeekdays() {
    return /*#__PURE__*/React.createElement(Weekdays, {
      calendarType: calendarType,
      formatShortWeekday: formatShortWeekday,
      formatWeekday: formatWeekday,
      locale: locale,
      onMouseLeave: onMouseLeave
    });
  }
  function renderWeekNumbers() {
    if (!showWeekNumbers) {
      return null;
    }
    return /*#__PURE__*/React.createElement(WeekNumbers, {
      activeStartDate: activeStartDate,
      calendarType: calendarType,
      onClickWeekNumber: onClickWeekNumber,
      onMouseLeave: onMouseLeave,
      showFixedNumberOfWeeks: showFixedNumberOfWeeks
    });
  }
  function renderDays() {
    return /*#__PURE__*/React.createElement(Days, _extends$1({
      calendarType: calendarType
    }, childProps));
  }
  var className = 'react-calendar__month-view';
  return /*#__PURE__*/React.createElement("div", {
    className: mergeClassNames(className, showWeekNumbers ? "".concat(className, "--weekNumbers") : '')
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      display: 'flex',
      alignItems: 'flex-end'
    }
  }, renderWeekNumbers(), /*#__PURE__*/React.createElement("div", {
    style: {
      flexGrow: 1,
      width: '100%'
    }
  }, renderWeekdays(), renderDays())));
}
MonthView.propTypes = {
  activeStartDate: PropTypes.instanceOf(Date).isRequired,
  calendarType: isCalendarType,
  formatShortWeekday: PropTypes.func,
  formatWeekday: PropTypes.func,
  locale: PropTypes.string,
  onClickWeekNumber: PropTypes.func,
  onMouseLeave: PropTypes.func,
  showFixedNumberOfWeeks: PropTypes.bool,
  showWeekNumbers: PropTypes.bool
};

var _excluded = ["activeStartDate", "defaultActiveStartDate", "defaultValue", "defaultView", "maxDetail", "minDetail", "value", "view"];
function _extends() {
  _extends = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends.apply(this, arguments);
}
function _typeof(obj) {
  "@babel/helpers - typeof";

  return _typeof = "function" == typeof Symbol && "symbol" == typeof Symbol.iterator ? function (obj) {
    return typeof obj;
  } : function (obj) {
    return obj && "function" == typeof Symbol && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj;
  }, _typeof(obj);
}
function _classCallCheck(instance, Constructor) {
  if (!(instance instanceof Constructor)) {
    throw new TypeError("Cannot call a class as a function");
  }
}
function _defineProperties(target, props) {
  for (var i = 0; i < props.length; i++) {
    var descriptor = props[i];
    descriptor.enumerable = descriptor.enumerable || false;
    descriptor.configurable = true;
    if ("value" in descriptor) descriptor.writable = true;
    Object.defineProperty(target, descriptor.key, descriptor);
  }
}
function _createClass(Constructor, protoProps, staticProps) {
  if (protoProps) _defineProperties(Constructor.prototype, protoProps);
  if (staticProps) _defineProperties(Constructor, staticProps);
  Object.defineProperty(Constructor, "prototype", {
    writable: false
  });
  return Constructor;
}
function _inherits(subClass, superClass) {
  if (typeof superClass !== "function" && superClass !== null) {
    throw new TypeError("Super expression must either be null or a function");
  }
  subClass.prototype = Object.create(superClass && superClass.prototype, {
    constructor: {
      value: subClass,
      writable: true,
      configurable: true
    }
  });
  Object.defineProperty(subClass, "prototype", {
    writable: false
  });
  if (superClass) _setPrototypeOf(subClass, superClass);
}
function _setPrototypeOf(o, p) {
  _setPrototypeOf = Object.setPrototypeOf ? Object.setPrototypeOf.bind() : function _setPrototypeOf(o, p) {
    o.__proto__ = p;
    return o;
  };
  return _setPrototypeOf(o, p);
}
function _createSuper(Derived) {
  var hasNativeReflectConstruct = _isNativeReflectConstruct();
  return function _createSuperInternal() {
    var Super = _getPrototypeOf(Derived),
      result;
    if (hasNativeReflectConstruct) {
      var NewTarget = _getPrototypeOf(this).constructor;
      result = Reflect.construct(Super, arguments, NewTarget);
    } else {
      result = Super.apply(this, arguments);
    }
    return _possibleConstructorReturn(this, result);
  };
}
function _possibleConstructorReturn(self, call) {
  if (call && (_typeof(call) === "object" || typeof call === "function")) {
    return call;
  } else if (call !== void 0) {
    throw new TypeError("Derived constructors may only return object or undefined");
  }
  return _assertThisInitialized(self);
}
function _assertThisInitialized(self) {
  if (self === void 0) {
    throw new ReferenceError("this hasn't been initialised - super() hasn't been called");
  }
  return self;
}
function _isNativeReflectConstruct() {
  if (typeof Reflect === "undefined" || !Reflect.construct) return false;
  if (Reflect.construct.sham) return false;
  if (typeof Proxy === "function") return true;
  try {
    Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function () {}));
    return true;
  } catch (e) {
    return false;
  }
}
function _getPrototypeOf(o) {
  _getPrototypeOf = Object.setPrototypeOf ? Object.getPrototypeOf.bind() : function _getPrototypeOf(o) {
    return o.__proto__ || Object.getPrototypeOf(o);
  };
  return _getPrototypeOf(o);
}
function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);
  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    enumerableOnly && (symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    })), keys.push.apply(keys, symbols);
  }
  return keys;
}
function _objectSpread(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = null != arguments[i] ? arguments[i] : {};
    i % 2 ? ownKeys(Object(source), !0).forEach(function (key) {
      _defineProperty(target, key, source[key]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) {
      Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
  }
  return target;
}
function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }
  return obj;
}
function _objectWithoutProperties(source, excluded) {
  if (source == null) return {};
  var target = _objectWithoutPropertiesLoose(source, excluded);
  var key, i;
  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }
  return target;
}
function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;
  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }
  return target;
}
function _toConsumableArray(arr) {
  return _arrayWithoutHoles(arr) || _iterableToArray(arr) || _unsupportedIterableToArray(arr) || _nonIterableSpread();
}
function _nonIterableSpread() {
  throw new TypeError("Invalid attempt to spread non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
function _unsupportedIterableToArray(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen);
}
function _iterableToArray(iter) {
  if (typeof Symbol !== "undefined" && iter[Symbol.iterator] != null || iter["@@iterator"] != null) return Array.from(iter);
}
function _arrayWithoutHoles(arr) {
  if (Array.isArray(arr)) return _arrayLikeToArray(arr);
}
function _arrayLikeToArray(arr, len) {
  if (len == null || len > arr.length) len = arr.length;
  for (var i = 0, arr2 = new Array(len); i < len; i++) {
    arr2[i] = arr[i];
  }
  return arr2;
}
var defaultMinDate = new Date();
defaultMinDate.setFullYear(1, 0, 1);
defaultMinDate.setHours(0, 0, 0, 0);
var defaultMaxDate = new Date(8.64e15);
var baseClassName = 'react-calendar';
var allViews = ['century', 'decade', 'year', 'month'];
var allValueTypes = [].concat(_toConsumableArray(allViews.slice(1)), ['day']);
function toDate(value) {
  if (value instanceof Date) {
    return value;
  }
  return new Date(value);
}
/**
 * Returns views array with disallowed values cut off.
 */

function getLimitedViews(minDetail, maxDetail) {
  return allViews.slice(allViews.indexOf(minDetail), allViews.indexOf(maxDetail) + 1);
}
/**
 * Determines whether a given view is allowed with currently applied settings.
 */

function isViewAllowed(view, minDetail, maxDetail) {
  var views = getLimitedViews(minDetail, maxDetail);
  return views.indexOf(view) !== -1;
}
/**
 * Gets either provided view if allowed by minDetail and maxDetail, or gets
 * the default view if not allowed.
 */

function getView(view, minDetail, maxDetail) {
  if (isViewAllowed(view, minDetail, maxDetail)) {
    return view;
  }
  return maxDetail;
}
/**
 * Returns value type that can be returned with currently applied settings.
 */

function getValueType(maxDetail) {
  return allValueTypes[allViews.indexOf(maxDetail)];
}
function getValue(value, index) {
  if (!value) {
    return null;
  }
  var rawValue = Array.isArray(value) && value.length === 2 ? value[index] : value;
  if (!rawValue) {
    return null;
  }
  var valueDate = toDate(rawValue);
  if (isNaN(valueDate.getTime())) {
    throw new Error("Invalid date: ".concat(value));
  }
  return valueDate;
}
function getDetailValue(_ref, index) {
  var value = _ref.value,
    minDate = _ref.minDate,
    maxDate = _ref.maxDate,
    maxDetail = _ref.maxDetail;
  var valuePiece = getValue(value, index);
  if (!valuePiece) {
    return null;
  }
  var valueType = getValueType(maxDetail);
  var detailValueFrom = [getBegin, getEnd][index](valueType, valuePiece);
  return between(detailValueFrom, minDate, maxDate);
}
var getDetailValueFrom = function getDetailValueFrom(args) {
  return getDetailValue(args, 0);
};
var getDetailValueTo = function getDetailValueTo(args) {
  return getDetailValue(args, 1);
};
var getDetailValueArray = function getDetailValueArray(args) {
  var value = args.value;
  if (Array.isArray(value)) {
    return value;
  }
  return [getDetailValueFrom, getDetailValueTo].map(function (fn) {
    return fn(args);
  });
};
function getActiveStartDate(props) {
  var maxDate = props.maxDate,
    maxDetail = props.maxDetail,
    minDate = props.minDate,
    minDetail = props.minDetail,
    value = props.value,
    view = props.view;
  var rangeType = getView(view, minDetail, maxDetail);
  var valueFrom = getDetailValueFrom({
    value: value,
    minDate: minDate,
    maxDate: maxDate,
    maxDetail: maxDetail
  }) || new Date();
  return getBegin(rangeType, valueFrom);
}
function getInitialActiveStartDate(props) {
  var activeStartDate = props.activeStartDate,
    defaultActiveStartDate = props.defaultActiveStartDate,
    defaultValue = props.defaultValue,
    defaultView = props.defaultView,
    maxDetail = props.maxDetail,
    minDetail = props.minDetail,
    value = props.value,
    view = props.view,
    otherProps = _objectWithoutProperties(props, _excluded);
  var rangeType = getView(view, minDetail, maxDetail);
  var valueFrom = activeStartDate || defaultActiveStartDate;
  if (valueFrom) {
    return getBegin(rangeType, valueFrom);
  }
  return getActiveStartDate(_objectSpread({
    maxDetail: maxDetail,
    minDetail: minDetail,
    value: value || defaultValue,
    view: view || defaultView
  }, otherProps));
}
var getIsSingleValue = function getIsSingleValue(value) {
  return value && [].concat(value).length === 1;
};
var Calendar$1 = /*#__PURE__*/function (_Component) {
  _inherits(Calendar, _Component);
  var _super = _createSuper(Calendar);
  function Calendar() {
    var _this;
    _classCallCheck(this, Calendar);
    for (var _len = arguments.length, _args = new Array(_len), _key = 0; _key < _len; _key++) {
      _args[_key] = arguments[_key];
    }
    _this = _super.call.apply(_super, [this].concat(_args));
    _defineProperty(_assertThisInitialized(_this), "state", {
      activeStartDate: _this.props.defaultActiveStartDate,
      value: _this.props.defaultValue,
      view: _this.props.defaultView
    });
    _defineProperty(_assertThisInitialized(_this), "setStateAndCallCallbacks", function (nextState, event, callback) {
      var _assertThisInitialize = _assertThisInitialized(_this),
        previousActiveStartDate = _assertThisInitialize.activeStartDate,
        previousView = _assertThisInitialize.view;
      var _this$props = _this.props,
        allowPartialRange = _this$props.allowPartialRange,
        onActiveStartDateChange = _this$props.onActiveStartDateChange,
        onChange = _this$props.onChange,
        onViewChange = _this$props.onViewChange,
        selectRange = _this$props.selectRange;
      var prevArgs = {
        activeStartDate: previousActiveStartDate,
        view: previousView
      };
      _this.setState(nextState, function () {
        var args = {
          action: nextState.action,
          activeStartDate: nextState.activeStartDate || _this.activeStartDate,
          value: nextState.value || _this.value,
          view: nextState.view || _this.view
        };
        function shouldUpdate(key) {
          return (
            // Key must exist, and…
            key in nextState && (
            // …key changed from undefined to defined or the other way around, or…
            _typeof(nextState[key]) !== _typeof(prevArgs[key]) || (
            // …value changed.
            nextState[key] instanceof Date ? nextState[key].getTime() !== prevArgs[key].getTime() : nextState[key] !== prevArgs[key]))
          );
        }
        if (shouldUpdate('activeStartDate')) {
          if (onActiveStartDateChange) onActiveStartDateChange(args);
        }
        if (shouldUpdate('view')) {
          if (onViewChange) onViewChange(args);
        }
        if (shouldUpdate('value')) {
          if (onChange) {
            if (selectRange) {
              var isSingleValue = getIsSingleValue(nextState.value);
              if (!isSingleValue) {
                onChange(nextState.value, event);
              } else if (allowPartialRange) {
                onChange([nextState.value], event);
              }
            } else {
              onChange(nextState.value, event);
            }
          }
        }
        if (callback) callback(args);
      });
    });
    _defineProperty(_assertThisInitialized(_this), "setActiveStartDate", function (nextActiveStartDate, action) {
      _this.setStateAndCallCallbacks({
        action: action,
        activeStartDate: nextActiveStartDate
      });
    });
    _defineProperty(_assertThisInitialized(_this), "drillDown", function (nextActiveStartDate, event) {
      if (!_this.drillDownAvailable) {
        return;
      }
      _this.onClickTile(nextActiveStartDate, event);
      var _assertThisInitialize2 = _assertThisInitialized(_this),
        view = _assertThisInitialize2.view,
        views = _assertThisInitialize2.views;
      var onDrillDown = _this.props.onDrillDown;
      var nextView = views[views.indexOf(view) + 1];
      _this.setStateAndCallCallbacks({
        action: 'drillDown',
        activeStartDate: nextActiveStartDate,
        view: nextView
      }, undefined, onDrillDown);
    });
    _defineProperty(_assertThisInitialized(_this), "drillUp", function () {
      if (!_this.drillUpAvailable) {
        return;
      }
      var _assertThisInitialize3 = _assertThisInitialized(_this),
        activeStartDate = _assertThisInitialize3.activeStartDate,
        view = _assertThisInitialize3.view,
        views = _assertThisInitialize3.views;
      var onDrillUp = _this.props.onDrillUp;
      var nextView = views[views.indexOf(view) - 1];
      var nextActiveStartDate = getBegin(nextView, activeStartDate);
      _this.setStateAndCallCallbacks({
        action: 'drillUp',
        activeStartDate: nextActiveStartDate,
        view: nextView
      }, undefined, onDrillUp);
    });
    _defineProperty(_assertThisInitialized(_this), "onChange", function (value, event) {
      var _assertThisInitialize4 = _assertThisInitialized(_this),
        previousValue = _assertThisInitialize4.value;
      var _this$props2 = _this.props,
        goToRangeStartOnSelect = _this$props2.goToRangeStartOnSelect,
        selectRange = _this$props2.selectRange;
      _this.onClickTile(value, event);
      var isFirstValueInRange = selectRange && !getIsSingleValue(previousValue);
      var nextValue;
      if (selectRange) {
        // Range selection turned on
        var _assertThisInitialize5 = _assertThisInitialized(_this),
          valueType = _assertThisInitialize5.valueType;
        if (isFirstValueInRange) {
          // Value has 0 or 2 elements - either way we're starting a new array
          // First value
          nextValue = getBegin(valueType, value);
        } else {
          // Second value
          nextValue = getValueRange(valueType, previousValue, value);
        }
      } else {
        // Range selection turned off
        nextValue = _this.getProcessedValue(value);
      }
      var nextActiveStartDate =
      // Range selection turned off
      !selectRange ||
      // Range selection turned on, first value
      isFirstValueInRange ||
      // Range selection turned on, second value, goToRangeStartOnSelect toggled on
      goToRangeStartOnSelect ? getActiveStartDate(_objectSpread(_objectSpread({}, _this.props), {}, {
        value: nextValue
      })) : null;
      event.persist();
      _this.setStateAndCallCallbacks({
        action: 'onChange',
        activeStartDate: nextActiveStartDate,
        value: nextValue
      }, event);
    });
    _defineProperty(_assertThisInitialized(_this), "onClickTile", function (value, event) {
      var _assertThisInitialize6 = _assertThisInitialized(_this),
        view = _assertThisInitialize6.view;
      var _this$props3 = _this.props,
        onClickDay = _this$props3.onClickDay,
        onClickDecade = _this$props3.onClickDecade,
        onClickMonth = _this$props3.onClickMonth,
        onClickYear = _this$props3.onClickYear;
      var callback = function () {
        switch (view) {
          case 'century':
            return onClickDecade;
          case 'decade':
            return onClickYear;
          case 'year':
            return onClickMonth;
          case 'month':
            return onClickDay;
          default:
            throw new Error("Invalid view: ".concat(view, "."));
        }
      }();
      if (callback) callback(value, event);
    });
    _defineProperty(_assertThisInitialized(_this), "onMouseOver", function (value) {
      _this.setState(function (prevState) {
        if (prevState.hover && prevState.hover.getTime() === value.getTime()) {
          return null;
        }
        return {
          hover: value
        };
      });
    });
    _defineProperty(_assertThisInitialized(_this), "onMouseLeave", function () {
      _this.setState({
        hover: null
      });
    });
    return _this;
  }
  _createClass(Calendar, [{
    key: "activeStartDate",
    get: function get() {
      var activeStartDateProps = this.props.activeStartDate;
      var activeStartDateState = this.state.activeStartDate;
      return activeStartDateProps || activeStartDateState || getInitialActiveStartDate(this.props);
    }
  }, {
    key: "value",
    get: function get() {
      var _this$props4 = this.props,
        selectRange = _this$props4.selectRange,
        valueProps = _this$props4.value;
      var valueState = this.state.value; // In the middle of range selection, use value from state

      if (selectRange && getIsSingleValue(valueState)) {
        return valueState;
      }
      return valueProps !== undefined ? valueProps : valueState;
    }
  }, {
    key: "valueType",
    get: function get() {
      var maxDetail = this.props.maxDetail;
      return getValueType(maxDetail);
    }
  }, {
    key: "view",
    get: function get() {
      var _this$props5 = this.props,
        minDetail = _this$props5.minDetail,
        maxDetail = _this$props5.maxDetail,
        viewProps = _this$props5.view;
      var viewState = this.state.view;
      return getView(viewProps || viewState, minDetail, maxDetail);
    }
  }, {
    key: "views",
    get: function get() {
      var _this$props6 = this.props,
        minDetail = _this$props6.minDetail,
        maxDetail = _this$props6.maxDetail;
      return getLimitedViews(minDetail, maxDetail);
    }
  }, {
    key: "hover",
    get: function get() {
      var selectRange = this.props.selectRange;
      var hover = this.state.hover;
      return selectRange ? hover : null;
    }
  }, {
    key: "drillDownAvailable",
    get: function get() {
      var view = this.view,
        views = this.views;
      return views.indexOf(view) < views.length - 1;
    }
  }, {
    key: "drillUpAvailable",
    get: function get() {
      var view = this.view,
        views = this.views;
      return views.indexOf(view) > 0;
    }
    /**
     * Gets current value in a desired format.
     */
  }, {
    key: "getProcessedValue",
    value: function getProcessedValue(value) {
      var _this$props7 = this.props,
        minDate = _this$props7.minDate,
        maxDate = _this$props7.maxDate,
        maxDetail = _this$props7.maxDetail,
        returnValue = _this$props7.returnValue;
      var processFunction = function () {
        switch (returnValue) {
          case 'start':
            return getDetailValueFrom;
          case 'end':
            return getDetailValueTo;
          case 'range':
            return getDetailValueArray;
          default:
            throw new Error('Invalid returnValue.');
        }
      }();
      return processFunction({
        value: value,
        minDate: minDate,
        maxDate: maxDate,
        maxDetail: maxDetail
      });
    }
  }, {
    key: "renderContent",
    value: function renderContent(next) {
      var currentActiveStartDate = this.activeStartDate,
        onMouseOver = this.onMouseOver,
        valueType = this.valueType,
        value = this.value,
        view = this.view;
      var _this$props8 = this.props,
        calendarType = _this$props8.calendarType,
        locale = _this$props8.locale,
        maxDate = _this$props8.maxDate,
        minDate = _this$props8.minDate,
        selectRange = _this$props8.selectRange,
        tileClassName = _this$props8.tileClassName,
        tileContent = _this$props8.tileContent,
        tileDisabled = _this$props8.tileDisabled;
      var hover = this.hover;
      var activeStartDate = next ? getBeginNext(view, currentActiveStartDate) : getBegin(view, currentActiveStartDate);
      var onClick = this.drillDownAvailable ? this.drillDown : this.onChange;
      var commonProps = {
        activeStartDate: activeStartDate,
        hover: hover,
        locale: locale,
        maxDate: maxDate,
        minDate: minDate,
        onClick: onClick,
        onMouseOver: selectRange ? onMouseOver : null,
        tileClassName: tileClassName,
        tileContent: tileContent,
        tileDisabled: tileDisabled,
        value: value,
        valueType: valueType
      };
      switch (view) {
        case 'century':
          {
            var formatYear = this.props.formatYear;
            return /*#__PURE__*/React.createElement(CenturyView, _extends({
              formatYear: formatYear
            }, commonProps));
          }
        case 'decade':
          {
            var _formatYear = this.props.formatYear;
            return /*#__PURE__*/React.createElement(DecadeView, _extends({
              formatYear: _formatYear
            }, commonProps));
          }
        case 'year':
          {
            var _this$props9 = this.props,
              formatMonth = _this$props9.formatMonth,
              formatMonthYear = _this$props9.formatMonthYear;
            return /*#__PURE__*/React.createElement(YearView, _extends({
              formatMonth: formatMonth,
              formatMonthYear: formatMonthYear
            }, commonProps));
          }
        case 'month':
          {
            var _this$props10 = this.props,
              formatDay = _this$props10.formatDay,
              formatLongDate = _this$props10.formatLongDate,
              formatShortWeekday = _this$props10.formatShortWeekday,
              formatWeekday = _this$props10.formatWeekday,
              onClickWeekNumber = _this$props10.onClickWeekNumber,
              showDoubleView = _this$props10.showDoubleView,
              showFixedNumberOfWeeks = _this$props10.showFixedNumberOfWeeks,
              showNeighboringMonth = _this$props10.showNeighboringMonth,
              showWeekNumbers = _this$props10.showWeekNumbers;
            var onMouseLeave = this.onMouseLeave;
            return /*#__PURE__*/React.createElement(MonthView, _extends({
              calendarType: calendarType,
              formatDay: formatDay,
              formatLongDate: formatLongDate,
              formatShortWeekday: formatShortWeekday,
              formatWeekday: formatWeekday,
              onClickWeekNumber: onClickWeekNumber,
              onMouseLeave: selectRange ? onMouseLeave : null,
              showFixedNumberOfWeeks: typeof showFixedNumberOfWeeks !== 'undefined' ? showFixedNumberOfWeeks : showDoubleView,
              showNeighboringMonth: showNeighboringMonth,
              showWeekNumbers: showWeekNumbers
            }, commonProps));
          }
        default:
          throw new Error("Invalid view: ".concat(view, "."));
      }
    }
  }, {
    key: "renderNavigation",
    value: function renderNavigation() {
      var showNavigation = this.props.showNavigation;
      if (!showNavigation) {
        return null;
      }
      var activeStartDate = this.activeStartDate,
        view = this.view,
        views = this.views;
      var _this$props11 = this.props,
        formatMonthYear = _this$props11.formatMonthYear,
        formatYear = _this$props11.formatYear,
        locale = _this$props11.locale,
        maxDate = _this$props11.maxDate,
        minDate = _this$props11.minDate,
        navigationAriaLabel = _this$props11.navigationAriaLabel,
        navigationAriaLive = _this$props11.navigationAriaLive,
        navigationLabel = _this$props11.navigationLabel,
        next2AriaLabel = _this$props11.next2AriaLabel,
        next2Label = _this$props11.next2Label,
        nextAriaLabel = _this$props11.nextAriaLabel,
        nextLabel = _this$props11.nextLabel,
        prev2AriaLabel = _this$props11.prev2AriaLabel,
        prev2Label = _this$props11.prev2Label,
        prevAriaLabel = _this$props11.prevAriaLabel,
        prevLabel = _this$props11.prevLabel,
        showDoubleView = _this$props11.showDoubleView;
      return /*#__PURE__*/React.createElement(Navigation, {
        activeStartDate: activeStartDate,
        drillUp: this.drillUp,
        formatMonthYear: formatMonthYear,
        formatYear: formatYear,
        locale: locale,
        maxDate: maxDate,
        minDate: minDate,
        navigationAriaLabel: navigationAriaLabel,
        navigationAriaLive: navigationAriaLive,
        navigationLabel: navigationLabel,
        next2AriaLabel: next2AriaLabel,
        next2Label: next2Label,
        nextAriaLabel: nextAriaLabel,
        nextLabel: nextLabel,
        prev2AriaLabel: prev2AriaLabel,
        prev2Label: prev2Label,
        prevAriaLabel: prevAriaLabel,
        prevLabel: prevLabel,
        setActiveStartDate: this.setActiveStartDate,
        showDoubleView: showDoubleView,
        view: view,
        views: views
      });
    }
  }, {
    key: "render",
    value: function render() {
      var _this$props12 = this.props,
        className = _this$props12.className,
        inputRef = _this$props12.inputRef,
        selectRange = _this$props12.selectRange,
        showDoubleView = _this$props12.showDoubleView;
      var onMouseLeave = this.onMouseLeave,
        value = this.value;
      var valueArray = [].concat(value);
      return /*#__PURE__*/React.createElement("div", {
        className: mergeClassNames(baseClassName, selectRange && valueArray.length === 1 && "".concat(baseClassName, "--selectRange"), showDoubleView && "".concat(baseClassName, "--doubleView"), className),
        ref: inputRef
      }, this.renderNavigation(), /*#__PURE__*/React.createElement("div", {
        className: "".concat(baseClassName, "__viewContainer"),
        onBlur: selectRange ? onMouseLeave : null,
        onMouseLeave: selectRange ? onMouseLeave : null
      }, this.renderContent(), showDoubleView && this.renderContent(true)));
    }
  }]);
  return Calendar;
}(Component);
Calendar$1.defaultProps = {
  goToRangeStartOnSelect: true,
  maxDate: defaultMaxDate,
  maxDetail: 'month',
  minDate: defaultMinDate,
  minDetail: 'century',
  returnValue: 'start',
  showNavigation: true,
  showNeighboringMonth: true
};
var isActiveStartDate = PropTypes.instanceOf(Date);
var isLooseValue = PropTypes.oneOfType([PropTypes.string, isValue]);
Calendar$1.propTypes = {
  activeStartDate: isActiveStartDate,
  allowPartialRange: PropTypes.bool,
  calendarType: isCalendarType,
  className: isClassName,
  defaultActiveStartDate: isActiveStartDate,
  defaultValue: isLooseValue,
  defaultView: isView,
  formatDay: PropTypes.func,
  formatLongDate: PropTypes.func,
  formatMonth: PropTypes.func,
  formatMonthYear: PropTypes.func,
  formatShortWeekday: PropTypes.func,
  formatWeekday: PropTypes.func,
  formatYear: PropTypes.func,
  goToRangeStartOnSelect: PropTypes.bool,
  inputRef: isRef,
  locale: PropTypes.string,
  maxDate: isMaxDate,
  maxDetail: PropTypes.oneOf(allViews),
  minDate: isMinDate,
  minDetail: PropTypes.oneOf(allViews),
  navigationAriaLabel: PropTypes.string,
  navigationAriaLive: PropTypes.oneOf(['off', 'polite', 'assertive']),
  navigationLabel: PropTypes.func,
  next2AriaLabel: PropTypes.string,
  next2Label: PropTypes.node,
  nextAriaLabel: PropTypes.string,
  nextLabel: PropTypes.node,
  onActiveStartDateChange: PropTypes.func,
  onChange: PropTypes.func,
  onClickDay: PropTypes.func,
  onClickDecade: PropTypes.func,
  onClickMonth: PropTypes.func,
  onClickWeekNumber: PropTypes.func,
  onClickYear: PropTypes.func,
  onDrillDown: PropTypes.func,
  onDrillUp: PropTypes.func,
  onViewChange: PropTypes.func,
  prev2AriaLabel: PropTypes.string,
  prev2Label: PropTypes.node,
  prevAriaLabel: PropTypes.string,
  prevLabel: PropTypes.node,
  returnValue: PropTypes.oneOf(['start', 'end', 'range']),
  selectRange: PropTypes.bool,
  showDoubleView: PropTypes.bool,
  showFixedNumberOfWeeks: PropTypes.bool,
  showNavigation: PropTypes.bool,
  showNeighboringMonth: PropTypes.bool,
  showWeekNumbers: PropTypes.bool,
  tileClassName: PropTypes.oneOfType([PropTypes.func, isClassName]),
  tileContent: PropTypes.oneOfType([PropTypes.func, PropTypes.node]),
  tileDisabled: PropTypes.func,
  value: isLooseValue,
  view: isView
};

var Calendar = Calendar$1;

var styles$a = createUseStyles({
  Calendar: {
    width: '350px',
    maxWidth: '100%',
    background: 'white',
    border: '1px solid #a0a096',
    borderRadius: ({
      theme,
      rounded,
      size
    }) => rounded ? theme.radius[size] : 0,
    fontFamily: ({
      theme
    }) => theme.fontFamilies.body,
    '&.react-calendar--doubleView': {
      width: '700px',
      '& .react-calendar__viewContainer': {
        display: 'flex',
        margin: '-0.5em'
      },
      '& .react-calendar__viewContainer > *': {
        width: '50%',
        margin: '0.5em'
      }
    },
    '& button': {
      margin: '0',
      border: '0',
      outline: 'none'
    },
    '& button:enabled:hover': {
      cursor: 'pointer'
    },
    '& .react-calendar__navigation': {
      display: 'flex',
      height: '44px',
      marginBottom: '1em'
    },
    '& .react-calendar__navigation button': {
      minWidth: '44px',
      background: 'none'
    },
    '& .react-calendar__navigation > :nth-child(1)': {
      borderTopLeftRadius: ({
        theme,
        rounded,
        size
      }) => rounded ? theme.radius[size] : 0
    },
    '& .react-calendar__navigation > :last-child': {
      borderTopRightRadius: ({
        theme,
        rounded,
        size
      }) => rounded ? theme.radius[size] : 0
    },
    '& .react-calendar__navigation button:disabled': {
      backgroundColor: '#f0f0f0'
    },
    '& .react-calendar__navigation button:enabled:hover': {
      backgroundColor: '#e6e6e6'
    },
    '& .react-calendar__navigation button:enabled:focus': {
      backgroundColor: '#e6e6e6'
    },
    '& .react-calendar__month-view__weekdays': {
      textAlign: 'center',
      textTransform: 'uppercase',
      fontWeight: 'bold',
      fontSize: '0.75em',
      color: ({
        theme
      }) => theme.colors.neutral200
    },
    '& .react-calendar__month-view__weekdays__weekday': {
      padding: '0.5em'
    },
    '& .react-calendar__month-view__weekNumbers .react-calendar__tile': {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '0.75em',
      fontWeight: 'bold'
    },
    '& .react-calendar__month-view__days__day--weekend': {
      color: 'black',
      fontWeight: 'bold'
    },
    '& .react-calendar__month-view__days__day--neighboringMonth': {
      color: '#757575'
    },
    '& .react-calendar__year-view .react-calendar__tile': {
      padding: '2em 0.5em'
    },
    '& .react-calendar__decade-view .react-calendar__tile': {
      padding: '2em 0.5em'
    },
    '& .react-calendar__century-view .react-calendar__tile': {
      padding: '2em 0.5em'
    },
    '& .react-calendar__tile': {
      maxWidth: '100%',
      padding: '10px 6.6667px',
      background: 'none',
      textAlign: 'center',
      lineHeight: '16px'
    },
    '& .react-calendar__tile:disabled': {
      backgroundColor: '#f0f0f0'
    },
    '& .react-calendar__tile:enabled:hover': {},
    '& .react-calendar__tile:enabled:focus': {
      backgroundColor: '#e6e6e6'
    },
    '& .react-calendar__tile--now': {
      backgroundColor: ({
        theme
      }) => theme.colors.secondary200
    },
    '& .react-calendar__tile--now:enabled:hover': {
      backgroundColor: ({
        theme
      }) => theme.colors.secondary300
    },
    '& .react-calendar__tile--now:enabled:focus': {
      backgroundColor: ({
        theme
      }) => theme.colors.secondary300
    },
    '& .react-calendar__tile--hasActive': {
      background: ({
        theme
      }) => theme.colors.primary100
    },
    '& .react-calendar__tile--hasActive:enabled:hover': {
      background: ({
        theme
      }) => theme.colors.primary200
    },
    '& .react-calendar__tile--hasActive:enabled:focus': {
      background: ({
        theme
      }) => theme.colors.primary200
    },
    '& .react-calendar__tile--active': {
      backgroundColor: ({
        theme
      }) => theme.colors.primary,
      color: 'white'
    },
    '& .react-calendar__tile--active:enabled:hover': {
      backgroundColor: ({
        theme
      }) => theme.colors.primary400
    },
    '& .react-calendar__tile--active:enabled:focus': {
      backgroundColor: ({
        theme
      }) => theme.colors.primary400
    },
    '& .react-calendar--selectRange .react-calendar__tile--hover': {
      backgroundColor: '#e6e6e6'
    }
  }
});

function setYear(year, date = new Date()) {
  date.setFullYear(year);
  return date;
}
const FieldDateCalendar = props => {
  const {
    name,
    value,
    startYear,
    endYear,
    attachToForm,
    detachFromForm,
    onInput,
    onChange,
    onFocus,
    onBlur,
    onValidate,
    label,
    id,
    format,
    color,
    inputTxtColor,
    icon,
    size,
    align,
    tabIndex,
    rules,
    minDate,
    maxDate,
    bgColor,
    borderColor,
    rounded,
    padding,
    margin,
    font
  } = props;
  const [dateVal, setDateVal] = useState(value);
  const [calVal, setCalVal] = useState();
  const [startDate, setStartDate] = useState(false);
  const [showCal, setShowCal] = useState(false);
  const theme = useContext(ThemeContext);
  const classes = styles$a({
    theme,
    ...props
  });
  const handleChange = date => {
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const year = date.getFullYear();
    const newVal = `${month}/${day}/${year}`;
    setDateVal(newVal);
    setCalVal(date);
    setStartDate(false);
    setShowCal(false);
  };
  const updateCal = date => {
    setCalVal(date);
    setStartDate(date);
    if (typeof onChange === 'function') {
      onChange(date);
    }
  };
  const dateEndDate = setYear(endYear);
  const dateStartDate = setYear(startYear);
  const handleFocus = e => {
    setShowCal(true);
    if (typeof onFocus === 'function') {
      onFocus(e);
    }
  };
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Box, {
    width: "350px",
    margin: "auto"
  }, /*#__PURE__*/React.createElement(FieldDate, {
    name: name,
    value: dateVal,
    label: label,
    id: id,
    startYear: startYear,
    endYear: endYear,
    onChange: updateCal,
    onFocus: handleFocus,
    onValidate: onValidate,
    onBlur: onBlur,
    attachToForm: attachToForm,
    detachFromForm: detachFromForm,
    onInput: onInput,
    format: format,
    color: color,
    icon: icon,
    size: size,
    font: font,
    align: align,
    tabIndex: tabIndex,
    rules: rules,
    minDate: minDate,
    maxDate: maxDate,
    bgColor: bgColor,
    borderColor: borderColor,
    rounded: rounded,
    padding: padding,
    margin: margin,
    inputTxtColor: inputTxtColor
  })), showCal && /*#__PURE__*/React.createElement(Box, {
    align: "center",
    justify: "center",
    padding: [0, 0, 'lg', 0]
  }, /*#__PURE__*/React.createElement(Calendar, {
    minDetail: "decade",
    showFixedNumberOfWeeks: true,
    activeStartDate: startDate,
    value: calVal,
    onChange: handleChange,
    onActiveStartDateChange: () => setStartDate(false),
    maxDate: dateStartDate < dateEndDate ? dateEndDate : dateStartDate,
    minDate: dateStartDate > dateEndDate ? dateEndDate : dateStartDate,
    className: classes.Calendar
  })));
};
FieldDateCalendar.propTypes = {
  attachToForm: PropTypes.func,
  detachFromForm: PropTypes.func,
  onInput: PropTypes.func,
  onChange: PropTypes.func,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  onValidate: PropTypes.func,
  name: PropTypes.string,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  id: PropTypes.string,
  format: PropTypes.string,
  value: PropTypes.string,
  color: PropTypes.string,
  inputTxtColor: PropTypes.string,
  icon: PropTypes.string,
  theme: PropTypes.string,
  size: PropTypes.string,
  font: PropTypes.string,
  align: PropTypes.string,
  tabIndex: PropTypes.number,
  rules: PropTypes.object,
  minDate: PropTypes.object,
  maxDate: PropTypes.object,
  bgColor: PropTypes.string,
  borderColor: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  rounded: PropTypes.oneOfType([PropTypes.string, PropTypes.bool, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  padding: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  margin: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))])
};
FieldDateCalendar.defaultProps = {
  color: 'primary',
  bgColor: 'white',
  borderColor: 'neutral200',
  inputTxtColor: 'black',
  size: 'md',
  rounded: 'md',
  align: '',
  id: 'date-field',
  format: 'MM/DD/YYYY'
};

const splitDate = (value, format) => {
  let day = '';
  let month = '';
  let year = '';
  if (value) {
    // Extract day, month, and year from value based on parsed date format positions
    day = value.substring(format.d[0], format.d[format.d.length - 1] + 1);
    month = value.substring(format.m[0], format.m[format.m.length - 1] + 1);
    year = value.substring(format.y[0], format.y[format.y.length - 1] + 1);
  }
  return {
    day,
    month,
    year
  };
};
const toIterableFormatString = format => {
  const formatStrings = 'DMY';
  return format.split('').filter((letter, index) => formatStrings.includes(letter) ? format.indexOf(letter) === index : true).join('');
};
const parseDateFormat = fmt => {
  const d = [];
  const m = [];
  const y = [];
  let delimiter;
  for (let i = 0; i < fmt.length; i += 1) {
    switch (fmt[i]) {
      case 'D':
        d.push(i);
        break;
      case 'M':
        m.push(i);
        break;
      case 'Y':
        y.push(i);
        break;
      default:
        delimiter = fmt[i];
    }
  }
  return {
    d,
    m,
    y,
    delimiter
  };
};
const formatDateValue = (format, day, month, year) => {
  const order = toIterableFormatString(format);
  return order.split('').map(letter => {
    let value = '';
    switch (letter) {
      case 'D':
        value = day;
        break;
      case 'M':
        value = month;
        break;
      case 'Y':
        value = year;
        break;
      default:
        value = letter;
    }
    return value;
  }).join('');
};
const dayOptions = [{
  value: '01',
  label: '01'
}, {
  value: '02',
  label: '02'
}, {
  value: '03',
  label: '03'
}, {
  value: '04',
  label: '04'
}, {
  value: '05',
  label: '05'
}, {
  value: '06',
  label: '06'
}, {
  value: '07',
  label: '07'
}, {
  value: '08',
  label: '08'
}, {
  value: '09',
  label: '09'
}, {
  value: '10',
  label: '10'
}, {
  value: '11',
  label: '11'
}, {
  value: '12',
  label: '12'
}, {
  value: '13',
  label: '13'
}, {
  value: '14',
  label: '14'
}, {
  value: '15',
  label: '15'
}, {
  value: '16',
  label: '16'
}, {
  value: '17',
  label: '17'
}, {
  value: '18',
  label: '18'
}, {
  value: '19',
  label: '19'
}, {
  value: '20',
  label: '20'
}, {
  value: '21',
  label: '21'
}, {
  value: '22',
  label: '22'
}, {
  value: '23',
  label: '23'
}, {
  value: '24',
  label: '24'
}, {
  value: '25',
  label: '25'
}, {
  value: '26',
  label: '26'
}, {
  value: '27',
  label: '27'
}, {
  value: '28',
  label: '28'
}, {
  value: '29',
  label: '29'
}, {
  value: '30',
  label: '30'
}, {
  value: '31',
  label: '31'
}];
const monthOptions = [{
  value: '01',
  label: '01'
}, {
  value: '02',
  label: '02'
}, {
  value: '03',
  label: '03'
}, {
  value: '04',
  label: '04'
}, {
  value: '05',
  label: '05'
}, {
  value: '06',
  label: '06'
}, {
  value: '07',
  label: '07'
}, {
  value: '08',
  label: '08'
}, {
  value: '09',
  label: '09'
}, {
  value: '10',
  label: '10'
}, {
  value: '11',
  label: '11'
}, {
  value: '12',
  label: '12'
}];
const Field$4 = /*#__PURE__*/forwardRef((props, ref) => {
  const {
    id,
    filled,
    children,
    focused,
    validated,
    invalid,
    valid,
    invalidMessage,
    name,
    label,
    placeholder,
    value,
    format,
    rules,
    minDate,
    maxDate,
    color,
    borderColor,
    icon,
    size,
    font,
    rounded,
    align,
    type,
    tabIndex,
    onValidate,
    handleInputDay,
    handleInputMonth,
    handleInputYear,
    onFocus,
    onBlur,
    startYear,
    endYear,
    day,
    month,
    year
  } = props;
  const theme = useContext(ThemeContext);
  const [yearOptions, setYearOptions] = useState([{}]);
  const classes = styles$d({
    theme,
    ...props
  });
  const yearOptionsFunc = (start, end) => {
    let year = Number(start);
    let output = [];
    if (start > end) {
      while (year >= end) {
        output.push({
          value: year,
          label: year
        });
        year -= 1;
      }
      return output;
    } else {
      while (year <= end) {
        output.push({
          value: year,
          label: year
        });
        year += 1;
      }
      return output;
    }
  };
  useEffect(() => {
    setYearOptions(yearOptionsFunc(startYear, endYear));
  }, [startYear, endYear]);
  const outputDateFields = () => {
    const order = toIterableFormatString(format);
    return order.split('').map(letter => {
      let value = '';
      switch (letter) {
        case 'D':
          value = /*#__PURE__*/React.createElement(Select, {
            key: `${name}_day`,
            className: classes.Control,
            placeholder: "DD",
            options: dayOptions,
            value: day,
            autotab: true,
            tabIndex: tabIndex,
            onValidate: onValidate,
            onChange: handleInputDay,
            onFocus: onFocus,
            onBlur: onBlur,
            maxlength: 2,
            width: "auto",
            font: font,
            rules: {
              maxlength: 2
            },
            size: size
          });
          break;
        case 'M':
          value = /*#__PURE__*/React.createElement(Select, {
            key: `${name}_month`,
            className: classes.Control,
            placeholder: "MM",
            options: monthOptions,
            value: month,
            autotab: true,
            tabIndex: tabIndex,
            onValidate: onValidate,
            onChange: handleInputMonth,
            onFocus: onFocus,
            onBlur: onBlur,
            maxlength: 2,
            width: "auto",
            font: font,
            rules: {
              maxlength: 2
            },
            size: size
          });
          break;
        case 'Y':
          value = /*#__PURE__*/React.createElement(Select, {
            key: `${name}_year`,
            className: classes.Control,
            placeholder: "YYYY",
            options: yearOptions,
            value: year,
            tabIndex: tabIndex,
            onValidate: onValidate,
            onChange: handleInputYear,
            onFocus: onFocus,
            onBlur: onBlur,
            maxlength: 4,
            width: "auto",
            font: font,
            rules: {
              maxlength: 4
            },
            size: size
          });
          break;
        default:
          value = /*#__PURE__*/React.createElement("span", {
            key: uId(`${name}_del`),
            className: classes.Delimiter
          }, letter);
      }
      return value;
    });
  };
  return /*#__PURE__*/React.createElement("div", {
    className: classes.Field
  }, label && /*#__PURE__*/React.createElement("div", {
    className: classes.Header
  }, /*#__PURE__*/React.createElement("label", {
    className: classes.Label,
    htmlFor: id
  }, label)), /*#__PURE__*/React.createElement("label", {
    className: classes.Body,
    style: {
      justifyContent: 'center'
    }
  }, icon && /*#__PURE__*/React.createElement("div", {
    className: classes.Icon
  }, icon), /*#__PURE__*/React.createElement(HiddenInput, {
    ref: ref,
    type: "hidden",
    name: name,
    value: value,
    rules: {
      date: {
        format,
        min: minDate,
        max: maxDate
      },
      ...rules
    }
  }), outputDateFields()), /*#__PURE__*/React.createElement(ToolTip, {
    color: "danger",
    in: invalid === true && focused,
    size: size
  }, invalidMessage));
});
Field$4.displayName = 'FieldDateSelectControl';
class FieldDateSelect extends React.Component {
  static displayName = 'FieldDateSelect';
  constructor(props) {
    super(props);
    const format = parseDateFormat(props.format || 'MM/DD/YYYY');
    const value = splitDate(props.value, format);
    this.delimiter = format.delimiter;
    this.theField = this.theField.bind(this);
    this.val = this.val.bind(this);
    this.validate = this.validate.bind(this);
    this.updateValue = this.updateValue.bind(this);
    this.handleInputDay = this.handleInputDay.bind(this);
    this.handleInputMonth = this.handleInputMonth.bind(this);
    this.handleInputYear = this.handleInputYear.bind(this);
    this.handleBlur = this.handleBlur.bind(this);
    this.handleFocus = this.handleFocus.bind(this);
    this.state = {
      id: uId(props.id),
      validated: false,
      value: formatDateValue(props.format, value.day, value.month, value.year),
      day: value.day,
      month: value.month,
      year: value.year,
      lastVal: props.value
    };
  }
  componentDidMount() {
    const {
      attachToForm
    } = this.props;
    if (typeof attachToForm === 'function') {
      attachToForm(this);
    }
  }
  componentWillUnmount() {
    const {
      detachFromForm
    } = this.props;
    if (typeof detachFromForm === 'function') {
      detachFromForm(this);
    }
  }
  async componentDidUpdate() {
    const {
      format,
      value
    } = this.props;
    const {
      lastVal
    } = this.state;
    if (value !== lastVal) {
      const format = parseDateFormat(format || 'MM/DD/YYYY');
      const splitVal = splitDate(value, format);
      await this.setState({
        lastVal: value
      });
      this.updateValue(splitVal.day, splitVal.month, splitVal.year);
    }
  }
  theField() {
    return this.field;
  }
  val() {
    return this.state.value;
  }
  async validate() {
    const valid = await this.field.validate();
    this.setState({
      valid: valid.valid,
      invalid: !valid.valid,
      invalidMessage: !valid.valid ? valid.message : '',
      validated: true
    });
    return valid;
  }
  async updateValue(day, month, year) {
    const {
      format,
      onInput
    } = this.props;
    const value = formatDateValue(format, day, month, year);
    await this.setState({
      value,
      day,
      month,
      year
    });
    if (typeof onInput === 'function') {
      onInput({
        target: this.field.theField()
      });
    }
  }
  async handleInputDay(e) {
    const {
      onDayInput,
      onChange
    } = this.props;
    const {
      validated,
      month,
      year
    } = this.state;
    const {
      value: day
    } = e.target;
    if (typeof onDayInput === 'function') {
      onDayInput(e);
    }
    if (typeof onChange === 'function' && year && month && day) {
      onChange(new Date(year, Number(month - 1), day));
    }
    this.setState({
      filled: this.field.val() === true
    });
    await this.updateValue(day, month, year);
    if (validated) {
      this.validate();
    }
  }
  async handleInputMonth(e) {
    const {
      onMonthInput,
      onChange
    } = this.props;
    const {
      validated,
      day,
      year
    } = this.state;
    const {
      value: month
    } = e.target;
    if (typeof onMonthInput === 'function') {
      onMonthInput(e);
    }
    if (typeof onChange === 'function' && year && month && day) {
      onChange(new Date(year, Number(month - 1), day));
    }
    this.setState({
      filled: this.field.val() === true
    });
    await this.updateValue(day, month, year);
    if (validated) {
      this.validate();
    }
  }
  async handleInputYear(e) {
    const {
      onYearInput,
      onChange
    } = this.props;
    const {
      validated,
      day,
      month
    } = this.state;
    const {
      value: year
    } = e.target;
    if (typeof onYearInput === 'function') {
      onYearInput(e);
    }
    if (typeof onChange === 'function' && year && month && day) {
      onChange(new Date(year, Number(month - 1), day));
    }
    this.setState({
      filled: this.field.val() === true
    });
    await this.updateValue(day, month, year);
    if (validated) {
      this.validate();
    }
  }
  handleBlur(e) {
    const {
      onBlur
    } = this.props;
    if (typeof onBlur === 'function') {
      onBlur(e);
    }
    this.timer = setTimeout(() => {
      this.setState({
        focused: false
      });
    }, 15);
  }
  handleFocus(e) {
    const {
      onFocus
    } = this.props;
    clearTimeout(this.timer);
    this.setState({
      focused: true
    });
    if (typeof onFocus === 'function') {
      onFocus(e);
    }
  }
  render() {
    const {
      id,
      filled,
      focused,
      validated,
      onValidate,
      invalidMessage,
      invalid,
      valid,
      value,
      day,
      month,
      year
    } = this.state;
    return /*#__PURE__*/React.createElement(Field$4, {
      ref: field => {
        this.field = field;
      },
      id: id,
      filled: filled,
      focused: focused,
      validated: validated,
      invalid: invalid,
      valid: valid,
      value: value,
      day: day,
      month: month,
      year: year,
      onValidate: onValidate,
      handleInputDay: this.handleInputDay,
      handleInputMonth: this.handleInputMonth,
      handleInputYear: this.handleInputYear,
      invalidMessage: invalidMessage,
      onFocus: this.handleFocus,
      onBlur: this.handleBlur,
      startYear: this.props.startYear,
      endYear: this.props.endYear,
      attachToForm: this.props.attachToForm,
      detachFromForm: this.props.detachFromForm,
      onInput: this.props.onInput,
      name: this.props.name,
      label: this.props.label,
      format: this.props.format,
      color: this.props.color,
      icon: this.props.icon,
      size: this.props.size,
      font: this.props.font,
      align: this.props.align,
      tabIndex: this.props.tabIndex,
      rules: this.props.rules,
      minDate: this.props.minDate,
      maxDate: this.props.maxDate,
      bgColor: this.props.bgColor,
      borderColor: this.props.borderColor,
      rounded: this.props.rounded,
      padding: this.props.padding,
      margin: this.props.margin,
      inputTxtColor: this.props.inputTxtColor
    });
  }
}
FieldDateSelect.propTypes = {
  attachToForm: PropTypes.func,
  detachFromForm: PropTypes.func,
  onChange: PropTypes.func,
  onInput: PropTypes.func,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  onValidate: PropTypes.func,
  name: PropTypes.string,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  id: PropTypes.string,
  format: PropTypes.string,
  startYear: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  endYear: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  value: PropTypes.string,
  color: PropTypes.string,
  inputTxtColor: PropTypes.string,
  icon: PropTypes.string,
  theme: PropTypes.string,
  size: PropTypes.string,
  font: PropTypes.string,
  align: PropTypes.string,
  tabIndex: PropTypes.number,
  rules: PropTypes.object,
  minDate: PropTypes.object,
  maxDate: PropTypes.object,
  bgColor: PropTypes.string,
  borderColor: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  rounded: PropTypes.oneOfType([PropTypes.string, PropTypes.bool, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  padding: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  margin: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))])
};
FieldDateSelect.defaultProps = {
  color: 'primary',
  bgColor: 'white',
  borderColor: 'neutral200',
  inputTxtColor: 'black',
  size: 'md',
  rounded: 'md',
  align: '',
  id: 'text-field',
  type: 'text',
  format: 'MM/DD/YYYY'
};

var styles$9 = createUseStyles({
  Field: {
    display: 'block',
    position: 'relative',
    margin: ({
      theme,
      margin
    }) => spacing(theme, margin)
  },
  Body: {
    display: 'flex',
    position: 'relative',
    padding: 0,
    borderStyle: 'solid',
    borderWidth: ({
      chunky
    }) => chunky ? '4px' : '1px',
    background: ({
      theme,
      bgColor
    }) => theme.colors[bgColor],
    borderColor: ({
      theme,
      borderColor,
      invalid
    }) => invalid ? theme.colors.danger : theme.colors[borderColor] || 'rgba(255, 255, 255, 0)',
    borderRadius: ({
      theme,
      rounded,
      size
    }) => rounded ? theme.radius[size] : 0
  },
  Label: ({
    theme,
    font,
    txtColor
  }) => ({
    ...theme.typography[font],
    color: theme.colors[txtColor]
  }),
  Input: {
    width: '60px',
    flexGrow: 0,
    marginLeft: ({
      theme
    }) => theme.spacing.md,
    textAlign: 'center'
  },
  InputLabel: ({
    theme
  }) => ({
    ...theme.typography.small
  }),
  Icon: {
    display: 'flex',
    flexShrink: '0',
    alignItems: 'center',
    justifyContent: 'center',
    width: '2.5rem',
    color: '#fff',
    borderTopLeftRadius: 'radius(field)',
    borderBottomLeftRadius: 'radius(field)',
    background: ({
      theme,
      color,
      invalid
    }) => invalid ? theme.colors.danger : theme.colors[color],
    borderRadius: ({
      theme,
      rounded,
      size
    }) => rounded ? [[theme.radius[size], 0, 0, theme.radius[size]]] : 0,
    margin: '-1px 0 -1px -1px'
  }
});

const formatHeight = inches => {
  let remainder = inches % 12;
  remainder = remainder ? ` ${remainder}"` : '';
  return `${Math.floor(inches / 12)}'${remainder}`;
};
const getInches = inches => inches % 12;
const getFeet = inches => Math.floor(inches / 12);
const processFields = (total, min, max) => {
  let value = total;
  value = Math.min(value, max);
  value = Math.max(value, min);
  const feet = getFeet(value);
  const inches = getInches(value);
  return {
    value,
    feet,
    inches
  };
};
const Field$3 = /*#__PURE__*/forwardRef(({
  id,
  filled,
  focused,
  validated,
  invalid,
  valid,
  value,
  inches,
  feet,
  name,
  label,
  rules,
  color,
  size,
  align,
  type,
  min,
  max,
  tabIndex,
  onValidate,
  onInput,
  onFocus,
  onBlur,
  onFeetInput,
  onInchesInput,
  txtColor,
  ...props
}, ref) => {
  const theme = useContext(ThemeContext);
  const classes = styles$9({
    theme,
    ...props
  });
  return /*#__PURE__*/React.createElement("div", {
    className: classes.Field
  }, label && /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("label", {
    className: classes.Label,
    htmlFor: id
  }, label)), /*#__PURE__*/React.createElement(Grid, {
    columns: "3fr minmax(120px, 1fr)",
    columnGap: "md"
  }, /*#__PURE__*/React.createElement(Range, {
    ref: ref,
    className: "field-height__control",
    type: type,
    id: id,
    name: name,
    value: Math.min(value, max),
    min: min,
    max: max,
    tabIndex: tabIndex,
    formatValue: formatHeight,
    onValidate: onValidate,
    onInput: onInput,
    onFocus: onFocus,
    onBlur: onBlur,
    rules: rules,
    size: size,
    txtColor: txtColor,
    controlled: true
  }), /*#__PURE__*/React.createElement(Box, {
    display: "flex"
  }, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("label", {
    htmlFor: `${name}-feet`,
    className: classes.InputLabel
  }, "Feet"), /*#__PURE__*/React.createElement(Input, {
    className: classes.Control,
    value: feet,
    id: `${name}-feet`,
    onInput: onFeetInput,
    tabIndex: tabIndex,
    onValidate: onValidate,
    onFocus: onFocus,
    onBlur: onBlur,
    txtColor: txtColor,
    type: "tel",
    controlled: true
  })), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("label", {
    htmlFor: `${name}-inches`,
    className: classes.InputLabel
  }, "Inches"), /*#__PURE__*/React.createElement(Input, {
    className: "field-height__control",
    value: inches,
    id: `${name}-inches`,
    onInput: onInchesInput,
    tabIndex: tabIndex,
    onValidate: onValidate,
    onFocus: onFocus,
    onBlur: onBlur,
    txtColor: txtColor,
    type: "tel",
    controlled: true
  })))));
});
Field$3.displayName = 'FieldHeightControl';
class FieldHeight extends React.Component {
  static displayName = 'FieldHeight';
  constructor(props) {
    super(props);
    this.theField = this.theField.bind(this);
    this.val = this.val.bind(this);
    this.validate = this.validate.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.handleFeetInput = this.handleFeetInput.bind(this);
    this.handleInchesInput = this.handleInchesInput.bind(this);
    this.handleBlur = this.handleBlur.bind(this);
    this.handleFocus = this.handleFocus.bind(this);
    this.state = {
      id: uId(props.id),
      value: props.defaultValue,
      feet: getFeet(props.defaultValue),
      inches: getInches(props.defaultValue),
      validated: false
    };
  }
  componentDidMount() {
    const {
      attachToForm
    } = this.props;
    if (typeof attachToForm === 'function') {
      attachToForm(this);
    }
  }
  componentWillUnmount() {
    const {
      detachFromForm
    } = this.props;
    if (typeof detachFromForm === 'function') {
      detachFromForm(this);
    }
  }
  theField() {
    return this.field.theField();
  }
  val() {
    return this.field.val();
  }
  async validate() {
    const valid = await this.field.validate();
    this.setState({
      valid: valid.valid,
      invalid: !valid.valid,
      validated: true
    });
    return valid;
  }
  async handleFeetInput(e) {
    const {
      onChange
    } = this.props;
    const {
      validated
    } = this.state;
    const {
      inches
    } = this.state;
    const feet = Number(e.target.value);
    const value = feet * 12 + inches;
    await this.setState({
      value,
      inches,
      feet,
      filled: this.field.val() === true
    });
    if (typeof onChange === 'function') {
      onChange({
        target: this.field
      });
    }
    if (validated) {
      this.validate();
    }
  }
  async handleInchesInput(e) {
    const {
      onChange
    } = this.props;
    const {
      validated
    } = this.state;
    const {
      feet
    } = this.state;
    const inches = Number(e.target.value);
    const value = feet * 12 + inches;
    await this.setState({
      value,
      feet,
      inches,
      filled: this.field.val() === true
    });
    if (typeof onChange === 'function') {
      onChange({
        target: this.field
      });
    }
    if (validated) {
      this.validate();
    }
  }
  handleChange(e, val) {
    const {
      onInput
    } = this.props;
    const {
      validated
    } = this.state;
    const {
      value
    } = e.target;
    const feet = getFeet(value);
    const inches = getInches(value);
    this.setState({
      value,
      feet,
      inches,
      filled: this.field.val() === true
    });
    if (validated) {
      this.validate();
    }
    if (typeof onInput === 'function') {
      onInput(e);
    }
  }
  handleBlur(e) {
    const {
      onBlur,
      max,
      min
    } = this.props;
    const {
      value
    } = this.state;
    this.setState({
      ...processFields(value, min, max),
      filled: this.field.val() === true
    });
    if (typeof onBlur === 'function') {
      onBlur(e);
    }
    this.setState({
      focused: false
    });
  }
  handleFocus(e) {
    const {
      onFocus
    } = this.props;
    this.setState({
      focused: true
    });
    if (typeof onFocus === 'function') {
      onFocus(e);
    }
  }
  render() {
    const {
      id,
      filled,
      focused,
      validated,
      invalid,
      valid,
      value,
      inches,
      feet
    } = this.state;
    const {
      name,
      label,
      rules,
      color,
      theme,
      size,
      align,
      type,
      min,
      max,
      tabIndex,
      onValidate,
      txtColor,
      margin
    } = this.props;
    return /*#__PURE__*/React.createElement(Field$3, {
      ref: field => {
        this.field = field;
      },
      id: id,
      filled: filled,
      focused: focused,
      validated: validated,
      invalid: invalid,
      valid: valid,
      value: value,
      inches: inches,
      feet: feet,
      onInput: this.handleChange,
      onFocus: this.handleFocus,
      onBlur: this.handleBlur,
      onFeetInput: this.handleFeetInput,
      onInchesInput: this.handleInchesInput,
      min: min,
      max: max,
      txtColor: txtColor,
      margin: margin
    });
  }
}
FieldHeight.propTypes = {
  attachToForm: PropTypes.func,
  detachFromForm: PropTypes.func,
  onChange: PropTypes.func,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  onValidate: PropTypes.func,
  name: PropTypes.string,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  id: PropTypes.string,
  value: PropTypes.number,
  max: PropTypes.number,
  min: PropTypes.number,
  color: PropTypes.string,
  size: PropTypes.string,
  align: PropTypes.string,
  rules: PropTypes.object,
  type: PropTypes.string,
  txtColor: PropTypes.string,
  tabIndex: PropTypes.number,
  padding: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  margin: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))])
};
FieldHeight.defaultProps = {
  color: 'light',
  size: 'md',
  align: '',
  id: 'text-field',
  type: 'range',
  defaultValue: 72
};

var styles$8 = createUseStyles({
  Field: {
    display: 'block',
    position: 'relative',
    margin: ({
      theme,
      margin
    }) => spacing(theme, margin)
  },
  Body: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
    padding: 0,
    borderStyle: 'solid',
    borderWidth: ({
      chunky
    }) => chunky ? '4px' : '1px',
    background: ({
      theme,
      bgColor
    }) => theme.colors[bgColor],
    borderColor: ({
      theme,
      borderColor,
      invalid
    }) => invalid ? theme.colors.danger : theme.colors[borderColor] || 'rgba(255, 255, 255, 0)',
    borderRadius: ({
      theme,
      rounded,
      size
    }) => rounded ? theme.radius[size] : 0,
    '& p': {
      color: ({
        theme,
        inputTxtColor
      }) => theme.colors[inputTxtColor] || '#333',
      margin: 'auto 0',
      flex: 0
    }
  },
  Label: ({
    theme,
    font,
    txtColor
  }) => ({
    ...theme.typography[font],
    display: 'inline-block',
    marginBottom: theme.spacing['xs'],
    color: theme.colors[txtColor]
  }),
  Delimiter: {
    display: 'inline-flex',
    alignItems: 'center',
    color: ({
      theme,
      inputTxtColor
    }) => theme.colors[inputTxtColor]
  },
  Icon: {
    display: 'flex',
    flexShrink: '0',
    alignItems: 'center',
    justifyContent: 'center',
    width: '2.5rem',
    color: '#fff',
    borderTopLeftRadius: 'radius(field)',
    borderBottomLeftRadius: 'radius(field)',
    background: ({
      theme,
      color,
      invalid
    }) => invalid ? theme.colors.danger : theme.colors[color],
    borderRadius: ({
      theme,
      rounded,
      size
    }) => rounded ? [[theme.radius[size], 0, 0, theme.radius[size]]] : 0,
    margin: '-1px 0 -1px -1px'
  }
});

const Field$2 = /*#__PURE__*/forwardRef((props, ref) => {
  const {
    value,
    label,
    icon,
    invalid,
    focused,
    size,
    invalidMessage,
    children,
    id,
    onClick,
    name,
    rules
  } = props;
  const theme = useContext(ThemeContext);
  const classes = styles$8({
    theme,
    ...props
  });
  return /*#__PURE__*/React.createElement("div", {
    className: classes.Field,
    onClick: onClick
  }, label && /*#__PURE__*/React.createElement("div", {
    className: classes.Header
  }, /*#__PURE__*/React.createElement("label", {
    className: classes.Label,
    htmlFor: id
  }, label)), /*#__PURE__*/React.createElement("div", {
    className: classes.Body
  }, icon && /*#__PURE__*/React.createElement("div", {
    className: classes.Icon
  }, icon), children, /*#__PURE__*/React.createElement(HiddenInput, {
    ref: ref,
    type: "hidden",
    name: name,
    value: value,
    rules: {
      phone: true,
      ...rules
    }
  })), /*#__PURE__*/React.createElement(ToolTip, {
    color: "danger",
    in: invalid === true && focused,
    size: size
  }, invalidMessage));
});
Field$2.displayName = 'FieldPhoneControl';
class FieldPhone extends React.Component {
  static displayName = 'FieldPhone';
  constructor(props) {
    super(props);
    this.field = {};
    this.theField = this.theField.bind(this);
    this.validate = this.validate.bind(this);
    this.handleClick = this.handleClick.bind(this);
    this.handleInput1 = this.handleInput1.bind(this);
    this.handleInput2 = this.handleInput2.bind(this);
    this.handleInput3 = this.handleInput3.bind(this);
    this.handlePaste = this.handlePaste.bind(this);
    this.handleBlur = this.handleBlur.bind(this);
    this.handleFocus = this.handleFocus.bind(this);
    this.state = {
      id: uId(props.id),
      validated: false
    };
  }
  componentDidMount() {
    const {
      attachToForm
    } = this.props;
    if (typeof attachToForm === 'function') {
      attachToForm(this);
    }
  }
  componentWillUnmount() {
    const {
      detachFromForm
    } = this.props;
    if (typeof detachFromForm === 'function') {
      detachFromForm(this);
    }
  }
  theField() {
    return this.field;
  }
  val() {
    return this.value;
  }
  async validate() {
    const valid = await this.field.validate();
    this.setState({
      valid: valid.valid,
      invalid: !valid.valid,
      invalidMessage: !valid.valid ? valid.message : '',
      validated: true
    });
    return valid;
  }
  handleInput1(e) {
    const {
      val2,
      val3,
      validated
    } = this.state;
    const validate = this.validate;
    const {
      onInput
    } = this.props;
    const value = e.target.value;
    if (typeof onInput === 'function') {
      onInput(e);
    }
    this.setState({
      filled: value ? true : false,
      val1: value,
      value: value + val2 + val3
    }, () => {
      if (validated) {
        validate();
      }
    });
  }
  handleInput2(e) {
    const {
      val1,
      val3,
      validated
    } = this.state;
    this.validate;
    const {
      onInput
    } = this.props;
    const value = e.target.value;
    if (typeof onInput === 'function') {
      onInput(e);
    }
    this.setState({
      filled: value ? true : false,
      val2: value,
      value: val1 + value + val3
    }, () => {
      if (validated) {
        this.validate();
      }
    });
  }
  handleInput3(e) {
    const {
      val1,
      val2,
      validated
    } = this.state;
    const validate = this.validate;
    const {
      onInput
    } = this.props;
    const value = e.target.value;
    if (typeof onInput === 'function') {
      onInput(e);
    }
    this.setState({
      filled: value ? true : false,
      val3: value,
      value: val1 + val2 + value
    }, () => {
      if (validated) {
        validate();
      }
    });
  }
  handleBlur(e) {
    const {
      onBlur
    } = this.props;
    if (typeof onBlur === 'function') {
      onBlur(e);
    }
    clearTimeout(this.timer);
    this.timer = setTimeout(() => {
      this.setState({
        focused: false
      });
    }, 15);
  }
  handleFocus(e) {
    const {
      onFocus
    } = this.props;
    clearTimeout(this.timer);
    this.setState({
      focused: true
    });
    if (typeof onFocus === 'function') {
      onFocus(e);
    }
  }
  handleClick(e) {
    const element = e.currentTarget.querySelector('select') || e.currentTarget.querySelector('input') || e.currentTarget.querySelector('textarea');
    element.focus();
  }
  handlePaste(e) {
    e.clipboardData.items[0].getAsString(text => {
      const val = text.replace(/\D/g, '');
      this.setState({
        value: val,
        val1: val.slice(0, 3),
        val2: val.slice(3, 6),
        val3: val.slice(6, 10)
      });
    });
    e.preventDefault();
  }
  render() {
    const {
      filled,
      focused,
      validated,
      invalid,
      valid,
      invalidMessage,
      value,
      val1,
      val2,
      val3
    } = this.state;
    const {
      font,
      size,
      value: defaultValue,
      ...props
    } = this.props;
    return /*#__PURE__*/React.createElement(Field$2, _extends$d({
      ref: field => {
        this.field = field;
      },
      value: value,
      filled: filled,
      focused: focused,
      validated: validated,
      invalid: invalid,
      valid: valid,
      invalidMessage: invalidMessage,
      onClick: this.handleClick,
      defaultValue: defaultValue
    }, props), /*#__PURE__*/React.createElement(P, null, "("), /*#__PURE__*/React.createElement(Input, {
      inputSize: "3",
      placeholder: "555",
      type: "tel",
      autotab: true,
      maxlength: 3,
      width: "auto",
      font: font,
      size: size,
      onInput: this.handleInput1,
      onPaste: this.handlePaste,
      onBlur: this.handleBlur,
      onFocus: this.handleFocus,
      onClick: this.handleClick,
      value: val1,
      controlled: true,
      rules: {
        required: true,
        number: true,
        minlength: 3,
        maxlength: 3
      }
    }), /*#__PURE__*/React.createElement(P, null, ") "), /*#__PURE__*/React.createElement(Input, {
      inputSize: "3",
      placeholder: "555",
      type: "tel",
      autotab: true,
      maxlength: 3,
      width: "auto",
      font: font,
      size: size,
      onInput: this.handleInput2,
      onPaste: this.handlePaste,
      onBlur: this.handleBlur,
      onFocus: this.handleFocus,
      onClick: this.handleClick,
      value: val2,
      controlled: true,
      rules: {
        required: true,
        number: true,
        minlength: 3,
        maxlength: 3
      }
    }), /*#__PURE__*/React.createElement(P, null, "-"), /*#__PURE__*/React.createElement(Input, {
      inputSize: "4",
      placeholder: "5555",
      type: "tel",
      maxlength: 4,
      width: "auto",
      font: font,
      size: size,
      onInput: this.handleInput3,
      onPaste: this.handlePaste,
      onBlur: this.handleBlur,
      onFocus: this.handleFocus,
      onClick: this.handleClick,
      value: val3,
      controlled: true,
      rules: {
        required: true,
        number: true,
        minlength: 4,
        maxlength: 4
      }
    }));
  }
}
FieldPhone.propTypes = {
  attachToForm: PropTypes.func,
  detachFromForm: PropTypes.func,
  onInput: PropTypes.func,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  onValidate: PropTypes.func,
  name: PropTypes.string,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  id: PropTypes.string,
  value: PropTypes.string,
  color: PropTypes.string,
  borderColor: PropTypes.string,
  bgColor: PropTypes.string,
  icon: PropTypes.node,
  size: PropTypes.string,
  rounded: PropTypes.oneOfType([PropTypes.string, PropTypes.bool, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  align: PropTypes.string,
  tabIndex: PropTypes.number,
  rules: PropTypes.object,
  padding: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  margin: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))])
};
FieldPhone.defaultProps = {
  color: 'primary',
  bgColor: 'white',
  borderColor: 'neutral200',
  size: 'md',
  rounded: 'md',
  align: '',
  id: 'text-field',
  type: 'text'
};

var styles$7 = createUseStyles({
  Field: {
    display: 'block',
    position: 'relative',
    marginBottom: '1rem',
    color: ({
      theme,
      txtColor
    }) => theme.colors[txtColor]
  },
  Body: {
    display: 'flex',
    position: 'relative',
    padding: 0
  },
  Label: ({
    theme,
    font
  }) => ({
    ...theme.typography[font]
  }),
  Control: {
    flexGrow: 1
  }
});

const Field$1 = /*#__PURE__*/forwardRef((props, ref) => {
  const {
    id,
    name,
    label,
    color,
    value,
    min,
    max,
    size,
    step,
    formatValue,
    rules,
    tabIndex,
    onInput,
    onValidate,
    onChange,
    onFocus,
    onBlur
  } = props;
  const theme = useContext(ThemeContext);
  const classes = styles$7({
    theme,
    ...props
  });
  return /*#__PURE__*/React.createElement("div", {
    className: classes.Field
  }, label && /*#__PURE__*/React.createElement("div", {
    className: classes.Header
  }, /*#__PURE__*/React.createElement("label", {
    className: classes.Label,
    htmlFor: id
  }, label)), /*#__PURE__*/React.createElement("div", {
    className: classes.Body
  }, /*#__PURE__*/React.createElement(Range, {
    ref: ref,
    className: classes.Control,
    id: id,
    name: name,
    color: color,
    value: value,
    min: min,
    max: max,
    tabIndex: tabIndex,
    formatValue: formatValue,
    onInput: onInput,
    onValidate: onValidate,
    onChange: onChange,
    onFocus: onFocus,
    onBlur: onBlur,
    rules: rules,
    size: size,
    step: step
  })));
});
Field$1.displayName = 'FieldRangeControl';
class FieldRange extends React.Component {
  static displayName = 'FieldRange';
  constructor(props) {
    super(props);
    this.theField = this.theField.bind(this);
    this.val = this.val.bind(this);
    this.validate = this.validate.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.handleBlur = this.handleBlur.bind(this);
    this.handleFocus = this.handleFocus.bind(this);
    this.state = {
      id: uId(props.id),
      validated: false
    };
  }
  componentDidMount() {
    const {
      attachToForm
    } = this.props;
    if (typeof attachToForm === 'function') {
      attachToForm(this);
    }
  }
  componentWillUnmount() {
    const {
      detachFromForm
    } = this.props;
    if (typeof detachFromForm === 'function') {
      detachFromForm(this);
    }
  }
  theField() {
    return this.field.theField();
  }
  val() {
    return this.field.val();
  }
  async validate() {
    const valid = await this.field.validate();
    this.setState({
      valid: valid.valid,
      invalid: !valid.valid,
      validated: true
    });
    return valid;
  }
  handleChange(e) {
    const {
      onChange
    } = this.props;
    const {
      validated
    } = this.state;
    if (typeof onChange === 'function') {
      onChange(e);
    }
    this.setState({
      filled: this.field.val() === true
    });
    if (validated) {
      this.validate();
    }
  }
  handleBlur(e) {
    const {
      onBlur
    } = this.props;
    if (typeof onBlur === 'function') {
      onBlur(e);
    }
    this.setState({
      focused: false
    });
  }
  handleFocus(e) {
    const {
      onFocus
    } = this.props;
    this.setState({
      focused: true
    });
    if (typeof onFocus === 'function') {
      onFocus(e);
    }
  }
  render() {
    const {
      filled,
      focused,
      validated,
      invalid,
      valid,
      value
    } = this.state;
    return /*#__PURE__*/React.createElement(Field$1, _extends$d({
      ref: field => {
        this.field = field;
      },
      value: value,
      onInput: this.handleInput,
      onFocus: this.handleFocus,
      onBlur: this.handleBlur,
      filled: filled,
      focused: focused,
      validated: validated,
      invalid: invalid,
      valid: valid
    }, this.props));
  }
}
FieldRange.propTypes = {
  attachToForm: PropTypes.func,
  detachFromForm: PropTypes.func,
  onChange: PropTypes.func,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  onValidate: PropTypes.func,
  formatValue: PropTypes.func,
  name: PropTypes.string,
  label: PropTypes.string,
  id: PropTypes.string,
  value: PropTypes.number,
  color: PropTypes.string,
  txtColor: PropTypes.string,
  theme: PropTypes.string,
  size: PropTypes.string,
  align: PropTypes.string,
  rules: PropTypes.object,
  tabIndex: PropTypes.number,
  min: PropTypes.number,
  max: PropTypes.number,
  padding: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  margin: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))])
};
FieldRange.defaultProps = {
  color: 'primary',
  size: 'md',
  align: '',
  id: 'text-field',
  txtColor: 'neutral900'
};

const Field = /*#__PURE__*/forwardRef((props, ref) => {
  const theme = useContext(ThemeContext);
  const classes = styles$d({
    theme,
    ...props
  });
  const {
    id,
    invalid,
    focused,
    size,
    invalidMessage,
    label,
    icon
  } = props;
  return /*#__PURE__*/React.createElement("div", {
    className: classes.Field
  }, label && /*#__PURE__*/React.createElement("div", {
    className: classes.Header
  }, /*#__PURE__*/React.createElement("label", {
    className: classes.Label,
    htmlFor: id
  }, label)), /*#__PURE__*/React.createElement("div", {
    className: classes.Body
  }, icon && /*#__PURE__*/React.createElement("div", {
    className: classes.Icon
  }, icon), /*#__PURE__*/React.createElement(Select, _extends$d({
    ref: ref,
    className: classes.Control
  }, props))), /*#__PURE__*/React.createElement(ToolTip, {
    color: "danger",
    in: invalid === true && focused,
    size: size
  }, invalidMessage));
});
Field.displayName = 'FieldSelectControl';
class FieldSelect extends React.Component {
  static displayName = 'FieldSelect';
  constructor(props) {
    super(props);
    this.theField = this.theField.bind(this);
    this.val = this.val.bind(this);
    this.validate = this.validate.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.handleBlur = this.handleBlur.bind(this);
    this.handleFocus = this.handleFocus.bind(this);
    this.state = {
      id: uId(props.id),
      validated: false
    };
  }
  componentDidMount() {
    const {
      attachToForm
    } = this.props;
    if (typeof attachToForm === 'function') {
      attachToForm(this);
    }
  }
  componentWillUnmount() {
    const {
      detachFromForm
    } = this.props;
    if (typeof detachFromForm === 'function') {
      detachFromForm(this);
    }
  }
  theField() {
    return this.field.theField();
  }
  val() {
    return this.field.val();
  }
  async validate() {
    const valid = await this.field.validate();
    this.setState({
      valid: valid.valid,
      invalid: !valid.valid,
      validated: true,
      invalidMessage: !valid.valid ? valid.message : ''
    });
    return valid;
  }
  handleChange(e) {
    const {
      onChange
    } = this.props;
    const {
      validated
    } = this.state;
    if (typeof onChange === 'function') {
      onChange(e);
    }
    this.setState({
      filled: this.field.val() === true
    });
    if (validated) {
      this.validate();
    }
  }
  handleBlur(e) {
    const {
      onBlur
    } = this.props;
    if (typeof onBlur === 'function') {
      onBlur(e);
    }
    this.setState({
      focused: false
    });
  }
  handleFocus(e) {
    const {
      onFocus
    } = this.props;
    this.setState({
      focused: true
    });
    if (typeof onFocus === 'function') {
      onFocus(e);
    }
  }
  render() {
    const {
      id,
      filled,
      focused,
      validated,
      valid,
      invalid,
      invalidMessage
    } = this.state;
    return /*#__PURE__*/React.createElement(Field, _extends$d({
      id: id || this.props.name,
      ref: field => {
        this.field = field;
      },
      onChange: this.handleChange,
      onFocus: this.handleFocus,
      onBlur: this.handleBlur,
      filled: filled,
      focused: focused,
      validated: validated,
      invalid: invalid,
      valid: valid,
      invalidMessage: invalidMessage
    }, this.props));
  }
}
FieldSelect.propTypes = {
  attachToForm: PropTypes.func,
  detachFromForm: PropTypes.func,
  onChange: PropTypes.func,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  onValidate: PropTypes.func,
  name: PropTypes.string,
  options: PropTypes.oneOfType([PropTypes.objectOf(PropTypes.string), PropTypes.arrayOf(PropTypes.object)]).isRequired,
  placeholder: PropTypes.string,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  id: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  color: PropTypes.string,
  bgColor: PropTypes.string,
  checkedOutline: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  icon: PropTypes.node,
  size: PropTypes.string,
  rounded: PropTypes.oneOfType([PropTypes.string, PropTypes.bool, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  chunky: PropTypes.bool,
  align: PropTypes.string,
  tabIndex: PropTypes.number,
  rules: PropTypes.object,
  padding: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  margin: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))])
};
FieldSelect.defaultProps = {
  size: 'md',
  color: 'primary',
  bgColor: 'white',
  borderColor: 'neutral200',
  rounded: 'md',
  align: '',
  id: 'select-field'
};

const sizeMap$1 = {
  height: {
    xxs: '1rem',
    xs: '1.5rem',
    sm: '2rem',
    md: '3rem',
    lg: '5rem',
    xl: '6rem',
    xxl: '8rem'
  },
  border: {
    xxs: '0.1rem',
    xs: '0.15rem',
    sm: '0.2rem',
    md: '0.3rem',
    lg: '0.5rem',
    xl: '0.6rem',
    xxl: '0.8rem'
  }
};
var styles$6 = createUseStyles({
  '@keyframes Spin': {
    '0%': {
      transform: 'rotate(0deg)'
    },
    '100%': {
      transform: 'rotate(360deg)'
    }
  },
  Spinner: {
    display: 'inline-block',
    position: 'relative',
    width: ({
      theme,
      size
    }) => sizeMap$1.height[size],
    height: ({
      size
    }) => sizeMap$1.height[size],
    '& div': {
      boxSizing: 'border-box',
      display: 'block',
      position: 'absolute',
      width: '100%',
      height: '100%',
      border: ({
        size
      }) => `${sizeMap$1.border[size]} solid`,
      borderRadius: '50%',
      animation: '$Spin 1.2s cubic-bezier(0.5, 0, 0.5, 1) infinite',
      borderColor: ({
        theme,
        color
      }) => `${theme.colors[color]} transparent transparent transparent`
    },
    '& div:nth-child(1)': {
      animationDelay: '-0.45s'
    },
    '& div:nth-child(2)': {
      animationDelay: '-0.3s'
    },
    '& div:nth-child(3)': {
      animationDelay: '-0.15s'
    }
  }
});

const Spinner = ({
  color,
  size
}) => {
  const theme = useContext(ThemeContext);
  const classes = styles$6({
    theme,
    color,
    size
  });
  return /*#__PURE__*/React.createElement("div", {
    className: classes.Spinner
  }, /*#__PURE__*/React.createElement("div", null), /*#__PURE__*/React.createElement("div", null), /*#__PURE__*/React.createElement("div", null), /*#__PURE__*/React.createElement("div", null));
};
Spinner.propTypes = {
  color: PropTypes.string,
  size: PropTypes.string
};
Spinner.defaultProps = {
  color: 'white',
  size: 'md'
};

const sizeMap = {
  height: {
    xxs: '1rem',
    xs: '1.5rem',
    sm: '2rem',
    md: '3rem',
    lg: '5rem',
    xl: '6rem',
    xxl: '8rem'
  },
  border: {
    xxs: '0.1rem',
    xs: '0.15rem',
    sm: '0.2rem',
    md: '0.3rem',
    lg: '0.5rem',
    xl: '0.6rem',
    xxl: '0.8rem'
  }
};
var styles$5 = createUseStyles({
  ProgressBar: {
    width: '100%',
    display: ({
      display
    }) => display,
    position: 'relative',
    height: ({
      size
    }) => sizeMap.height[size],
    backgroundColor: ({
      theme,
      bgColor
    }) => theme.colors[bgColor] || bgColor,
    borderRadius: ({
      theme,
      rounded,
      size
    }) => theme.radius[rounded] || rounded,
    overflow: 'hidden',
    '& div': {
      display: 'block',
      position: 'absolute',
      left: 0,
      top: 0,
      width: ({
        progress
      }) => `${progress * 100}%`,
      backgroundColor: ({
        theme,
        progressColor
      }) => theme.colors[progressColor] || progressColor,
      height: '100%',
      transition: ({
        transition
      }) => transition ? 'all 0.3s ease' : false
    }
  }
});

const ProgressBar = ({
  color,
  size,
  rounded,
  progress,
  bgColor,
  progressColor,
  transition,
  display
}) => {
  const theme = useContext(ThemeContext);
  const classes = styles$5({
    theme,
    progress,
    bgColor,
    progressColor,
    rounded,
    size,
    transition,
    display
  });
  return /*#__PURE__*/React.createElement("div", {
    className: classes.ProgressBar
  }, /*#__PURE__*/React.createElement("div", null));
};
ProgressBar.propTypes = {
  bgColor: PropTypes.string,
  progressColor: PropTypes.string,
  size: PropTypes.string,
  progress: PropTypes.number,
  rounded: PropTypes.string,
  transition: PropTypes.bool,
  display: PropTypes.string
};
ProgressBar.defaultProps = {
  bgColor: 'white',
  progressColor: 'primary',
  size: 'md',
  progress: 0,
  rounded: 'xl',
  transition: true,
  display: 'inline-block'
};

const styles$4 = createUseStyles({
  Pagination: {
    display: 'flex',
    flexWrap: 'wrap',
    width: '100%',
    justifyContent: ({
      align
    }) => align,
    '& *': {
      margin: 8
    },
    '& *:first-child': {
      marginLeft: 0
    },
    '& *:last-child': {
      marginRight: 0
    }
  }
});
const Pagination$1 = ({
  page,
  total,
  change,
  activeColor,
  inactiveColor,
  activeTxtColor,
  inactiveTxtColor,
  align,
  rounded
}) => {
  const classes = styles$4({
    align
  });
  const next = () => {
    if (typeof change === 'function') {
      change(Math.min(page + 1, total - 1));
    }
  };
  const prev = () => {
    if (typeof change === 'function') {
      change(Math.max(page - 1, 0));
    }
  };
  const goToPage = p => {
    if (typeof change === 'function') {
      change(p);
    }
  };
  const generateButtons = () => {
    const count = Math.min(10, total);
    let offset = 0;
    if (page >= 9) {
      offset = Math.floor((page + 1) / 10) * 10 - 1;
    }
    const buttons = [];
    for (let i = 0; i < count; i += 1) {
      buttons.push( /*#__PURE__*/React.createElement(Btn, {
        key: `pagei-${i + offset}`,
        color: i + offset === page ? activeColor : inactiveColor,
        txtColor: i + offset === page ? activeTxtColor : inactiveTxtColor,
        size: "xs",
        font: "small",
        onClick: () => goToPage(i + offset),
        rounded: rounded
      }, i + offset + 1));
    }
    return buttons;
  };
  return /*#__PURE__*/React.createElement("div", {
    className: classes.Pagination
  }, /*#__PURE__*/React.createElement(Btn, {
    size: "xs",
    color: "none",
    txtColor: "neutral600",
    onClick: prev,
    rounded: rounded,
    font: "small"
  }, "Back"), generateButtons(), /*#__PURE__*/React.createElement(Btn, {
    color: inactiveColor,
    txtColor: inactiveTxtColor,
    size: "xs",
    font: "small",
    onClick: next,
    rounded: rounded
  }, "Next"));
};
Pagination$1.propTypes = {
  activeColor: PropTypes.string,
  inactiveColor: PropTypes.string,
  activeTxtColor: PropTypes.string,
  inactiveTxtColor: PropTypes.string,
  align: PropTypes.string,
  page: PropTypes.number,
  total: PropTypes.number,
  rounded: PropTypes.bool,
  change: PropTypes.func
};
Pagination$1.defaultProps = {
  activeColor: 'primary',
  inactiveColor: 'none',
  activeTxtColor: 'white',
  inactiveTxtColor: 'neutral700'
};

const btnHeights = {
  xs: '0.5rem',
  sm: '0.75rem',
  md: '1rem',
  lg: '1.25rem',
  xl: '1.5rem',
  xxl: '2rem'
};
const styles$3 = createUseStyles({
  Steps: {
    display: 'flex',
    width: '100%',
    justifyContent: 'center',
    alignItems: 'center'
  },
  Step: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
    paddingBottom: '1rem',
    color: ({
      theme,
      inactiveColor
    }) => theme.colors[inactiveColor] || inactiveColor
  },
  StepActive: {
    color: ({
      theme,
      activeColor
    }) => theme.colors[activeColor] || activeColor
  },
  Label: {
    position: 'absolute',
    left: '50%',
    bottom: 0,
    transform: 'translateX(-50%)'
  },
  Line: {
    width: '100%',
    height: '100%',
    position: 'relative',
    marginBottom: 'auto',
    '& div': {
      position: 'absolute',
      transition: 'all ease 0.3s',
      left: 0,
      top: ({
        size
      }) => btnHeights[size],
      height: 2,
      background: ({
        theme,
        activeColor
      }) => theme.colors[activeColor] || activeColor
    }
  }
});
const Steps = ({
  onChange,
  activeColor,
  inactiveColor,
  font,
  step: propStep,
  steps,
  size,
  completedIcon,
  navigation
}) => {
  const theme = useContext(ThemeContext);
  const classes = styles$3({
    theme,
    size,
    activeColor,
    inactiveColor
  });
  const [step, setStep] = useState(propStep);
  const [timer, setTimer] = useState();
  const [completed, setCompleted] = useState(propStep);
  const change = s => {
    if (s > completed) {
      setCompleted(s);
    }
    setStep(s);
    if (typeof onChange === 'function') {
      clearTimeout(timer);
      setTimer(setTimeout(() => onChange(s), 400));
    }
  };
  const goToStep = s => {
    let direction = s > step ? 1 : -1;
    let currentStep = step;
    const increment = () => {
      currentStep = currentStep + direction;
      change(currentStep);
      if (currentStep !== s) {
        setTimeout(increment, 400);
      }
    };
    if (s !== step) {
      increment();
    }
  };
  useEffect(() => {
    goToStep(propStep);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propStep]);
  const clickToStep = s => {
    if (navigation > 0 || navigation === -1 && s <= completed) {
      goToStep(s);
    }
  };
  return /*#__PURE__*/React.createElement("div", {
    className: classes.Steps
  }, steps.map((item, i) => /*#__PURE__*/React.createElement(React.Fragment, {
    key: `step-${i}`
  }, /*#__PURE__*/React.createElement("div", {
    className: `${classes.Step} ${i === step ? classes.StepActive : ''}`
  }, /*#__PURE__*/React.createElement(BtnDot, {
    color: i <= step ? activeColor : inactiveColor,
    outline: i >= step,
    size: size,
    onClick: () => clickToStep(i),
    margin: [0, 0, 'xs', 0],
    icon: i < step ? completedIcon || /*#__PURE__*/React.createElement(Type, {
      font: font
    }, i + 1) : /*#__PURE__*/React.createElement(Type, {
      font: font
    }, i + 1)
  }), /*#__PURE__*/React.createElement("div", {
    className: classes.Label
  }, /*#__PURE__*/React.createElement(Type, {
    tag: "p",
    font: font,
    align: "center",
    weight: "bold",
    color: i <= step ? activeColor : inactiveColor
  }, item))), i < steps.length - 1 && /*#__PURE__*/React.createElement("div", {
    className: classes.Line
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      width: i < step ? '100%' : 0
    }
  })))));
};
Steps.propTypes = {
  activeColor: PropTypes.string,
  inactiveColor: PropTypes.string,
  size: PropTypes.string,
  font: PropTypes.string,
  step: PropTypes.number,
  completedIcon: PropTypes.node,
  navigation: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),
  steps: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.node])),
  onChange: PropTypes.func
};
Steps.defaultProps = {
  navigation: -1,
  activeColor: 'primary',
  inactiveColor: 'neutral300',
  size: 'lg',
  font: 'small'
};

const alignment = {
  center: 'center',
  right: 'flex-end',
  left: 'flex-start'
};
var styles$2 = createUseStyles({
  Tabs: {
    position: 'relative',
    width: '100%'
  },
  Navigation: {
    display: 'flex',
    justifyContent: ({
      align
    }) => alignment[align]
  },
  Body: {
    display: 'flex',
    position: 'relative'
  }
});

const PanelTab = props => {
  const {
    children,
    className,
    align,
    tabColor,
    tabActiveColor,
    tabSize,
    tabs
  } = props;
  const [tab, setTab] = useState(0);
  const changeTab = tab => {
    setTab(tab);
  };
  const classes = styles$2({
    align
  });
  const navigation = tabs.map((content, i) => /*#__PURE__*/React.createElement(Btn, {
    key: content.id,
    color: i === tab ? tabActiveColor : tabColor,
    size: tabSize,
    onClick: () => {
      changeTab(i);
    }
  }, content));
  const sections = children.map((el, i) => /*#__PURE__*/React.createElement("div", {
    key: el.props.key,
    style: {
      display: i === tab ? 'block' : 'none'
    }
  }, el));
  return /*#__PURE__*/React.createElement("div", {
    className: classes.Tabs
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.Navigation
  }, navigation), /*#__PURE__*/React.createElement("div", {
    className: classes.Body
  }, sections));
};
PanelTab.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node,
  tabs: PropTypes.arrayOf(PropTypes.string),
  align: PropTypes.string,
  tabColor: PropTypes.string,
  tabActiveColor: PropTypes.string,
  tabSize: PropTypes.string
};
PanelTab.defaultProps = {
  align: 'stretch',
  tabColor: 'neutral-light',
  tabActiveColor: 'primary',
  tabSize: 'md'
};

const styles$1 = createUseStyles({
  Pagination: {
    display: 'flex',
    flexWrap: 'wrap',
    width: '100%',
    justifyContent: ({
      align
    }) => align,
    '& *': {
      margin: 8
    },
    '& *:first-child': {
      marginLeft: 0
    },
    '& *:last-child': {
      marginRight: 0
    }
  }
});
const Pagination = ({
  page,
  total,
  change,
  activeColor,
  inactiveColor,
  activeTxtColor,
  inactiveTxtColor,
  align,
  rounded
}) => {
  const classes = styles$1({
    align
  });
  const next = () => {
    if (typeof change === 'function') {
      change(Math.min(page + 1, total - 1));
    }
  };
  const prev = () => {
    if (typeof change === 'function') {
      change(Math.max(page - 1, 0));
    }
  };
  const goToPage = p => {
    if (typeof change === 'function') {
      change(p);
    }
  };
  const generateButtons = () => {
    const count = Math.min(10, total);
    let offset = 0;
    if (page >= 9) {
      offset = Math.floor((page + 1) / 10) * 10 - 1;
    }
    const buttons = [];
    for (let i = 0; i < count; i += 1) {
      buttons.push( /*#__PURE__*/React.createElement(Btn, {
        key: `pagei-${i + offset}`,
        color: i + offset === page ? activeColor : inactiveColor,
        txtColor: i + offset === page ? activeTxtColor : inactiveTxtColor,
        size: "xs",
        font: "small",
        onClick: () => goToPage(i + offset),
        rounded: rounded
      }, i + offset + 1));
    }
    return buttons;
  };
  return /*#__PURE__*/React.createElement("div", {
    className: classes.Pagination
  }, /*#__PURE__*/React.createElement(Btn, {
    size: "xs",
    color: "none",
    txtColor: "neutral600",
    onClick: prev,
    rounded: rounded,
    font: "small"
  }, "Back"), generateButtons(), /*#__PURE__*/React.createElement(Btn, {
    color: inactiveColor,
    txtColor: inactiveTxtColor,
    size: "xs",
    font: "small",
    onClick: next,
    rounded: rounded
  }, "Next"));
};
Pagination.propTypes = {
  activeColor: PropTypes.string,
  inactiveColor: PropTypes.string,
  activeTxtColor: PropTypes.string,
  inactiveTxtColor: PropTypes.string,
  align: PropTypes.string,
  page: PropTypes.number,
  total: PropTypes.number,
  rounded: PropTypes.bool,
  change: PropTypes.func
};
Pagination.defaultProps = {
  activeColor: 'primary',
  inactiveColor: 'none',
  activeTxtColor: 'white',
  inactiveTxtColor: 'neutral700'
};

var styles = createUseStyles({
  Table: {
    width: '100%',
    maxWidth: ({
      width
    }) => width
  },
  Tbody: {
    maxHeight: '400px',
    overflowY: 'auto'
  },
  Th: {
    position: 'sticky',
    top: 0,
    fontWeight: 'bold',
    textAlign: 'left',
    background: 'white',
    padding: ({
      theme
    }) => theme.spacing.xs,
    cursor: 'pointer',
    userSelect: 'none'
  },
  Td: {
    padding: ({
      theme
    }) => theme.spacing.xs,
    '& > div': {
      display: 'flex',
      justifyContent: 'space between'
    }
  },
  Tr: {
    '&:nth-child(odd)': {
      '& $Td': {
        backgroundColor: ({
          theme,
          bgColor
        }) => typeof bgColor === 'string' ? theme.colors[bgColor] : theme.colors[bgColor[0]]
      }
    },
    '&:nth-child(even)': {
      '& $Td': {
        backgroundColor: ({
          theme,
          bgColor
        }) => typeof bgColor === 'string' ? theme.colors[bgColor] : theme.colors[bgColor[1]]
      }
    }
  },
  Thead: {},
  Arrow: {
    float: 'right'
  },
  Footer: {
    display: 'flex',
    justifyContent: 'center'
  }
});

const chunkArray = (array, size) => {
  let result = [];
  let arrayCopy = [...array];
  while (arrayCopy.length > 0) {
    result.push(arrayCopy.splice(0, size));
  }
  return result;
};
const greaterThan = (a, b) => {
  return typeof a === 'string' && typeof b === 'string' ? a.localeCompare(b, undefined, {
    sensitivity: 'accent'
  }) > 0 : a > b;
};
const Table = ({
  rows,
  columns,
  font,
  limit,
  onPageChange,
  onSort,
  ...props
}) => {
  const theme = useContext(ThemeContext);
  const [sortBy, setSortBy] = useState([]);
  const [rowData, setRowData] = useState(rows);
  const [page, setPage] = useState(0);
  const isSimple = typeof rowData[0][0] === 'string' || /*#__PURE__*/React.isValidElement(rowData[0][0]);
  const classes = styles({
    theme,
    ...props
  });
  const arrow = dir => dir > 0 ? /*#__PURE__*/React.createElement(ChevronUp, {
    key: "arrow-up",
    size: "16"
  }) : /*#__PURE__*/React.createElement(ChevronUp, {
    key: "arrow-up",
    size: "16"
  });
  const changePage = page => {
    setPage(page);
    if (typeof onPageChange === 'function') {
      onPageChange(page);
    }
  };
  const sort = (column, key) => {
    const dir = sortBy[0] !== key || sortBy[0] === key && sortBy[1] < 0 ? 1 : -1;
    const newData = [...rowData].sort((a, b) => dir > 0 ? greaterThan(a[column].value, b[column].value) : greaterThan(b[column].value, a[column].value));
    setSortBy([key, dir]);
    setRowData(newData);
    changePage(0);
    if (typeof onSort === 'function') {
      onSort([key, dir]);
    }
  };
  const renderColumns = cols => cols.map((col, index) => /*#__PURE__*/React.createElement("td", {
    className: classes.Td,
    key: `column-${index}`
  }, isSimple ? col : col.label));
  const renderRows = () => {
    if (Array.isArray(rowData)) {
      const rows = chunkArray(rowData, limit);
      return rows[page].map((row, index) => /*#__PURE__*/React.createElement("tr", {
        className: classes.Tr,
        key: `row-${index}`
      }, renderColumns(row)));
    }
    return;
  };
  const renderHeading = () => {
    if (Array.isArray(columns)) {
      return columns.map((col, index) => /*#__PURE__*/React.createElement("th", {
        className: classes.Th,
        onClick: () => sort(index, col.key),
        key: col.key
      }, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Type, {
        weight: "bold"
      }, col.title), /*#__PURE__*/React.createElement("span", {
        className: classes.Arrow
      }, sortBy[0] === col.key && arrow(sortBy[1])))));
    }
    return;
  };
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("table", {
    className: classes.Table
  }, /*#__PURE__*/React.createElement("thead", {
    className: classes.Tr
  }, /*#__PURE__*/React.createElement("tr", null, renderHeading())), /*#__PURE__*/React.createElement("tbody", {
    className: classes.Tbody
  }, renderRows())), rowData.length > limit && /*#__PURE__*/React.createElement("div", {
    className: classes.Footer
  }, /*#__PURE__*/React.createElement(Pagination, {
    page: page,
    total: Math.ceil(rowData.length / limit),
    change: changePage,
    align: "center"
  })));
};
Table.propTypes = {
  bgColor: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
  txtColor: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
  font: PropTypes.string,
  columns: PropTypes.arrayOf(PropTypes.object),
  rows: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.object, PropTypes.string]))),
  margin: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  className: PropTypes.string,
  limit: PropTypes.number,
  onSort: PropTypes.func,
  onPageChange: PropTypes.func
};
Table.defaultProps = {
  limit: 10,
  bgColor: 'none'
};

var colorString$1 = {exports: {}};

var colorName$1 = {
  "aliceblue": [240, 248, 255],
  "antiquewhite": [250, 235, 215],
  "aqua": [0, 255, 255],
  "aquamarine": [127, 255, 212],
  "azure": [240, 255, 255],
  "beige": [245, 245, 220],
  "bisque": [255, 228, 196],
  "black": [0, 0, 0],
  "blanchedalmond": [255, 235, 205],
  "blue": [0, 0, 255],
  "blueviolet": [138, 43, 226],
  "brown": [165, 42, 42],
  "burlywood": [222, 184, 135],
  "cadetblue": [95, 158, 160],
  "chartreuse": [127, 255, 0],
  "chocolate": [210, 105, 30],
  "coral": [255, 127, 80],
  "cornflowerblue": [100, 149, 237],
  "cornsilk": [255, 248, 220],
  "crimson": [220, 20, 60],
  "cyan": [0, 255, 255],
  "darkblue": [0, 0, 139],
  "darkcyan": [0, 139, 139],
  "darkgoldenrod": [184, 134, 11],
  "darkgray": [169, 169, 169],
  "darkgreen": [0, 100, 0],
  "darkgrey": [169, 169, 169],
  "darkkhaki": [189, 183, 107],
  "darkmagenta": [139, 0, 139],
  "darkolivegreen": [85, 107, 47],
  "darkorange": [255, 140, 0],
  "darkorchid": [153, 50, 204],
  "darkred": [139, 0, 0],
  "darksalmon": [233, 150, 122],
  "darkseagreen": [143, 188, 143],
  "darkslateblue": [72, 61, 139],
  "darkslategray": [47, 79, 79],
  "darkslategrey": [47, 79, 79],
  "darkturquoise": [0, 206, 209],
  "darkviolet": [148, 0, 211],
  "deeppink": [255, 20, 147],
  "deepskyblue": [0, 191, 255],
  "dimgray": [105, 105, 105],
  "dimgrey": [105, 105, 105],
  "dodgerblue": [30, 144, 255],
  "firebrick": [178, 34, 34],
  "floralwhite": [255, 250, 240],
  "forestgreen": [34, 139, 34],
  "fuchsia": [255, 0, 255],
  "gainsboro": [220, 220, 220],
  "ghostwhite": [248, 248, 255],
  "gold": [255, 215, 0],
  "goldenrod": [218, 165, 32],
  "gray": [128, 128, 128],
  "green": [0, 128, 0],
  "greenyellow": [173, 255, 47],
  "grey": [128, 128, 128],
  "honeydew": [240, 255, 240],
  "hotpink": [255, 105, 180],
  "indianred": [205, 92, 92],
  "indigo": [75, 0, 130],
  "ivory": [255, 255, 240],
  "khaki": [240, 230, 140],
  "lavender": [230, 230, 250],
  "lavenderblush": [255, 240, 245],
  "lawngreen": [124, 252, 0],
  "lemonchiffon": [255, 250, 205],
  "lightblue": [173, 216, 230],
  "lightcoral": [240, 128, 128],
  "lightcyan": [224, 255, 255],
  "lightgoldenrodyellow": [250, 250, 210],
  "lightgray": [211, 211, 211],
  "lightgreen": [144, 238, 144],
  "lightgrey": [211, 211, 211],
  "lightpink": [255, 182, 193],
  "lightsalmon": [255, 160, 122],
  "lightseagreen": [32, 178, 170],
  "lightskyblue": [135, 206, 250],
  "lightslategray": [119, 136, 153],
  "lightslategrey": [119, 136, 153],
  "lightsteelblue": [176, 196, 222],
  "lightyellow": [255, 255, 224],
  "lime": [0, 255, 0],
  "limegreen": [50, 205, 50],
  "linen": [250, 240, 230],
  "magenta": [255, 0, 255],
  "maroon": [128, 0, 0],
  "mediumaquamarine": [102, 205, 170],
  "mediumblue": [0, 0, 205],
  "mediumorchid": [186, 85, 211],
  "mediumpurple": [147, 112, 219],
  "mediumseagreen": [60, 179, 113],
  "mediumslateblue": [123, 104, 238],
  "mediumspringgreen": [0, 250, 154],
  "mediumturquoise": [72, 209, 204],
  "mediumvioletred": [199, 21, 133],
  "midnightblue": [25, 25, 112],
  "mintcream": [245, 255, 250],
  "mistyrose": [255, 228, 225],
  "moccasin": [255, 228, 181],
  "navajowhite": [255, 222, 173],
  "navy": [0, 0, 128],
  "oldlace": [253, 245, 230],
  "olive": [128, 128, 0],
  "olivedrab": [107, 142, 35],
  "orange": [255, 165, 0],
  "orangered": [255, 69, 0],
  "orchid": [218, 112, 214],
  "palegoldenrod": [238, 232, 170],
  "palegreen": [152, 251, 152],
  "paleturquoise": [175, 238, 238],
  "palevioletred": [219, 112, 147],
  "papayawhip": [255, 239, 213],
  "peachpuff": [255, 218, 185],
  "peru": [205, 133, 63],
  "pink": [255, 192, 203],
  "plum": [221, 160, 221],
  "powderblue": [176, 224, 230],
  "purple": [128, 0, 128],
  "rebeccapurple": [102, 51, 153],
  "red": [255, 0, 0],
  "rosybrown": [188, 143, 143],
  "royalblue": [65, 105, 225],
  "saddlebrown": [139, 69, 19],
  "salmon": [250, 128, 114],
  "sandybrown": [244, 164, 96],
  "seagreen": [46, 139, 87],
  "seashell": [255, 245, 238],
  "sienna": [160, 82, 45],
  "silver": [192, 192, 192],
  "skyblue": [135, 206, 235],
  "slateblue": [106, 90, 205],
  "slategray": [112, 128, 144],
  "slategrey": [112, 128, 144],
  "snow": [255, 250, 250],
  "springgreen": [0, 255, 127],
  "steelblue": [70, 130, 180],
  "tan": [210, 180, 140],
  "teal": [0, 128, 128],
  "thistle": [216, 191, 216],
  "tomato": [255, 99, 71],
  "turquoise": [64, 224, 208],
  "violet": [238, 130, 238],
  "wheat": [245, 222, 179],
  "white": [255, 255, 255],
  "whitesmoke": [245, 245, 245],
  "yellow": [255, 255, 0],
  "yellowgreen": [154, 205, 50]
};

var simpleSwizzle = {exports: {}};

var isArrayish$1 = function isArrayish(obj) {
  if (!obj || typeof obj === 'string') {
    return false;
  }
  return obj instanceof Array || Array.isArray(obj) || obj.length >= 0 && (obj.splice instanceof Function || Object.getOwnPropertyDescriptor(obj, obj.length - 1) && obj.constructor.name !== 'String');
};

var isArrayish = isArrayish$1;
var concat = Array.prototype.concat;
var slice = Array.prototype.slice;
var swizzle$1 = simpleSwizzle.exports = function swizzle(args) {
  var results = [];
  for (var i = 0, len = args.length; i < len; i++) {
    var arg = args[i];
    if (isArrayish(arg)) {
      // http://jsperf.com/javascript-array-concat-vs-push/98
      results = concat.call(results, slice.call(arg));
    } else {
      results.push(arg);
    }
  }
  return results;
};
swizzle$1.wrap = function (fn) {
  return function () {
    return fn(swizzle$1(arguments));
  };
};

/* MIT license */

var colorNames = colorName$1;
var swizzle = simpleSwizzle.exports;
var hasOwnProperty = Object.hasOwnProperty;
var reverseNames = Object.create(null);

// create a list of reverse color names
for (var name in colorNames) {
  if (hasOwnProperty.call(colorNames, name)) {
    reverseNames[colorNames[name]] = name;
  }
}
var cs = colorString$1.exports = {
  to: {},
  get: {}
};
cs.get = function (string) {
  var prefix = string.substring(0, 3).toLowerCase();
  var val;
  var model;
  switch (prefix) {
    case 'hsl':
      val = cs.get.hsl(string);
      model = 'hsl';
      break;
    case 'hwb':
      val = cs.get.hwb(string);
      model = 'hwb';
      break;
    default:
      val = cs.get.rgb(string);
      model = 'rgb';
      break;
  }
  if (!val) {
    return null;
  }
  return {
    model: model,
    value: val
  };
};
cs.get.rgb = function (string) {
  if (!string) {
    return null;
  }
  var abbr = /^#([a-f0-9]{3,4})$/i;
  var hex = /^#([a-f0-9]{6})([a-f0-9]{2})?$/i;
  var rgba = /^rgba?\(\s*([+-]?\d+)(?=[\s,])\s*(?:,\s*)?([+-]?\d+)(?=[\s,])\s*(?:,\s*)?([+-]?\d+)\s*(?:[,|\/]\s*([+-]?[\d\.]+)(%?)\s*)?\)$/;
  var per = /^rgba?\(\s*([+-]?[\d\.]+)\%\s*,?\s*([+-]?[\d\.]+)\%\s*,?\s*([+-]?[\d\.]+)\%\s*(?:[,|\/]\s*([+-]?[\d\.]+)(%?)\s*)?\)$/;
  var keyword = /^(\w+)$/;
  var rgb = [0, 0, 0, 1];
  var match;
  var i;
  var hexAlpha;
  if (match = string.match(hex)) {
    hexAlpha = match[2];
    match = match[1];
    for (i = 0; i < 3; i++) {
      // https://jsperf.com/slice-vs-substr-vs-substring-methods-long-string/19
      var i2 = i * 2;
      rgb[i] = parseInt(match.slice(i2, i2 + 2), 16);
    }
    if (hexAlpha) {
      rgb[3] = parseInt(hexAlpha, 16) / 255;
    }
  } else if (match = string.match(abbr)) {
    match = match[1];
    hexAlpha = match[3];
    for (i = 0; i < 3; i++) {
      rgb[i] = parseInt(match[i] + match[i], 16);
    }
    if (hexAlpha) {
      rgb[3] = parseInt(hexAlpha + hexAlpha, 16) / 255;
    }
  } else if (match = string.match(rgba)) {
    for (i = 0; i < 3; i++) {
      rgb[i] = parseInt(match[i + 1], 0);
    }
    if (match[4]) {
      if (match[5]) {
        rgb[3] = parseFloat(match[4]) * 0.01;
      } else {
        rgb[3] = parseFloat(match[4]);
      }
    }
  } else if (match = string.match(per)) {
    for (i = 0; i < 3; i++) {
      rgb[i] = Math.round(parseFloat(match[i + 1]) * 2.55);
    }
    if (match[4]) {
      if (match[5]) {
        rgb[3] = parseFloat(match[4]) * 0.01;
      } else {
        rgb[3] = parseFloat(match[4]);
      }
    }
  } else if (match = string.match(keyword)) {
    if (match[1] === 'transparent') {
      return [0, 0, 0, 0];
    }
    if (!hasOwnProperty.call(colorNames, match[1])) {
      return null;
    }
    rgb = colorNames[match[1]];
    rgb[3] = 1;
    return rgb;
  } else {
    return null;
  }
  for (i = 0; i < 3; i++) {
    rgb[i] = clamp(rgb[i], 0, 255);
  }
  rgb[3] = clamp(rgb[3], 0, 1);
  return rgb;
};
cs.get.hsl = function (string) {
  if (!string) {
    return null;
  }
  var hsl = /^hsla?\(\s*([+-]?(?:\d{0,3}\.)?\d+)(?:deg)?\s*,?\s*([+-]?[\d\.]+)%\s*,?\s*([+-]?[\d\.]+)%\s*(?:[,|\/]\s*([+-]?(?=\.\d|\d)(?:0|[1-9]\d*)?(?:\.\d*)?(?:[eE][+-]?\d+)?)\s*)?\)$/;
  var match = string.match(hsl);
  if (match) {
    var alpha = parseFloat(match[4]);
    var h = (parseFloat(match[1]) % 360 + 360) % 360;
    var s = clamp(parseFloat(match[2]), 0, 100);
    var l = clamp(parseFloat(match[3]), 0, 100);
    var a = clamp(isNaN(alpha) ? 1 : alpha, 0, 1);
    return [h, s, l, a];
  }
  return null;
};
cs.get.hwb = function (string) {
  if (!string) {
    return null;
  }
  var hwb = /^hwb\(\s*([+-]?\d{0,3}(?:\.\d+)?)(?:deg)?\s*,\s*([+-]?[\d\.]+)%\s*,\s*([+-]?[\d\.]+)%\s*(?:,\s*([+-]?(?=\.\d|\d)(?:0|[1-9]\d*)?(?:\.\d*)?(?:[eE][+-]?\d+)?)\s*)?\)$/;
  var match = string.match(hwb);
  if (match) {
    var alpha = parseFloat(match[4]);
    var h = (parseFloat(match[1]) % 360 + 360) % 360;
    var w = clamp(parseFloat(match[2]), 0, 100);
    var b = clamp(parseFloat(match[3]), 0, 100);
    var a = clamp(isNaN(alpha) ? 1 : alpha, 0, 1);
    return [h, w, b, a];
  }
  return null;
};
cs.to.hex = function () {
  var rgba = swizzle(arguments);
  return '#' + hexDouble(rgba[0]) + hexDouble(rgba[1]) + hexDouble(rgba[2]) + (rgba[3] < 1 ? hexDouble(Math.round(rgba[3] * 255)) : '');
};
cs.to.rgb = function () {
  var rgba = swizzle(arguments);
  return rgba.length < 4 || rgba[3] === 1 ? 'rgb(' + Math.round(rgba[0]) + ', ' + Math.round(rgba[1]) + ', ' + Math.round(rgba[2]) + ')' : 'rgba(' + Math.round(rgba[0]) + ', ' + Math.round(rgba[1]) + ', ' + Math.round(rgba[2]) + ', ' + rgba[3] + ')';
};
cs.to.rgb.percent = function () {
  var rgba = swizzle(arguments);
  var r = Math.round(rgba[0] / 255 * 100);
  var g = Math.round(rgba[1] / 255 * 100);
  var b = Math.round(rgba[2] / 255 * 100);
  return rgba.length < 4 || rgba[3] === 1 ? 'rgb(' + r + '%, ' + g + '%, ' + b + '%)' : 'rgba(' + r + '%, ' + g + '%, ' + b + '%, ' + rgba[3] + ')';
};
cs.to.hsl = function () {
  var hsla = swizzle(arguments);
  return hsla.length < 4 || hsla[3] === 1 ? 'hsl(' + hsla[0] + ', ' + hsla[1] + '%, ' + hsla[2] + '%)' : 'hsla(' + hsla[0] + ', ' + hsla[1] + '%, ' + hsla[2] + '%, ' + hsla[3] + ')';
};

// hwb is a bit different than rgb(a) & hsl(a) since there is no alpha specific syntax
// (hwb have alpha optional & 1 is default value)
cs.to.hwb = function () {
  var hwba = swizzle(arguments);
  var a = '';
  if (hwba.length >= 4 && hwba[3] !== 1) {
    a = ', ' + hwba[3];
  }
  return 'hwb(' + hwba[0] + ', ' + hwba[1] + '%, ' + hwba[2] + '%' + a + ')';
};
cs.to.keyword = function (rgb) {
  return reverseNames[rgb.slice(0, 3)];
};

// helpers
function clamp(num, min, max) {
  return Math.min(Math.max(min, num), max);
}
function hexDouble(num) {
  var str = Math.round(num).toString(16).toUpperCase();
  return str.length < 2 ? '0' + str : str;
}

var colorName = {
  "aliceblue": [240, 248, 255],
  "antiquewhite": [250, 235, 215],
  "aqua": [0, 255, 255],
  "aquamarine": [127, 255, 212],
  "azure": [240, 255, 255],
  "beige": [245, 245, 220],
  "bisque": [255, 228, 196],
  "black": [0, 0, 0],
  "blanchedalmond": [255, 235, 205],
  "blue": [0, 0, 255],
  "blueviolet": [138, 43, 226],
  "brown": [165, 42, 42],
  "burlywood": [222, 184, 135],
  "cadetblue": [95, 158, 160],
  "chartreuse": [127, 255, 0],
  "chocolate": [210, 105, 30],
  "coral": [255, 127, 80],
  "cornflowerblue": [100, 149, 237],
  "cornsilk": [255, 248, 220],
  "crimson": [220, 20, 60],
  "cyan": [0, 255, 255],
  "darkblue": [0, 0, 139],
  "darkcyan": [0, 139, 139],
  "darkgoldenrod": [184, 134, 11],
  "darkgray": [169, 169, 169],
  "darkgreen": [0, 100, 0],
  "darkgrey": [169, 169, 169],
  "darkkhaki": [189, 183, 107],
  "darkmagenta": [139, 0, 139],
  "darkolivegreen": [85, 107, 47],
  "darkorange": [255, 140, 0],
  "darkorchid": [153, 50, 204],
  "darkred": [139, 0, 0],
  "darksalmon": [233, 150, 122],
  "darkseagreen": [143, 188, 143],
  "darkslateblue": [72, 61, 139],
  "darkslategray": [47, 79, 79],
  "darkslategrey": [47, 79, 79],
  "darkturquoise": [0, 206, 209],
  "darkviolet": [148, 0, 211],
  "deeppink": [255, 20, 147],
  "deepskyblue": [0, 191, 255],
  "dimgray": [105, 105, 105],
  "dimgrey": [105, 105, 105],
  "dodgerblue": [30, 144, 255],
  "firebrick": [178, 34, 34],
  "floralwhite": [255, 250, 240],
  "forestgreen": [34, 139, 34],
  "fuchsia": [255, 0, 255],
  "gainsboro": [220, 220, 220],
  "ghostwhite": [248, 248, 255],
  "gold": [255, 215, 0],
  "goldenrod": [218, 165, 32],
  "gray": [128, 128, 128],
  "green": [0, 128, 0],
  "greenyellow": [173, 255, 47],
  "grey": [128, 128, 128],
  "honeydew": [240, 255, 240],
  "hotpink": [255, 105, 180],
  "indianred": [205, 92, 92],
  "indigo": [75, 0, 130],
  "ivory": [255, 255, 240],
  "khaki": [240, 230, 140],
  "lavender": [230, 230, 250],
  "lavenderblush": [255, 240, 245],
  "lawngreen": [124, 252, 0],
  "lemonchiffon": [255, 250, 205],
  "lightblue": [173, 216, 230],
  "lightcoral": [240, 128, 128],
  "lightcyan": [224, 255, 255],
  "lightgoldenrodyellow": [250, 250, 210],
  "lightgray": [211, 211, 211],
  "lightgreen": [144, 238, 144],
  "lightgrey": [211, 211, 211],
  "lightpink": [255, 182, 193],
  "lightsalmon": [255, 160, 122],
  "lightseagreen": [32, 178, 170],
  "lightskyblue": [135, 206, 250],
  "lightslategray": [119, 136, 153],
  "lightslategrey": [119, 136, 153],
  "lightsteelblue": [176, 196, 222],
  "lightyellow": [255, 255, 224],
  "lime": [0, 255, 0],
  "limegreen": [50, 205, 50],
  "linen": [250, 240, 230],
  "magenta": [255, 0, 255],
  "maroon": [128, 0, 0],
  "mediumaquamarine": [102, 205, 170],
  "mediumblue": [0, 0, 205],
  "mediumorchid": [186, 85, 211],
  "mediumpurple": [147, 112, 219],
  "mediumseagreen": [60, 179, 113],
  "mediumslateblue": [123, 104, 238],
  "mediumspringgreen": [0, 250, 154],
  "mediumturquoise": [72, 209, 204],
  "mediumvioletred": [199, 21, 133],
  "midnightblue": [25, 25, 112],
  "mintcream": [245, 255, 250],
  "mistyrose": [255, 228, 225],
  "moccasin": [255, 228, 181],
  "navajowhite": [255, 222, 173],
  "navy": [0, 0, 128],
  "oldlace": [253, 245, 230],
  "olive": [128, 128, 0],
  "olivedrab": [107, 142, 35],
  "orange": [255, 165, 0],
  "orangered": [255, 69, 0],
  "orchid": [218, 112, 214],
  "palegoldenrod": [238, 232, 170],
  "palegreen": [152, 251, 152],
  "paleturquoise": [175, 238, 238],
  "palevioletred": [219, 112, 147],
  "papayawhip": [255, 239, 213],
  "peachpuff": [255, 218, 185],
  "peru": [205, 133, 63],
  "pink": [255, 192, 203],
  "plum": [221, 160, 221],
  "powderblue": [176, 224, 230],
  "purple": [128, 0, 128],
  "rebeccapurple": [102, 51, 153],
  "red": [255, 0, 0],
  "rosybrown": [188, 143, 143],
  "royalblue": [65, 105, 225],
  "saddlebrown": [139, 69, 19],
  "salmon": [250, 128, 114],
  "sandybrown": [244, 164, 96],
  "seagreen": [46, 139, 87],
  "seashell": [255, 245, 238],
  "sienna": [160, 82, 45],
  "silver": [192, 192, 192],
  "skyblue": [135, 206, 235],
  "slateblue": [106, 90, 205],
  "slategray": [112, 128, 144],
  "slategrey": [112, 128, 144],
  "snow": [255, 250, 250],
  "springgreen": [0, 255, 127],
  "steelblue": [70, 130, 180],
  "tan": [210, 180, 140],
  "teal": [0, 128, 128],
  "thistle": [216, 191, 216],
  "tomato": [255, 99, 71],
  "turquoise": [64, 224, 208],
  "violet": [238, 130, 238],
  "wheat": [245, 222, 179],
  "white": [255, 255, 255],
  "whitesmoke": [245, 245, 245],
  "yellow": [255, 255, 0],
  "yellowgreen": [154, 205, 50]
};

/* MIT license */

/* eslint-disable no-mixed-operators */
const cssKeywords = colorName;

// NOTE: conversions should only return primitive values (i.e. arrays, or
//       values that give correct `typeof` results).
//       do not use box values types (i.e. Number(), String(), etc.)

const reverseKeywords = {};
for (const key of Object.keys(cssKeywords)) {
  reverseKeywords[cssKeywords[key]] = key;
}
const convert$2 = {
  rgb: {
    channels: 3,
    labels: 'rgb'
  },
  hsl: {
    channels: 3,
    labels: 'hsl'
  },
  hsv: {
    channels: 3,
    labels: 'hsv'
  },
  hwb: {
    channels: 3,
    labels: 'hwb'
  },
  cmyk: {
    channels: 4,
    labels: 'cmyk'
  },
  xyz: {
    channels: 3,
    labels: 'xyz'
  },
  lab: {
    channels: 3,
    labels: 'lab'
  },
  lch: {
    channels: 3,
    labels: 'lch'
  },
  hex: {
    channels: 1,
    labels: ['hex']
  },
  keyword: {
    channels: 1,
    labels: ['keyword']
  },
  ansi16: {
    channels: 1,
    labels: ['ansi16']
  },
  ansi256: {
    channels: 1,
    labels: ['ansi256']
  },
  hcg: {
    channels: 3,
    labels: ['h', 'c', 'g']
  },
  apple: {
    channels: 3,
    labels: ['r16', 'g16', 'b16']
  },
  gray: {
    channels: 1,
    labels: ['gray']
  }
};
var conversions$2 = convert$2;

// Hide .channels and .labels properties
for (const model of Object.keys(convert$2)) {
  if (!('channels' in convert$2[model])) {
    throw new Error('missing channels property: ' + model);
  }
  if (!('labels' in convert$2[model])) {
    throw new Error('missing channel labels property: ' + model);
  }
  if (convert$2[model].labels.length !== convert$2[model].channels) {
    throw new Error('channel and label counts mismatch: ' + model);
  }
  const {
    channels,
    labels
  } = convert$2[model];
  delete convert$2[model].channels;
  delete convert$2[model].labels;
  Object.defineProperty(convert$2[model], 'channels', {
    value: channels
  });
  Object.defineProperty(convert$2[model], 'labels', {
    value: labels
  });
}
convert$2.rgb.hsl = function (rgb) {
  const r = rgb[0] / 255;
  const g = rgb[1] / 255;
  const b = rgb[2] / 255;
  const min = Math.min(r, g, b);
  const max = Math.max(r, g, b);
  const delta = max - min;
  let h;
  let s;
  if (max === min) {
    h = 0;
  } else if (r === max) {
    h = (g - b) / delta;
  } else if (g === max) {
    h = 2 + (b - r) / delta;
  } else if (b === max) {
    h = 4 + (r - g) / delta;
  }
  h = Math.min(h * 60, 360);
  if (h < 0) {
    h += 360;
  }
  const l = (min + max) / 2;
  if (max === min) {
    s = 0;
  } else if (l <= 0.5) {
    s = delta / (max + min);
  } else {
    s = delta / (2 - max - min);
  }
  return [h, s * 100, l * 100];
};
convert$2.rgb.hsv = function (rgb) {
  let rdif;
  let gdif;
  let bdif;
  let h;
  let s;
  const r = rgb[0] / 255;
  const g = rgb[1] / 255;
  const b = rgb[2] / 255;
  const v = Math.max(r, g, b);
  const diff = v - Math.min(r, g, b);
  const diffc = function (c) {
    return (v - c) / 6 / diff + 1 / 2;
  };
  if (diff === 0) {
    h = 0;
    s = 0;
  } else {
    s = diff / v;
    rdif = diffc(r);
    gdif = diffc(g);
    bdif = diffc(b);
    if (r === v) {
      h = bdif - gdif;
    } else if (g === v) {
      h = 1 / 3 + rdif - bdif;
    } else if (b === v) {
      h = 2 / 3 + gdif - rdif;
    }
    if (h < 0) {
      h += 1;
    } else if (h > 1) {
      h -= 1;
    }
  }
  return [h * 360, s * 100, v * 100];
};
convert$2.rgb.hwb = function (rgb) {
  const r = rgb[0];
  const g = rgb[1];
  let b = rgb[2];
  const h = convert$2.rgb.hsl(rgb)[0];
  const w = 1 / 255 * Math.min(r, Math.min(g, b));
  b = 1 - 1 / 255 * Math.max(r, Math.max(g, b));
  return [h, w * 100, b * 100];
};
convert$2.rgb.cmyk = function (rgb) {
  const r = rgb[0] / 255;
  const g = rgb[1] / 255;
  const b = rgb[2] / 255;
  const k = Math.min(1 - r, 1 - g, 1 - b);
  const c = (1 - r - k) / (1 - k) || 0;
  const m = (1 - g - k) / (1 - k) || 0;
  const y = (1 - b - k) / (1 - k) || 0;
  return [c * 100, m * 100, y * 100, k * 100];
};
function comparativeDistance(x, y) {
  /*
  	See https://en.m.wikipedia.org/wiki/Euclidean_distance#Squared_Euclidean_distance
  */
  return (x[0] - y[0]) ** 2 + (x[1] - y[1]) ** 2 + (x[2] - y[2]) ** 2;
}
convert$2.rgb.keyword = function (rgb) {
  const reversed = reverseKeywords[rgb];
  if (reversed) {
    return reversed;
  }
  let currentClosestDistance = Infinity;
  let currentClosestKeyword;
  for (const keyword of Object.keys(cssKeywords)) {
    const value = cssKeywords[keyword];

    // Compute comparative distance
    const distance = comparativeDistance(rgb, value);

    // Check if its less, if so set as closest
    if (distance < currentClosestDistance) {
      currentClosestDistance = distance;
      currentClosestKeyword = keyword;
    }
  }
  return currentClosestKeyword;
};
convert$2.keyword.rgb = function (keyword) {
  return cssKeywords[keyword];
};
convert$2.rgb.xyz = function (rgb) {
  let r = rgb[0] / 255;
  let g = rgb[1] / 255;
  let b = rgb[2] / 255;

  // Assume sRGB
  r = r > 0.04045 ? ((r + 0.055) / 1.055) ** 2.4 : r / 12.92;
  g = g > 0.04045 ? ((g + 0.055) / 1.055) ** 2.4 : g / 12.92;
  b = b > 0.04045 ? ((b + 0.055) / 1.055) ** 2.4 : b / 12.92;
  const x = r * 0.4124 + g * 0.3576 + b * 0.1805;
  const y = r * 0.2126 + g * 0.7152 + b * 0.0722;
  const z = r * 0.0193 + g * 0.1192 + b * 0.9505;
  return [x * 100, y * 100, z * 100];
};
convert$2.rgb.lab = function (rgb) {
  const xyz = convert$2.rgb.xyz(rgb);
  let x = xyz[0];
  let y = xyz[1];
  let z = xyz[2];
  x /= 95.047;
  y /= 100;
  z /= 108.883;
  x = x > 0.008856 ? x ** (1 / 3) : 7.787 * x + 16 / 116;
  y = y > 0.008856 ? y ** (1 / 3) : 7.787 * y + 16 / 116;
  z = z > 0.008856 ? z ** (1 / 3) : 7.787 * z + 16 / 116;
  const l = 116 * y - 16;
  const a = 500 * (x - y);
  const b = 200 * (y - z);
  return [l, a, b];
};
convert$2.hsl.rgb = function (hsl) {
  const h = hsl[0] / 360;
  const s = hsl[1] / 100;
  const l = hsl[2] / 100;
  let t2;
  let t3;
  let val;
  if (s === 0) {
    val = l * 255;
    return [val, val, val];
  }
  if (l < 0.5) {
    t2 = l * (1 + s);
  } else {
    t2 = l + s - l * s;
  }
  const t1 = 2 * l - t2;
  const rgb = [0, 0, 0];
  for (let i = 0; i < 3; i++) {
    t3 = h + 1 / 3 * -(i - 1);
    if (t3 < 0) {
      t3++;
    }
    if (t3 > 1) {
      t3--;
    }
    if (6 * t3 < 1) {
      val = t1 + (t2 - t1) * 6 * t3;
    } else if (2 * t3 < 1) {
      val = t2;
    } else if (3 * t3 < 2) {
      val = t1 + (t2 - t1) * (2 / 3 - t3) * 6;
    } else {
      val = t1;
    }
    rgb[i] = val * 255;
  }
  return rgb;
};
convert$2.hsl.hsv = function (hsl) {
  const h = hsl[0];
  let s = hsl[1] / 100;
  let l = hsl[2] / 100;
  let smin = s;
  const lmin = Math.max(l, 0.01);
  l *= 2;
  s *= l <= 1 ? l : 2 - l;
  smin *= lmin <= 1 ? lmin : 2 - lmin;
  const v = (l + s) / 2;
  const sv = l === 0 ? 2 * smin / (lmin + smin) : 2 * s / (l + s);
  return [h, sv * 100, v * 100];
};
convert$2.hsv.rgb = function (hsv) {
  const h = hsv[0] / 60;
  const s = hsv[1] / 100;
  let v = hsv[2] / 100;
  const hi = Math.floor(h) % 6;
  const f = h - Math.floor(h);
  const p = 255 * v * (1 - s);
  const q = 255 * v * (1 - s * f);
  const t = 255 * v * (1 - s * (1 - f));
  v *= 255;
  switch (hi) {
    case 0:
      return [v, t, p];
    case 1:
      return [q, v, p];
    case 2:
      return [p, v, t];
    case 3:
      return [p, q, v];
    case 4:
      return [t, p, v];
    case 5:
      return [v, p, q];
  }
};
convert$2.hsv.hsl = function (hsv) {
  const h = hsv[0];
  const s = hsv[1] / 100;
  const v = hsv[2] / 100;
  const vmin = Math.max(v, 0.01);
  let sl;
  let l;
  l = (2 - s) * v;
  const lmin = (2 - s) * vmin;
  sl = s * vmin;
  sl /= lmin <= 1 ? lmin : 2 - lmin;
  sl = sl || 0;
  l /= 2;
  return [h, sl * 100, l * 100];
};

// http://dev.w3.org/csswg/css-color/#hwb-to-rgb
convert$2.hwb.rgb = function (hwb) {
  const h = hwb[0] / 360;
  let wh = hwb[1] / 100;
  let bl = hwb[2] / 100;
  const ratio = wh + bl;
  let f;

  // Wh + bl cant be > 1
  if (ratio > 1) {
    wh /= ratio;
    bl /= ratio;
  }
  const i = Math.floor(6 * h);
  const v = 1 - bl;
  f = 6 * h - i;
  if ((i & 0x01) !== 0) {
    f = 1 - f;
  }
  const n = wh + f * (v - wh); // Linear interpolation

  let r;
  let g;
  let b;
  /* eslint-disable max-statements-per-line,no-multi-spaces */
  switch (i) {
    default:
    case 6:
    case 0:
      r = v;
      g = n;
      b = wh;
      break;
    case 1:
      r = n;
      g = v;
      b = wh;
      break;
    case 2:
      r = wh;
      g = v;
      b = n;
      break;
    case 3:
      r = wh;
      g = n;
      b = v;
      break;
    case 4:
      r = n;
      g = wh;
      b = v;
      break;
    case 5:
      r = v;
      g = wh;
      b = n;
      break;
  }
  /* eslint-enable max-statements-per-line,no-multi-spaces */

  return [r * 255, g * 255, b * 255];
};
convert$2.cmyk.rgb = function (cmyk) {
  const c = cmyk[0] / 100;
  const m = cmyk[1] / 100;
  const y = cmyk[2] / 100;
  const k = cmyk[3] / 100;
  const r = 1 - Math.min(1, c * (1 - k) + k);
  const g = 1 - Math.min(1, m * (1 - k) + k);
  const b = 1 - Math.min(1, y * (1 - k) + k);
  return [r * 255, g * 255, b * 255];
};
convert$2.xyz.rgb = function (xyz) {
  const x = xyz[0] / 100;
  const y = xyz[1] / 100;
  const z = xyz[2] / 100;
  let r;
  let g;
  let b;
  r = x * 3.2406 + y * -1.5372 + z * -0.4986;
  g = x * -0.9689 + y * 1.8758 + z * 0.0415;
  b = x * 0.0557 + y * -0.2040 + z * 1.0570;

  // Assume sRGB
  r = r > 0.0031308 ? 1.055 * r ** (1.0 / 2.4) - 0.055 : r * 12.92;
  g = g > 0.0031308 ? 1.055 * g ** (1.0 / 2.4) - 0.055 : g * 12.92;
  b = b > 0.0031308 ? 1.055 * b ** (1.0 / 2.4) - 0.055 : b * 12.92;
  r = Math.min(Math.max(0, r), 1);
  g = Math.min(Math.max(0, g), 1);
  b = Math.min(Math.max(0, b), 1);
  return [r * 255, g * 255, b * 255];
};
convert$2.xyz.lab = function (xyz) {
  let x = xyz[0];
  let y = xyz[1];
  let z = xyz[2];
  x /= 95.047;
  y /= 100;
  z /= 108.883;
  x = x > 0.008856 ? x ** (1 / 3) : 7.787 * x + 16 / 116;
  y = y > 0.008856 ? y ** (1 / 3) : 7.787 * y + 16 / 116;
  z = z > 0.008856 ? z ** (1 / 3) : 7.787 * z + 16 / 116;
  const l = 116 * y - 16;
  const a = 500 * (x - y);
  const b = 200 * (y - z);
  return [l, a, b];
};
convert$2.lab.xyz = function (lab) {
  const l = lab[0];
  const a = lab[1];
  const b = lab[2];
  let x;
  let y;
  let z;
  y = (l + 16) / 116;
  x = a / 500 + y;
  z = y - b / 200;
  const y2 = y ** 3;
  const x2 = x ** 3;
  const z2 = z ** 3;
  y = y2 > 0.008856 ? y2 : (y - 16 / 116) / 7.787;
  x = x2 > 0.008856 ? x2 : (x - 16 / 116) / 7.787;
  z = z2 > 0.008856 ? z2 : (z - 16 / 116) / 7.787;
  x *= 95.047;
  y *= 100;
  z *= 108.883;
  return [x, y, z];
};
convert$2.lab.lch = function (lab) {
  const l = lab[0];
  const a = lab[1];
  const b = lab[2];
  let h;
  const hr = Math.atan2(b, a);
  h = hr * 360 / 2 / Math.PI;
  if (h < 0) {
    h += 360;
  }
  const c = Math.sqrt(a * a + b * b);
  return [l, c, h];
};
convert$2.lch.lab = function (lch) {
  const l = lch[0];
  const c = lch[1];
  const h = lch[2];
  const hr = h / 360 * 2 * Math.PI;
  const a = c * Math.cos(hr);
  const b = c * Math.sin(hr);
  return [l, a, b];
};
convert$2.rgb.ansi16 = function (args, saturation = null) {
  const [r, g, b] = args;
  let value = saturation === null ? convert$2.rgb.hsv(args)[2] : saturation; // Hsv -> ansi16 optimization

  value = Math.round(value / 50);
  if (value === 0) {
    return 30;
  }
  let ansi = 30 + (Math.round(b / 255) << 2 | Math.round(g / 255) << 1 | Math.round(r / 255));
  if (value === 2) {
    ansi += 60;
  }
  return ansi;
};
convert$2.hsv.ansi16 = function (args) {
  // Optimization here; we already know the value and don't need to get
  // it converted for us.
  return convert$2.rgb.ansi16(convert$2.hsv.rgb(args), args[2]);
};
convert$2.rgb.ansi256 = function (args) {
  const r = args[0];
  const g = args[1];
  const b = args[2];

  // We use the extended greyscale palette here, with the exception of
  // black and white. normal palette only has 4 greyscale shades.
  if (r === g && g === b) {
    if (r < 8) {
      return 16;
    }
    if (r > 248) {
      return 231;
    }
    return Math.round((r - 8) / 247 * 24) + 232;
  }
  const ansi = 16 + 36 * Math.round(r / 255 * 5) + 6 * Math.round(g / 255 * 5) + Math.round(b / 255 * 5);
  return ansi;
};
convert$2.ansi16.rgb = function (args) {
  let color = args % 10;

  // Handle greyscale
  if (color === 0 || color === 7) {
    if (args > 50) {
      color += 3.5;
    }
    color = color / 10.5 * 255;
    return [color, color, color];
  }
  const mult = (~~(args > 50) + 1) * 0.5;
  const r = (color & 1) * mult * 255;
  const g = (color >> 1 & 1) * mult * 255;
  const b = (color >> 2 & 1) * mult * 255;
  return [r, g, b];
};
convert$2.ansi256.rgb = function (args) {
  // Handle greyscale
  if (args >= 232) {
    const c = (args - 232) * 10 + 8;
    return [c, c, c];
  }
  args -= 16;
  let rem;
  const r = Math.floor(args / 36) / 5 * 255;
  const g = Math.floor((rem = args % 36) / 6) / 5 * 255;
  const b = rem % 6 / 5 * 255;
  return [r, g, b];
};
convert$2.rgb.hex = function (args) {
  const integer = ((Math.round(args[0]) & 0xFF) << 16) + ((Math.round(args[1]) & 0xFF) << 8) + (Math.round(args[2]) & 0xFF);
  const string = integer.toString(16).toUpperCase();
  return '000000'.substring(string.length) + string;
};
convert$2.hex.rgb = function (args) {
  const match = args.toString(16).match(/[a-f0-9]{6}|[a-f0-9]{3}/i);
  if (!match) {
    return [0, 0, 0];
  }
  let colorString = match[0];
  if (match[0].length === 3) {
    colorString = colorString.split('').map(char => {
      return char + char;
    }).join('');
  }
  const integer = parseInt(colorString, 16);
  const r = integer >> 16 & 0xFF;
  const g = integer >> 8 & 0xFF;
  const b = integer & 0xFF;
  return [r, g, b];
};
convert$2.rgb.hcg = function (rgb) {
  const r = rgb[0] / 255;
  const g = rgb[1] / 255;
  const b = rgb[2] / 255;
  const max = Math.max(Math.max(r, g), b);
  const min = Math.min(Math.min(r, g), b);
  const chroma = max - min;
  let grayscale;
  let hue;
  if (chroma < 1) {
    grayscale = min / (1 - chroma);
  } else {
    grayscale = 0;
  }
  if (chroma <= 0) {
    hue = 0;
  } else if (max === r) {
    hue = (g - b) / chroma % 6;
  } else if (max === g) {
    hue = 2 + (b - r) / chroma;
  } else {
    hue = 4 + (r - g) / chroma;
  }
  hue /= 6;
  hue %= 1;
  return [hue * 360, chroma * 100, grayscale * 100];
};
convert$2.hsl.hcg = function (hsl) {
  const s = hsl[1] / 100;
  const l = hsl[2] / 100;
  const c = l < 0.5 ? 2.0 * s * l : 2.0 * s * (1.0 - l);
  let f = 0;
  if (c < 1.0) {
    f = (l - 0.5 * c) / (1.0 - c);
  }
  return [hsl[0], c * 100, f * 100];
};
convert$2.hsv.hcg = function (hsv) {
  const s = hsv[1] / 100;
  const v = hsv[2] / 100;
  const c = s * v;
  let f = 0;
  if (c < 1.0) {
    f = (v - c) / (1 - c);
  }
  return [hsv[0], c * 100, f * 100];
};
convert$2.hcg.rgb = function (hcg) {
  const h = hcg[0] / 360;
  const c = hcg[1] / 100;
  const g = hcg[2] / 100;
  if (c === 0.0) {
    return [g * 255, g * 255, g * 255];
  }
  const pure = [0, 0, 0];
  const hi = h % 1 * 6;
  const v = hi % 1;
  const w = 1 - v;
  let mg = 0;

  /* eslint-disable max-statements-per-line */
  switch (Math.floor(hi)) {
    case 0:
      pure[0] = 1;
      pure[1] = v;
      pure[2] = 0;
      break;
    case 1:
      pure[0] = w;
      pure[1] = 1;
      pure[2] = 0;
      break;
    case 2:
      pure[0] = 0;
      pure[1] = 1;
      pure[2] = v;
      break;
    case 3:
      pure[0] = 0;
      pure[1] = w;
      pure[2] = 1;
      break;
    case 4:
      pure[0] = v;
      pure[1] = 0;
      pure[2] = 1;
      break;
    default:
      pure[0] = 1;
      pure[1] = 0;
      pure[2] = w;
  }
  /* eslint-enable max-statements-per-line */

  mg = (1.0 - c) * g;
  return [(c * pure[0] + mg) * 255, (c * pure[1] + mg) * 255, (c * pure[2] + mg) * 255];
};
convert$2.hcg.hsv = function (hcg) {
  const c = hcg[1] / 100;
  const g = hcg[2] / 100;
  const v = c + g * (1.0 - c);
  let f = 0;
  if (v > 0.0) {
    f = c / v;
  }
  return [hcg[0], f * 100, v * 100];
};
convert$2.hcg.hsl = function (hcg) {
  const c = hcg[1] / 100;
  const g = hcg[2] / 100;
  const l = g * (1.0 - c) + 0.5 * c;
  let s = 0;
  if (l > 0.0 && l < 0.5) {
    s = c / (2 * l);
  } else if (l >= 0.5 && l < 1.0) {
    s = c / (2 * (1 - l));
  }
  return [hcg[0], s * 100, l * 100];
};
convert$2.hcg.hwb = function (hcg) {
  const c = hcg[1] / 100;
  const g = hcg[2] / 100;
  const v = c + g * (1.0 - c);
  return [hcg[0], (v - c) * 100, (1 - v) * 100];
};
convert$2.hwb.hcg = function (hwb) {
  const w = hwb[1] / 100;
  const b = hwb[2] / 100;
  const v = 1 - b;
  const c = v - w;
  let g = 0;
  if (c < 1) {
    g = (v - c) / (1 - c);
  }
  return [hwb[0], c * 100, g * 100];
};
convert$2.apple.rgb = function (apple) {
  return [apple[0] / 65535 * 255, apple[1] / 65535 * 255, apple[2] / 65535 * 255];
};
convert$2.rgb.apple = function (rgb) {
  return [rgb[0] / 255 * 65535, rgb[1] / 255 * 65535, rgb[2] / 255 * 65535];
};
convert$2.gray.rgb = function (args) {
  return [args[0] / 100 * 255, args[0] / 100 * 255, args[0] / 100 * 255];
};
convert$2.gray.hsl = function (args) {
  return [0, 0, args[0]];
};
convert$2.gray.hsv = convert$2.gray.hsl;
convert$2.gray.hwb = function (gray) {
  return [0, 100, gray[0]];
};
convert$2.gray.cmyk = function (gray) {
  return [0, 0, 0, gray[0]];
};
convert$2.gray.lab = function (gray) {
  return [gray[0], 0, 0];
};
convert$2.gray.hex = function (gray) {
  const val = Math.round(gray[0] / 100 * 255) & 0xFF;
  const integer = (val << 16) + (val << 8) + val;
  const string = integer.toString(16).toUpperCase();
  return '000000'.substring(string.length) + string;
};
convert$2.rgb.gray = function (rgb) {
  const val = (rgb[0] + rgb[1] + rgb[2]) / 3;
  return [val / 255 * 100];
};

const conversions$1 = conversions$2;

/*
	This function routes a model to all other models.

	all functions that are routed have a property `.conversion` attached
	to the returned synthetic function. This property is an array
	of strings, each with the steps in between the 'from' and 'to'
	color models (inclusive).

	conversions that are not possible simply are not included.
*/

function buildGraph() {
  const graph = {};
  // https://jsperf.com/object-keys-vs-for-in-with-closure/3
  const models = Object.keys(conversions$1);
  for (let len = models.length, i = 0; i < len; i++) {
    graph[models[i]] = {
      // http://jsperf.com/1-vs-infinity
      // micro-opt, but this is simple.
      distance: -1,
      parent: null
    };
  }
  return graph;
}

// https://en.wikipedia.org/wiki/Breadth-first_search
function deriveBFS(fromModel) {
  const graph = buildGraph();
  const queue = [fromModel]; // Unshift -> queue -> pop

  graph[fromModel].distance = 0;
  while (queue.length) {
    const current = queue.pop();
    const adjacents = Object.keys(conversions$1[current]);
    for (let len = adjacents.length, i = 0; i < len; i++) {
      const adjacent = adjacents[i];
      const node = graph[adjacent];
      if (node.distance === -1) {
        node.distance = graph[current].distance + 1;
        node.parent = current;
        queue.unshift(adjacent);
      }
    }
  }
  return graph;
}
function link(from, to) {
  return function (args) {
    return to(from(args));
  };
}
function wrapConversion(toModel, graph) {
  const path = [graph[toModel].parent, toModel];
  let fn = conversions$1[graph[toModel].parent][toModel];
  let cur = graph[toModel].parent;
  while (graph[cur].parent) {
    path.unshift(graph[cur].parent);
    fn = link(conversions$1[graph[cur].parent][cur], fn);
    cur = graph[cur].parent;
  }
  fn.conversion = path;
  return fn;
}
var route$1 = function (fromModel) {
  const graph = deriveBFS(fromModel);
  const conversion = {};
  const models = Object.keys(graph);
  for (let len = models.length, i = 0; i < len; i++) {
    const toModel = models[i];
    const node = graph[toModel];
    if (node.parent === null) {
      // No possible conversion, or this node is the source model.
      continue;
    }
    conversion[toModel] = wrapConversion(toModel, graph);
  }
  return conversion;
};

const conversions = conversions$2;
const route = route$1;
const convert$1 = {};
const models = Object.keys(conversions);
function wrapRaw(fn) {
  const wrappedFn = function (...args) {
    const arg0 = args[0];
    if (arg0 === undefined || arg0 === null) {
      return arg0;
    }
    if (arg0.length > 1) {
      args = arg0;
    }
    return fn(args);
  };

  // Preserve .conversion property if there is one
  if ('conversion' in fn) {
    wrappedFn.conversion = fn.conversion;
  }
  return wrappedFn;
}
function wrapRounded(fn) {
  const wrappedFn = function (...args) {
    const arg0 = args[0];
    if (arg0 === undefined || arg0 === null) {
      return arg0;
    }
    if (arg0.length > 1) {
      args = arg0;
    }
    const result = fn(args);

    // We're assuming the result is an array here.
    // see notice in conversions.js; don't use box types
    // in conversion functions.
    if (typeof result === 'object') {
      for (let len = result.length, i = 0; i < len; i++) {
        result[i] = Math.round(result[i]);
      }
    }
    return result;
  };

  // Preserve .conversion property if there is one
  if ('conversion' in fn) {
    wrappedFn.conversion = fn.conversion;
  }
  return wrappedFn;
}
models.forEach(fromModel => {
  convert$1[fromModel] = {};
  Object.defineProperty(convert$1[fromModel], 'channels', {
    value: conversions[fromModel].channels
  });
  Object.defineProperty(convert$1[fromModel], 'labels', {
    value: conversions[fromModel].labels
  });
  const routes = route(fromModel);
  const routeModels = Object.keys(routes);
  routeModels.forEach(toModel => {
    const fn = routes[toModel];
    convert$1[fromModel][toModel] = wrapRounded(fn);
    convert$1[fromModel][toModel].raw = wrapRaw(fn);
  });
});
var colorConvert = convert$1;

const colorString = colorString$1.exports;
const convert = colorConvert;
const skippedModels = [
// To be honest, I don't really feel like keyword belongs in color convert, but eh.
'keyword',
// Gray conflicts with some method names, and has its own method defined.
'gray',
// Shouldn't really be in color-convert either...
'hex'];
const hashedModelKeys = {};
for (const model of Object.keys(convert)) {
  hashedModelKeys[[...convert[model].labels].sort().join('')] = model;
}
const limiters = {};
function Color(object, model) {
  if (!(this instanceof Color)) {
    return new Color(object, model);
  }
  if (model && model in skippedModels) {
    model = null;
  }
  if (model && !(model in convert)) {
    throw new Error('Unknown model: ' + model);
  }
  let i;
  let channels;
  if (object == null) {
    // eslint-disable-line no-eq-null,eqeqeq
    this.model = 'rgb';
    this.color = [0, 0, 0];
    this.valpha = 1;
  } else if (object instanceof Color) {
    this.model = object.model;
    this.color = [...object.color];
    this.valpha = object.valpha;
  } else if (typeof object === 'string') {
    const result = colorString.get(object);
    if (result === null) {
      throw new Error('Unable to parse color from string: ' + object);
    }
    this.model = result.model;
    channels = convert[this.model].channels;
    this.color = result.value.slice(0, channels);
    this.valpha = typeof result.value[channels] === 'number' ? result.value[channels] : 1;
  } else if (object.length > 0) {
    this.model = model || 'rgb';
    channels = convert[this.model].channels;
    const newArray = Array.prototype.slice.call(object, 0, channels);
    this.color = zeroArray(newArray, channels);
    this.valpha = typeof object[channels] === 'number' ? object[channels] : 1;
  } else if (typeof object === 'number') {
    // This is always RGB - can be converted later on.
    this.model = 'rgb';
    this.color = [object >> 16 & 0xFF, object >> 8 & 0xFF, object & 0xFF];
    this.valpha = 1;
  } else {
    this.valpha = 1;
    const keys = Object.keys(object);
    if ('alpha' in object) {
      keys.splice(keys.indexOf('alpha'), 1);
      this.valpha = typeof object.alpha === 'number' ? object.alpha : 0;
    }
    const hashedKeys = keys.sort().join('');
    if (!(hashedKeys in hashedModelKeys)) {
      throw new Error('Unable to parse color from object: ' + JSON.stringify(object));
    }
    this.model = hashedModelKeys[hashedKeys];
    const {
      labels
    } = convert[this.model];
    const color = [];
    for (i = 0; i < labels.length; i++) {
      color.push(object[labels[i]]);
    }
    this.color = zeroArray(color);
  }

  // Perform limitations (clamping, etc.)
  if (limiters[this.model]) {
    channels = convert[this.model].channels;
    for (i = 0; i < channels; i++) {
      const limit = limiters[this.model][i];
      if (limit) {
        this.color[i] = limit(this.color[i]);
      }
    }
  }
  this.valpha = Math.max(0, Math.min(1, this.valpha));
  if (Object.freeze) {
    Object.freeze(this);
  }
}
Color.prototype = {
  toString() {
    return this.string();
  },
  toJSON() {
    return this[this.model]();
  },
  string(places) {
    let self = this.model in colorString.to ? this : this.rgb();
    self = self.round(typeof places === 'number' ? places : 1);
    const args = self.valpha === 1 ? self.color : [...self.color, this.valpha];
    return colorString.to[self.model](args);
  },
  percentString(places) {
    const self = this.rgb().round(typeof places === 'number' ? places : 1);
    const args = self.valpha === 1 ? self.color : [...self.color, this.valpha];
    return colorString.to.rgb.percent(args);
  },
  array() {
    return this.valpha === 1 ? [...this.color] : [...this.color, this.valpha];
  },
  object() {
    const result = {};
    const {
      channels
    } = convert[this.model];
    const {
      labels
    } = convert[this.model];
    for (let i = 0; i < channels; i++) {
      result[labels[i]] = this.color[i];
    }
    if (this.valpha !== 1) {
      result.alpha = this.valpha;
    }
    return result;
  },
  unitArray() {
    const rgb = this.rgb().color;
    rgb[0] /= 255;
    rgb[1] /= 255;
    rgb[2] /= 255;
    if (this.valpha !== 1) {
      rgb.push(this.valpha);
    }
    return rgb;
  },
  unitObject() {
    const rgb = this.rgb().object();
    rgb.r /= 255;
    rgb.g /= 255;
    rgb.b /= 255;
    if (this.valpha !== 1) {
      rgb.alpha = this.valpha;
    }
    return rgb;
  },
  round(places) {
    places = Math.max(places || 0, 0);
    return new Color([...this.color.map(roundToPlace(places)), this.valpha], this.model);
  },
  alpha(value) {
    if (value !== undefined) {
      return new Color([...this.color, Math.max(0, Math.min(1, value))], this.model);
    }
    return this.valpha;
  },
  // Rgb
  red: getset('rgb', 0, maxfn(255)),
  green: getset('rgb', 1, maxfn(255)),
  blue: getset('rgb', 2, maxfn(255)),
  hue: getset(['hsl', 'hsv', 'hsl', 'hwb', 'hcg'], 0, value => (value % 360 + 360) % 360),
  saturationl: getset('hsl', 1, maxfn(100)),
  lightness: getset('hsl', 2, maxfn(100)),
  saturationv: getset('hsv', 1, maxfn(100)),
  value: getset('hsv', 2, maxfn(100)),
  chroma: getset('hcg', 1, maxfn(100)),
  gray: getset('hcg', 2, maxfn(100)),
  white: getset('hwb', 1, maxfn(100)),
  wblack: getset('hwb', 2, maxfn(100)),
  cyan: getset('cmyk', 0, maxfn(100)),
  magenta: getset('cmyk', 1, maxfn(100)),
  yellow: getset('cmyk', 2, maxfn(100)),
  black: getset('cmyk', 3, maxfn(100)),
  x: getset('xyz', 0, maxfn(95.047)),
  y: getset('xyz', 1, maxfn(100)),
  z: getset('xyz', 2, maxfn(108.833)),
  l: getset('lab', 0, maxfn(100)),
  a: getset('lab', 1),
  b: getset('lab', 2),
  keyword(value) {
    if (value !== undefined) {
      return new Color(value);
    }
    return convert[this.model].keyword(this.color);
  },
  hex(value) {
    if (value !== undefined) {
      return new Color(value);
    }
    return colorString.to.hex(this.rgb().round().color);
  },
  hexa(value) {
    if (value !== undefined) {
      return new Color(value);
    }
    const rgbArray = this.rgb().round().color;
    let alphaHex = Math.round(this.valpha * 255).toString(16).toUpperCase();
    if (alphaHex.length === 1) {
      alphaHex = '0' + alphaHex;
    }
    return colorString.to.hex(rgbArray) + alphaHex;
  },
  rgbNumber() {
    const rgb = this.rgb().color;
    return (rgb[0] & 0xFF) << 16 | (rgb[1] & 0xFF) << 8 | rgb[2] & 0xFF;
  },
  luminosity() {
    // http://www.w3.org/TR/WCAG20/#relativeluminancedef
    const rgb = this.rgb().color;
    const lum = [];
    for (const [i, element] of rgb.entries()) {
      const chan = element / 255;
      lum[i] = chan <= 0.04045 ? chan / 12.92 : ((chan + 0.055) / 1.055) ** 2.4;
    }
    return 0.2126 * lum[0] + 0.7152 * lum[1] + 0.0722 * lum[2];
  },
  contrast(color2) {
    // http://www.w3.org/TR/WCAG20/#contrast-ratiodef
    const lum1 = this.luminosity();
    const lum2 = color2.luminosity();
    if (lum1 > lum2) {
      return (lum1 + 0.05) / (lum2 + 0.05);
    }
    return (lum2 + 0.05) / (lum1 + 0.05);
  },
  level(color2) {
    // https://www.w3.org/TR/WCAG/#contrast-enhanced
    const contrastRatio = this.contrast(color2);
    if (contrastRatio >= 7) {
      return 'AAA';
    }
    return contrastRatio >= 4.5 ? 'AA' : '';
  },
  isDark() {
    // YIQ equation from http://24ways.org/2010/calculating-color-contrast
    const rgb = this.rgb().color;
    const yiq = (rgb[0] * 2126 + rgb[1] * 7152 + rgb[2] * 722) / 10000;
    return yiq < 128;
  },
  isLight() {
    return !this.isDark();
  },
  negate() {
    const rgb = this.rgb();
    for (let i = 0; i < 3; i++) {
      rgb.color[i] = 255 - rgb.color[i];
    }
    return rgb;
  },
  lighten(ratio) {
    const hsl = this.hsl();
    hsl.color[2] += hsl.color[2] * ratio;
    return hsl;
  },
  darken(ratio) {
    const hsl = this.hsl();
    hsl.color[2] -= hsl.color[2] * ratio;
    return hsl;
  },
  saturate(ratio) {
    const hsl = this.hsl();
    hsl.color[1] += hsl.color[1] * ratio;
    return hsl;
  },
  desaturate(ratio) {
    const hsl = this.hsl();
    hsl.color[1] -= hsl.color[1] * ratio;
    return hsl;
  },
  whiten(ratio) {
    const hwb = this.hwb();
    hwb.color[1] += hwb.color[1] * ratio;
    return hwb;
  },
  blacken(ratio) {
    const hwb = this.hwb();
    hwb.color[2] += hwb.color[2] * ratio;
    return hwb;
  },
  grayscale() {
    // http://en.wikipedia.org/wiki/Grayscale#Converting_color_to_grayscale
    const rgb = this.rgb().color;
    const value = rgb[0] * 0.3 + rgb[1] * 0.59 + rgb[2] * 0.11;
    return Color.rgb(value, value, value);
  },
  fade(ratio) {
    return this.alpha(this.valpha - this.valpha * ratio);
  },
  opaquer(ratio) {
    return this.alpha(this.valpha + this.valpha * ratio);
  },
  rotate(degrees) {
    const hsl = this.hsl();
    let hue = hsl.color[0];
    hue = (hue + degrees) % 360;
    hue = hue < 0 ? 360 + hue : hue;
    hsl.color[0] = hue;
    return hsl;
  },
  mix(mixinColor, weight) {
    // Ported from sass implementation in C
    // https://github.com/sass/libsass/blob/0e6b4a2850092356aa3ece07c6b249f0221caced/functions.cpp#L209
    if (!mixinColor || !mixinColor.rgb) {
      throw new Error('Argument to "mix" was not a Color instance, but rather an instance of ' + typeof mixinColor);
    }
    const color1 = mixinColor.rgb();
    const color2 = this.rgb();
    const p = weight === undefined ? 0.5 : weight;
    const w = 2 * p - 1;
    const a = color1.alpha() - color2.alpha();
    const w1 = ((w * a === -1 ? w : (w + a) / (1 + w * a)) + 1) / 2;
    const w2 = 1 - w1;
    return Color.rgb(w1 * color1.red() + w2 * color2.red(), w1 * color1.green() + w2 * color2.green(), w1 * color1.blue() + w2 * color2.blue(), color1.alpha() * p + color2.alpha() * (1 - p));
  }
};

// Model conversion methods and static constructors
for (const model of Object.keys(convert)) {
  if (skippedModels.includes(model)) {
    continue;
  }
  const {
    channels
  } = convert[model];

  // Conversion methods
  Color.prototype[model] = function (...args) {
    if (this.model === model) {
      return new Color(this);
    }
    if (args.length > 0) {
      return new Color(args, model);
    }
    return new Color([...assertArray(convert[this.model][model].raw(this.color)), this.valpha], model);
  };

  // 'static' construction methods
  Color[model] = function (...args) {
    let color = args[0];
    if (typeof color === 'number') {
      color = zeroArray(args, channels);
    }
    return new Color(color, model);
  };
}
function roundTo(number, places) {
  return Number(number.toFixed(places));
}
function roundToPlace(places) {
  return function (number) {
    return roundTo(number, places);
  };
}
function getset(model, channel, modifier) {
  model = Array.isArray(model) ? model : [model];
  for (const m of model) {
    (limiters[m] || (limiters[m] = []))[channel] = modifier;
  }
  model = model[0];
  return function (value) {
    let result;
    if (value !== undefined) {
      if (modifier) {
        value = modifier(value);
      }
      result = this[model]();
      result.color[channel] = value;
      return result;
    }
    result = this[model]().color[channel];
    if (modifier) {
      result = modifier(result);
    }
    return result;
  };
}
function maxfn(max) {
  return function (v) {
    return Math.max(0, Math.min(max, v));
  };
}
function assertArray(value) {
  return Array.isArray(value) ? value : [value];
}
function zeroArray(array, length) {
  for (let i = 0; i < length; i++) {
    if (typeof array[i] !== 'number') {
      array[i] = 0;
    }
  }
  return array;
}
var color = Color;

var Color$1 = color;

const processColors = (value, name, pastels) => {
  let bLow = 30;
  let sHigh = 100;
  let sLow = 7;
  const hsv = Color$1(value).hsv().array();
  const h = hsv[0];
  const s = hsv[1];
  const v = hsv[2];
  if (s === 0) {
    sLow = 0;
    sHigh = 0;
  } else if (s < 10) {
    sLow = 2;
    sHigh = 10;
  } else if (s < 20) {
    sLow = 3;
    sHigh = 10;
  } else if (s < 40) {
    sLow = 5;
    sHigh = 40;
  }
  const sTep = (s - sLow) / 5;
  const vStep = (v - bLow) / 5;
  const pastelColors = () => ({
    [`${name}P50`]: Color$1.hsv(h, s - sTep * 5, v + vStep * 5).string(),
    [`${name}P75`]: Color$1.hsv(h, s - sTep * 5, v + vStep * 4.5).string(),
    [`${name}P100`]: Color$1.hsv(h, s - sTep * 5, v + vStep * 4).string(),
    [`${name}P200`]: Color$1.hsv(h, s - sTep * 5, v + vStep * 3).string(),
    [`${name}P300`]: Color$1.hsv(h, s - sTep * 5, v + vStep * 2).string(),
    [`${name}P400`]: Color$1.hsv(h, s - sTep * 5, v + vStep * 1).string(),
    [`${name}`]: Color$1.hsv(h, s - sTep * 5, v).string()
  });
  let colors = {
    [`${name}50`]: Color$1.hsv(h, s - sTep * 5, v + vStep * 5).string(),
    [`${name}75`]: Color$1.hsv(h, s - sTep * 4, v + vStep * 4.5).string(),
    [`${name}100`]: Color$1.hsv(h, s - sTep * 4, v + vStep * 4).string(),
    [`${name}200`]: Color$1.hsv(h, s - sTep * 3, v + vStep * 3).string(),
    [`${name}300`]: Color$1.hsv(h, s - sTep * 2, v + vStep * 2).string(),
    [`${name}400`]: Color$1.hsv(h, s - sTep * 1, v + vStep * 1).string(),
    [name]: Color$1.hsv(h, s, v).string(),
    [`${name}600`]: Color$1.hsv(h, s, v - vStep * 1).string(),
    [`${name}700`]: Color$1.hsv(h, s, v - vStep * 2.5).string(),
    [`${name}800`]: Color$1.hsv(h, s, v - vStep * 4).string(),
    [`${name}900`]: Color$1.hsv(h, s, v - vStep * 5).string(),
    [`${name}A100`]: Color$1.hsv(h, sHigh - sTep * 3, v + vStep * 4).string(),
    [`${name}A200`]: Color$1.hsv(h, sHigh - sTep * 2, v + vStep * 3).string(),
    [`${name}A400`]: Color$1.hsv(h, sHigh, v + vStep * 1).string(),
    [`${name}A700`]: Color$1.hsv(h, sHigh, v - vStep * 1.5).string(),
    [`${name}D900`]: Color$1.hsv(h, s, v - vStep * 6).string()
  };
  if (pastels) {
    colors = {
      ...colors,
      ...pastelColors()
    };
  }
  return colors;
};

const theme = {
  baseline: '0.5rem',
  fontFamilies: {
    heading: ['Helvetica', 'sans-serif'],
    body: ['Helvetica', 'sans-serif']
  },
  colors: {
    primary: {
      process: '#055dff'
    },
    secondary: {
      process: '#da2128'
    },
    neutral: {
      process: '#9095A3'
    },
    disabled: 'hsla(0, 0%, 0%, .1)',
    success: '#9dcb50',
    info: '#f7ce00',
    alert: '#f7ce00',
    warning: '#de7e00',
    danger: '#ff5042',
    red: '#d34f4f',
    orange: '#ed8937',
    yellow: '#f7ce00',
    green: '#26c431',
    blue: '#29abe2',
    indigo: '#3e3ecc',
    violet: '#744cd6',
    black: '#000',
    white: '#fff'
  },
  typography: {
    p: {
      fontFamily: 'body',
      fontSize: '1rem',
      lineHeight: '1.5rem'
    },
    h1: {
      fontFamily: 'heading',
      fontSize: '2rem',
      lineHeight: '2.5rem'
    },
    h2: {
      fontFamily: 'heading',
      fontSize: '1.75rem',
      lineHeight: '2rem'
    },
    h3: {
      fontFamily: 'heading',
      fontSize: '1.5rem',
      lineHeight: '2rem'
    },
    h4: {
      fontFamily: 'heading',
      fontSize: '1.3125rem',
      lineHeight: '2rem'
    },
    h5: {
      fontFamily: 'heading',
      fontSize: '1.125rem',
      lineHeight: '1.5rem'
    },
    h6: {
      fontFamily: 'heading',
      fontSize: '0.875rem',
      lineHeight: '1.5rem'
    },
    large: {
      fontFamily: 'heading',
      fontSize: '3rem',
      lineHeight: '4rem'
    },
    jumbo: {
      fontFamily: 'heading',
      fontSize: '3.4375rem',
      lineHeight: '4rem'
    },
    giant: {
      fontFamily: 'heading',
      fontSize: '6rem',
      lineHeight: '6rem'
    },
    title: {
      fontFamily: 'heading',
      fontSize: '1.125rem',
      lineHeight: '1.5rem'
    },
    subtitle: {
      fontFamily: 'heading',
      fontSize: '0.75rem',
      lineHeight: '1.25rem'
    },
    label: {
      fontFamily: 'body',
      fontSize: '0.875rem',
      lineHeight: '1.5rem'
    },
    leader: {
      fontFamily: 'body',
      fontSize: '1.3125rem',
      lineHeight: '2rem'
    },
    control: {
      fontFamily: 'body',
      fontSize: '1rem',
      lineHeight: '1.5rem'
    },
    small: {
      fontFamily: 'body',
      fontSize: '0.75rem',
      lineHeight: '1rem'
    },
    micro: {
      fontFamily: 'body',
      fontSize: '0.625rem',
      lineHeight: '0.75rem'
    },
    inherit: {
      fontSize: 'inherit',
      fontFamily: 'inherit'
    }
  },
  spacing: {
    xxs: '0.25rem',
    xs: '0.5rem',
    sm: '1rem',
    md: '1.5rem',
    lg: '2rem',
    xl: '3rem',
    xxl: '5rem'
  },
  radius: {
    xs: '0.125rem',
    sm: '0.25rem',
    md: '0.375rem',
    lg: '0.5rem',
    xl: '1rem',
    xxl: '1rem'
  },
  border: {
    xs: '1px',
    sm: '2px',
    md: '4px',
    lg: '0.5rem',
    xl: '1rem',
    xxl: '2rem'
  },
  depth: {
    xs: '0 2px 4px rgba(0, 0, 0, 0.2)',
    sm: '0 4px 6px rgba(0, 0, 0, 0.2)',
    md: '0 4px 8px rgba(0, 0, 0, 0.2)',
    lg: '0 6px 12px rgba(0, 0, 0, 0.2)',
    xl: '0 6px 16px rgba(0, 0, 0, 0.25)',
    xxl: '0 8px 24px rgba(0, 0, 0, 0.25)'
  },
  breakpoints: {
    phone: {
      minWidth: 20
    },
    tablet: {
      minWidth: 575
    },
    laptop: {
      minWidth: 769
    },
    desktop: {
      minWidth: 1025
    },
    hd: {
      minWidth: 1680
    }
  }
};

const mergeTheme = (defaultTheme, newTheme) => {
  const merged = {};
  let colors = {
    ...defaultTheme.colors,
    ...newTheme.colors
  };
  colors = Object.keys(colors).reduce((accumulator, color) => {
    if (colors[color].process) {
      return {
        ...accumulator,
        ...processColors(colors[color].process, color)
      };
    }
    accumulator[color] = colors[color];
    return accumulator;
  }, {});

  // Merge Font Font Familes
  merged.fontFamilies = newTheme.fontFamilies ? {
    ...defaultTheme.fontFamilies,
    ...newTheme.fontFamilies
  } : defaultTheme.fontFamilies;

  // Merge Type1
  merged.typography = newTheme.typography ? {
    ...defaultTheme.typography,
    ...newTheme.typography
  } : defaultTheme.typography;

  // Process Font fontFamilies
  Object.keys(merged.typography).forEach(key => {
    const type = merged.typography[key];
    if (type.fontFamily) {
      type.fontFamily = merged.fontFamilies[type.fontFamily] || type.fontFamily;
    }
  });

  // Colors
  merged.colors = colors;
  // Spacing
  merged.spacing = newTheme.spacing ? {
    ...defaultTheme.spacing,
    ...newTheme.spacing
  } : defaultTheme.spacing;
  // Radius
  merged.radius = newTheme.radius ? {
    ...defaultTheme.radius,
    ...newTheme.radius
  } : defaultTheme.radius;
  // Border
  merged.border = newTheme.border ? {
    ...defaultTheme.border,
    ...newTheme.border
  } : defaultTheme.border;
  // depth
  merged.depth = newTheme.depth ? {
    ...defaultTheme.depth,
    ...newTheme.depth
  } : defaultTheme.depth;
  //breakpoints
  merged.breakpoints = newTheme.breakpoints ? {
    ...defaultTheme.breakpoints,
    ...newTheme.breakpoints
  } : defaultTheme.breakpoints;
  merged.config = newTheme.config;
  return merged;
};
const ThemeProvider = ({
  theme: theme$1,
  children
}) => {
  const mergedTheme = useMemo(() => mergeTheme(theme, theme$1), [theme$1]);
  const mediaQueries = Object.keys(mergedTheme.breakpoints).reduce((reducer, key) => {
    reducer[key] = useMediaQuery(mergedTheme.breakpoints[key]);
    return reducer;
  }, {});
  return /*#__PURE__*/React.createElement(ThemeContext.Provider, {
    value: {
      mediaQueries,
      ...mergedTheme
    }
  }, children);
};

const utils = {
  responsive
};

export { A, Box, Btn, BtnDot, BtnText, Cell, Checkbox, Container, FieldAmount, FieldArea, FieldCheckbox, FieldComposite, FieldDate, FieldDateCalendar, FieldDateSelect, FieldHeight, FieldPhone, FieldRadio, FieldRange, FieldSelect, FieldText, Grid, H1, H2, H3, H4, H5, H6, HiddenInput, Input, Li, List, P, Pagination$1 as Pagination, PanelSteps, PanelTab, ProgressBar, Radio, Range, Select, Spacer, Spinner, Steps, Strong, Table, ThemeContext, ThemeProvider, ToolTip, Transition, Type, ValidationForm, useResponsive, utils };
